import { authoringGroundingLabels } from './authoringGroundingLabels';
import { transcriptExpiryLabels } from './transcriptExpiryLabels';
import { blockedObjectLabels } from './blockedObjectLabels';
import { auditOriginLabels } from './auditOriginLabels';
import { materialReadinessLabels } from './materialReadinessLabels';
import { materialPreviewHistoryLabels } from './materialPreviewHistoryLabels';
import { notificationPanelLabels } from './interfaceResources';
import {writingResources} from './writingResources';
export const namespaces = [
  'common',
  'auth',
  'admin',
  'candidate',
  'invigilator',
  'mainSite',
  'errors',
  'interviews',
  'reports',
  'notifications'
] as const;

export const defaultNamespace = 'common';

export const resources = {
  en: {
    common: {
      notificationPanel: notificationPanelLabels.en,
      transcriptExpiry: transcriptExpiryLabels.en,
      blockedObjects: blockedObjectLabels.en,
      authoringGrounding: authoringGroundingLabels.en,
      writing:writingResources.en,
      completionConfirm: {"submittingTitle": "Confirming submission", "submitting": "Keep this page open while we confirm that the server accepted your completed session.", "uncertainTitle": "Submission not confirmed", "uncertain": "Your submission has not been confirmed. Keep this tab open and do not edit answers, reload or open another session tab. Check your connection, then retry confirmation. The original submission is retained here; contact your administrator if confirmation stays unavailable.", "acceptedTitle": "Submission accepted", "accepted": "The server confirmed your completed session. Processing and result release follow the existing review rules.", "retry": "Retry confirmation", "continue": "Continue"},
      evaluationDraft: {"acceptedDefinition": "Uses the review definition and quality settings captured when this session started.", "processing": {"queued": "Draft queued. The configured worker will process this request; refresh to check.", "running": "Draft generation is running. Refresh to check.", "retry": "Draft generation will retry within its existing attempt budget.", "succeeded": "Processing finished. Refresh to retrieve the current draft.", "failed": "Draft processing stopped. Contact your administrator; a new request cannot reset its attempt budget.", "timed_out": "Draft processing timed out. Refresh to check bounded recovery.", "cancelled": "Draft processing was cancelled. Contact your administrator.", "missing": "Draft dispatch is missing. Administrator investigation is required."}, "reconciliation": {"title": "Ledger reconciliation", "note": "Records how each accepted draft related to the human review entry written at that time. The human review is the authority; a missing record means no comparison was recorded, never agreement.", "unavailable": "Reconciliation history unavailable.", "none": "No review entry has been compared with an advisory draft yet.", "entry": "Review entry {{index}}", "authoritative": "human score", "relation": {"agreement": "within tolerance of the human score", "disagreement": "differs from the human score beyond tolerance", "insufficient_evidence": "draft reported insufficient evidence", "not_comparable": "no human score to compare", "advisory_only": "advisory observation only"}, "evidence": {"current": "draft pinned to the evidence reviewed", "stale": "draft pinned to earlier evidence", "unknown": "evidence match not verifiable"}}, "strengths": "Strengths", "weaknesses": "Areas for improvement", "indicators": "Reported observations", "title": "Advisory review draft", "note": "Uses saved evidence and captured assessment definitions. A draft does not store a grade, decide misconduct, change review decisions or release results. Confidence is uncalibrated.", "final_evaluation": "Assessment draft", "integrity_analysis": "Integrity draft", "generate": "Generate draft", "refresh": "Refresh draft", "loading": "Checking draft?", "unavailable": "Draft unavailable. Verify saved evidence and provider configuration, then refresh or retry. A retry retrieves accepted work when available.", "none": "No draft recorded for this evidence.", "pending": "Draft is processing. Refresh to check; retry only after the previous attempt expires.", "stale": "Saved evidence changed. Generate a new draft before relying on it.", "accepted": "Advisory draft available", "score": "Suggested score", "risk": "Suggested risk", "confidence": "Uncalibrated confidence", "limitations": "Evidence limitations", "references": "Evidence references", "entry": "Transcript entry", "event": "Integrity event", "coverage": "Included {{included}} of {{total}} transcript entries; {{eventsIncluded}} of {{eventsTotal}} integrity events; {{materialIncluded}} of {{materialTotal}} reference characters. {{truncated}} transcript entries shortened.", "unknown": "Unknown"},
      sessionSave: {"conflict": "Autosave stopped because newer progress or a different assessment stage was recorded. Keep this tab open, stop editing here and use one session tab. Do not resubmit answers; contact your administrator before recovering unsaved work.", "unavailable": "Progress has not been confirmed saved. Keep this tab open and check your connection. Avoid reloading or opening another tab while recovery is uncertain; contact your administrator if saving does not resume."},
      completionRepair: {"preview": "Preview recovery", "summary": "Component: {{component}}. Attempts remaining: {{count}}. The preview expires after five minutes.", "part_a": "Part A ranking", "oral": "Oral scoring", "confirm": "I investigated and resolved the transient failure. Retry only the original work within its existing budget; preserve evidence, human decisions and result release.", "apply": "Confirm recovery", "loading": "Checking recovery…", "unavailable": "Recovery is unavailable or requires further investigation. Active, cancelled, exhausted, changed-source or reviewed work cannot be restarted here.", "failed": "Recovery could not be confirmed. Retry this same confirmation to retrieve its receipt, or obtain a new preview if it expired. Do not resubmit answers.", "accepted": "Recovery accepted and audited. Refresh processing status after the worker runs. Results remain subject to existing release controls.", "receipt": "Audit receipt"},
      completionReview: {"title": "Completion processing review", "note": "Inventory of completed sessions with missing or duplicate assessments, stopped scoring, or untracked processing. Refresh to see changes. Recovery requires investigation and an eligible preview; reading this list does not retry scoring or release results.", "empty": "No matching completed sessions in this group.", "session": "Session", "candidate": "Candidate", "interview": "Interview", "assessment": "Assessment", "available": "Available", "missing": "Missing", "ambiguous": "Multiple assessments", "not_submitted": "Not submitted", "more": "Load more"},
      completionStatus: {"title": "Completion processing", "refresh": "Refresh status", "loading": "Checking processing status…", "unavailable": "Status is unavailable. Refresh or try again later.", "unknown": "Scoring status is not recorded. Contact your administrator if results remain pending.", "not_submitted": "This session has not been accepted as completed.", "queued": "Your submission is accepted. Scoring is queued.", "running": "Your submission is accepted. Scoring is in progress.", "retrying": "Your submission is accepted. Scoring will retry automatically.", "processed": "Scoring has been processed.", "needs_attention": "Your submission is recorded. Processing needs administrator attention.", "releaseNote": "Processing status does not release results or change the review decision."},
      app: {
        name: 'VivaAssess',
        tagline: 'Credible AI Interview Platform'
      },
      skipToMainContent: 'Skip to main content',
      nav: {
        home: 'Home',
        product: 'Product',
        solutions: 'Solutions',
        pricing: 'Pricing',
        trust: 'Trust',
        compare: 'Compare',
        contact: 'Contact',
        resources: 'Resources',
        features: 'Features',
        about: 'About',
        teams: 'Teams',
        docs: 'Docs',
        caseStudies: 'Case Studies'
      },
      actions: {
        goToDashboard: 'Go to Dashboard',
        signOut: 'Sign out',
        close: 'Close',
        expandNavigation: 'Expand navigation',
        collapseNavigation: 'Collapse navigation',
        backToPortal: 'Back to Portal',
        help: 'Help'
      },
      labels: {
        language: 'Language',
        searchModules: 'Search modules...',
        pinned: 'Pinned',
        allModules: 'All Modules',
        navigation: 'Navigation',
        noModulesFound: 'No modules found',
        noShort: 'No'
      },
      roles: {
        adminControl: 'Admin Control',
        invigilator: 'Invigilator'
      },
      accessibility: {
        goToHomepage: 'Go to VivaAssess homepage',
        closeNavigationMenu: 'Close navigation menu',
        openNavigationMenu: 'Open navigation menu',
        navigationMenu: 'Navigation menu'
      },
      language: {
        english: 'English',
        french: 'French',
        hungarian: 'Hungarian',
        russian: 'Russian',
        german: 'German'
      },
      privacy: {
        title: 'Privacy',
        accountIntro: 'Review the current notice, download a copy of your data, or request deletion. Exam scores may be kept in anonymized form when academic integrity requires it.',
        loading: 'Loading privacy options...',
        loadFailed: 'Could not load privacy information.',
        actionFailed: 'That request could not be completed.',
        acknowledge: 'I have read this notice',
        acknowledged: 'Acknowledgement recorded.',
        alreadyAcked: 'You have acknowledged the current notice.',
        downloadMyData: 'Download my data',
        requestDeletion: 'Request deletion',
        requestDoNotSell: 'Record do-not-sell preference',
        requestSent: 'Request received.',
        exportReady: 'Your data download started.',
        finishSavedExport: 'Finish saved export',
        freshExport: 'Create fresh export',
        doNotSellTrue: 'This platform does not sell personal information or share it for cross-context advertising.',
        doNotSellFalse: 'Ask your institution how personal information is shared.',
        loginBanner: 'Essential cookies and browser storage only (sign-in, theme, language).',
        readNotice: 'Read the privacy notice',
        ackTitle: 'Privacy notice',
        noNotice: 'No published notice is available.',
        publicTitle: 'Privacy notice',
        publicIntro: 'Published notices only. Sign in to download your own data or request deletion.',
        backToLogin: 'Back to sign in'
      }
    },
    auth: {
      login: {
        welcomeBack: 'Welcome Back',
        subtitle: 'Sign in to continue your VivaAssess workflow',
        emailAddress: 'Email Address',
        password: 'Password',
        signIn: 'Sign In',
        firstTimeTitle: 'First time here?',
        firstTimeBody: 'Use your invitation email to set your password securely.',
        trustedBadge: 'Trusted AI Interviewing',
        premiumDescription: 'A premium interview intelligence platform for fair, explainable, and data-driven hiring decisions.',
        adaptiveAI: 'Adaptive AI',
        adaptiveAIDescription: 'Dynamic follow-up and context-aware probing.',
        biasAware: 'Bias Aware',
        biasAwareDescription: 'Objective, consistent scoring at scale.',
        auditReady: 'Audit Ready',
        auditReadyDescription: 'Traceability and governance by default.',
        interviews: 'Interviews',
        signalAccuracy: 'Signal Accuracy',
        organizations: 'Organizations',
        emailPlaceholder: 'name@organization.com',
        passwordPlaceholder: 'Enter your password',
        toggleTheme: 'Toggle theme'
      },
      mcqRichContent: {
        figureUnavailable: 'Figure not generated yet — regenerate from the manager.'
      }
    },
    admin: {
      auditOrigin: auditOriginLabels.en,
      readinessPreview: materialReadinessLabels.en,
      previewHistory: materialPreviewHistoryLabels.en,
      assignmentReadinessBlocked: "Activate the interview, enable an assessment part, and select an active template for any linked oral placement before assigning.",
      nav: {
        overview: {
          label: 'Overview',
          description: 'Platform status, metrics, and activity'
        },
        interviews: {
          label: 'Interviews',
          description: 'Create and manage interview templates'
        },
        mcqSets: {
          label: 'MCQ Manager',
          description: 'Design and organize MCQ sets'
        },
        materials: {
          label: 'Materials',
          description: 'Upload and curate interview resources'
        },
        candidates: {
          label: 'Candidates',
          description: 'Invite, assign, and monitor candidates'
        },
        sections: {
          label: 'Candidate groups',
          description: 'Batch candidates for scheduling and placement enrolment'
        },
        invigilators: {
          label: 'Invigilators',
          description: 'Manage invigilator assignments'
        },
        proctoringReports: {
          label: 'Proctoring reports',
          description: 'Browse integrity report history and screenshots'
        },
        liveMonitoring: {
          label: 'Live Supervision',
          description: 'Monitor active sessions with real-time camera and screen feeds'
        },
        accounts: {
          label: 'Accounts',
          description: 'Manage all user accounts'
        },
        placement: {
          label: 'Placement Center',
          description: 'Templates, question bank, ranking, and student onboarding'
        },
        myAccount: {
          label: 'My Account',
          description: 'Manage your admin profile and security'
        },
        llmSettings: {
          label: 'LLM Settings',
          description: 'Configure AI providers and models'
        },
        voiceStudio: {
          label: 'AI Voice Studio',
          description: 'Manage human-like interviewer voices and overrides'
        },
        assessments: {
          label: 'Assessments',
          description: 'Review and approve assessment outcomes'
        },
        transcripts: {
          label: 'Transcripts',
          description: 'Browse interview session Q&A transcripts in real time and history'
        },
        feedback: {
          label: 'Interview Feedback',
          description: 'Review candidate experience and export feedback data'
        },
        notifications: {
          label: 'Notifications',
          description: 'Manage delivery history and report email behavior'
        },
        systemIssues: {
          label: 'System Issues',
          description: 'Track platform warnings and incidents'
        },
        resetSettings: {
          label: 'Reset Settings',
          description: 'Restore product defaults across LLM, MCQ, proctoring, and preferences'
        },
        auditLogs: {
          label: 'Audit Logs',
          description: 'Inspect administrative activity logs'
        },
        help: {
          label: 'Help',
          description: 'Role-specific guides for this dashboard'
        },
        userGuides: {
          label: 'User guides',
          description: 'Create and edit in-app guides and screenshots'
        },
        privacy: {
          label: 'Privacy',
          description: 'Notices, subject-rights requests, and retention'
        },
        groupHeadings: {
          examDay: 'Exam day',
          people: 'People',
          content: 'Content',
          review: 'Review',
          platform: 'Platform'
        }
      },
      help: {
        title: 'Help',
        subtitle: 'Published guides for the screens you can use',
        back: 'Back',
        loading: 'Loading guides...',
        loadFailed: 'Failed to load guides.',
        emptyTitle: 'No guides yet',
        emptySubtitle: 'Published guides for your role will appear here.',
        guides: 'Guides',
        noSteps: 'This guide has no steps yet.',
        selectGuide: 'Select a guide to read it.'
      },
      userGuides: {
        title: 'User guides',
        subtitle: 'Create, publish, and translate in-app help for each role.',
        listTitle: 'Guides',
        loading: 'Loading guides...',
        empty: 'No guides yet',
        selectOrCreate: 'Select a guide or create one.',
        fields: {
          title: 'Title',
          slug: 'Slug',
          audience: 'Audience',
          locale: 'Locale',
          module: 'Module',
          noModule: 'No module',
          published: 'Published',
          summary: 'Summary'
        },
        audiences: {
          candidate: 'Candidate',
          invigilator: 'Invigilator',
          admin: 'Admin',
          super_admin: 'Super Admin'
        },
        status: {
          published: 'Published',
          draft: 'Draft'
        },
        filters: {
          allAudiences: 'All audiences',
          allLocales: 'All locales'
        },
        steps: {
          title: 'Steps',
          add: 'Add step',
          stepNumber: 'Step {{count}}',
          titlePlaceholder: 'Step title',
          bodyPlaceholder: 'Explanation (plain text or simple markdown)',
          uploadImage: 'Upload screenshot',
          removeImage: 'Remove image'
        },
        actions: {
          create: 'Create guide',
          save: 'Save',
          saving: 'Saving...',
          delete: 'Delete',
          cancel: 'Cancel',
          duplicate: 'Copy to locale',
          preview: 'Preview'
        },
        messages: {
          loadFailed: 'Failed to load guides.',
          created: 'Guide created.',
          saved: 'Guide saved.',
          saveFailed: 'Failed to save guide.',
          deleted: 'Guide deleted.',
          deleteFailed: 'Failed to delete guide.',
          duplicated: 'Locale copy created.',
          duplicateFailed: 'Failed to duplicate guide.',
          saveStepsFirst: 'Save the steps first, then upload screenshots.',
          imageUploaded: 'Screenshot uploaded.',
          imageFailed: 'Failed to update screenshot.'
        },
        modals: {
          createTitle: 'Create user guide',
          previewTitle: 'Preview',
          deletePrompt: 'Delete this guide and its screenshots?'
        }
      },
      privacy: {
        title: 'Privacy',
        subtitle: 'Notices, subject-rights requests, retention, and subprocessors. This is an operations console, not a legal certification.',
        tabs: {
          requests: 'Requests',
          notices: 'Notices',
          settings: 'Settings',
          subprocessors: 'Subprocessors',
          incidents: 'Incidents',
          holds: 'Blocked objects'
        },
        loadFailed: 'Failed to load privacy data.',
        actionFailed: 'The privacy action failed.',
        allStatuses: 'All statuses',
        subject: 'Subject',
        type: 'Type',
        status: 'Status',
        due: 'Due',
        actionsLabel: 'Actions',
        export: 'Export',
        erase: 'Erase',
        globalAuthorityNotice: 'Institution staff can review scoped requests. Subject-wide exports and erasures require an active super-administrator.',
        eraseReviewTitle: 'Review erasure request',
        executionRecords: 'Erasure execution records',
        downloadExport: 'Download export',
        requestAccountErasure: 'Request erasure',
        accountErasurePrompt: 'Submit an erasure review request for {{name}} ({{email}})? The account remains unchanged until authorized Privacy Center review and execution.',
        accountErasureRequested: 'Erasure review request {{id}} recorded in Privacy Center. The account has not been erased.',
        refreshRecords: 'Refresh records',
        continueCleanup: 'Continue captured cleanup',
        cleanupReviewTitle: 'Review captured cleanup',
        cleanupReviewBody: 'Data erasure is already committed. Continue only the saved file cleanup intents, up to 25 files per action. This does not erase or anonymize the account again. Changed, shared or unproven files remain blocked for inspection.',
        cleanupSummary: 'Data erasure committed. Files pending: {{pending}}; blocked: {{blocked}}.',
        cleanupStillPending: 'Captured cleanup processed. Some files still require inspection or another cleanup batch.',
        cleanupCompleted: 'Captured file cleanup and the execution receipt are complete.',
        eraseReviewBody: 'Verify the subject, authority, retention policy and any holds before proceeding. This action affects the whole account across institutions. Assessment history may be retained in anonymized form. Interrupted execution requires reconciliation before further action.',
        cancelReview: 'Cancel',
        confirmErase: 'Confirm reviewed erasure',
        reconciliationNeeded: 'Execution started; inspect its record before further action.',
        reject: 'Reject',
        noRequests: 'No requests yet.',
        published: 'Published',
        draft: 'Draft',
        selectNotice: 'Select a notice to edit.',
        saveDraft: 'Save draft',
        publish: 'Publish',
        preview: 'Preview',
        acknowledgements: 'Acknowledgements',
        loading: 'Loading...',
        evidenceDays: 'Evidence retention (days)',
        exportDays: 'Export file retention (days)',
        exportLineageNote: 'A fresh export includes owned interview text, institution membership, and metadata for turns, answers, guidance slots, provider jobs and material counts. It omits private prompts, material text, answer keys, digests, provider payloads and unregistered copies. It is not a complete subject-data file.',
        lawfulBasis: 'Proctoring lawful-basis label',
        dpoEmail: 'DPO contact email',
        doNotSell: 'We do not sell personal information',
        requireAck: 'Require privacy acknowledgement after login',
        eraseMode: 'Default erase mode',
        transferNote: 'Cross-border transfer note',
        saved: 'Settings saved.',
        saving: 'Saving...',
        save: 'Save',
        subprocessorName: 'Name',
        add: 'Add',
        remove: 'Remove',
        incidentTitle: 'Incident title'
      },
      voiceStudio: {
        title: 'AI Voice Studio',
        subtitle: 'Configure the oral examiner voice: provider, catalog default, and optional per-interview override.',
        tabs: {
          label: 'Voice Studio sections',
          voices: 'Voices',
          provider: 'Provider',
          assignment: 'Assignment'
        },
        status: {
          providerChip: 'Provider: {{name}}',
          globalChip: 'Global: {{voice}}',
          globalFallback: 'Using fallback',
          interviewChip: 'Interview: {{title}}',
          noInterview: 'No interview selected'
        },
        filters: {
          title: 'Catalog filters',
          searchLabel: 'Search',
          languageLabel: 'Language',
          accentLabel: 'Accent',
          genderLabel: 'Gender',
          styleLabel: 'Speaking style',
          providerLabel: 'Provider',
          searchPlaceholder: 'Search voices…',
          allLanguages: 'All languages',
          allAccents: 'All accents',
          allGenders: 'All genders',
          allSpeakingStyles: 'All speaking styles',
          allProviders: 'All providers',
          moreFilters: 'More filters',
          fewerFilters: 'Fewer filters',
          clearFilters: 'Clear filters'
        },
        catalog: {
          title: 'Voice catalog',
          showing: 'Showing {{shown}} of {{total}}',
          loading: 'Loading voices…',
          noMatches: 'No voices match these filters.',
          defaultDescription: 'Natural neural voice profile for interviews.',
          qualityLabel: 'Quality',
          free: 'Free',
          languageMatch: 'Matches interview',
          nowPlaying: 'Now playing',
          emptyHint: 'Try a different search or clear filters to see every voice.',
          selected: 'Selected'
        },
        providerConfig: {
          title: 'Neural Voice Provider Configuration',
          loading: 'Loading provider configuration...',
          sourceDb: 'Saved in database',
          sourceEnv: 'Environment defaults',
          configured: 'Configured',
          keyOptional: 'Key Optional',
          missingKey: 'Missing Key',
          testPassed: 'Test Passed',
          testFailed: 'Test Failed',
          providerLabel: 'Provider',
          modelLabel: 'Model',
          modelsDirLabel: 'Voice Models Folder',
          baseUrlLabel: 'Base URL',
          apiKeyLabel: 'API Key',
          piperPathHelp: 'Local folder of .onnx voice models. In Docker Compose this is typically /app/piper-voices.',
          confirmClearKey: 'Clear the stored API key for this provider? The key is not shown and cannot be recovered from the UI.',
          latencyMs: '{{ms}} ms',
          modelAvailable: 'Configured model is available.',
          modelUnavailable: 'Configured model was not listed by the provider.',
          connectionSuccessful: 'Connection successful.',
          groupSpeaksWith: 'This group speaks with {{name}}.',
          showAdvanced: 'Show advanced settings',
          hideAdvanced: 'Hide advanced settings',
          summaryKeyOn: 'Key configured',
          summaryKeyOff: 'No key stored'
        },
        tuning: {
          title: 'Voice Tuning',
          selectedVoice: 'Selected voice: {{name}} ({{provider}})',
          languageLabel: 'Language',
          styleLabel: 'Speaking Style',
          stylePlaceholder: 'calm, professional, energetic',
          rateLabel: 'Speaking Rate',
          pitchLabel: 'Pitch',
          pitchFallbackOnly: '(provider fallback only)',
          minLabel: 'Min {{value}}',
          maxLabel: 'Max {{value}}',
          pronunciationTitle: 'Pronunciation',
          pronunciationHelp: 'These terms are spoken differently. The question on screen does not change.',
          termLabel: 'Term',
          spokenLabel: 'Spoken as',
          addTerm: 'Add term',
          removeTerm: 'Remove'
        },
        preview: {
          title: 'Voice Preview',
          sampleTextLabel: 'Sample text',
          statusLabel: 'Status',
          ready: 'Ready',
          generating: 'Generating audio...',
          playing: 'Playing',
          paused: 'Paused',
          error: 'Error',
          elapsed: '{{current}} / {{duration}}'
        },
        assignment: {
          title: 'Voice Assignment Rules',
          globalTitle: 'Global Default Voice',
          globalDescription: 'Used whenever no interview-specific override exists.',
          noGlobalDefault: 'No global default configured',
          overrideTitle: 'Per-Interview Override',
          overrideDescription: 'Overrides the global setting for one interview.',
          interviewLabel: 'Interview',
          selectInterview: 'Select interview...',
          loadingOverride: 'Loading interview override...',
          overrideActive: 'Override active: {{provider}} / {{voice}}',
          noInterviewOverride: 'No override configured for this interview.',
          selectInterviewHint: 'Select an interview to configure an override.',
          interviewLanguage: 'Interview assessment language: {{language}}',
          languageMismatch: 'Interview language is {{interviewLanguage}} ({{bcp47}}), but the selected voice language is {{voiceLanguage}}. Opening/closing speech may sound incorrect — choose a matching voice or language code.',
          useMatchingVoice: 'Use matching catalog voice ({{name}}) — not saved until you click Save',
          effectiveTitle: 'Effective voice for this interview',
          effectiveSelectInterview: 'Select an interview to see the resolved voice.',
          effectiveLoading: 'Resolving effective voice…',
          effectiveUnavailable: 'Effective voice is not available yet.',
          effectiveSource: 'Source',
          sourceInterview: 'Interview override',
          sourceGlobal: 'Global default',
          sourceFallback: 'Safe fallback',
          sourceLanguageMatch: 'Language-matched catalog voice',
          sourceUnknown: 'Unknown',
          languageMatchLabel: 'Language match',
          languageMatchYes: 'Yes',
          languageMatchNo: 'No',
          effectiveOrderTitle: 'Effective Voice Resolution Order',
          orderOverride: 'Interview-specific override',
          orderGlobal: 'Global default voice',
          orderFallback: 'Safe fallback voice',
          fallbackReadOnly: 'Safe fallback is environment-driven (VOICE_SAFE_FALLBACK_*) and is not edited here.',
          chainTitle: 'What the candidate will hear',
          stepActive: 'Active'
        },
        actions: {
          refresh: 'Refresh',
          save: 'Save',
          saving: 'Saving...',
          testing: 'Testing...',
          testConnection: 'Test Connection',
          clearing: 'Clearing...',
          clearKey: 'Clear Key',
          play: 'Play',
          pause: 'Pause',
          resume: 'Resume',
          stop: 'Stop',
          retry: 'Retry',
          saveGlobal: 'Save Global',
          resetToDefaults: 'Reset to defaults',
          saveInterviewOverride: 'Save Interview Override',
          clearInterviewOverride: 'Clear Interview Override'
        },
        messages: {
          catalogLoadFailed: 'Unable to load voice catalog',
          loadSettingsFailed: 'Unable to load voice studio settings.',
          selectVoiceForPreview: 'Select a voice profile before previewing audio.',
          previewTextRequired: 'Preview text is required.',
          speechSynthesisRequired: 'App Library voice preview requires browser speech synthesis support.',
          localPreviewFailed: 'Local App Library voice preview failed.',
          audioPlaybackFailed: 'Audio playback failed. Try again.',
          previewGenerationFailed: 'Preview generation failed',
          resumePreviewFailed: 'Unable to resume preview audio.',
          providerRequired: 'Provider is required.',
          baseUrlRequired: 'Base URL is required.',
          modelRequired: 'Model is required.',
          providerConfigSaved: 'Neural voice provider configuration saved.',
          saveProviderConfigFailed: 'Unable to save provider configuration.',
          providerFieldsRequiredForTest: 'Provider, Base URL, and Model are required before testing.',
          connectionTestFailed: 'Connection test failed.',
          providerFieldsRequiredForClear: 'Provider, Base URL, and Model are required before clearing key.',
          providerKeyCleared: 'Provider API key cleared.',
          clearProviderKeyFailed: 'Unable to clear provider API key.',
          selectVoiceForGlobal: 'Select a voice before saving global settings.',
          globalSaved: 'Global voice setting saved.',
          saveGlobalFailed: 'Unable to save global setting.',
          globalCleared: 'Global voice setting cleared (product default resolution restored).',
          clearGlobalFailed: 'Unable to clear global setting.',
          selectInterviewForOverride: 'Select an interview before saving an override.',
          selectVoiceForOverride: 'Select a voice before saving interview override.',
          overrideSaved: 'Interview voice override saved.',
          saveOverrideFailed: 'Unable to save interview voice override.',
          selectInterviewForClearOverride: 'Select an interview before clearing an override.',
          overrideCleared: 'Interview override cleared.',
          clearOverrideFailed: 'Unable to clear interview override.',
          selectVoiceToTune: 'Select a voice profile to tune its behavior.'
        },
        hints: {
          storedKey: 'Stored key: ****{{last4}}',
          keyOptional: 'No API key stored. This provider can run without a key when your endpoint allows anonymous access.',
          keyMissing: 'No API key stored for this provider.'
        },
        placeholders: {
          keepCurrentKey: 'Leave blank to keep current key',
          optionalApiKey: 'Optional: enter provider API key if your endpoint requires authentication',
          enterApiKey: 'Enter provider API key'
        },
        guidance: {
          title: 'Naturalness Guidance',
          item1: 'Use punctuation-aware preview text to hear realistic pauses and emphasis.',
          item2: 'Keep rate between 0.90 and 1.10 for human pacing in most interviews.',
          item3: 'Use style hints like "professional", "warm", or "analytical" for expressiveness.'
        },
        reset: {
          globalTitle: 'Reset global default voice',
          globalScope: 'This group (global voice override)',
          globalWill: 'No global default — interviews fall back to product voice resolution',
          globalWont: {
            overrides: 'Per-interview voice overrides',
            provider: 'Provider API configuration and catalog'
          }
        }
      },
      assessmentReview: {
        title: 'Assessment review',
        subtitle: 'Review AI scores, Part A/B evidence, integrity, and record a decision.',
        searchPlaceholder: 'Search candidate, email, or interview',
        loading: 'Loading assessments…',
        empty: 'No assessments match these filters.',
        emptyCriteria: 'No criterion scores recorded.',
        emptyMcq: 'No Part A attempt on this session.',
        emptyTranscript: 'No oral transcript available.',
        emptyHistory: 'No review history yet.',
        filters: {
          allStatuses: 'All statuses',
          pending: 'Pending',
          inReview: 'In review',
          reviewed: 'Reviewed',
          approved: 'Approved',
          rejected: 'Rejected',
          requiresHumanReview: 'Needs human review',
          resultsHidden: 'Results hidden',
          sortBy: 'Sort by',
          order: 'Order',
          from: 'From',
          to: 'To',
          clear: 'Clear filters',
          sortCreated: 'Created',
          sortStatus: 'Status',
          sortAi: 'AI score',
          sortFinal: 'Final score',
          sortIntegrity: 'Integrity',
          orderAsc: 'Ascending',
          orderDesc: 'Descending'
        },
        status: {
          pending: 'Pending',
          inReview: 'In review',
          reviewed: 'Reviewed',
          approved: 'Approved',
          rejected: 'Rejected'
        },
        origin: {
          human: 'Decided by administrator',
          auto_approve: 'Auto-approved',
          ai: 'Decided by AI',
          fallback: 'Sent back to human review'
        },
        aiReview: {
          confidence: 'AI confidence',
          fallbackReason: 'Fallback reason'
        },
        decision: {
          proceed: 'Proceed',
          reject: 'Reject',
          hold: 'Hold',
          reinterview: 'Reinterview'
        },
        tabs: {
          overview: 'Overview',
          partA: 'Part A',
          partB: 'Part B',
          integrity: 'Integrity',
          review: 'Review'
        },
        fields: {
          candidate: 'Candidate',
          interview: 'Interview',
          duration: 'Duration',
          integrityScore: 'Integrity',
          aiScore: 'AI score',
          aiConfidence: 'AI confidence',
          recommendation: 'Recommendation',
          finalScore: 'Final score',
          mcqScore: 'MCQ score',
          reviewer: 'Reviewer',
          createdAt: 'Created',
          sessionStarted: 'Started',
          sessionCompleted: 'Completed',
          criterion: 'Criteria',
          strengths: 'Strengths',
          weaknesses: 'Weaknesses',
          detailedFeedback: 'Detailed feedback',
          comments: 'Comments',
          scoreReason: 'Score override reason',
          reviewHistory: 'Review history',
          unknownReviewer: 'Unknown reviewer',
          adminOverride: 'admin override',
          aiOrMcq: 'AI or MCQ'
        },
        actions: {
          viewDetails: 'View details',
          startReview: 'Start review',
          markReviewed: 'Mark reviewed',
          approve: 'Approve',
          reject: 'Reject',
          hold: 'Hold',
          reinterview: 'Reinterview',
          saveScore: 'Save score',
          downloadPdf: 'Download PDF',
          downloadJson: 'Download JSON',
          grantRetake: 'Grant retake',
          toggleVisibility: 'Toggle result visibility',
          messageCandidate: 'Message candidate',
          overrideQuestionScore: 'Override oral score',
          openPlacement: 'Open Placement Center',
          openProctoring: 'Open proctoring reports',
          openTranscript: 'Open transcripts',
          openAuditLogs: 'Open audit logs',
          bulkSetStatus: 'Set status for selected',
          apply: 'Apply',
          selectPage: 'Select page',
          assignmentRequired: 'No linked assignment for this session'
        },
        badges: {
          requiresHumanReview: 'Human review',
          resultsHidden: 'Results hidden',
          proctoringFlags: 'Proctoring flags',
          figure: 'Figure'
        },
        pagination: {
          range: '{{start}}–{{end}} of {{total}}',
          previous: 'Previous',
          next: 'Next'
        },
        mcq: {
          correct: 'Correct',
          incorrect: 'Incorrect',
          unanswered: 'Unanswered'
        },
        placement: {
          partA: 'Part A (MCQ)',
          partB: 'Part B (Oral)',
          notLinked: 'This interview is not linked to a placement, so Part A/B ranking scores are not available here.',
          transferHistory: 'Transfer history',
          humanAdjusted: 'Human-adjusted',
          autoScored: 'Auto',
          aiScored: 'AI'
        },
        recommendation: {
          strongly_recommend: 'Strongly recommend',
          recommend: 'Recommend',
          recommended: 'Recommended',
          needsReview: 'Needs further review',
          notRecommended: 'Not recommended',
          not_recommend: 'Do not recommend',
          strongly_not_recommend: 'Strongly do not recommend',
          strong_hire: 'Strong hire',
          hire: 'Hire',
          no_hire: 'No hire',
          neutral: 'Neutral',
          pending: 'Pending',
          provisional: 'Provisional'
        },
        messages: {
          updateStatusFailed: 'Failed to update assessment status.',
          loadDetailsFailed: 'Failed to load assessment details.',
          approved: 'Assessment approved.',
          rejected: 'Assessment rejected.',
          hold: 'Assessment held.',
          reinterview: 'Reinterview recorded.',
          submitReviewFailed: 'Failed to submit the review.',
          reportDownloaded: 'Report downloaded.',
          downloadReportFailed: 'Failed to download the report.',
          movedInReview: 'Assessment moved to in-review.',
          markedReviewed: 'Assessment marked as reviewed.',
          scoreSaved: 'Score override saved.',
          visibilityUpdated: 'Result visibility updated.',
          overrideSaved: 'Oral score override saved.',
          retakeGranted: 'Retake granted.',
          bulkComplete: 'Updated {{applied}}, failed {{failed}}.'
        },
        validation: {
          commentsRequired: 'Comments are required.',
          scoreOverrideRange: 'Score must be between 0 and 100.',
          scoreReasonRequired: 'A reason is required when changing a score.'
        },
        errors: {
          loadListFailed: 'Failed to load assessments.',
          saveScoreFailed: 'Failed to save the score override.',
          grantRetakeFailed: 'Failed to grant a retake.',
          visibilityFailed: 'Failed to update result visibility.',
          overrideFailed: 'Failed to override the oral score.'
        }
      },
      placement: {
        common: {
          yes: 'Yes',
          no: 'No',
          notApplicable: '—',
          close: 'Close',
          refresh: 'Refresh'
        },
        errors: {
          loadRankingFailed: 'Failed to load ranking',
          exportFailed: 'Export failed',
          loadTemplatesFailed: 'Failed to load templates',
          saveFailed: 'Save failed',
          importFailed: 'Import failed',
          retentionSaveFailed: 'Failed to save retention setting',
          resultVisibilityFailed: 'Failed to update result visibility',
          transferPreviewFailed: 'Failed to preview the result transfer',
          transferFailed: 'Failed to transfer results'
        },
        tabs: {
          placements: 'Placements',
          ranking: 'Ranking',
          fairness: 'Fairness',
          outcomes: 'Outcomes',
          reports: 'Reports',
          template: 'Template',
          declarations: 'Declarations',
          bank: 'Question Bank',
          students: 'Student Onboarding'
        },
        switcher: {
          label: 'Active placement',
          empty: 'No placements yet',
          stats: '{{enrolled}} enrolled · {{completed}} completed · {{interviews}} interviews'
        },
        placements: {
          create: 'Create placement',
          name: 'Name',
          namePlaceholder: 'Name (e.g. Autumn 2026)',
          code: 'Code',
          codePlaceholder: 'Code (optional)',
          partAPoints: 'Part A max points (MCQ)',
          partBPoints: 'Part B max points (oral hint)',
          partBPointsHint: 'Oral max still comes from the scored question set; this budget is a planning hint.',
          createButton: 'Create',
          empty: 'No placements yet.',
          showArchived: 'Show archived placements ({{count}})',
          columns: {
            name: 'Name',
            mode: 'Mode',
            status: 'Status',
            enrolled: 'Enrolled',
            progress: 'Progress',
            actions: 'Actions'
          },
          statuses: {
            draft: 'Draft',
            open: 'Open',
            closed: 'Closed',
            archived: 'Archived'
          },
          modes: {
            hybrid: 'Hybrid',
            part_a: 'Part A',
            part_b: 'Part B'
          },
          actions: {
            open: 'Open',
            close: 'Close',
            edit: 'Edit',
            duplicate: 'Duplicate',
            archive: 'Archive',
            delete: 'Delete'
          },
          confirm: {
            cancel: 'Cancel',
            proceed: 'Continue',
            duplicateTitle: 'Duplicate placement',
            duplicateBody: 'Create a draft copy of “{{name}}”? The oral template is copied too; candidates, results and enrollments are not.',
            archiveTitle: 'Archive placement',
            archiveBody: 'Archive “{{name}}”? It stops accepting new sessions but keeps all results and stays recoverable.'
          },
          edit: {
            title: 'Edit placement',
            description: 'Description',
            status: 'Status',
            opensAt: 'Opens at',
            closesAt: 'Closes at',
            save: 'Save changes',
            cancel: 'Cancel',
            staleWarning: 'Changing the assessment mode or point budget does not rescore the {{count}} result(s) already stored. The ranking will stay on the old totals until those results are re-scored or transferred.'
          },
          delete: {
            title: 'Delete placement',
            intro: 'Permanently delete “{{name}}” ({{code}}). This cannot be undone.',
            loading: 'Checking what this placement holds…',
            willDestroy: 'Will be permanently deleted',
            willUnlink: '{{count}} linked interview(s) will be kept but unlinked from this placement.',
            counts: {
              results: 'Results and ranking rows',
              question_sets: 'Generated oral question sets',
              declarations: 'Candidate declarations',
              academic_outcomes: 'Academic outcome records',
              enrollments: 'Enrollments',
              section_links: 'Linked candidate groups'
            },
            blocked: 'This placement still holds assessment data, so it cannot be deleted. Archive it instead, or ask a super admin to force-delete it.',
            forceAcknowledge: 'I understand this destroys assessment data that cannot be recovered.',
            typeToConfirm: 'Type the placement code “{{code}}” to confirm',
            recommendArchive: 'Archiving is the safer option: it hides the placement from day-to-day use while keeping every result.',
            confirm: 'Delete permanently',
            cancel: 'Cancel'
          },
          errors: {
            createFailed: 'Failed to create placement',
            updateFailed: 'Failed to update placement',
            duplicateFailed: 'Failed to duplicate placement',
            deleteFailed: 'Failed to delete placement',
            dependenciesFailed: 'Failed to check what this placement holds'
          }
        },
        ranking: {
          title: 'Placement ranking',
          name: 'Name',
          neptun: 'Neptun',
          score: 'Score',
          mcqScore: 'Part A (MCQ)',
          oralScore: 'Part B (Oral)',
          percentile: 'Percentile',
          integrity: 'Integrity',
          flags: 'Flags',
          acceptsCourse: 'Alg. course',
          acceptsAdvanced: 'Adv. group',
          cutoffLabel: 'Advanced group cut-off score',
          cutoffPreview: '{{eligible}} students above cut-off, {{accepting}} accept the advanced group',
          declarationDetail: 'Declaration',
          empty: 'No students yet.',
          exportCsv: 'CSV',
          exportXlsx: 'XLSX',
          releaseScores: 'Release scores',
          hideScores: 'Hide scores',
          confirmRelease: 'Release scores for all assignments linked to this placement? The full ranking table stays instructor-only. Released candidates will see their own scores and rank in the portal.',
          confirmHide: 'Hide scores for all assignments linked to this placement? Candidates will not see numbers or rank until you release them again.',
          scoresReleased: 'Scores released for {{count}} assignment(s). Candidates can now see their own result and rank; the full ranking table stays instructor-only.',
          scoresHidden: 'Scores hidden for {{count}} assignment(s).'
        },
        transfer: {
          title: 'Transfer results',
          sourcePlacement: 'Source placement',
          components: 'Components to transfer',
          partA: 'Part A (MCQ)',
          partB: 'Part B (Oral)',
          partUnavailable: 'This placement does not score this part.',
          conflictOverride: 'Override existing results',
          conflictFill: 'Only fill missing results',
          reason: 'Reason',
          preview: 'Preview',
          apply: 'Apply transfer',
          previewRequired: 'Preview the transfer before applying.',
          success: 'Transferred {{applied}}, skipped {{skipped}}.',
          selectedCount: '{{count}} selected',
          noOtherPlacements: 'No other placements in this group.',
          skip: {
            notEnrolled: 'Not enrolled in the target placement',
            noSourceResult: 'No result in the source placement',
            sourceMissingPartA: 'Source has no Part A score',
            sourceMissingPartB: 'Source has no Part B score',
            targetHasPartA: 'Target already has a Part A score',
            targetHasPartB: 'Target already has a Part B score'
          }
        },
        template: {
          title: 'Placement template',
          totalPoints: 'Total points',
          namePlaceholder: 'Template name',
          save: 'Save & activate',
          saved: 'Template saved.',
          mustBe35: 'Total must be exactly 35 points across 10 questions',
          topicFallback: 'Topic {{number}}',
          formats: {
            numeric: 'Auto-scored numeric',
            verbal: 'AI-evaluated verbal',
            numeric_with_followup: 'Numeric + verbal follow-up'
          }
        },
        bank: {
          importTitle: 'Import Moodle XML question bank',
          importHint: 'Upload the legacy Moodle XML export. Categories, numeric/cloze answers, TeX and embedded images are parsed automatically; the three declaration questions (q1–q3) are skipped.',
          chooseFile: 'Choose XML file',
          importing: 'Importing…',
          summaryTitle: 'Import summary',
          imported: 'Imported',
          skippedDeclaration: 'Skipped (declaration q1–q3)',
          unsupported: 'Unsupported interactive',
          images: 'Images stored',
          categories: 'Category nodes',
          errors: 'Errors',
          unmapped: 'Families without topic mapping',
          browseTitle: 'Reference questions',
          allTopics: 'All topics',
          familyFilter: 'Family…',
          topic: 'topic',
          points: 'pts',
          referenceOnly: 'reference-only',
          empty: 'No reference questions imported yet.'
        },
        students: {
          title: 'Bulk student onboarding',
          hint: 'Upload a CSV or XLSX roster with columns: name, email, and optional Neptun code (max 500 rows). Name and email are required; Neptun may be omitted or blank. Accounts are created and invitation emails are sent in batches.',
          chooseFile: 'Choose roster file',
          importing: 'Importing…',
          created: 'Created',
          skipped: 'Skipped',
          invitations: 'Invitations dispatched',
          totalRows: 'Total rows',
          rowErrors: 'Validation errors per row',
          retentionTitle: 'Proctoring evidence retention (GDPR)',
          retentionHint: 'Proctoring evidence older than this many days is eligible for deletion. Per-student data export and deletion are available from the ranking table.',
          days: 'days',
          saveRetention: 'Save'
        },
        declaration: {
          item1Short: 'Accepts Algorithmic Thinking course',
          item2Short: 'Accepts advanced group',
          item3Short: 'Programming background (info only — excluded from scoring)',
          noBackground: 'Has not learned programming before'
        },
        declarationForms: {
          title: 'Declaration forms',
          subtitle: 'Create questionnaires and link one form to many placements. Candidates fill the form linked to their placement.',
          create: 'Create form',
          edit: 'Edit',
          duplicate: 'Duplicate',
          archive: 'Archive',
          save: 'Save',
          cancel: 'Cancel',
          name: 'Name',
          description: 'Description',
          status: 'Status',
          addQuestion: 'Add question',
          labelEn: 'Label (English)',
          labelHu: 'Label (Hungarian)',
          instructorOnly: 'Instructor only (never sent to the AI)',
          linkPlacements: 'Link to placements',
          linkedForm: 'Declaration form',
          none: 'None — not required',
          defaultBadge: 'default',
          columns: { name: 'Name', status: 'Status', linked: 'Linked placements', actions: 'Actions' },
          statuses: { draft: 'Draft', published: 'Published', archived: 'Archived' },
          types: {
            boolean: 'Yes / No',
            checkbox: 'Checkbox',
            text: 'Short text',
            enum: 'Choice',
            repeating_group: 'Repeating rows'
          },
          errors: {
            loadFailed: 'Failed to load declaration forms',
            saveFailed: 'Failed to save the declaration form'
          }
        }
      },
      candidateManagement: {
        header: {
          title: 'Candidate Management',
          subtitle: 'Manage candidates and schedule AI-powered interviews'
        },
        stats: {
          totalCandidates: 'Total Candidates',
          active: 'Active',
          scheduled: 'Scheduled',
          unscheduled: 'Unscheduled'
        },
        actions: {
          addCandidate: 'Add Candidate',
          bulkImport: 'Bulk Import',
          exportCsv: 'Export CSV',
          scheduleBatch: 'Schedule Batch ({{count}})',
          unselectVisible: 'Unselect Visible',
          selectAllVisible: 'Select All Visible',
          clearSelection: 'Clear Selection',
          close: 'Close',
          runImport: 'Run Import',
          importing: 'Importing...',
          addFirstCandidate: 'Add Your First Candidate',
          cancel: 'Cancel',
          removeCandidate: 'Remove Candidate',
          viewProfile: 'View Profile',
          scheduleInterview: 'Schedule Interview',
          reschedule: 'Reschedule',
          setSchedule: 'Set Schedule',
          sendEmail: 'Send Email',
          resendInvitation: 'Resend Invitation',
          remove: 'Remove',
          assignNow: 'Assign now',
          setScheduleLower: 'Set schedule',
          view: 'View',
          rescheduleInterview: 'Reschedule Interview',
          delete: 'Delete',
          enableDetailedResults: 'Enable detailed results',
          hideDetailedResults: 'Hide detailed results',
          cancelling: 'Cancelling...',
          cancelAssignment: 'Cancel Assignment',
          grantRetake: 'Grant retake',
          editProfile: 'Edit Profile'
        },
        filters: {
          searchPlaceholder: 'Search by name, email, or Neptun...',
          allStatus: 'All Status',
          pendingInvitation: 'Pending Invitation',
          allInterviews: 'All Interviews',
          scheduled: 'Scheduled',
          unscheduled: 'Unscheduled',
          allSections: 'All groups',
          allDeadlines: 'All Deadlines',
          dueSoonHours: 'Due Soon ({{hours}}h)',
          dueSoonHint: 'Due Soon includes deadlines in the next {{hours}} hours.',
          clearChipFilter: 'Clear {{label}} filter',
          filterByChip: 'Filter by {{label}}'
        },
        status: {
          pending: 'Pending',
          active: 'Active',
          inactive: 'Inactive',
          suspended: 'Suspended'
        },
        assignmentStatus: {
          scheduled: 'Scheduled',
          pending: 'Pending',
          in_progress: 'In progress',
          completed: 'Completed',
          cancelled: 'Cancelled'
        },
        deadline: {
          upcoming: 'Upcoming',
          dueSoon: 'Due Soon',
          expired: 'Expired',
          noDeadline: 'No Deadline'
        },
        relativeTime: {
          inDays: 'in {{count}} days',
          tomorrow: 'Tomorrow',
          inHours: 'in {{count}}h',
          startingSoon: 'Starting soon',
          past: 'Past'
        },
        selection: {
          selected_one: '{{count}} candidate selected',
          selected_other: '{{count}} candidates selected',
          visibleWithFilters_one: '{{count}} selected candidate visible with current filters',
          visibleWithFilters_other: '{{count}} selected candidates visible with current filters'
        },
        loading: 'Loading candidates...',
        empty: {
          noMatchTitle: 'No candidates match your filters',
          noMatchDescription: 'No matching slots, open windows, or roster rows for these filters.',
          noCandidatesTitle: 'No candidates yet',
          noCandidatesDescription: 'Add your first candidate to start scheduling AI-powered interviews.'
        },
        fields: {
          neptun: 'Neptun'
        },
        schedule: {
          kindSlot: 'Slot',
          kindWindow: 'Window',
          kindNone: 'Unscheduled',
          windowOpen: 'Window open',
          window: 'Assessment window'
        },
        card: {
          interviewsCount_other: '{{count}} interviews',
          deadline: 'Deadline',
          interviewAssigned: 'Interview assigned',
          noInterviewAssigned: 'No interview assigned'
        },
        table: {
          candidate: 'Candidate',
          status: 'Status',
          interview: 'Interview',
          resultsAccess: 'Results Access',
          deadline: 'Deadline',
          created: 'Created',
          actions: 'Actions',
          moreAssignments_one: '+{{count}} more assignment',
          moreAssignments_other: '+{{count}} more assignments',
          schedulePending: 'Schedule pending',
          notAssigned: 'Not assigned',
          hidden: 'Hidden',
          visible: 'Visible'
        },
        aria: {
          selectCandidate: 'Select {{name}}',
          selectAllVisible: 'Select all visible candidates',
          openMenu: 'Actions for {{name}}'
        },
        modals: {
          addNewCandidate: 'Add New Candidate',
          bulkImportCandidates: 'Bulk Import Candidates',
          scheduleAssessment: 'Schedule Assessment',
          candidateProfile: 'Candidate Profile',
          removeCandidate: 'Remove Candidate',
          editProfile: 'Edit Profile',
          sendMessage: 'Send message'
        },
        edit: {
          fullName: 'Full name',
          neptunOptional: 'Neptun code (optional)',
          neptunPlaceholder: '6 alphanumeric characters',
          neptunInvalid: 'Neptun code must be 6 alphanumeric characters, or left blank.',
          status: 'Status',
          save: 'Save',
          saving: 'Saving...'
        },
        bulkImport: {
          csvIntro: 'Paste CSV rows with headers: {{headers}}',
          neptunHint: '{{neptunCode}} is optional — leave it blank when the roster has no code. When present it must be 6 alphanumeric characters; it is stored on the candidate profile and shown in the placement ranking export. {{interviewId}}/{{scheduledAt}}/{{interviewType}} columns are optional per-row overrides — for onboarding a whole cohort at once, leave them blank and use the defaults below instead.',
          cohortTitle: 'Assign & schedule the whole cohort (optional)',
          cohortHint: "Applied to every row that doesn't already specify its own interview_id/scheduled_at.",
          interview: 'Interview',
          scheduledAt: 'Scheduled at',
          modality: 'Modality',
          noneAssignLater: 'None (assign later)',
          text: 'Text',
          voice: 'Voice',
          totalRows: 'Total rows: {{count}}',
          createdUsers: 'Created users: {{count}}',
          reusedUsers: 'Reused users: {{count}}',
          assignmentsUpdated: 'Assignments updated: {{count}}',
          invitationsDispatched: 'Invitations dispatched: {{count}}',
          rowError: 'Row {{row}}: {{error}}'
        },
        profile: {
          accountStatus: 'Account Status',
          memberSince: 'Member Since',
          assignedInterviews: 'Assigned Interviews ({{count}})',
          assignedInterviewTemplate: 'Assigned interview template',
          statusLabel: 'Status: {{status}}',
          scheduleNotSet: 'Schedule is not set yet.',
          resultsVisible: 'Results visible',
          resultsHidden: 'Results hidden',
          noAssignments: 'No interview assignments for this candidate yet.',
          reviewModeLabel: 'Review mode',
          reviewMode: {
            manual: 'Manual review',
            auto_approve: 'Auto-approve',
            auto_review: 'Auto-reviewed (AI decides)'
          }
        },
        delete: {
          confirmTitle: 'Remove "{{name}}"?',
          confirmBody: 'This action cannot be undone. All data associated with this candidate will be permanently removed.'
        },
        confirm: {
          cancelAssignment: 'Cancel assignment "{{interviewTitle}}" for {{candidateName}}?'
        },
        email: {
          subject: 'VivaAssess Interview Update'
        },
        messages: {
          selectVisibleCandidatesForAssign: 'Select at least one visible candidate to assign an interview.',
          candidateRemoved: 'Candidate removed successfully.',
          removeCandidateFailed: 'Failed to remove candidate.',
          invitationCopied: 'Invitation link regenerated and copied to clipboard.',
          invitationRegeneratedShare: 'Invitation link regenerated. Please share it with the candidate.',
          unknownError: 'Unknown error.',
          resendInvitationFailed: 'Failed to resend invitation: {{reason}}',
          noActiveAssignmentFound: 'No active interview assignment was found for this candidate.',
          resultsVisible: 'Candidate can now view detailed results.',
          resultsHidden: 'Candidate detailed results were hidden.',
          updateResultVisibilityFailed: 'Failed to update result visibility: {{reason}}',
          assignmentCancelUnsupported: 'This assignment cannot be cancelled from this view. Please refresh and try again.',
          assignmentStatusNotCancellable: 'Cannot cancel an assignment in status {{status}}.',
          assignmentCancelled: 'Interview assignment cancelled.',
          cancelAssignmentFailed: 'Failed to cancel assignment: {{reason}}',
          candidateCsvExported: 'Candidate CSV exported.',
          exportCandidatesFailed: 'Failed to export candidates.',
          bulkImportSuccess: 'Imported {{total}} records successfully.',
          bulkImportPartial: 'Imported with {{errorCount}} row errors.',
          bulkImportFailed: 'Bulk import failed.',
          profileUpdated: 'Candidate profile updated.',
          actionFailed: 'Failed: {{reason}}'
        }
      },
      createCandidateForm: {
        intro: 'Create a new candidate account. An invitation link will be generated for them to set their password.',
        fields: {
          fullName: 'Full name',
          fullNamePlaceholder: "Enter candidate's full name",
          email: 'Email address',
          emailPlaceholder: "Enter candidate's email address",
          neptun: 'Neptun',
          neptunOptional: 'Neptun code (optional)',
          neptunPlaceholder: '6 alphanumeric characters'
        },
        validation: {
          neptun: 'Neptun code must be 6 alphanumeric characters, or left blank.'
        },
        next: {
          title: 'What happens next?',
          invite: 'An invitation link will be generated (valid for 7 days)',
          share: 'Share the link with the candidate to set their password',
          assign: 'You can then assign an interview from this page'
        },
        actions: {
          cancel: 'Cancel',
          create: 'Create candidate'
        },
        success: {
          title: 'Candidate created',
          shareWith: 'Share the invitation link below with {{name}}',
          expires: 'Link expires: {{date}}',
          invitationLink: 'Invitation link',
          copy: 'Copy',
          copied: 'Copied',
          nextStepsTitle: 'Next steps',
          stepShare: 'Share this invitation link with the candidate',
          stepPassword: 'They will set their password using this link',
          stepAssign: 'Assign an interview when they are ready',
          sendEmail: 'Send email',
          assignInterview: 'Assign interview',
          done: 'Done'
        },
        messages: {
          createFailed: 'Unable to create candidate. Please try again.'
        }
      },
      invigilatorManagement: {
        hero: {
          title: 'Invigilators',
          subtitle: 'Manage invigilator readiness, rebalance workload, and intervene quickly in flagged sessions.',
          statusSummary: '{{active}} active, {{pending}} pending activation, {{inactive}} inactive'
        },
        actions: {
          assignInvigilator: 'Assign invigilator',
          addInvigilator: 'Add invigilator',
          activate: 'Activate',
          deactivate: 'Deactivate',
          updating: 'Updating...',
          activateSelected: 'Activate selected',
          deactivateSelected: 'Deactivate selected',
          clearSelection: 'Clear selection',
          removing: 'Removing...',
          remove: 'Remove',
          cancel: 'Cancel',
          confirm: 'Confirm'
        },
        shortcuts: {
          title: 'Operational shortcuts',
          subtitle: 'Filter high-risk queues in one tap and keep assignment pressure below {{threshold}} active assignments per invigilator.',
          focusFlagged: 'Focus flagged sessions',
          focusLive: 'Focus live sessions',
          showOverloaded: 'Show overloaded team',
          clearFilters: 'Clear all filters'
        },
        metrics: {
          invigilators: 'Invigilators',
          overloaded: 'Overloaded',
          assignments: 'Assignments',
          liveSessions: 'Live sessions',
          flaggedSessions: 'Flagged sessions',
          active: 'Active',
          inactive: 'Inactive',
          threshold: 'Threshold',
          activeLower: 'active',
          liveSessionsHint: 'Based on current assignment filters',
          flaggedHint: 'Score < {{threshold}} or session issue',
          invigilatorsAria: 'Show all invigilators',
          overloadedAria: 'Show overloaded invigilators',
          assignmentsAria: 'Show all assignments',
          liveAria: 'Focus live sessions',
          flaggedAria: 'Focus flagged sessions'
        },
        status: {
          active: 'Active',
          pending: 'Pending',
          inactive: 'Inactive'
        },
        sync: {
          none: 'No sync yet',
          unknown: 'Unknown sync time'
        },
        team: {
          title: 'Invigilator team',
          searchPlaceholder: 'Search invigilator name or email',
          pending: 'Pending',
          selectVisible: 'Select visible',
          unselectVisible: 'Unselect visible',
          showingCount: 'Showing {{visible}} of {{total}} invigilators',
          selectedForBulk: '{{count}} invigilator(s) selected for bulk action',
          loading: 'Loading invigilators...',
          empty: 'No invigilators found',
          emptyHint: 'Adjust filters or add a new invigilator to continue.',
          selectInvigilator: 'Select {{name}}',
          workload: 'Workload: {{total}} active assignment(s), {{live}} live session(s)',
          highWorkload: 'high workload',
          highWorkloadTitle: 'This invigilator currently has {{threshold}} or more active assignments.'
        },
        filters: {
          allStatuses: 'All statuses',
          sortWorkloadHigh: 'Sort: workload high to low',
          sortWorkloadLow: 'Sort: workload low to high',
          sortName: 'Sort: name (A-Z)',
          sortStatus: 'Sort: status',
          showOverloadedOnly: 'Show overloaded only',
          showingOverloadedOnly: 'Showing overloaded only'
        },
        queue: {
          title: 'Invigilator assignments',
          refresh: 'Refresh',
          searchPlaceholder: 'Search candidate, Neptun, invigilator, interview',
          allInvigilators: 'All invigilators',
          allInterviews: 'All interviews',
          activeAssignments: 'Active assignments',
          inactiveAssignments: 'Inactive assignments',
          allVisibility: 'All visibility states',
          liveOnly: 'Live sessions only',
          flaggedOnly: 'Flagged sessions only',
          sortRecent: 'Sort: most recent updates',
          sortIntegrityLow: 'Sort: lowest integrity first',
          sortIntegrityHigh: 'Sort: highest integrity first',
          sortCandidate: 'Sort: candidate name (A-Z)',
          total: 'total: {{count}}',
          live: 'live: {{count}}',
          flagged: 'flagged: {{count}}',
          lastSync: 'last sync: {{time}}',
          loading: 'Loading assignments...',
          empty: 'No assignments found for the selected filters.',
          invigilatorLine: 'Invigilator: {{name}} ({{email}})',
          sessionStatus: 'session: {{status}}',
          integrity: 'integrity: {{score}}',
          flaggedTitle: 'Flagged due to integrity events, session issues, or score below {{threshold}}',
          flaggedChip: 'flagged',
          viewInteractions: 'View interactions',
          noSessionYet: 'No session yet',
          deactivateAssignment: 'Deactivate assignment',
          reactivateAssignment: 'Reactivate assignment',
          reassignTo: 'Reassign to...',
          autoPick: 'Auto pick',
          reassign: 'Reassign',
          reassigning: 'Reassigning...',
          suggestedTarget: 'Suggested target: {{name}}'
        },
        interaction: {
          title: 'Interaction monitor',
          interview: 'Interview: {{title}}',
          invigilator: 'Invigilator: {{name}}',
          liveRefresh: 'Live session (auto-refresh every 5s)',
          historyRefresh: 'Session history (auto-refresh every 5s)',
          started: 'Started: {{time}}',
          openLiveSupervision: 'Open in Live Supervision',
          loading: 'Loading session interaction data...',
          chatTitle: 'Invigilator-candidate chat history',
          noChat: 'No chat messages have been exchanged in this session yet.',
          timelineTitle: 'Proctoring timeline',
          noTimeline: 'No timeline events recorded for this session yet.',
          invigilatorRole: 'Invigilator',
          candidateRole: 'Candidate',
          systemRole: 'System'
        },
        modals: {
          create: 'Add invigilator',
          assign: 'Assign invigilator',
          confirm: 'Confirm action',
          sendEmail: 'Send email'
        },
        labels: {
          thisInvigilator: 'this invigilator',
          targetInvigilator: 'a target invigilator',
          unknown: 'Unknown',
          notAvailable: 'n/a',
          unassigned: 'Unassigned'
        },
        confirm: {
          removeInvigilator: 'Remove {{name}}? Any active assignments linked to this account may stop working.',
          deactivateAssignment: 'Deactivate assignment for {{candidateName}} in {{interviewTitle}}?',
          toggleInvigilatorStatus: '{{action}} {{name}} account?',
          bulkStatusUpdate: '{{action}} {{count}} selected invigilator account(s)?'
        },
        messages: {
          auditUnknownError: 'Unknown audit error',
          loadAssignmentsFailed: 'Failed to load invigilator assignments.',
          loadSessionDetailsFailed: 'Unable to load session details.',
          loadChatMessagesFailed: 'Unable to load chat messages.',
          loadTimelineFailed: 'Unable to load timeline events.',
          loadInteractionDataFailed: 'Failed to load interaction monitor data.',
          invigilatorRemoved: 'Invigilator removed successfully.',
          removeInvigilatorFailed: 'Failed to remove invigilator.',
          assignmentDeactivated: 'Assignment deactivated successfully.',
          removeAssignmentFailed: 'Failed to remove assignment.',
          invigilatorDeactivated: 'Invigilator deactivated successfully.',
          invigilatorActivated: 'Invigilator activated successfully.',
          updateInvigilatorStatusFailed: 'Failed to update invigilator status.',
          selectForBulkStatus: 'Select at least one invigilator for bulk status updates.',
          bulkAlreadyInStatus: 'All selected invigilators already have this status.',
          bulkUpdatedSuccess: '{{successCount}} invigilator account(s) updated successfully.',
          bulkUpdatedPartial: '{{successCount}} updated, {{failedCount}} failed. Review account permissions and retry.',
          reactivateWithoutInvigilator: 'Cannot reactivate assignment without invigilator reference.',
          assignmentReactivated: 'Assignment reactivated successfully.',
          reactivateAssignmentFailed: 'Failed to reactivate assignment.',
          selectInvigilatorForReassign: 'Select an invigilator to reassign this candidate.',
          assignmentAlreadyOwned: 'This assignment already belongs to the selected invigilator.',
          assignmentReassigned: 'Assignment reassigned successfully.',
          reassignAssignmentFailed: 'Failed to reassign assignment.',
          noReassignTarget: 'No active reassignment target is currently available.',
          suggestedTarget: 'Suggested {{name}} based on current workload.',
          noSessionYet: 'No session has started yet for this assignment.'
        }
      },
      createInvigilatorForm: {
        intro: 'Create a new invigilator account. An invitation link will be generated for them to set their password.',
        fields: {
          fullName: 'Full name',
          fullNamePlaceholder: 'Enter invigilator full name',
          email: 'Email address',
          emailPlaceholder: 'Enter invigilator email'
        },
        pendingHint: 'Invigilators start in pending status until they accept their invitation.',
        actions: {
          creating: 'Creating...',
          create: 'Create invigilator'
        },
        success: {
          title: 'Invigilator created',
          shareWith: 'Share the invitation link below with {{name}}',
          expires: 'Link expires: {{date}}',
          invitationLink: 'Invitation link',
          copy: 'Copy link',
          copied: 'Copied',
          nextStepsTitle: 'Next steps',
          stepShare: 'Send the invitation link to the invigilator.',
          stepPassword: 'Ask them to complete account setup before their first monitoring slot.',
          stepAssign: 'Assign candidates after they accept the invitation and the account is active.',
          sendEmail: 'Send email',
          emailSubject: 'Your VivaAssess invigilator invitation',
          done: 'Done'
        },
        messages: {
          nameAndEmailRequired: 'Full name and email are required.',
          createFailed: 'Failed to create invigilator. Please try again.'
        }
      },
      assignInvigilatorForm: {
        title: 'Assign invigilator',
        subtitle: 'Match eligible candidates with an available invigilator for active interview monitoring.',
        mode: {
          candidates: 'Select candidates',
          section: 'Entire candidate group'
        },
        fields: {
          interview: 'Interview',
          selectInterview: 'Select interview',
          invigilator: 'Invigilator',
          selectInvigilator: 'Select invigilator',
          candidateGroup: 'Candidate group',
          selectGroup: 'Select candidate group',
          memberCount: '{{count}} members',
          groupHint: 'Members already assigned to the selected interview will be linked to this invigilator.'
        },
        status: {
          pending: 'Pending',
          scheduled: 'Scheduled',
          inProgress: 'In progress'
        },
        picker: {
          title: 'Eligible candidates',
          total: 'total: {{count}}',
          pendingCount: 'pending: {{count}}',
          scheduledCount: 'scheduled: {{count}}',
          inProgressCount: 'in progress: {{count}}',
          selectVisible: 'Select visible',
          selectAll: 'Select all',
          clearVisible: 'Clear visible',
          clearAll: 'Clear all',
          searchPlaceholder: 'Search name, email, or Neptun',
          allStatuses: 'All candidate statuses',
          selectInterviewHint: 'Select an interview to load eligible candidates.',
          empty: 'No candidates match the current filters.',
          selectCandidate: 'Select {{name}}',
          selectedCount: 'Selected: {{count}} candidate(s)'
        },
        actions: {
          assigning: 'Assigning...',
          assign: 'Assign invigilator',
          assignGroup: 'Assign to candidate group'
        },
        messages: {
          interviewAndInvigilatorRequired: 'Please choose an interview and invigilator before assigning candidates.',
          selectGroupRequired: 'Please select a candidate group to assign.',
          groupPartialSuccess: '{{addedCount}} assignment(s) saved from the candidate group. {{skippedCount}} member(s) were skipped (not assigned to this interview).',
          groupNoneCreated: 'No assignments were created. Check that group members are assigned to this interview.',
          groupAssignFailed: 'Failed to assign invigilator by candidate group.',
          selectCandidateRequired: 'Please select at least one eligible candidate.',
          assignmentsPartialSuccess: '{{addedCount}} assignment(s) saved. {{skippedCount}} candidate(s) were skipped because they are not currently assigned to this interview.',
          noAssignmentsCreated: 'No assignments were created. Please check candidate eligibility and try again.',
          assignFailed: 'Failed to assign invigilator. Please try again.',
          noActiveInvigilators: 'No active invigilators are currently available.'
        }
      },
      interviewManagement: {
        title: 'Interview Templates',
        subtitle: 'Create and manage AI-powered interview assessments',
        actions: {
          createInterview: 'Create Interview',
          createFirstInterview: 'Create Your First Interview',
          view: 'View',
          viewDetails: 'View Details',
          edit: 'Edit',
          editInterview: 'Edit Interview',
          duplicate: 'Duplicate',
          uploadMaterials: 'Upload Materials',
          activate: 'Activate',
          deactivate: 'Deactivate',
          delete: 'Delete',
          deleteInterview: 'Delete Interview',
          cancel: 'Cancel',
          done: 'Done',
          releaseScores: 'Release scores',
          hideScores: 'Hide scores',
          openPlacementTemplate: 'Open Placement Center → Template',
          addOralQuestions: 'Add oral questions',
          linkPlacement: 'Link placement for the official template bank'
        },
        stats: {
          total: 'Total',
          active: 'Active',
          draft: 'Draft',
          archived: 'Archived'
        },
        filters: {
          searchPlaceholder: 'Search by title, placement, focus areas, or difficulty...',
          allStatus: 'All Status',
          allLevels: 'All Levels',
          allPlacements: 'All placements',
          placementLinked: 'Placement linked',
          placementUnlinked: 'Not linked',
          allModes: 'All modes',
          allPartB: 'All Part B sources',
          partBAuthored: 'Authored questions',
          placementAria: 'Filter by placement link',
          modeAria: 'Filter by assessment mode',
          partBAria: 'Filter by Part B source'
        },
        status: {
          active: 'Active',
          draft: 'Draft',
          archived: 'Archived'
        },
        states: {
          loading: 'Loading...',
          loadingInterviews: 'Loading interviews...',
          preparingCopy: 'Preparing copy...'
        },
        empty: {
          noMatchTitle: 'No interviews match your filters',
          noMatchDescription: 'Try adjusting your search or filter criteria',
          noInterviewsTitle: 'No interviews yet',
          noInterviewsDescription: 'Create your first interview template to start assessing candidates with AI-powered evaluations.'
        },
        card: {
          noFocusAreas: 'No focus areas',
          moreFocusAreas: '+{{count}} more'
        },
        window: {
          range: '{{from}} → {{until}}',
          opens: 'Opens {{from}}',
          closes: 'Closes {{until}}',
          perCandidate: 'Per-candidate'
        },
        table: {
          interview: 'Interview',
          status: 'Status',
          duration: 'Duration',
          difficulty: 'Difficulty',
          language: 'Language',
          candidates: 'Candidates',
          window: 'Window',
          actions: 'Actions',
          durationMinutes: '{{minutes}} min'
        },
        modals: {
          duplicateInterview: 'Duplicate Interview',
          createNewInterview: 'Create New Interview',
          interviewDetails: 'Interview Details',
          editInterview: 'Edit Interview',
          manageMaterials: 'Manage Reference Materials',
          deleteInterview: 'Delete Interview'
        },
        view: {
          noDescription: 'No description provided',
          status: 'Status',
          duration: 'Duration',
          difficulty: 'Difficulty',
          language: 'Language',
          examinerName: 'Examiner name',
          examinerNameUnset: 'Not set',
          durationMinutes: '{{minutes}} minutes',
          focusAreas: 'Focus Areas',
          instructions: 'Instructions',
          interviewQuestions: 'Interview Questions ({{count}})',
          questionType: 'Type: {{type}}',
          questionDifficulty: 'Difficulty: {{difficulty}}',
          questionTime: 'Time: {{seconds}}s',
          noPredefinedQuestions: 'No predefined questions configured.',
          questionsFromTemplate: 'No authored questions on this interview. Part B uses the placement template bank.',
          questionsDynamic: 'No authored oral questions. Candidates will receive dynamic AI questions.',
          questionsNotReady: 'No authored oral questions, and the linked placement has no active template yet.',
          materialsHint: 'Upload materials for the AI to reference during question generation',
          placement: 'Linked placement',
          assessmentMode: 'Assessment mode',
          timers: 'Timers: {{duration}}',
          oralSource: 'Part B oral source'
        },
        readiness: {
          placementBadge: 'Placement: {{label}}',
          placementLinked: '{{name}} · {{status}}',
          placementUnnamed: 'Placement',
          placementNotLinked: 'Not linked',
          modeBadge: 'Mode: {{mode}}',
          partBBadge: 'Part B: {{source}}',
          partAEnabled: 'Part A on',
          partAReady: 'Part A · MCQ settings',
          partADisabled: 'Part A off',
          sourceTemplate: 'Template',
          sourceAuthored: 'Authored questions ({{count}})',
          sourceDynamic: 'Dynamic AI',
          sourceNotReady: 'Not ready',
          sourcePartAOnly: 'Part B off',
          mode: {
            hybrid: 'Hybrid',
            part_a: 'Part A',
            part_b: 'Part B'
          },
          explainTemplate: 'Part B will use the linked placement’s active oral template (ranking bank).',
          explainAuthored: 'Part B will use {{count}} authored interview question(s) on this template.',
          explainDynamic: 'Part B will start with dynamic AI questions. The viva can still run; ranking will not use a placement template bank.',
          explainNotReady: 'Part B ranking generate needs an active template on the linked placement. Candidates may still fall back to authored or dynamic questions.',
          explainPartAOnly: 'Part B is disabled. Candidates only sit Part A (MCQ).'
        },
        guards: {
          notReadyBlock: 'This interview is linked to a placement without an active oral template. Part B generate will fail (409) until you save and activate a template in Placement Center.',
          dynamicConfirm: 'No placement template and no authored oral questions. Candidates will get dynamic AI questions. Continue?'
        },
        delete: {
          confirmTitle: 'Delete "{{title}}"?',
          confirmBody: 'This action cannot be undone. All data associated with this interview will be permanently removed.'
        },
        duplicate: {
          copySuffix: '(Copy)'
        },
        confirm: {
          releaseScores: 'Release scores to all assignees of "{{title}}"? Candidates will see their own scores and rank in the portal. The full ranking table stays instructor-only.',
          hideScores: 'Hide scores from all assignees of "{{title}}"? Candidates will not see numbers or rank until you release them again.'
        },
        messages: {
          loadDetailsFailed: 'Interview details could not be fully loaded.',
          loadDetailsForEditFailed: 'Interview details could not be fully loaded. Editing basic fields only.',
          deleted: 'Interview deleted successfully.',
          deleteFailed: 'Failed to delete interview.',
          statusMoved: 'Interview moved to {{status}}.',
          updateStatusFailed: 'Failed to update interview status.',
          duplicated: 'Interview duplicated. Review and save the new template.',
          duplicatedBasicOnly: 'Interview duplicated with basic fields only.',
          saved: 'Saved successfully.',
          interviewAndMcqSaved: 'Interview and MCQ settings saved successfully.',
          interviewSaved: 'Interview saved successfully.',
          mcqSaved: 'MCQ settings saved successfully.',
          mcqRegenerated: 'MCQs regenerated successfully.',
          scoresReleased: 'Scores released for {{count}} assignment(s). Candidates can now see their own result and rank.',
          scoresHidden: 'Scores hidden for {{count}} assignment(s).',
          resultVisibilityFailed: 'Failed to update result visibility.'
        }
      },
      privateOpeningPrompt: {
        label: "Custom oral opening (advanced)",
        hint: "Optional. Blank uses the default opening. Used for the greeting and first question; changes apply to new sessions.",
        format: "Return JSON with greeting and first_question, including question, question_type, difficulty, expected_topics and max_response_time_seconds. Available placeholders:",
      },
      interviewForm: {
        answerReview: {
          title: 'Part B answer review',
          hint: 'After speech is transcribed, the candidate can correct the text before that turn is scored. Auto-submit stays available. The Part B timer does not pause.',
          allow: 'Allow the candidate to review and correct each answer',
          defaultMode: 'Starting capture mode',
          modeAuto: 'Auto-submit after silence',
          modeReview: 'Review before submit',
          timeout: 'Review time limit (seconds, 0 = none)'
        },
        fields: {
          title: 'Interview Title *',
          titlePlaceholder: 'e.g., Software Engineer L3, Medical School Admission',
          titlePlaceholderShort: 'e.g., Software Engineer L3',
          description: 'Description',
          descriptionPlaceholder: 'Describe the purpose and content of this interview',
          difficultyLevel: 'Difficulty Level',
          durationMinutes: 'Duration (minutes) *',
          language: 'Language',
          examinerName: 'Examiner name',
          examinerNamePlaceholder: 'e.g. Dr. Kovács',
          examinerNameHint: 'Used in the opening greeting in the interview language. This is not the Voice Studio voice label.',
          maxCandidates: 'Max Candidates',
          status: 'Status',
          focusAreas: 'Focus Areas',
          focusAreaPlaceholder: 'e.g., Technical knowledge, Communication skills, Problem solving',
          focusAreaPlaceholderShort: 'e.g., Problem solving',
          addFocusArea: 'Add Focus Area',
          instructionsForCandidates: 'Instructions for Candidates',
          instructionsPlaceholder: 'Provide any specific instructions for candidates taking this interview',
          instructionsPlaceholderShort: 'Provide specific instructions for candidates',
          placement: 'Placement',
          noPlacement: 'No placement linked',
          placementModeAuthoritative: 'Placement assessment mode is authoritative: Part A/B flags sync from the placement when you link or save.'
        },
        difficulty: {
          beginner: 'Beginner',
          intermediate: 'Intermediate',
          advanced: 'Advanced',
          expert: 'Expert'
        },
        interviewStatus: {
          draft: 'Draft',
          active: 'Active',
          archived: 'Archived'
        },
        languages: {
          en: 'English',
          es: 'Spanish',
          fr: 'French',
          de: 'German',
          hu: 'Hungarian'
        },
        proctoring: {
          title: 'AI Proctoring Policy',
          description: 'Configure camera/audio anomaly detection defaults for all assignments of this interview.',
          sensitivity: 'Sensitivity',
          evidenceMode: 'Evidence Mode',
          requireConsent: 'Require candidate consent',
          sensitivityLow: 'Low',
          sensitivityBalanced: 'Balanced',
          sensitivityHigh: 'High',
          metadataOnly: 'Metadata only',
          extendedEvidence: 'Extended evidence',
          secondaryCamera: 'Secondary room camera (phone side view)',
          secondaryCameraRequired: 'Require room camera before start'
        },
        assessmentWindow: {
          title: 'Assessment Window (Scheduling)',
          descriptionCreate: 'Optional cohort-wide window during which ALL assigned candidates (e.g. an entire incoming class of ~400 students) may start this assessment. Leave blank to schedule candidates individually instead.',
          descriptionEdit: 'Optional cohort-wide window during which ALL assigned candidates may start this assessment. Leave blank to schedule candidates individually instead.',
          opensAt: 'Opens at',
          closesAt: 'Closes at'
        },
        questions: {
          title: 'Interview Questions',
          subtitleCreate: 'Optional structured questions for the interview flow.',
          subtitleEdit: 'Manage predefined interview questions and order.',
          addQuestion: 'Add Question',
          emptyCreate: 'No predefined questions. Candidates will rely on dynamic prompts/MCQs.',
          emptyEdit: 'No predefined questions configured.',
          questionNumber: 'Question {{number}}',
          removeQuestionAria: 'Remove question {{number}}',
          questionPlaceholder: 'Enter interview question',
          type: 'Type',
          difficulty: 'Difficulty',
          expectedTimeSec: 'Expected time (sec)',
          required: 'Required',
          types: {
            open: 'Open',
            technical: 'Technical',
            behavioral: 'Behavioral',
            situational: 'Situational',
            follow_up: 'Follow-up'
          }
        },
        assessmentMode: {
          title: 'Assessment mode',
          subtitle: 'Choose what the candidate will take. At least one part is always selected.',
          hybridTitle: 'Part A + Part B',
          hybridBody: 'Timed MCQ first, then the AI oral viva. Recommended for placement.',
          recommended: 'Recommended',
          partATitle: 'Part A only (MCQ)',
          partABody: 'Candidates only take the multiple-choice test. The session ends after submit.',
          partBTitle: 'Part B only (oral viva)',
          partBBody: 'Candidates skip MCQ and go straight to the voice interview.',
          partASettingsHidden: 'Part A (MCQ) settings are hidden because Assessment mode is Part B only.'
        },
        partBSource: {
          title: 'Part B oral questions',
          subtitle: 'Choose how the viva will get its questions. Placement assessment mode still syncs when a placement is linked.',
          templateTitle: 'Placement template',
          templateBody: 'Recommended for ranking. Requires a linked placement with an active oral template.',
          recommended: 'Recommended',
          authoredTitle: 'Authored interview questions',
          authoredBody: 'Use the question list on this interview. Candidates skip the placement generate step.',
          dynamicTitle: 'Dynamic AI examiner',
          dynamicBody: 'No template and no authored list. The viva uses LLM prompts and the emergency question bank.',
          needPlacement: 'Select a placement above. Ranking uses that placement’s active Template in Placement Center.',
          templateReady: '“{{name}}” has an active oral template. Authored questions on this interview are optional.',
          templateMissing: 'Part B generate will return 409 placement_template_required until a template is saved and activated for this placement.',
          openPlacementTemplate: 'Open Placement Center → Template',
          authoredStillUsed: '{{count}} authored oral question(s) are still on this interview and will be used before dynamic AI. Remove them to use the LLM examiner only.',
          dynamicWarn: 'Candidates will sit a dynamic oral, not the PlacementAB template bank.',
          dynamicSaveConfirm: 'Candidates will sit a dynamic oral, not the official placement template bank. Continue?',
          notReadySaveConfirm: 'Part B generate will 409 placement_template_required until a template is saved and activated. Continue saving?'
        },
        mcq: {
          moduleTitle: 'MCQ Module',
          moduleSubtitle: 'Configure AI-generated multiple choice assessment.',
          settingsTitle: 'MCQ Settings',
          settingsSubtitle: 'Edit MCQ behavior directly for this interview.',
          enable: 'Enable MCQ',
          partARequiredBadge: 'Part A required',
          partARequiredHint: 'Part A (MCQ) is always enabled for the combined Part A + Part B assessment.',
          loadingSettings: 'Loading MCQ settings...',
          partASourceTitle: 'Part A Question Source',
          partASourceDescription: "Should Part A's questions be grounded in the uploaded Moodle XML reference question bank (learns subject areas, difficulty and style from imported exemplars without repeating them), or generated independently by the AI from this interview's materials and instructions only?",
          useQuestionBank: 'Yes — use question bank',
          useQuestionBankHint: 'Ground Part A in the imported reference questions',
          generateIndependently: 'No — generate independently',
          generateIndependentlyHintCreate: 'AI-only, from materials/instructions below',
          generateIndependentlyHintEdit: 'AI-only, from materials/instructions',
          topicScopeHint: 'Optionally scope generation to specific topic areas (leave all unselected to use every topic):',
          allTopicsUsed: 'All topic areas will be used.',
          totalPointsPartA: 'Total Points (Part A)',
          totalPointsHintCreate: "Combined with Part B's oral score for the final ranking score (legacy placement test used 35).",
          totalPointsHintEdit: "Combined with Part B's oral score to make up the final ranking score.",
          questionCount: 'Question Count',
          optionsPerQuestion: 'Options per Question',
          optionsCount: '{{count}} options',
          difficulty: 'Difficulty',
          questionStyle: 'Question Style',
          estimatedTime: 'Estimated MCQ time:',
          sectionMismatch: 'Section totals add up to {{sectionTotal}}, but the question count is {{questionCount}}.',
          needsMaterials: 'Selected generation mode needs materials, but no materials or links are selected.',
          timeLimitMode: 'Time Limit Mode',
          perQuestion: 'Per Question',
          totalTestTime: 'Total Test Time',
          secondsPerQuestion: 'Seconds per Question',
          totalTimeSeconds: 'Total Time (seconds)',
          optional: 'Optional',
          randomizeQuestions: 'Randomize questions',
          randomizeOptions: 'Randomize options',
          allowReview: 'Allow review & flagging',
          allowReviewEdit: 'Allow review and flagging',
          aiFeedback: 'AI feedback summary',
          requireExplanations: 'Require explanations',
          avoidTrickQuestions: 'Avoid trick questions',
          referenceMaterialsOnly: 'Use reference materials only',
          negativeMarking: 'Negative marking',
          negativeMarkRatio: 'Negative mark ratio',
          mcqInstructions: 'MCQ Instructions (AI)',
          mcqInstructionsEdit: 'MCQ instructions (AI)',
          mcqInstructionsPlaceholder: 'e.g., Focus on algorithms and data structures. Avoid definitions.',
          mcqInstructionsPlaceholderEdit: 'e.g., Focus on applied reasoning and avoid memorization-heavy questions.',
          sectionGrouping: 'Section-based grouping (optional)',
          addSection: 'Add section',
          noSections: 'No sections defined. Questions will be mixed.',
          sectionNamePlaceholder: 'Section name',
          resourceLinks: 'Resource links (optional)',
          addLink: 'Add link',
          addResourceLink: 'Add resource link',
          resourceLinkPlaceholder: 'https://example.com/resource',
          resourceLinkPlaceholderEdit: 'https://example.com/reference',
          generateOnSave: 'Generate MCQs immediately after save',
          regenerateAfterSave: 'Regenerate MCQs after saving settings',
          generationSource: 'MCQ generation source',
          generationSourceBoth: 'Interview + materials',
          generationSourceInterviewOnly: 'Interview info only',
          generationSourceMaterialsOnly: 'Materials only',
          generationLabelMaterials: 'materials',
          generationLabelInterview: 'interview information',
          generationLabelBoth: 'interview information and materials',
          mcqDifficulty: {
            easy: 'Easy',
            medium: 'Medium',
            hard: 'Hard',
            adaptive: 'Adaptive'
          },
          questionStyles: {
            mixed: 'Mixed',
            direct: 'Direct',
            scenario: 'Scenario-based',
            application: 'Applied'
          }
        },
        materials: {
          title: 'Reference Materials',
          description: 'Upload PDFs, documents, or text files. The AI will use their content to generate relevant interview questions.',
          section: 'Section',
          noSection: 'No section',
          category: 'Category',
          categoryPlaceholder: 'e.g., Job Description',
          clickToUpload: 'Click to upload',
          dragAndDrop: 'or drag and drop',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (max 20MB each)',
          readyToUpload: 'Ready to upload',
          filesWillUpload: '{{count}} file(s) will be uploaded with the interview',
          libraryTitle: 'Library Materials',
          libraryDescription: 'Select previously uploaded materials to reuse.',
          selectedCount: 'Selected: {{count}}',
          allSections: 'All sections',
          search: 'Search',
          searchPlaceholder: 'Search materials',
          loadingLibrary: 'Loading library materials...',
          noLibraryMaterials: 'No materials found in the library.',
          sectionLabel: 'Section: {{name}}',
          categoryLabel: 'Category: {{category}}'
        },
        actions: {
          cancel: 'Cancel'
        }
      },
      createInterviewForm: {
        actions: {
          createInterview: 'Create Interview'
        },
        validation: {
          unsupportedFileType: 'Unsupported file type: {{ext}}',
          fileTooLarge: 'File too large (max 20MB)'
        },
        messages: {
          linkingLibraryMaterials: 'Linking {{count}} library material(s)...',
          linkLibraryMaterialsFailed: 'Interview created but linking library materials failed. You can add them later.',
          uploadingReferenceMaterials: 'Uploading {{count}} reference material(s)...',
          materialsUploaded: 'Materials uploaded successfully!',
          materialUploadFailed: 'Interview created but material upload failed. You can upload them later from the interview details.',
          savingMcqSettings: 'Saving MCQ settings...',
          saveMcqSettingsFailed: 'Interview created but MCQ settings failed to save. You can configure them later.',
          mcqGenerationSkipped: 'MCQ generation skipped: add reference materials or change generation source.',
          generatingMcq: 'Generating MCQs from {{generationLabel}}...',
          mcqGenerated: 'MCQs generated successfully.',
          mcqGenerationFailed: 'Interview created but MCQ generation failed. You can regenerate later.',
          createFailed: 'Failed to create interview.'
        }
      },
      editInterviewForm: {
        actions: {
          saveChanges: 'Save Changes'
        },
        messages: {
          loadMcqSettingsFailed: 'Unable to load MCQ settings. You can still save interview details.',
          titleRequired: 'Interview title is required.',
          mcqRegenerationFailed: 'MCQ regeneration failed.',
          savedButMcqFailed: 'Saved successfully, but MCQ generation failed: {{message}}',
          updateInterviewFailed: 'Failed to update interview.'
        }
      },
      assignInterviewForm: {
        header: {
          title: 'Schedule Assessment Batch',
          description: 'Applies one combined Part A + Part B interview and schedule to this batch. Run this again with a different selection (and, if the interview has no fixed window, a different time) to schedule another batch.'
        },
        batch: {
          summary_one: 'Batch of {{count}} candidate',
          summary_other: 'Batch of {{count}} candidates',
          moreCandidates: ', +{{count}} more',
          currentAssignment: 'Current assignment: {{title}}{{statusSuffix}}',
          interviewAlreadyAssigned: 'Interview already assigned'
        },
        fields: {
          interview: 'Interview (Part A + Part B assessment)',
          chooseInterviewTemplate: 'Choose an interview template',
          noActiveInterviews: 'No active interviews available. Please activate an interview first.',
          batchDate: 'Batch Date',
          batchTime: 'Batch Time',
          deadlineDate: 'Completion Deadline Date (Optional)',
          deadlineTime: 'Completion Deadline Time (Optional)'
        },
        assessmentWindow: {
          title: "Uses the interview's assessment window",
          description: '{{start}} → {{end}}. This batch will follow that window automatically — no per-batch time needed. Change the window from the interview\'s settings if you need to move it.',
          noStartLimit: 'No start limit',
          noEndLimit: 'No end limit'
        },
        modality: {
          label: 'Part B Modality (oral interview)',
          text: 'Text-Based',
          voice: 'Voice-Based'
        },
        combinedFormat: {
          title: 'Combined assessment format',
          description: 'Every scheduled candidate receives Part A (MCQ) followed by Part B (voice interview). Interview type is fixed and cannot be changed.',
          partA: 'Part A: MCQ',
          partB: 'Part B: Voice interview'
        },
        advanced: {
          toggle: 'Advanced settings (proctoring override, access restriction, auto-approve)'
        },
        proctoring: {
          title: 'AI Proctoring Override',
          hint: 'Keep disabled to inherit the interview-level policy, or enable to set this batch explicitly.',
          enabledLabel: 'Proctoring enabled',
          enabled: 'Enabled',
          disabled: 'Disabled',
          sensitivity: 'Sensitivity',
          sensitivityLow: 'Low',
          sensitivityBalanced: 'Balanced',
          sensitivityHigh: 'High'
        },
        accessRestriction: {
          title: 'Optional Access Restriction',
          hint: 'Restrict interview start to approved public IP ranges and/or detected locations.',
          allowedIp: 'Allowed IP or CIDR entries',
          allowedIpPlaceholder: '203.0.113.10\n198.51.100.0/24\n2001:db8::/32',
          allowedIpHint: 'Use comma, semicolon, or newline separation. Supports IPv4 and IPv6 single IP and CIDR ranges.',
          allowedLocations: 'Allowed location names',
          allowedLocationsPlaceholder: 'Budapest\nBudapest, Pest\nHungary',
          allowedLocationsHint: 'Enter city, region, country, or combined values like "City, Region".',
          matchMode: 'Match mode',
          matchModeAny: 'any (IP or location can match)',
          matchModeAll: 'all (all configured groups must match)',
          enforcementMode: 'Enforcement mode',
          enforcementStartOnly: 'start_only',
          enforcementContinuous: 'continuous',
          failAction: 'Fail action',
          failActionBlock: 'block interview start',
          failActionWarn: 'warn candidate and allow start'
        },
        autoApprove: {
          label: 'Auto-approve final assessment',
          hint: 'Skips manual admin review and marks each completed assessment in this batch as approved.'
        },
        reviewMode: {
          label: 'Assessment review mode',
          hint: 'Choose who decides the final approve/reject outcome after the candidate finishes.',
          manual: 'Manual review',
          manualHint: 'A human administrator reviews and decides.',
          autoApprove: 'Auto-approve',
          autoApproveHint: 'Every completed assessment is marked approved without review.',
          autoReview: 'Auto-reviewed (AI decides)',
          autoReviewHint: 'The LLM approves or rejects from the AI evaluation. If it is unsure or unavailable, a human reviews instead.'
        },
        resultVisibility: {
          label: 'Allow candidates to view scores after completion',
          hint: 'Off by default. Leave unchecked to keep overall score, MCQ %, integrity, and reports hidden until an admin releases them.'
        },
        afterScheduling: {
          title: 'After Scheduling',
          notifyEmail: 'Every candidate in this batch is notified by email with their assessment details',
          partOrder: 'They complete Part A (MCQ) first, then Part B (oral) in the same session',
          singleAttempt: 'Every student gets exactly one attempt; reset a specific attempt from the Placement Center if needed',
          resultsPolicy: 'Scores stay hidden unless you enable candidate result visibility above (or release later from Interview Management)',
          trackProgress: 'Track progress and results from the Placement Center ranking dashboard'
        },
        actions: {
          cancel: 'Cancel',
          scheduling: 'Scheduling...',
          scheduleBatch: 'Schedule Batch ({{count}})'
        },
        validation: {
          restrictionEntryRequired: 'At least one IP/CIDR or location entry is required when restriction is enabled.',
          invalidIpEntries: 'Invalid IP/CIDR entries: {{entries}}',
          selectBatchAndInterview: 'Please select at least one candidate and an interview.',
          requiredFieldsBeforeAssign: 'Please complete all required fields before scheduling this batch.',
          deadlineDateTimePairRequired: 'Please provide both deadline date and time, or leave both empty.',
          invalidInterviewSchedule: 'Invalid interview date or time. Please choose a valid schedule.',
          invalidCompletionDeadline: 'Invalid completion deadline. Please choose a valid date and time.',
          deadlineBeforeSchedule: 'Completion deadline must be on or after the interview schedule.'
        },
        messages: {
          unknownError: 'Unknown error',
          partialAssignmentFailure: 'Scheduled {{successfulCount}} of {{totalCount}} candidates. Failed: {{failedNames}}.',
          assignSelectedCandidatesFailed: 'Failed to schedule the selected candidates: {{failedNames}}.',
          assignInterviewFailed: 'Failed to schedule this batch. Please try again.'
        }
      },
      sections: {
        title: 'Candidate groups',
        subtitle: 'Batch candidates for scheduling, placement enrolment, and bulk interview assignment. Not the same as institution/tenant groups.',
        searchPlaceholder: 'Search candidate groups by name or description',
        showingCount: 'Showing {{filtered}} of {{total}}',
        loading: 'Loading candidate groups…',
        listTitle: 'Candidate groups',
        noDescription: 'No description',
        archiveConfirm: 'Archive “{{name}}”? Members and past assignments are kept; the group is hidden from active lists and can be restored later. Prefer archive over permanent delete.',
        fields: {
          neptun: 'Neptun'
        },
        status: {
          active: 'Active',
          archived: 'Archived'
        },
        filters: {
          active: 'Active',
          archived: 'Archived',
          all: 'All'
        },
        stats: {
          total: 'Total',
          active: 'Active',
          archived: 'Archived',
          candidates: 'Candidates: {{count}}',
          interviews: 'Interview assignments: {{count}}'
        },
        empty: {
          title: 'No candidate groups found',
          hint: 'Create a group (e.g. MSc Cohort A), add members, then enrol into a placement or schedule interviews.'
        },
        actions: {
          create: 'Create candidate group',
          edit: 'Edit',
          members: 'Members',
          schedule: 'Schedule interviews',
          enrolPlacement: 'Enrol in placement',
          archive: 'Archive',
          archiving: 'Archiving…',
          restore: 'Restore',
          cancel: 'Cancel',
          done: 'Done'
        },
        modals: {
          edit: 'Edit candidate group',
          create: 'Create candidate group',
          members: 'Manage members',
          assign: 'Schedule interview for candidate group',
          enrol: 'Enrol candidate group in placement',
          archive: 'Archive candidate group'
        },
        messages: {
          groupArchived: 'Candidate group archived.',
          archiveFailed: 'Failed to archive candidate group.',
          groupRestored: 'Candidate group restored.',
          restoreFailed: 'Failed to restore candidate group.',
          enrolledInPlacement: '“{{group}}” enrolled in the selected placement (members + placement link).'
        },
        enrol: {
          blurb: 'Links this candidate group to a placement and enrols all current members (enrolled_via = group). Use Placement Center for ranking and reports afterward.',
          placement: 'Placement',
          loading: 'Loading placements…',
          none: 'No placements yet',
          loadFailed: 'Failed to load placements',
          failed: 'Enrolment failed',
          memberCount: '{{count}} members',
          enrolledCount: '{{count}} enrolled',
          alreadyLinked: 'Already linked groups: {{names}}',
          enrolling: 'Enrolling…',
          submit: 'Enrol group',
          status: {
            draft: 'draft',
            open: 'open',
            closed: 'closed',
            archived: 'archived'
          }
        },
        forms: {
          create: {
            createTitle: 'Create a candidate group',
            editTitle: 'Edit candidate group',
            subtitle: 'Batch candidates for scheduling and placement enrolment. Not the same as institution/tenant groups.',
            nameLabel: 'Group name',
            namePlaceholder: 'e.g., MSc Cohort A',
            descriptionLabel: 'Description (optional)',
            descriptionPlaceholder: 'Cohort notes or intake details',
            creating: 'Creating…',
            saving: 'Saving…',
            create: 'Create group',
            save: 'Save changes',
            validation: {
              nameRequired: 'Candidate group name is required.'
            },
            messages: {
              createdSuccess: 'Candidate group created successfully.',
              updatedSuccess: 'Candidate group updated successfully.',
              saveFailed: 'Failed to save candidate group.'
            }
          },
          members: {
            title: 'Manage group members',
            groupLabel: 'Candidate group: {{name}}',
            unboundedHint: 'No membership cap. You can add every candidate in this institution.',
            addHeading: 'Add candidates',
            currentHeading: 'Current members ({{count}})',
            searchAvailable: 'Search candidates by name, email, or Neptun',
            searchMembers: 'Search current members by name, email, or Neptun',
            showingNone: 'No eligible candidates',
            showingRange: 'Showing {{start}}–{{end}} of {{total}} eligible',
            selectedCount: '{{count}} selected',
            selectVisible: 'Select visible',
            unselectVisible: 'Unselect visible',
            selectAllMatching: 'Select all matching search',
            selecting: 'Selecting…',
            clearSelection: 'Clear selection',
            loadingAvailable: 'Loading eligible candidates…',
            noneAvailable: 'No available candidates for this group.',
            loadingMembers: 'Loading members…',
            noneMembers: 'No members in this group yet.',
            previous: 'Previous',
            next: 'Next',
            adding: 'Adding…',
            addSelected: 'Add selected ({{count}})',
            remove: 'Remove',
            removing: 'Removing…',
            removeNamed: 'Remove {{name}}',
            selectCandidate: 'Select {{name}}',
            confirmRemove: 'Remove {{name}} from the group? Existing interview assignments will not be deleted.',
            validation: {
              selectAtLeastOne: 'Select at least one candidate.'
            },
            messages: {
              loadFailed: 'Failed to load members',
              loadAvailableFailed: 'Failed to load eligible candidates',
              selectedMatching: '{{count}} matching candidates selected.',
              selectAllFailed: 'Failed to select matching candidates.',
              addedNotice: '{{count}} members added.',
              addedToSection: '{{count}} members added to {{sectionName}}.',
              addFailed: 'Failed to add members',
              removedNotice: 'Member removed from the candidate group. Existing interview assignments were left intact.',
              removedFromSection: 'Member removed from {{sectionName}}.',
              removeFailed: 'Failed to remove member'
            }
          }
        },
        assign: {
          title: 'Schedule interview',
          groupLabel: 'Candidate group: {{name}}',
          policyNote: 'Schedules Part A (MCQ) + Part B (voice) for all current members. Scores stay hidden unless you allow candidates to view them below. Retakes stay off unless you enable them.',
          interviewTemplate: 'Interview template',
          chooseTemplate: 'Choose an interview template',
          date: 'Interview date',
          time: 'Interview time',
          deadlineDate: 'Completion deadline date (optional)',
          deadlineTime: 'Completion deadline time (optional)',
          deadlineLabel: 'Deadline',
          currentList: 'Current group schedules',
          loading: 'Loading schedules…',
          empty: 'No schedules for this group yet.',
          untitledInterview: 'Interview',
          kindSlot: 'Slot',
          kindWindow: 'Window',
          kindNone: 'Unscheduled',
          remove: 'Remove',
          removing: 'Removing…',
          removeHint: 'Removes the group-level schedule only; per-candidate assignments stay unless cleared separately.',
          removeDisclaimer: 'Removing a group schedule does not automatically clear individual candidate assignments.',
          scheduling: 'Scheduling…',
          submit: 'Schedule interview',
          window: {
            title: "Uses the interview's assessment window",
            description: '{{start}} → {{end}}. This group will follow that window — no per-member slot needed.',
            noStart: 'No start limit',
            noEnd: 'No end limit'
          },
          resultVisibility: {
            label: 'Allow candidates to view scores after completion',
            hint: 'Off by default. Applies to current members and later members who inherit this schedule.'
          },
          retake: {
            title: 'Retake policy',
            hint: 'Default is no retake. Scope applies to all current members and later members who inherit this schedule.',
            allow: 'Allow retake',
            scope: 'Retake scope',
            maxAttempts: 'Max attempts (0 = unlimited)',
            scopeOption: {
              both: 'Both — full assessment',
              part_a: 'Part A only — MCQ; oral kept',
              part_b: 'Part B only — oral; MCQ kept'
            }
          },
          validation: {
            interviewRequired: 'Please select an interview.',
            interviewDateTimeRequired: 'Please select an interview, date, and time.',
            deadlineDateTimePairRequired: 'Please provide both deadline date and time, or leave both empty.',
            invalidScheduleDateTime: 'Invalid date/time selected.',
            invalidCompletionDeadline: 'Invalid completion deadline selected.',
            deadlineBeforeSchedule: 'Completion deadline must be on or after the interview schedule.'
          },
          messages: {
            loadAssignmentsFailed: 'Failed to load group assignments.',
            assignmentSavedNotice: 'Interview scheduled for this candidate group.',
            assignmentSavedForSection: 'Interview scheduled for {{sectionName}}.',
            assignInterviewFailed: 'Failed to schedule interview.',
            assignmentRemovedNotice: 'Group-level schedule removed. Individual candidate assignments were left intact.',
            assignmentRemovedFromSection: 'Schedule removed from {{sectionName}}.',
            removeAssignmentFailed: 'Failed to remove assignment.'
          },
          reviewMode: {
            label: 'Assessment review mode',
            hint: 'Choose who decides the final approve/reject outcome for this group.',
            manual: 'Manual review',
            manualHint: 'A human administrator reviews and decides.',
            autoApprove: 'Auto-approve',
            autoApproveHint: 'Completed interviews are marked approved without manual review.',
            autoReview: 'Auto-reviewed (AI decides)',
            autoReviewHint: 'The LLM approves or rejects. Failures go to a human reviewer.',
            autoApproveBadge: 'Auto-approve enabled',
            autoReviewBadge: 'AI auto-review enabled'
          }
        }
      },
      llmAutoReview: {
        title: 'Automatic assessment review',
        hint: 'Used when an assignment is set to Auto-reviewed. Failures always fall back to human review.',
        passThreshold: 'Pass threshold (0–100)',
        minConfidence: 'Minimum confidence (0–1)',
        escalateIntegrity: 'Send integrity flags to a human',
        stuckMinutes: 'Stuck in-review timeout (minutes)',
        save: 'Save auto-review settings',
        saved: 'Auto-review settings saved.'
      },
      overview: {
        title: 'Dashboard Overview',
        subtitle: 'Monitor your AI-powered interview platform performance',
        loading: 'Loading dashboard...',
        updated: 'Updated',
        refresh: 'Refresh',
        systemOperational: 'System Operational',
        health: {
          allClear: 'All clear',
          inProgress: '{{count}} in progress',
          inProgress_one: '{{count}} in progress',
          inProgress_other: '{{count}} in progress',
          issues: '{{count}} issues',
          issues_one: '{{count}} issue',
          issues_other: '{{count}} issues',
          unknown: 'Status unknown'
        },
        error: {
          title: 'Failed to Load Dashboard',
          tryAgain: 'Try Again'
        },
        messages: {
          loadDashboardUnavailable: 'Unable to load dashboard data right now. Please try again.',
          loadDashboardFailed: 'Failed to load dashboard data'
        },
        modules: {
          statistics: 'Statistics',
          recentActivity: 'Recent activity',
          upcomingInterviews: 'Upcoming interviews',
          pendingReviews: 'Pending reviews',
          dropoffAnalytics: 'Dropoff analytics',
          completionTrends: 'Completion trends',
          cohortComparison: 'Cohort comparison',
          completionDistribution: 'Completion distribution',
          systemIssues: 'System issues',
          interviewReadiness: 'Interview readiness',
          openPlacements: 'Open placements'
        },
        warnings: {
          partialData: 'Some dashboard data is unavailable.',
          affectedWidgets: 'Affected widgets: {{widgets}}.'
        },
        stats: {
          totalInterviews: 'Total Interviews',
          activeSub: '{{count}} active',
          activeCandidates: 'Active Candidates',
          totalSub: '{{count}} total',
          pendingReviews: 'Pending Reviews',
          totalAssessments: '{{count}} total assessments',
          completionRate: 'Completion Rate',
          completedSessions: '{{count}} completed sessions',
          vsLastMonth: 'vs last month',
          vsPrevious30Days: 'vs previous 30 days'
        },
        examDay: {
          liveNow: 'Live now',
          openLive: 'Live Supervision',
          liveCount: '{{count}} sessions in progress',
          liveCount_one: '{{count}} session in progress',
          liveCount_other: '{{count}} sessions in progress',
          noLiveSessions: 'No live sessions',
          readiness: 'Intake / readiness',
          openInterviews: 'Interviews',
          notReady: '{{count}} Part B not ready',
          openPlacements: '{{count}} open placements'
        },
        analytics: {
          windowLabel: 'Analytics window',
          windowDays: '{{count}} days',
          dropoffFunnel: 'Assignment funnel ({{days}}d)',
          assignmentCompletion: 'Assignment Completion',
          assignmentDropoff: 'Assignment Dropoff',
          sessionDropoff: 'Session dropoff: {{rate}}% ({{started}} started)',
          noAnalyticsData: 'No analytics data available.',
          completionTrend: 'Completion trend ({{days}}d)',
          completedRatio: '{{completed}}/{{created}} completed',
          noTrendData: 'No trend data available.',
          topCohorts: 'Top Cohorts & Score Bands',
          interviews: 'Interviews',
          completionDistribution: 'Completion Distribution',
          stagePending: 'Pending',
          stageScheduled: 'Scheduled',
          stageInProgress: 'In progress',
          stageCompleted: 'Completed',
          stageCancelled: 'Cancelled',
          roleComparison: 'Voice vs text',
          roleVoice: 'Voice / Part B',
          roleText: 'Text / Part A',
          roleOther: 'Other'
        },
        activity: {
          justNow: 'just now',
          minutesAgo: '{{count}} minutes ago',
          minutesAgo_one: '{{count}} minute ago',
          minutesAgo_other: '{{count}} minutes ago',
          hoursAgo: '{{count}} hours ago',
          hoursAgo_one: '{{count}} hour ago',
          hoursAgo_other: '{{count}} hours ago',
          daysAgo: '{{count}} days ago',
          daysAgo_one: '{{count}} day ago',
          daysAgo_other: '{{count}} days ago',
          createInterview: 'Interview "{{title}}" created',
          updateInterview: 'Interview "{{title}}" updated',
          deleteInterview: 'Interview "{{title}}" deleted',
          createCandidate: 'Candidate "{{name}}" added',
          assignInterview: 'Interview assigned to {{name}}',
          startSession: 'Interview session started by {{user}}',
          completeSession: 'Interview session completed by {{user}}',
          approveAssessment: 'Assessment approved for {{name}}',
          rejectAssessment: 'Assessment rejected for {{name}}',
          retakeGranted: 'Retake granted for {{name}}',
          retakeStarted: 'Retake started for {{name}}',
          placementCreated: 'Placement "{{title}}" created',
          placementUpdated: 'Placement "{{title}}" updated',
          systemIssue: 'System issue logged',
          login: '{{user}} signed in',
          logout: '{{user}} signed out',
          generic: '{{action}} on {{resource}}'
        },
        recentActivity: {
          title: 'Recent Activity',
          viewAll: 'View All',
          empty: 'No recent activity'
        },
        upcomingInterviews: {
          title: 'Upcoming Interviews',
          viewAll: 'View All',
          empty: 'No upcoming slots or open assessment windows',
          unknownCandidate: 'Unknown Candidate',
          untitledInterview: 'Untitled Interview',
          durationMinutes: '{{count}} min',
          scheduleUnavailable: 'Schedule unavailable',
          inDays: 'in {{count}} days',
          tomorrow: 'Tomorrow',
          inHours: 'in {{count}}h',
          startingSoon: 'Starting soon',
          openNow: 'Window open',
          kindSlot: 'Slot',
          kindWindow: 'Window'
        },
        pendingReviews: {
          title: 'Assessments in Review Queue',
          subtitle: 'AI evaluations awaiting or undergoing admin review',
          reviewAll: 'Review All',
          allCaughtUp: 'All caught up!',
          allReviewed: 'No pending or in-review assessments',
          queueUnavailable: 'Review queue unavailable',
          queueUnavailableHint: 'Pending or in-review assessments could not be listed here. Use Review All or try Refresh.',
          openReview: 'Open review for {{name}}',
          table: {
            candidate: 'Candidate',
            interview: 'Interview',
            aiScore: 'AI Score',
            integrity: 'Integrity',
            queueStatus: 'Queue Status',
            recommendation: 'Recommendation',
            submitted: 'Submitted'
          },
          status: {
            inReview: 'In Review',
            pending: 'Pending'
          },
          recommendation: {
            strongHire: 'Strong Hire',
            hire: 'Hire',
            neutral: 'Neutral',
            noHire: 'No Hire',
            strongNoHire: 'Strong No Hire',
            pending: 'Pending'
          },
          unknown: 'Unknown',
          notAvailable: 'N/A'
        },
        quickActions: {
          createInterview: {
            title: 'Create Interview',
            description: 'Set up a new AI assessment'
          },
          addCandidate: {
            title: 'Add Candidate',
            description: 'Schedule a new interview'
          },
          reviewAssessments: {
            title: 'Review Assessments',
            description: '{{count}} in queue'
          },
          liveSupervision: {
            title: 'Live Supervision',
            description: '{{count}} in progress'
          }
        }
      },
      mcqManager: {
        title: 'MCQ Manager',
        subtitle: 'Generate, review, and activate MCQ sets per interview',
        refresh: 'Refresh',
        generateSet: 'Generate MCQ Set',
        noInterviews: {
          title: 'No interviews available',
          description: 'Create an interview first, then configure and generate MCQ sets here.'
        },
        fields: {
          interview: 'Interview',
          noInterviewsOption: 'No interviews available',
          generationSource: 'Generation source',
          forceGenerate: 'Force new generation'
        },
        generationSource: {
          both: 'Interview + materials',
          interviewOnly: 'Interview info only',
          materialsOnly: 'Materials only'
        },
        settings: {
          enabled: 'Enabled',
          questions: 'Questions',
          options: 'Options',
          style: 'Style',
          difficulty: 'Difficulty',
          strictMaterials: 'Strict materials',
          yes: 'Yes',
          no: 'No'
        },
        disabledWarning: 'MCQ is disabled for this interview. Enable it in the interview settings to generate new sets.',
        activeSet: {
          title: 'Active set',
          setLabel: 'Set {{id}}',
          notSet: 'Not set',
          selectedHint: 'Candidates will receive the selected set.',
          latestHint: 'Candidates will use the latest ready set.',
          status: 'Status:',
          questions: 'Questions:',
          created: 'Created:',
          interview: 'Interview:',
          clear: 'Clear active set'
        },
        status: {
          ready: 'Ready',
          draft: 'Draft',
          failed: 'Failed',
          active: 'Active'
        },
        generationSourceBadge: {
          both: 'Interview + Materials',
          interviewOnly: 'Interview Only',
          materialsOnly: 'Materials Only'
        },
        generationNotes: 'Generation notes',
        sets: {
          title: 'MCQ Sets',
          subtitle: 'Review generated sets and choose which one candidates should take',
          lastSync: 'Last sync: {{time}}',
          count: '{{count}} sets',
          count_one: '{{count}} set',
          count_other: '{{count}} sets',
          empty: 'No MCQ sets yet. Generate the first set to get started.',
          created: 'Created {{time}}',
          questions: 'Questions:',
          llm: 'LLM:',
          generatedBy: 'Generated by:',
          notes: 'Notes:',
          editInStudio: 'Edit in studio',
          viewQuestions: 'View questions',
          useThisSet: 'Use this set'
        },
        modal: {
          setTitle: 'MCQ Set {{id}}',
          questionsTitle: 'MCQ Questions',
          questionLabel: 'Question {{number}}',
          explanation: 'Explanation:',
          topic: 'Topic:',
          difficulty: 'Difficulty:',
          section: 'Section:',
          noQuestions: 'No questions found in this set.'
        },
        badges: {
          figure: 'Figure'
        },
        messages: {
          loadFailed: 'Failed to load MCQ data.',
          generationQueued: 'MCQ generation has been queued. Processing in background...',
          generationCompleted: 'MCQ generation completed ({{count}} question).',
          generationCompleted_other: 'MCQ generation completed ({{count}} questions).',
          generationCompletedSuccess: 'MCQ generation completed successfully.',
          generationInProgress: 'MCQ generation is still in progress. Refresh this page in a moment to see the new set.',
          generationJobFailed: 'Generation job ended with status: {{status}}',
          generateFailed: 'Failed to generate MCQ set.',
          updateActiveFailed: 'Failed to update the active set.',
          clearActiveSuccess: 'Active set cleared. Candidates will use the latest ready set automatically.',
          clearActiveFailed: 'Failed to clear active set.',
          loadQuestionsFailed: 'Failed to load questions for this set.',
          reusedSet: 'Existing MCQ set reused based on identical settings and materials.',
          unknownDate: 'Unknown'
        }
      },
      mcqAuthoringStudio: {
        actions: {
          regenerateFigures: 'Regenerate figures'
        },
        messages: {
          regenerateFiguresSuccess: 'Regenerated {{count}} figure(s).',
          regenerateFiguresFailure: 'Failed to regenerate figures.'
        }
      },
      llmSettings: {
        errors: {
          figuresRequireImageModel: 'Enable and configure MCQ image generation before turning on \'Include figures\'.'
        }
      },
      materialLibrary: {
        title: 'Materials Library',
        subtitle: 'Organize reusable material assets by section and category.',
        metrics: {
          totalMaterials: 'Total Materials',
          sections: 'Sections',
          filtered: 'Filtered',
          withExtractedText: 'With Extracted Text'
        },
        sections: {
          title: 'Material Sections',
          none: 'No sections created yet.',
          noDescription: 'No description provided',
          materialCount_one: '{{count}} material',
          materialCount_other: '{{count}} materials',
          createCta: 'Create Section'
        },
        labels: {
          selectedSection: 'Selected section',
          section: 'Section',
          category: 'Category',
          search: 'Search',
          yes: 'Yes',
          no: 'No',
          linkedInterviews: 'Linked interviews',
          selectedCount: '{{count}} selected',
          displayName: 'Display name',
          description: 'Description'
        },
        filters: {
          allSections: 'All sections',
          unassigned: 'Unassigned',
          allCategories: 'All categories',
          searchPlaceholder: 'Search by name or description',
          showing: 'Showing: {{name}}'
        },
        actions: {
          refresh: 'Refresh',
          refreshing: 'Refreshing...',
          createSection: 'Create Section',
          uploadMaterials: 'Upload Materials',
          edit: 'Edit',
          delete: 'Delete',
          download: 'Download',
          deleting: 'Deleting...',
          clearFilters: 'Clear Filters',
          cancel: 'Cancel',
          save: 'Save Changes',
          saving: 'Saving...',
          viewText: 'View text',
          reextract: 'Re-extract',
          reextracting: 'Re-extracting...',
          apply: 'Apply',
          selectAll: 'Select all',
          bulkAssignSection: 'Assign section',
          bulkSetCategory: 'Set category',
          bulkDelete: 'Delete selected'
        },
        materials: {
          title: 'Materials',
          loading: 'Loading materials...',
          noneTitle: 'No materials found',
          noneSubtitle: 'Upload files or adjust your filters to populate this library.',
          noDescription: 'No description',
          unassigned: 'Unassigned',
          general: 'General'
        },
        table: {
          name: 'Name',
          section: 'Section',
          category: 'Category',
          size: 'Size',
          extracted: 'Extracted',
          uploaded: 'Uploaded',
          linked: 'Linked',
          actions: 'Actions'
        },
        uploadForm: {
          section: 'Section',
          noSection: 'No section',
          category: 'Category',
          categoryPlaceholder: 'e.g., Assessment Brief',
          description: 'Description',
          files: 'Files',
          clickToSelect: 'Click to select files',
          orDragDrop: 'or drag and drop',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (max 20MB each, 5 files)',
          filesSelected: '{{count}} file(s) selected',
          remainingSlots: '{{count}} remaining',
          removeFile: 'Remove {{name}}',
          cancel: 'Cancel',
          upload: 'Upload',
          uploading: 'Uploading...',
          selectAtLeastOne: 'Select at least one file.',
          tooManyFiles: 'Too many files. Maximum is 5 per upload.',
          fileTooLarge: '{{name}} is too large. Maximum size is 20MB.',
          unsupportedType: 'Unsupported file type: {{name}}'
        },
        sectionForm: {
          createTitle: 'Create Material Section',
          editTitle: 'Edit Material Section',
          createSubtitle: 'Organize reusable materials into sections.',
          editSubtitle: 'Update this section for better library organization.',
          name: 'Section name',
          namePlaceholder: 'e.g., Data Structures',
          description: 'Description (optional)',
          descriptionPlaceholder: 'Add context for this section',
          nameRequired: 'Section name is required.',
          cancel: 'Cancel',
          create: 'Create Section',
          creating: 'Creating...',
          save: 'Save Changes',
          saving: 'Saving...'
        },
        editForm: {
          title: 'Edit Material',
          subtitle: 'Update library metadata. The stored file is not replaced.',
          displayNameRequired: 'Display name is required.'
        },
        extracted: {
          title: 'Extracted text',
          loading: 'Loading extracted text...',
          empty: 'No text was extracted from this file.',
          truncated: 'This extract was truncated because it exceeded the size limit.',
          charCount: '{{count}} characters',
          loadFailed: 'Failed to load extracted text.'
        },
        usage: {
          none: 'Not linked to any interview.',
          count_one: 'Linked to {{count}} interview.',
          count_other: 'Linked to {{count}} interviews.',
          unlinkWarning: 'Deleting it from the library will remove it from those interviews.',
          andMore: 'and {{count}} more'
        },
        bulk: {
          categoryPlaceholder: 'Category',
          deleteTitle: 'Delete selected materials',
          deletePrompt: 'Delete {{count}} material(s)? This will also unlink them from {{linked}} interview link(s).',
          noneSelected: 'Select at least one material.',
          applied: 'Updated {{count}} material(s).',
          deleted: 'Deleted {{count}} material(s).',
          partialDelete: 'Deleted {{deleted}} material(s). {{failed}} failed.'
        },
        modals: {
          createSectionTitle: 'Create Material Section',
          editSectionTitle: 'Edit Material Section',
          uploadMaterialsTitle: 'Upload Library Materials',
          editMaterialTitle: 'Edit Material',
          extractedTextTitle: 'Extracted text',
          deleteMaterialTitle: 'Delete Material',
          deleteMaterialPrompt: 'Delete {{name}} from the library?',
          deleteSectionTitle: 'Delete Section',
          deleteSectionPrompt: 'Delete section {{name}}? Linked materials will remain in the library and become unassigned.'
        },
        messages: {
          loadSectionsFailed: 'Failed to load sections.',
          loadMaterialsFailed: 'Failed to load library materials.',
          materialDeleted: 'Material deleted successfully.',
          deleteMaterialFailed: 'Failed to delete material.',
          sectionDeleted: 'Material section deleted successfully.',
          deleteSectionFailed: 'Failed to delete section.',
          refreshed: 'Library refreshed.',
          sectionCreated: 'Material section created.',
          sectionUpdated: 'Material section updated.',
          materialsUploaded: 'Materials uploaded successfully.',
          sectionCreateFailed: 'Failed to create section.',
          sectionUpdateFailed: 'Failed to update section.',
          materialUpdated: 'Material updated.',
          updateMaterialFailed: 'Failed to update material.',
          reextractSuccess: 'Text re-extracted.',
          reextractFailed: 'Failed to re-extract text.',
          bulkUpdateFailed: 'Failed to update selected materials.',
          bulkDeleteFailed: 'Failed to delete selected materials.'
        }
      },
      dashboard: {
        quickSearch: 'Quick Search',
        openCommandPalette: 'Open command palette',
        interview: 'Interview',
        candidate: 'Candidate',
        stats: {
          activeInterviews: '{{count}} active interviews',
          scheduledCandidates: '{{count}} scheduled candidates',
          pendingReviews: '{{count}} pending reviews'
        },
        commandPalette: {
          searchPlaceholder: 'Search modules and actions...',
          noResults: 'No matching modules or actions.',
          quickActions: 'Quick Actions',
          modules: 'Modules',
          open: 'Open',
          title: 'Command Palette',
          pressEsc: 'Press Esc to close',
          createInterview: {
            label: 'Create Interview',
            description: 'Open interview template creator'
          },
          createCandidate: {
            label: 'Create Candidate',
            description: 'Open candidate invitation form'
          },
          createSection: {
            label: 'Create candidate group',
            description: 'Open candidate group creator'
          },
          createInvigilator: {
            label: 'Create invigilator',
            description: 'Open invigilator invitation form'
          }
        },
        account: {
          title: 'Admin Account',
          description: 'Manage your admin profile details and password settings.',
          backLabel: 'Back to Overview'
        },
        sidebar: {
          addFavorite: 'Add {{label}} to favorites',
          removeFavorite: 'Remove {{label}} from favorites',
          profilePictureAlt: '{{name}} profile picture',
          adminFallback: 'Admin'
        }
      }
    },
    candidate: {
      secureCodeEditor: {
        messages: {
          executionUnavailable: 'Code execution is currently unavailable. You can continue editing and submit your code.',
          executeFailed: 'Failed to execute code. Please try again.'
        }
      },
      privacy: {
        title: 'Privacy'
      },
      help: {
        title: 'Help',
        subtitle: 'Guides for the candidate portal',
        back: 'Back',
        loading: 'Loading guides...',
        loadFailed: 'Failed to load guides.',
        emptyTitle: 'No guides yet',
        emptySubtitle: 'Published candidate guides will appear here.',
        guides: 'Guides',
        noSteps: 'This guide has no steps yet.',
        selectGuide: 'Select a guide to read it.'
      },
      portal: {
        myAccount: 'My Account',
        description: 'Manage your profile details and account security settings.',
        backLabel: 'Back to Portal'
      },
      placementResult: {
        title: 'Placement result',
        rank: 'Rank',
        partA: 'Part A',
        partB: 'Part B',
        total: 'Total'
      },
      overview: {
        modeLabels: {
          voice: 'Voice-Based',
          mcq: 'MCQ Assessment',
          text: 'Text-Based',
          combined: 'Part A + Part B',
          partAOnly: 'Part A only (MCQ)',
          partBOnly: 'Part B only (oral viva)'
        },
        combinedBriefing: {
          title: 'This assessment has two parts',
          description: 'This assessment has two parts. Part A is a multiple-choice test. After you finish Part A, you will continue to Part B, a voice interview with the AI interviewer.',
          partA: 'Part A: multiple-choice questions (complete first)',
          partB: 'Part B: voice interview with the AI interviewer'
        },
        header: {
          title: 'Candidate Portal',
          subtitle: 'VivaAssess interview operations',
          autoRefreshOn: 'Auto-refresh on',
          autoRefreshOff: 'Auto-refresh off',
          refreshTitle: 'Refresh interview details',
          refresh: 'Refresh',
          history: 'History',
          help: 'Help',
          myAccount: 'My Account',
          signOut: 'Sign Out'
        },
        hero: {
          welcome: 'Welcome back, {{name}}',
          defaultCandidate: 'Candidate',
          description: 'Track readiness, launch your interview, and monitor assignment updates from one place.',
          available: 'Interview available',
          waitingForWindow: 'Waiting for start window',
          windowClosed: 'Assessment window closed',
          autoRefreshIn: 'Auto-refresh in {{seconds}}s'
        },
        assigned: {
          title: 'Assigned Interviews',
          countHint_one: 'You have {{count}} active interview. Choose one to continue.',
          countHint_other: 'You have {{count}} active interviews. Choose one to continue.',
          selected: 'Selected: {{value}}',
          statusLabel: 'Status: {{value}}',
          scheduled: 'Scheduled',
          notSet: 'Not set',
          attempts: 'Attempts',
          deadline: 'Deadline',
          none: 'None',
          availability: 'Availability',
          inProgress: 'In progress',
          availableNow: 'Available now',
          upcoming: 'Upcoming',
          selectedButton: 'Selected',
          chooseInterview: 'Choose Interview',
          slot: 'Slot',
          window: 'Window',
          windowClosed: 'Window closed'
        },
        status: {
          pending: 'Pending',
          scheduled: 'Scheduled',
          inProgress: 'In progress',
          completed: 'Completed',
          retakeAvailable: 'Retake available'
        },
        countdown: {
          windowOpen: 'Interview window is open',
          windowOpenHelper: 'You can launch your interview whenever you are ready.',
          daysUnit_one: '{{count}} day',
          daysUnit_other: '{{count}} days',
          hoursUnit_one: '{{count}} hour',
          hoursUnit_other: '{{count}} hours',
          minutesUnit_one: '{{count}} minute',
          minutesUnit_other: '{{count}} minutes',
          veryClose: 'Starting very soon. Please prepare your setup now.',
          soon: 'Interview starts soon. Run a quick readiness check.',
          plentyOfTime: 'You still have preparation time.',
          windowClosed: 'Assessment window closed',
          windowClosedHelper: 'This assessment is no longer open. Contact your administrator if you still need to take it.'
        },
        active: {
          retakeAvailable: 'Retake Is Available',
          scheduled: 'Your Interview Is Scheduled',
          assigned: 'Interview Assigned',
          reviewHint: 'Review details and complete the launch checklist before starting.',
          statusLabel: 'Status: {{value}}',
          dateTime: 'Date & Time',
          tba: 'To be announced',
          duration: 'Duration',
          durationValue: '{{minutes}} minutes',
          countdown: 'Countdown',
          awaitingSchedule: 'Awaiting schedule',
          awaitingScheduleHelper: 'Your admin will set the schedule soon.',
          windowRange: '{{from}} – {{until}}',
          windowFrom: 'From {{from}}',
          windowUntil: 'Until {{until}}',
          readiness: 'Readiness'
        },
        readiness: {
          excellent: 'Excellent',
          good: 'Good',
          needsAttention: 'Needs Attention',
          notActive: 'Not Active'
        },
        details: {
          title: 'Interview Title',
          difficulty: 'Difficulty',
          unspecified: 'unspecified',
          completionDeadline: 'Completion Deadline',
          focusAreas: 'Focus Areas',
          description: 'Description'
        },
        actions: {
          continueInterview: 'Continue Interview',
          beginRetake: 'Begin Retake (System Check)',
          beginRetakePartA: 'Begin Part A retake (System Check)',
          beginRetakePartB: 'Begin Part B retake (System Check)',
          beginRetakeBoth: 'Begin full retake (System Check)',
          beginSystemCheck: 'Begin System Check',
          continueHint: 'Resume your current interview session from where you left off.',
          retakeHint: 'You can retake this interview now. System check verifies your setup before launch.',
          systemCheckHint: 'System check verifies camera, microphone, browser, and network before launch.'
        },
        noAssignment: {
          title: 'No Active Interview Assignment',
          deadlinePassed: 'The completion deadline for your latest assignment has passed. Please contact your administrator.',
          selectAbove: 'Select one of your assigned interviews above to continue.',
          notScheduledYet: 'Your interview has not been scheduled yet. You will see full details once assignment is active.'
        },
        placementResult: {
          title: 'Your placement result',
          subtitle: 'Your released scores and rank. Other candidates are not shown.',
          viewHistory: 'View in History',
          untitled: 'Assessment'
        },
        preparation: {
          environment: {
            label: 'Quiet and well-lit environment prepared',
            helper: 'Minimize noise and keep your face clearly visible.'
          },
          device: {
            label: 'Single-monitor and stable device setup',
            helper: 'Disconnect external displays and close background apps.'
          },
          network: {
            label: 'Reliable internet connection confirmed',
            helper: 'Prefer wired or strong Wi-Fi for uninterrupted interview flow.'
          },
          materials: {
            label: 'Role context and focus areas reviewed',
            helper: 'Review your assigned interview details and focus areas.'
          }
        },
        checklist: {
          title: 'Preparation Checklist',
          completeCount: '{{completed}}/{{total}} complete',
          markAllComplete: 'Mark all complete',
          resetChecklist: 'Reset checklist'
        },
        console: {
          title: 'Readiness Console',
          subtitle: 'Use this panel to validate preparation and share a quick interview brief.',
          readinessScore: 'Readiness Score',
          completionPercent: 'Preparation completion: {{percent}}%',
          briefCopied: 'Brief copied',
          copyBrief: 'Copy interview brief',
          openHistory: 'Open history',
          refreshAssignment: 'Refresh assignment details',
          autoRefreshStatus: 'Auto-refresh status',
          nextRefreshIn: 'Next refresh in {{seconds}}s',
          enabled: 'Enabled',
          disabled: 'Disabled'
        },
        expect: {
          title: 'What To Expect',
          step1Title: '1. System Check',
          step1Description: 'Validate camera, microphone, browser readiness, and network before interview launch.',
          step2TitleVoice: '2. Voice Interview',
          step2TitleMcq: '2. MCQ Assessment',
          step2TitleDefault: '2. AI Conversation',
          step2DescriptionVoice: 'Speak naturally while responses are transcribed and analyzed in real time.',
          step2DescriptionMcq: 'Complete timed multiple-choice questions with secure monitoring controls.',
          step2DescriptionDefault: 'Respond to structured AI questions tailored to your assigned role.',
          step3TitleMcq: '3. Results & Feedback',
          step3TitleDefault: '3. Writing Task & Wrap-up',
          step3DescriptionMcq: 'Track review status and final outcomes once your assessment is evaluated.',
          step3DescriptionDefault: 'Finish with a concise writing task before your overall evaluation is finalized.',
          remindersTitle: 'Important reminders',
          reminder1: 'Keep your face visible throughout the interview.',
          reminder2: 'Avoid switching tabs or opening unrelated applications.',
          reminder3: 'Maintain a stable network connection.',
          reminder4: 'Use a single-screen setup during monitored sections.',
          reminder5: 'Review instructions before launching the interview.',
          reminder6: 'If an issue appears, return to system check before retrying.'
        },
        footer: {
          tagline: 'where conversations become credible assessments.'
        },
        brief: {
          notAssigned: 'Not assigned yet',
          notScheduled: 'Not scheduled',
          noDeadline: 'No deadline provided',
          notSpecified: 'Not specified',
          interview: 'Interview: {{value}}',
          mode: 'Mode: {{value}}',
          scheduled: 'Scheduled: {{value}}',
          deadline: 'Deadline: {{value}}',
          duration: 'Estimated Duration: {{minutes}} minutes',
          focusAreas: 'Focus Areas: {{value}}',
          preparationProgress: 'Preparation Progress: {{percent}}%'
        }
      },
      voiceInterview: {
        messages: {
          reconnectingMicrophone: 'Reconnecting microphone...',
          microphoneUnstable: 'Microphone connection is unstable. Retrying...',
          noSpeechDetected: 'No speech detected for a while. Click Resume Listening and speak to continue.',
          localVoiceInUse: 'Using App Library free local voice for this prompt.',
          primaryVoiceFallbackUsed: 'Primary neural voice was unavailable. A backup voice was used for this prompt.',
          localFallbackVoiceActive: 'Neural voice service is temporarily unavailable. Local fallback voice is active.',
          audioUnavailableContinueOnScreen: 'Audio output is unavailable. Continue with on-screen prompts while the interview remains active.',
          generateNextQuestionFailed: 'Failed to generate next question. Please try again.',
          initializeFailed: 'Failed to initialize interview. Please check your camera and microphone permissions.',
          partAOnlyComplete: 'Part A is complete. This assessment does not include an oral viva. Thank you — you may leave this page.',
          partARetakeComplete: 'Part A retake is complete. Your oral score is unchanged. Thank you — you may leave this page.',
          browserNotSupported: 'Your browser does not support voice features. Please use Chrome or Edge.',
          neuralVoiceConfigUnavailable: 'Neural voice configuration could not be loaded. Local fallback speech will be used if available.',
          serviceTemporarilyUnavailable: 'AI interview service is temporarily unavailable. Please try again later.',
          responseRecoveryBlocked: 'An earlier interview response could not be restored safely. Keep this session and contact your administrator for recovery guidance.',
          repeatFailed: 'Unable to repeat the last question right now.',
          sessionRestored: 'Session restored. Continuing your voice interview where you left off.',
          sessionRestoredPartA: 'Session restored. Returning to Part A where you left off.',
          resumeContinuePrompt: 'Your session was paused. Continue when you are ready.'
        },
        partTransition: {
          title: 'Part A complete',
          description: 'Part A is complete. You will now continue to Part B: the voice interview.',
          bulletPartADone: 'Part A (multiple-choice) has been submitted.',
          bulletPartBNext: 'Part B is a live voice interview with the examiner.',
          bulletMicReady: 'Make sure your microphone and camera stay enabled.',
          continue: 'Continue to Part B (Voice Interview)'
        },
        conversation: {
          yourTurn: 'Your turn. Take your time — pause naturally when you are done.',
          yourTurnInterruptible: 'Your turn. Take your time — you can also start speaking while the examiner is talking.',
          examinerThinking: 'The examiner is considering your answer…',
          repeatingQuestion: 'Repeating the question.',
          rephrasingQuestion: 'Rephrasing the question.',
          movingOn: 'Moving on to the next question.',
          bargeInEnabled: 'You may interrupt the examiner at any time.'
        },
        chrome: {
          aiState: {
            idleLabel: 'Ready',
            listeningLabel: 'Listening',
            speakingLabel: 'Speaking',
            thinkingLabel: 'Analyzing',
            endingLabel: 'Wrapping up',
            completedLabel: 'Interview complete',
            idleDescription: 'The interviewer is prepared for the next exchange.',
            listeningDescription: 'Voice capture is active. Share your answer clearly and naturally.',
            speakingDescription: 'The interviewer is delivering your next prompt.',
            thinkingDescription: 'Your last response is being processed to shape the next question.',
            endingDescription: 'Final interview summary is being delivered before completion.',
            completedDescription: 'Session closed. Your responses are now saved for evaluation.'
          },
          browserNotSupportedTitle: 'Browser Not Supported',
          browserNotSupportedDescription: 'Voice interviews require Web Speech API support. Please use Google Chrome or Microsoft Edge.',
          goBack: 'Go Back',
          aiServiceUnavailableTitle: 'AI Service Unavailable',
          backToDashboard: 'Back to Dashboard',
          voiceStudio: 'Voice Studio',
          questionAbbrev: 'Q{{number}}',
          integrityPercent: 'Integrity {{percent}}%',
          voiceSource: 'Voice {{source}}',
          endInterview: 'End Interview',
          aiVoiceConductor: 'Examiner',
          examiner: 'Examiner',
          studioTitle: 'Live Voice Interview Studio',
          interviewStep: 'Interview Step',
          questionLabel: 'Question {{number}}',
          elapsed: 'Elapsed',
          captureMode: 'Capture Mode',
          autoSubmit: 'Auto submit',
          reviewBeforeSubmit: 'Review before submit',
          candidateCamera: 'Candidate Camera',
          liveFeedActive: 'Live feed active',
          videoStreamPaused: 'Video stream paused',
          initializing: 'Initializing',
          muted: 'Muted',
          micLive: 'Mic live',
          inputSensitivity: 'Input sensitivity',
          integritySignal: 'Integrity signal',
          recognitionConfidence: 'Recognition confidence',
          notAvailable: 'N/A',
          liveTranscription: 'Live transcription',
          responseAwaitingReview: 'Response awaiting review',
          wordCount_one: '{{count}} word',
          wordCount_other: '{{count}} words',
          submitResponse: 'Submit response',
          reviewEdited: 'Edited',
          reviewCountdown: '{{seconds}}s left to submit',
          reviewHint: 'Correct recognition mistakes, then submit. Ctrl+Enter submits. Limit {{count}} characters. The oral timer keeps running.',
          reviewTooShort: 'Add at least {{count}} words before submitting, or re-record.',
          reviewStale: 'That draft belonged to an earlier question, so it was cleared. Answer the question now on screen.',
          reviewRestored: 'An unsent correction was cleared when the session resumed. Answer the question now on screen.',
          showRecognised: 'Recognised by microphone',
          hideRecognised: 'Hide recognised text',
          restoreOriginal: 'Restore original',
          rerecord: 'Re-record',
          clearAndContinue: 'Clear and continue',
          latestAiPrompt: 'Question',
          currentQuestion: 'Question',
          sessionControls: 'Session Controls',
          sessionControlsHint: 'Manage capture, listening flow, and prompt replay.',
          autoSubmitAfterSilence: 'Auto-submit after silence',
          micOn: 'Mic On',
          videoOn: 'Video On',
          videoOff: 'Video Off',
          pause: 'Pause',
          resume: 'Resume',
          submit: 'Submit',
          repeat: 'Repeat',
          clarify: 'Clarify',
          end: 'End',
          voiceDiagnostics: 'Voice Diagnostics',
          audioLevel: 'Audio level',
          yourResponses: 'Your responses',
          violations: 'Violations',
          quickNotes: 'Quick Notes',
          clear: 'Clear',
          quickNotesPlaceholder: 'Private notes for structuring your answers...',
          autosavedLocally: 'Autosaved locally for this session.',
          conversationTranscript: 'Conversation Transcript',
          roleAi: 'Examiner',
          roleYou: 'You',
          preparingInterview: 'Preparing your interview...',
          invigilatorSupport: 'Invigilator Support',
          endInterviewConfirmTitle: 'End Interview?',
          endInterviewConfirmDescription: 'Are you sure you want to end the interview? Your responses will be saved and submitted for evaluation.',
          continueInterview: 'Continue Interview',
          errorLabel: 'Error'
        }
      },
      interviewHistory: {
        notAvailable: 'N/A',
        hidden: 'Hidden',
        loading: 'Loading your interview history...',
        title: 'Interview History',
        subtitle: 'Review completed interviews, score outcomes, and evaluator feedback.',
        durationFormat: '{{mins}}m {{secs}}s',
        stats: {
          total: 'Total',
          approved: 'Approved',
          inReview: 'In Review',
          avgScore: 'Avg Score',
          hidden: 'Hidden'
        },
        status: {
          approved: 'Approved',
          rejected: 'Rejected',
          inReview: 'In Review',
          pendingReview: 'Pending Review'
        },
        recommendation: {
          stronglyRecommend: 'Strongly Recommend',
          recommend: 'Recommend',
          neutral: 'Neutral',
          notRecommend: 'Do Not Recommend',
          stronglyNotRecommend: 'Strongly Do Not Recommend',
          recommendProvisional: 'Recommend (Provisional)',
          neutralProvisional: 'Neutral (Provisional)',
          notRecommendProvisional: 'Do Not Recommend (Provisional)',
          pending: 'Pending'
        },
        filters: {
          searchPlaceholder: 'Search title, description, or reviewer',
          statusAll: 'All statuses',
          statusPending: 'Pending',
          statusInReview: 'In review',
          statusReviewed: 'Reviewed',
          statusApproved: 'Approved',
          statusRejected: 'Rejected',
          visibilityAll: 'All visibility',
          visibilityVisible: 'Visible results',
          visibilityHidden: 'Hidden results',
          sortRecent: 'Sort: most recent',
          sortOldest: 'Sort: oldest first',
          sortScoreDesc: 'Sort: score high to low',
          sortScoreAsc: 'Sort: score low to high',
          sortStatus: 'Sort: status',
          showingCount: 'Showing {{shown}} of {{total}} interviews',
          clearFilters: 'Clear filters'
        },
        empty: {
          noHistoryTitle: 'No interview history yet',
          noHistoryDescription: 'Complete an interview to see your first evaluation in this dashboard.',
          noMatchesTitle: 'No matches for current filters',
          noMatchesDescription: 'Try broadening search, status, or visibility filters.'
        },
        card: {
          score: 'Score',
          integrity: 'Integrity',
          recommendation: 'Recommendation',
          mcqScoreLine: 'MCQ score: {{score}}% ({{correct}}/{{total}})',
          resultsHiddenMessage: 'Detailed results are currently hidden by your administrator.'
        },
        detail: {
          date: 'Date',
          duration: 'Duration',
          finalScore: 'Final Score',
          integrity: 'Integrity',
          resultsHiddenTitle: 'Detailed results are hidden',
          resultsHiddenMessage: 'Your administrator has hidden question-level and scoring details for this interview.',
          mcqSummaryTitle: 'MCQ Summary',
          mcqScoreLine: 'Score: {{score}}%',
          mcqCorrectLine: 'Correct: {{correct}}/{{total}}',
          reviewerComments: 'Reviewer Comments',
          reviewedBy: 'Reviewed by {{name}}',
          reviewedByOn: 'Reviewed by {{name}} on {{date}}',
          aiEvaluation: 'AI Evaluation',
          strengths: 'Strengths',
          areasForImprovement: 'Areas for Improvement',
          detailedFeedback: 'Detailed Feedback',
          criterionScores: 'Detailed Criterion Scores'
        },
        actions: {
          backToHistory: 'Back to History',
          backToDashboard: 'Back to Dashboard',
          summaryCopied: 'Summary copied',
          copySummary: 'Copy summary',
          exportCopied: 'Export copied',
          copyFilteredExport: 'Copy filtered export',
          generatingReport: 'Generating report...',
          reportHidden: 'Report Hidden by Admin',
          downloadReport: 'Download Test Report',
          retry: 'Retry',
          goToDashboard: 'Go to Dashboard',
          resetFilters: 'Reset Filters',
          resultsHiddenByAdmin: 'Results Hidden by Admin',
          viewFullDetails: 'View Full Details'
        },
        export: {
          csvHeader: 'Interview Title,Status,Visibility,Score,Integrity,Created At,Reviewer',
          visibilityHidden: 'hidden',
          visibilityVisible: 'visible',
          summaryInterview: 'Interview: {{title}}',
          summaryStatus: 'Status: {{status}}',
          summaryScore: 'Score: {{score}}',
          summaryIntegrity: 'Integrity: {{score}}',
          summaryCompleted: 'Completed: {{date}}',
          summaryRecommendation: 'Recommendation: {{value}}'
        },
        messages: {
          loadFailed: 'Failed to load interview history.',
          downloadReportFailed: 'Failed to download test report.'
        }
      },
      mcqPartA: {
        badge: 'Part A · Multiple Choice',
        progress: 'Question {{current}} of {{total}} — {{answered}} answered',
        loading: 'Preparing Part A: multiple-choice questions...',
        flag: 'Flag for review',
        previous: 'Previous',
        next: 'Next',
        submit: 'Submit Part A & continue to Part B',
        retry: 'Retry',
        messages: {
          startFailed: 'Unable to start the multiple-choice section. Please try again.',
          submitFailed: 'Unable to submit your answers right now. Please try again.',
          sessionRestored: 'Resuming where you left off. Your answers were kept.{{savedHint}} Your Part A timer was paused while you were away. Re-enable camera/mic if needed.',
          sessionRestoredSavedHint: ' Last saved: {{time}}.',
          timerPaused: 'Timer paused — reconnect or return to this tab to continue.'
        }
      },
      systemCheck: {
        header: {
          title: 'System Check',
          subtitle: 'Verify your system meets all requirements'
        },
        camera: {
          name: 'Camera Access',
          description: 'Video recording capability',
          denied: 'Camera access denied',
          previewTitle: 'Camera Preview',
          previewPlaceholder: 'Camera preview will appear here'
        },
        microphone: {
          name: 'Microphone Access',
          description: 'Audio recording capability',
          denied: 'Microphone access denied',
          levelTitle: 'Microphone Level',
          speakPrompt: 'Speak to test your microphone'
        },
        speaker: {
          name: 'Speaker Test',
          description: 'Audio playback capability',
          title: 'Speaker Test',
          clickToPlay: 'Click to play test sound',
          available: 'Audio output available',
          unverified: 'Could not verify speaker output',
          completed: 'Speaker test completed',
          playing: 'Playing...',
          playButton: 'Play Test Sound'
        },
        internet: {
          name: 'Internet Speed',
          description: 'Minimum 1 Mbps required',
          failed: 'Internet connection unstable or unavailable',
          unableToConnect: 'Unable to connect to internet',
          connectionQuality: 'Connection {{quality}} ({{latency}}ms latency)',
          quality: {
            excellent: 'Excellent',
            good: 'Good',
            moderate: 'Moderate'
          }
        },
        browser: {
          name: 'Browser Compatibility',
          description: 'Modern browser features'
        },
        screen: {
          name: 'Single Screen Setup',
          description: 'No external monitors',
          multipleMonitors: 'Multiple monitors detected. Please disconnect external displays.',
          singleDisplay: 'Single display confirmed',
          unverified: 'Unable to verify screen setup'
        },
        fullscreen: {
          name: 'Fullscreen Capability',
          description: 'Required for interview',
          available: 'Fullscreen mode available',
          unsupported: 'Fullscreen not supported in this browser'
        },
        requirements: {
          title: 'System Requirements'
        },
        actions: {
          retry: 'Retry',
          start: 'Start System Check',
          proceed: 'Proceed to Interview',
          runAgain: 'Run Checks Again'
        },
        status: {
          checking: 'Checking {{name}}...',
          someFailedTitle: 'Some checks failed',
          someFailedDescription: 'Please resolve the issues above before proceeding.'
        },
        tips: {
          title: 'Tips',
          item1: 'Allow camera and microphone permissions when prompted',
          item2: 'Use Chrome, Firefox, or Edge for best compatibility',
          item3: 'Disconnect external monitors for assessment mode',
          item4: 'Ensure stable internet connection'
        }
      },
      roomCamera: {
        title: 'Room camera (phone, side view)',
        hint: 'Place the phone so it shows your desk, your hands, and the floor beside the desk. Open this link on the phone. You stay signed in on this computer.',
        status: 'Status: {{status}}',
        skip: 'Continue without the room camera',
        pill: 'Room camera',
        urlLabel: 'Or open this link on the phone',
        systemCheck: 'This assessment asks for a second camera: your phone, placed to the side so the desk, your hands, and the floor stay visible. You will pair it after the secure lock, without signing in on the phone.',
        phoneTitle: 'Room camera',
        phonePurpose: 'This page shows your desk, hands, and the floor beside the desk to the invigilator. It does not show exam questions. Keep this page open.',
        placeSide: 'Put the phone on the side of the desk, about 1–2 metres away, in landscape.',
        placeShows: 'The picture should include your torso, hands, the desk, and the floor under the table. Your face does not need to fill the frame.',
        checklist: 'Placement check',
        checkDesk: 'The desk and my hands are visible',
        checkFloor: 'The floor under the table is visible',
        checkSide: 'This is a side view, not a close-up of my face',
        start: 'Start room camera',
        streaming: 'Streaming',
        paused: 'Paused while the page is hidden',
        missingToken: 'This link has no pairing token. Scan the QR code on the exam computer again.',
        permission: 'Camera permission is required',
        frameRejected: 'Frame rejected ({{status}})',
        switchToFront: 'Switch to front camera',
        switchToBack: 'Switch to back camera',
        otherCameraUnavailable: 'The other camera is not available.',
        connectedCheck: 'Room camera is connected. Check the picture, then continue.',
        confirmView: 'This view is correct — continue',
        hidePreview: 'Hide room camera',
        showPreview: 'Show room camera',
        pictureWaiting: 'A live room picture is waiting.',
        picturePending: 'The phone is connected, but the picture has not arrived yet.',
        previewLabel: 'Room camera'
      },
      systemCheckFlow: {
        modeLabels: { voice: 'voice', mcq: 'MCQ', text: 'text', combined: 'Part A + Part B' },
        modeConfig: {
          voice: { title: 'Voice Interview Mode', description: 'Your session will run as a voice conversation with live speech recognition.' },
          mcq: { title: 'MCQ Interview Mode', description: 'This session runs as a secure multiple-choice assessment.' },
          text: { title: 'Text Interview Mode', description: 'This session runs as a structured text-based conversation.' },
          combined: {
            title: 'Combined Assessment Mode',
            description: 'This assessment has two parts. Part A is a multiple-choice test. After you finish Part A, you will continue to Part B, a voice interview with the AI interviewer.'
          }
        },
        combinedBriefing: {
          title: 'This assessment has two parts',
          partA: 'Part A: multiple-choice questions (complete first)',
          partB: 'Part B: voice interview with the AI interviewer'
        },
        header: {
          backToDashboard: 'Back to dashboard',
          title: 'System Check',
          stepLabel: 'Step 1 of 3 before interview launch',
          modeSuffix: '{{mode}} mode'
        },
        intro: {
          title: 'Ready To Run Your Checks?',
          description: 'We verify permissions, browser compatibility, and monitoring requirements before interview start.',
          preparingFor: 'Preparing for',
          beforeYouBegin: 'Before you begin',
          tip1: 'Close unnecessary applications and browser tabs.',
          tip2: 'Stay in a quiet, well-lit location.',
          tip3: 'Use a single display and stable network.',
          tip4: 'Accept camera and microphone permission prompts.',
          startButton: 'Start system check',
          copyBaseline: 'Copy baseline diagnostics'
        },
        progress: {
          running: 'Running checks...',
          allReady: 'All systems ready',
          actionNeeded: 'Action needed',
          passedCount: '{{passed}}/{{total}} passed'
        },
        checks: {
          camera: { label: 'Camera Access', description: 'Video capture permissions', errorHint: 'Allow camera permissions in your browser and retry.' },
          microphone: { label: 'Microphone Access', description: 'Audio capture permissions', errorHint: 'Allow microphone permissions in your browser and retry.' },
          internet: { label: 'Network Stability', description: 'Connection to VivaAssess services', errorHint: 'Check network stability and ensure firewall allows platform access.' },
          browser: {
            label: 'Browser Compatibility',
            voiceDescription: 'Speech recognition and media support',
            defaultDescription: 'Modern media feature support',
            voiceHint: 'Voice mode requires speech recognition support (Chrome or Edge recommended).',
            defaultHint: 'Use Chrome, Firefox, or Edge for best compatibility.'
          },
          singleScreen: { label: 'Single Screen Setup', description: 'External monitors are not active', errorHint: 'Disconnect external displays before starting the interview.' }
        },
        network: {
          latencyLabel: 'Network latency: {{value}}',
          lastCheckedAt: 'Last checked at {{time}}',
          accessCheckTitle: 'Network access check',
          checkingPolicy: 'Checking assignment access policy...',
          publicIp: 'Public IP: {{value}}',
          detectedLocation: 'Detected location: {{value}}',
          unavailable: 'Unavailable',
          startOrRerun: 'Start or rerun checks to detect IP and location readiness.'
        },
        actions: {
          retryAll: 'Retry all',
          rerunFailed: 'Rerun failed checks',
          diagnosticsCopied: 'Diagnostics copied',
          copyDiagnostics: 'Copy diagnostics',
          verifyingAi: 'Verifying AI service',
          continueToInterview: 'Continue to interview',
          continueRestore: 'Continue restored session'
        },
        preview: {
          title: 'Camera & microphone preview',
          accessingCamera: 'Accessing camera...',
          awaitingPermission: 'Preview appears after permission is granted.',
          livePreview: 'Live preview',
          microphoneLevel: 'Microphone level',
          waiting: 'Waiting',
          goodSignal: 'Good signal',
          detected: 'Detected',
          low: 'Low',
          speakHint: 'Speak for a few seconds and verify the meter responds.',
          disableMirror: 'Disable mirror preview',
          enableMirror: 'Enable mirror preview',
          probeNetwork: 'Probe network'
        },
        diagnostics: {
          notRunYet: 'not run yet',
          notMeasured: 'not measured',
          unknown: 'unknown',
          header: 'VivaAssess Candidate Diagnostics',
          checkedAt: 'Checked at: {{value}}',
          interviewMode: 'Interview mode: {{mode}}',
          networkLatency: 'Network latency: {{value}}',
          accessDecision: 'Access policy decision: {{value}}',
          detectedIp: 'Detected public IP: {{value}}',
          userAgent: 'User agent: {{value}}',
          systemChecksHeader: 'System checks:',
          pass: 'PASS',
          fail: 'FAIL',
          pending: 'PENDING'
        },
        messages: {
          noAssignment: 'No interview assignment is currently selected. Return to your dashboard and retry.',
          accessVerificationFailed: 'Unable to verify network access restrictions. Please retry the system check.',
          accessPolicyDenied: 'Your current network does not satisfy interview access policy.',
          aiUnavailable: 'AI interview service is temporarily unavailable. Please try again later.'
        }
      },
      declaration: {
        title: 'Pre-assessment declaration',
        subtitle: 'Please answer the following questions before starting the placement assessment. This step is mandatory.',
        placementLabel: 'Placement: {{name}}',
        item1: 'If your placement test result justifies it, do you undertake to enrol in the "Algorithmic Thinking" course?',
        item2: 'If your placement test result allows it, do you undertake to participate in the advanced group?',
        item3: 'Your programming background',
        item3Notice: 'This information is background for your instructors only. It is excluded from scoring and group allocation, and it is never given to the AI examiner.',
        yes: 'Yes',
        no: 'No',
        noProgrammingBackground: 'I have not learned programming before',
        languagePlaceholder: 'Programming language',
        durationPlaceholder: 'Duration (e.g. 2 years)',
        sources: {
          school: 'School',
          extracurricular: 'Extracurricular course',
          independent: 'Independently',
          other: 'Other'
        },
        addRow: 'Add row',
        removeRow: 'Remove row',
        errors: {
          answerBoth: 'Please answer both declaration questions (Yes/No).',
          backgroundRequired: 'Add at least one programming background row or check "I have not learned programming before".',
          submitFailed: 'Failed to submit the declaration. Please try again.'
        },
        saving: 'Saving…',
        submitAndStart: 'Submit and start the assessment',
        alreadySubmittedTitle: 'Declaration already submitted',
        alreadySubmittedBody: 'You have already completed the pre-assessment declaration. You can proceed to the assessment.',
        continueButton: 'Continue to the assessment'
      },
      interviewComplete: {
        header: {
          subtitle: 'Assessment complete',
          exitPortal: 'Exit portal'
        },
        hero: {
          badge: 'Excellent work',
          title: 'Interview Complete',
          description: 'Your responses are securely saved and queued for review. You can now track progress from your history panel.'
        },
        stats: {
          duration: 'Duration',
          durationValue: '{{minutes}} min',
          mcqScore: 'MCQ Score',
          answered: 'Answered',
          questions: 'Questions',
          integrity: 'Integrity',
          pending: 'Pending',
          submitted: 'Submitted',
          complete: 'Complete'
        },
        nextSteps: {
          title: 'What happens next',
          immediate: 'Immediate',
          oneTwoDays: '1-2 days',
          threeFiveDays: '3-5 days',
          scoreValidation: {
            title: 'Score validation',
            description: 'Your submitted MCQ attempt is validated and stored with full integrity metadata.'
          },
          aiAnalysis: {
            title: 'AI analysis',
            description: 'Your transcript and response quality are analyzed for structure, depth, and alignment to interview goals.'
          },
          evaluatorReview: {
            title: 'Evaluator review',
            description: 'A reviewer validates the automated assessment and adds contextual notes for your final report.'
          },
          resultsDelivery: {
            title: 'Results delivery',
            description: 'You receive final status and detailed feedback through your candidate history and notification channels.',
            hiddenDescription: 'When your administrator releases results, scores and feedback will appear in History. Until then, History shows that results are hidden.'
          }
        },
        completionSummary: {
          title: 'Completion summary',
          candidate: 'Candidate',
          completed: 'Completed',
          interview: 'Interview',
          mcqScore: 'MCQ Score',
          correctAnswers: 'Correct Answers'
        },
        feedback: {
          title: 'Interview feedback',
          subtitle: 'Share your experience to help us improve interview quality and candidate experience.',
          receivedTitle: 'Feedback received',
          alreadyRecorded: 'Your feedback has already been recorded.',
          overallRating: 'Overall interview rating',
          difficultyBalance: 'Difficulty balance',
          questionClarity: 'Question clarity',
          platformExperience: 'Platform experience',
          fairness: 'Fairness',
          recommendQuestion: 'Would you recommend VivaAssess?',
          yes: 'Yes',
          no: 'No',
          positiveLabel: 'What worked well?',
          positivePlaceholder: 'Share positives from your interview experience',
          improvementLabel: 'What should we improve?',
          improvementPlaceholder: 'Suggest improvements for future interviews',
          submitting: 'Submitting...',
          submit: 'Submit feedback'
        },
        confirmation: {
          title: 'Confirmation reference',
          interviewId: 'Interview ID:',
          keepReference: 'Keep this reference for support inquiries and progress tracking.',
          mcqSubmittedOn: 'MCQ submitted on {{date}}',
          autoSubmitSuffix: '(auto-submit)',
          summaryCopied: 'Summary copied',
          copySummary: 'Copy summary',
          autoReturnActive: 'Auto return in {{seconds}}s',
          enableAutoReturn: 'Enable auto return',
          resultsHidden: 'Detailed scores are hidden by your administrator. You will see them on your dashboard and in History after they are released.'
        },
        actions: {
          goToDashboard: 'Go to Dashboard',
          viewHistory: 'View History',
          generatingReport: 'Generating Report...',
          checkingReport: 'Checking Report...',
          downloadReport: 'Download Test Report',
          reportHidden: 'Report Hidden by Admin',
          openHistoryForReport: 'Open History for Report',
          exitPortal: 'Exit Portal',
          reportReady: 'Your test report is ready for download.',
          reportPending: 'Test report becomes available after evaluator processing. You can access it from interview history.',
          safeToClose: 'You can safely close this page at any time.'
        },
        footer: {
          tagline: 'where conversations become credible assessments.',
          supportLabel: 'Support:'
        },
        summary: {
          notAvailable: 'N/A',
          header: 'VivaAssess Interview Completion Summary',
          candidate: 'Candidate: {{name}}',
          interview: 'Interview: {{title}}',
          completed: 'Completed: {{date}}',
          duration: 'Estimated Duration: {{minutes}} minutes',
          mcqScore: 'MCQ Score: {{score}}',
          mcqCorrect: 'MCQ Correct: {{correct}} / {{total}}',
          mcqAnswered: 'MCQ Answered: {{answered}} / {{total}}',
          submissionMode: 'Submission Mode: {{mode}}',
          autoSubmit: 'Auto-submit',
          manualSubmit: 'Manual submit',
          questionCount: 'Question Count: {{count}}',
          rank: 'Rank: {{rank}}',
          partA: 'Part A: {{score}}',
          partB: 'Part B: {{score}}',
          total: 'Total: {{score}}',
          integrityScore: 'Integrity Score: {{score}}%',
          resultsHidden: 'Detailed scores are hidden by your administrator.'
        },
        messages: {
          feedbackSubmitFailed: 'Unable to submit feedback right now.',
          missingContext: 'Interview context is missing. Refresh the page and try again.',
          rateOverall: 'Please rate your overall interview experience.',
          additionalFeedbackRequired: 'Please add at least one additional feedback detail.',
          commentTooLong: 'Comment fields must be 2000 characters or less.',
          feedbackSubmitted: 'Thank you. Your feedback has been submitted.',
          feedbackAlreadySubmitted: 'Feedback was already submitted for this interview assignment.'
        }
      },
      interviewSession: {
        chrome: {
          accuracy: 'Accuracy',
          aiInsights: 'AI Insights',
          aiInterviewer: 'Examiner',
          examiner: 'Examiner',
          aiIsSpeaking: 'Speaking',
          aiIsThinking: 'Thinking...',
          aiPreparingNextPrompt: 'Preparing the next question...',
          aiProctoringActive: 'AI Proctoring Active',
          aiPrompt: 'Question',
          aiSpeaking: 'Speaking',
          aiThinking: 'Thinking',
          all: 'All',
          allCount: 'All ({{count}})',
          allQuestionsAnswered: 'All questions are answered.',
          answered: 'Answered',
          answeredFlaggedSummary: 'Answered {{answered}}/{{total}} • Flagged {{flagged}}',
          answeredN: 'Answered {{count}}',
          answersLockedHint: 'Your answers will be locked and scoring will begin immediately.',
          approxMinutes: '~{{minutes}} min',
          assessmentSummary: 'Assessment Summary',
          autoSubmitSuffix: '(auto-submit)',
          backToDashboard: 'Back to Dashboard',
          buildingMcqAnalytics: 'Building your final MCQ analytics and detailed question review...',
          cameraIsOff: 'Camera is off',
          cameraMiniWindowHint: 'Camera stays in the movable mini window.',
          cameraOff: 'Camera off',
          candidateCam: 'Candidate Cam',
          candidateCamera: 'Candidate Camera',
          characterCount: '{{count}} characters',
          clear: 'Clear',
          clearCurrentAnswer: 'Clear Current Answer',
          completeInterview: 'Complete Interview',
          completeInterviewFlow: 'Complete Interview Flow',
          completePopupToContinue: 'Complete the pop-up window to continue the interview.',
          completePopupToFinish: 'Complete the pop-up window to finish the assessment.',
          completingEllipsis: 'Completing...',
          completingInterview: 'Completing Interview',
          conceptMasteryCheck: 'Concept Mastery Check',
          consentAndContinue: 'I Consent and Continue',
          consentPending: 'Consent Pending',
          continueInterview: 'Continue Interview',
          continueMcq: 'Continue MCQ',
          conversationTimeline: 'Conversation Timeline',
          correct: 'Correct',
          correctXOfY: 'Correct {{correct}} / {{total}}',
          currentQuestion: 'Current Question',
          elapsed: 'Elapsed',
          emotion: 'Emotion',
          enable: 'Enable',
          endInterview: 'End Interview',
          endInterviewEarly: 'End Interview Early?',
          endInterviewEarlyConfirm: 'You are about to end the interview before the session is complete. Are you sure?',
          entriesCount: '{{count}} entries',
          exitSession: 'Exit Session',
          explanation: 'Explanation',
          filterQuestions: 'Filter Questions',
          finalizeAssessment: 'Finalize Assessment',
          finalizeToCloseHint: 'Finalize to close this section and move to the completion page with your confirmation details.',
          finalizingInterview: 'Finalizing Interview',
          finalizingInterviewHint: 'Your responses are being processed securely. Please keep this page open.',
          flag: 'Flag',
          flagged: 'Flagged',
          flaggedCount: 'Flagged ({{count}})',
          flaggedN: 'Flagged {{count}}',
          focus: 'Focus',
          hide: 'Hide',
          hideEmotionAnalysis: 'Hide emotion analysis',
          incorrect: 'Incorrect',
          integrity: 'Integrity',
          integrityPercent: 'Integrity: {{percent}}%',
          interviewInProgress: 'Interview is in progress.',
          interviewServiceUnavailable: 'Interview Service Unavailable',
          invigilatorSupport: 'Invigilator Support',
          keepWindowActiveHint: 'Keep this window active. Switching tabs or leaving fullscreen will be logged.',
          listening: 'Listening',
          listeningEllipsis: 'Listening...',
          live: 'Live',
          liveDraft: 'Live Draft',
          liveInterview: 'LIVE INTERVIEW',
          liveSupportDesk: 'Live Support Desk',
          liveTranscript: 'Live Transcript',
          mcqAssessment: 'MCQ Assessment',
          mcqCheckpointInProgress: 'MCQ checkpoint in progress',
          mcqCompletion: 'MCQ Completion',
          mcqResults: 'MCQ Results',
          mcqResultsReady: 'MCQ results ready',
          mcqXOfY: 'MCQ {{current}} of {{total}}',
          mic: 'Mic',
          micLevel: 'Mic level',
          micLiveShort: 'Mic live',
          micMuted: 'Mic muted',
          microphoneLevel: 'Microphone Level',
          mute: 'Mute',
          muteMicrophone: 'Mute microphone',
          muted: 'Muted',
          next: 'Next',
          nextAction: 'Next Action',
          nextQuestion: 'Next Question',
          nextUnanswered: 'Next Unanswered',
          noMcqsAvailable: 'No MCQs available yet.',
          noQuestionsMatchFilter: 'No questions match this filter.',
          noQuestionsMatchSelectedFilter: 'No questions match the selected filter.',
          off: 'Off',
          pending: 'Pending',
          pendingReview: 'Pending Review',
          performanceCommandCenter: 'Performance Command Center',
          performanceCommandCenterHint: 'Explore detailed outcomes, filter question review, and finalize submission when ready.',
          phaseMcq: 'MCQ',
          phaseQA: 'Q&A',
          phaseResults: 'Results',
          phaseWritten: 'Written',
          pleaseWaitProcessing: 'Please wait while we process your responses...',
          preparingFirstQuestion: 'Preparing your first question...',
          preparingMcqSet: 'Preparing your MCQ set...',
          preparingNextPrompt: 'Preparing your next prompt...',
          preparingWrittenQuestions: 'Preparing written questions...',
          previous: 'Previous',
          proceedAnyway: 'Proceed Anyway',
          proctorControls: 'Proctor Controls',
          proctoringConsentBullet1: 'Camera visibility and face presence checks',
          proctoringConsentBullet2: 'Background audio anomaly detection',
          proctoringConsentBullet3: 'Automatic warning and invigilator escalation on high-risk incidents',
          proctoringConsentDescription: 'This interview uses AI-powered camera and audio anomaly detection. Consent is required before you can continue.',
          proctoringConsentRequired: 'Proctoring Consent Required',
          progress: 'Progress',
          qTime: 'Q Time',
          questionNumber: 'Question {{number}}',
          questionPalette: 'Question Palette',
          questionUnavailable: 'Question unavailable.',
          questionXOfY: 'Question {{current}} of {{total}}',
          quickActions: 'Quick Actions',
          recordedSecurelyNotice: 'Your interview is being recorded securely',
          reset: 'Reset',
          resetCam: 'Reset Cam',
          responseStudio: 'Response Studio',
          responseStudioHint: 'Craft clear, structured responses.',
          resultSummary: 'Result Summary',
          results: 'Results',
          resultsNotAvailableYet: 'Results are not available yet.',
          reviewLocked: 'Review locked',
          reviewResultsInPopup: 'Review your results in the pop-up window, then finish the assessment.',
          reviewUnanswered: 'Review Unanswered',
          riskScoreRecommendation: 'Risk score: {{score}} • Recommendation: {{recommendation}}',
          saving: 'Saving...',
          score: 'Score',
          scoringInProgress: 'Scoring is in progress. Preparing your results dashboard...',
          sessionPulse: 'Session Pulse',
          show: 'Show',
          showEmotionAnalysis: 'Show emotion analysis',
          skip: 'Skip',
          skipThisQuestion: 'Skip this question',
          starMethodTip: 'Tip: Use the STAR method (Situation, Task, Action, Result) for stronger answers.',
          stillHaveUnanswered: 'You still have {{count}} unanswered question{{plural}}.',
          strengths: 'Strengths',
          structured: 'Structured',
          submissionCompleted: 'Submission Completed',
          submissionCompletedHint: 'Your answers are safely submitted. Detailed per-question analysis is still being generated.',
          submit: 'Submit',
          submitMcq: 'Submit MCQ',
          submitMcqSection: 'Submit MCQ Section?',
          submitMcqs: 'Submit MCQs',
          submitResponse: 'Submit Response',
          submittedAt: 'Submitted {{datetime}}',
          submitting: 'Submitting...',
          submittingAnswersSecurely: 'Submitting your answers securely. Please keep this window open.',
          switchToListView: 'Switch to list view',
          switchToSingleView: 'Switch to single-question view',
          textInterviewStudio: 'TEXT INTERVIEW STUDIO',
          topicGaps: 'Topic Gaps',
          total: 'Total',
          totalQuestions: 'Total Questions',
          transcriptAppearsHere: 'Transcript appears here once the interview begins.',
          transcriptWillAppear: 'Transcript will appear here as the conversation progresses...',
          turnCameraOff: 'Turn camera off',
          turnCameraOn: 'Turn camera on',
          turnOffCamera: 'Turn off camera',
          turnOnCamera: 'Turn on camera',
          typeResponsePlaceholder: 'Type your response. Press Enter to submit, Shift+Enter for a new line.',
          typeYourResponsePlaceholder: 'Type your response here... (Press Enter to send, Shift+Enter for new line)',
          unanswered: 'Unanswered',
          unansweredCount: 'Unanswered ({{count}})',
          unansweredN: 'Unanswered {{count}}',
          unmute: 'Unmute',
          unmuteMicrophone: 'Unmute microphone',
          video: 'Video',
          waiting: 'Waiting...',
          waitingEllipsis: 'Waiting...',
          waitingForAiResponse: 'Waiting for AI response...',
          weaknesses: 'Weaknesses',
          wordCount: '{{count}} words',
          wordsCount: '{{count}} words',
          wrappingUp: 'Wrapping Up',
          writeYourAnswerPlaceholder: 'Write your answer here... (3-5 sentences recommended)',
          writtenAnswerPlaceholder: 'Write your answer with concrete examples and measurable outcomes.',
          writtenNumber: 'Written {{number}}',
          writtenQuestion: 'Written Question',
          writtenQuestionOf: 'Question {{current}} of {{total}}',
          writtenResponse: 'Written Response',
          writtenXOfY: 'Written {{current}} of {{total}}',
          you: 'You',
          yourPick: 'Your pick',
          yourResponse: 'Your Response',
          yourTurn: 'Your Turn',
          yourTurnToRespond: 'Your turn to respond'
        },
        messages: {
          aiInterviewerNotAvailable: 'AI Interviewer not available',
          aiInterviewerNotInitialized: 'AI Interviewer not initialized'
        }
      }
    },
    invigilator: {
      nav: {
        liveMonitoring: 'Assignment queue',
        liveAv: 'Live camera & screen',
        reportHistory: 'Report history',
        accountSettings: 'Account Settings',
        liveMonitoringDescription: 'Assignments, alerts, and session interventions',
        liveAvDescription: 'Watch candidate camera and entire-screen feeds',
        reportHistoryDescription: 'Review integrity reports and screenshots',
        accountSettingsDescription: 'Profile details and account security',
        help: 'Help',
        helpDescription: 'Guides for invigilator tools'
      },
      help: {
        title: 'Help',
        subtitle: 'Guides for invigilator tools',
        back: 'Back',
        loading: 'Loading guides...',
        loadFailed: 'Failed to load guides.',
        emptyTitle: 'No guides yet',
        emptySubtitle: 'Published invigilator guides will appear here.',
        guides: 'Guides',
        noSteps: 'This guide has no steps yet.',
        selectGuide: 'Select a guide to read it.'
      },
      shell: {
        overviewTitle: 'Assignment queue',
        overviewSubtitle: 'Interventions and session status',
        liveAvTitle: 'Live camera & screen',
        liveAvSubtitle: 'Real-time candidate feeds',
        reportHistoryTitle: 'Report history',
        reportHistorySubtitle: 'Integrity evidence archive',
        accountTitle: 'My Account',
        accountSubtitle: 'Profile and security',
        accountDescription: 'Manage your profile details and account security settings.',
        backToQueue: 'Back to queue'
      },
      overview: {
        title: 'Invigilation dashboard',
        subtitle: 'Monitor assigned candidates and intervene in real time.',
        refresh: 'Refresh queue',
        stats: {
          assigned: 'Assigned candidates',
          live: 'Live sessions',
          flagged: 'Flagged sessions',
          idle: 'No session started'
        },
        searchPlaceholder: 'Search name, email, Neptun, or interview',
        filters: {
          all: 'All assignments',
          live: 'Live only',
          flagged: 'Flagged only',
          idle: 'No session started'
        },
        emptyQueue: 'No candidates assigned yet.',
        emptyFilter: 'No assignments match this filter.',
        selectCandidate: 'Select a candidate to monitor their session.',
        listTitle: 'Assigned candidates',
        neptun: 'Neptun',
        candidate: 'Candidate',
        noActiveSession: 'No active session',
        integrity: 'Integrity: {{score}}%',
        sessionNotStarted: 'Session has not started yet. Live transcript, chat, and timeline become available once the candidate starts.',
        openLiveAv: 'Open live camera & screen',
        status: {
          live: 'LIVE',
          idle: 'Awaiting start',
          completed: 'Completed',
          flagged: 'Flagged',
          notStarted: 'Not started'
        },
        schedule: {
          slot: 'Slot',
          window: 'Window',
          none: 'Unscheduled'
        },
        violationBanner: 'New integrity violation detected.',
        notices: {
          messageSent: 'Message sent successfully.',
          interventionSent: '{{label}} sent to candidate.',
          timelineLogged: 'Timeline event logged successfully.',
          alertStatus: 'Alert marked as {{status}}.'
        },
        errors: {
          loadAssignments: 'Failed to load assignments',
          loadSession: 'Failed to load session',
          loadMessages: 'Failed to load messages',
          loadTimeline: 'Failed to load proctoring timeline',
          loadAlerts: 'Failed to load invigilator alerts',
          sendMessage: 'Failed to send message',
          runIntervention: 'Failed to run intervention',
          logEvent: 'Failed to log timeline event',
          updateAlert: 'Failed to update alert status',
          eventRequired: 'Select an event type and provide a note before logging.',
          sessionInactive: 'Candidate session is not active yet.',
          timelineUpdateFailed: 'Timeline update failed'
        },
        interventions: {
          title: 'Quick interventions',
          running: 'Running…',
          focusWarning: {
            label: 'Focus warning',
            message: 'Please keep your focus on the interview window and follow assessment rules.'
          },
          identityCheck: {
            label: 'Identity check',
            message: 'Please adjust your camera for identity verification and keep your face fully visible.'
          },
          technicalPause: {
            label: 'Technical pause',
            message: 'We noticed a technical issue. Pause briefly and confirm when your setup is stable again.'
          }
        },
        transcript: {
          title: 'Live transcript',
          empty: 'Transcript will appear once the interview starts.',
          ai: 'AI interviewer',
          candidate: 'Candidate'
        },
        alerts: {
          title: 'Integrity alerts',
          empty: 'No integrity events yet.',
          status: 'Status: {{value}}',
          acknowledge: 'Acknowledge',
          investigating: 'Investigating',
          resolve: 'Resolve',
          violation: 'Violation',
          policyViolation: 'Policy violation detected',
          statuses: {
            new: 'New',
            acknowledged: 'Acknowledged',
            investigating: 'Investigating',
            resolved: 'Resolved',
            dismissed: 'Dismissed'
          }
        },
        chat: {
          title: 'Chat with candidate',
          empty: 'No messages yet.',
          invigilator: 'Invigilator',
          candidate: 'Candidate',
          placeholder: 'Send instruction or clarification…',
          waiting: 'Waiting for active session…',
          send: 'Send',
          sending: 'Sending…'
        },
        timeline: {
          title: 'Proctoring timeline',
          empty: 'No timeline events recorded yet.',
          log: 'Log timeline event',
          logging: 'Logging…',
          notePlaceholder: 'Describe what you observed…',
          waiting: 'Waiting for active session…',
          system: 'System',
          severityLow: 'Low severity',
          severityMedium: 'Medium severity',
          severityHigh: 'High severity',
          severityCritical: 'Critical severity',
          events: {
            manual_note: 'Manual note',
            behavioral_concern: 'Behavioral concern',
            verification_step: 'Verification step',
            technical_observation: 'Technical observation',
            policy_reminder: 'Policy reminder'
          }
        }
      }
    },
    interviews: {
      status: {
        inProgress: 'In progress',
        completed: 'Completed',
        pending: 'Pending'
      }
    },
    reports: {
      generatedOn: 'Generated on {{date}}'
    },
    notifications: {
      newNotification: 'You have a new notification.'
    },
    mainSite: {
      experienceFallback: {
        product: {
          title: 'Product Overview',
          summary: 'Explore the VivaAssess platform and the capabilities behind reliable interview decisions.'
        },
        solutions: {
          title: 'Solutions',
          summary: 'Find VivaAssess solutions for hiring teams, recruiters, and learning operations.'
        },
        solutionsHrLeaders: {
          title: 'Solutions for HR Leaders',
          summary: 'Increase decision consistency and governance confidence for HR leadership.'
        },
        solutionsRecruiters: {
          title: 'Solutions for Recruiters',
          summary: 'Reduce pipeline friction and speed up candidate progression.'
        },
        solutionsEducationTraining: {
          title: 'Solutions for Education and Training Teams',
          summary: 'Scale interview and oral assessment workflows with quality controls.'
        },
        pricing: {
          title: 'Pricing',
          summary: 'Choose a VivaAssess plan that fits your stage and growth trajectory.'
        },
        securityTrustCenter: {
          title: 'Security and Trust Center',
          summary: 'Review security, privacy, AI governance, and reliability posture.'
        },
        caseStudies: {
          title: 'Case Studies',
          summary: 'See how teams transformed interview operations with VivaAssess.'
        },
        compare: {
          title: 'Compare VivaAssess',
          summary: 'Compare VivaAssess to manual processes, traditional tools, and generic AI assistants.'
        },
        compareManualProcess: {
          title: 'VivaAssess vs Manual Process',
          summary: 'Understand how structured workflows outperform manual interview operations.'
        },
        compareTraditionalTools: {
          title: 'VivaAssess vs Traditional Tools',
          summary: 'See the difference between legacy interview tooling and VivaAssess.'
        },
        compareGenericAiAssistant: {
          title: 'VivaAssess vs Generic AI Assistant Workflows',
          summary: 'Discover why domain-specific interview intelligence delivers stronger outcomes.'
        }
      }
    },
    errors: {
      actions: {
        goBack: 'Go Back',
        goHome: 'Go Home',
        goToLogin: 'Go to Login',
        retry: 'Retry'
      },
      requestIdLabel: 'Support request ID',
      pages: {
        badRequest: {
          title: 'Bad Request',
          message: 'The request could not be processed. Please review your input and try again.'
        },
        unauthorized: {
          title: 'Unauthorized',
          message: 'Your session is missing or has expired. Please sign in to continue.'
        },
        forbidden: {
          title: 'Forbidden',
          message: 'You do not have permission to access this page or resource.'
        },
        notFound: {
          title: 'Page Not Found',
          message: 'The page or resource you requested could not be found.'
        },
        internalServerError: {
          title: 'Internal Server Error',
          message: 'Something went wrong on our side. Please try again in a moment.'
        },
        serviceUnavailable: {
          title: 'Service Unavailable',
          message: 'The service is temporarily unavailable. Please retry shortly.'
        }
      },
      shortcuts: {
        title: 'Error Pages',
        description: 'Select an error page preview.'
      }
    }
  },
  fr: {
    common: {
      notificationPanel: notificationPanelLabels.fr,
      transcriptExpiry: transcriptExpiryLabels.fr,
      blockedObjects: blockedObjectLabels.fr,
      authoringGrounding: authoringGroundingLabels.fr,
      sessionSave: {"conflict": "L’enregistrement automatique est arrêté car une progression plus récente ou une autre étape a été enregistrée. Gardez cet onglet ouvert, cessez d’y modifier les réponses et utilisez un seul onglet. Ne soumettez pas à nouveau les réponses ; contactez un administrateur avant de récupérer le travail non enregistré.", "unavailable": "L’enregistrement de la progression n’est pas confirmé. Gardez cet onglet ouvert et vérifiez la connexion. Évitez de recharger ou d’ouvrir un autre onglet tant que la récupération est incertaine ; contactez un administrateur si l’enregistrement ne reprend pas."},
      completionRepair: {"preview": "Prévisualiser la reprise", "summary": "Composant : {{component}}. Tentatives restantes : {{count}}. La prévisualisation expire après cinq minutes.", "part_a": "Classement de la partie A", "oral": "Notation orale", "confirm": "J’ai examiné et résolu la panne temporaire. Relancer uniquement le travail initial dans la limite existante ; préserver les preuves, décisions humaines et règles de publication.", "apply": "Confirmer la reprise", "loading": "Vérification de la reprise…", "unavailable": "Reprise indisponible ou nécessitant une enquête. Les travaux actifs, annulés, épuisés, modifiés ou déjà examinés ne peuvent pas être relancés ici.", "failed": "La reprise n’a pas pu être confirmée. Réessayez la même confirmation pour obtenir son reçu ou demandez une nouvelle prévisualisation après expiration. Ne soumettez pas à nouveau les réponses.", "accepted": "Reprise acceptée et auditée. Actualisez après le traitement. Les règles de publication existantes restent applicables.", "receipt": "Reçu d’audit"},
      completionReview: {"title": "Vérification du traitement des sessions", "note": "Liste des sessions terminées avec évaluation absente ou multiple, notation arrêtée ou traitement non suivi. Actualisez pour voir les changements. La reprise exige une enquête et une prévisualisation admissible ; consulter la liste ne relance ni ne publie rien.", "empty": "Aucune session terminée correspondante dans ce groupe.", "session": "Session", "candidate": "Candidat", "interview": "Entretien", "assessment": "Évaluation", "available": "Disponible", "missing": "Absente", "ambiguous": "Plusieurs évaluations", "not_submitted": "Non soumise", "more": "Afficher plus"},
      completionStatus: {"title": "Traitement de la soumission", "refresh": "Actualiser le statut", "loading": "Vérification du traitement…", "unavailable": "Statut indisponible. Actualisez ou réessayez plus tard.", "unknown": "Le statut de notation n’est pas enregistré. Contactez votre administrateur si les résultats restent en attente.", "not_submitted": "Cette session n’a pas été acceptée comme terminée.", "queued": "Votre soumission est acceptée. La notation est en attente.", "running": "Votre soumission est acceptée. La notation est en cours.", "retrying": "Votre soumission est acceptée. La notation sera relancée automatiquement.", "processed": "La notation a été traitée.", "needs_attention": "Votre soumission est enregistrée. Le traitement nécessite l’intervention d’un administrateur.", "releaseNote": "Le statut de traitement ne publie pas les résultats et ne modifie pas la décision de révision."},
      app: {
        name: 'VivaAssess',
        tagline: 'Plateforme credibile pour entretiens IA'
      },
      skipToMainContent: 'Aller au contenu principal',
      nav: {
        home: 'Accueil',
        product: 'Produit',
        solutions: 'Solutions',
        pricing: 'Tarifs',
        trust: 'Confiance',
        compare: 'Comparer',
        contact: 'Contact',
        resources: 'Ressources',
        features: 'Fonctionnalites',
        about: 'A propos',
        teams: 'Equipes',
        docs: 'Docs',
        caseStudies: 'Etudes de cas'
      },
      actions: {
        goToDashboard: 'Aller au tableau de bord',
        signOut: 'Se deconnecter',
        close: 'Fermer',
        expandNavigation: 'Developper la navigation',
        collapseNavigation: 'Reduire la navigation',
        backToPortal: 'Retour au portail',
        help: 'Aide'
      },
      labels: {
        language: 'Langue',
        searchModules: 'Rechercher des modules...',
        pinned: 'Epingle',
        allModules: 'Tous les modules',
        navigation: 'Navigation',
        noModulesFound: 'Aucun module trouve',
        noShort: 'Non'
      },
      roles: {
        adminControl: 'Controle administrateur',
        invigilator: 'Surveillant'
      },
      accessibility: {
        goToHomepage: 'Aller a la page d accueil VivaAssess',
        closeNavigationMenu: 'Fermer le menu de navigation'
      },
      language: {
        english: 'Anglais',
        french: 'Francais',
        hungarian: 'Hongrois',
        russian: 'Russe',
        german: 'Allemand'
      },
      privacy: {
        title: 'Confidentialite',
        accountIntro: 'Consultez l avis actuel, telechargez une copie de vos donnees ou demandez la suppression. Les notes d examen peuvent etre conservees sous forme anonymisee si l integrite academique l exige.',
        loading: 'Chargement des options de confidentialite...',
        loadFailed: 'Impossible de charger les informations de confidentialite.',
        actionFailed: 'Cette demande n a pas pu etre terminee.',
        acknowledge: 'J ai lu cet avis',
        acknowledged: 'Accuse de reception enregistre.',
        alreadyAcked: 'Vous avez accuse reception de l avis actuel.',
        downloadMyData: 'Telecharger mes donnees',
        requestDeletion: 'Demander la suppression',
        requestDoNotSell: 'Enregistrer le refus de vente',
        requestSent: 'Demande recue.',
        exportReady: 'Le telechargement de vos donnees a commence.',
        finishSavedExport: 'Terminer l’export enregistré',
        freshExport: 'Créer un nouvel export',
        doNotSellTrue: 'Cette plateforme ne vend pas les informations personnelles et ne les partage pas pour la publicite inter-contextes.',
        doNotSellFalse: 'Demandez a votre institution comment les informations personnelles sont partagees.',
        loginBanner: 'Cookies et stockage essentiels uniquement (connexion, theme, langue).',
        readNotice: 'Lire l avis de confidentialite',
        ackTitle: 'Avis de confidentialite',
        noNotice: 'Aucun avis publie n est disponible.',
        publicTitle: 'Avis de confidentialite',
        publicIntro: 'Avis publies uniquement. Connectez-vous pour telecharger vos donnees ou demander la suppression.',
        backToLogin: 'Retour a la connexion'
      }
    },
    auth: {
      login: {
        welcomeBack: 'Bon retour',
        subtitle: 'Connectez-vous pour continuer votre workflow VivaAssess',
        emailAddress: 'Adresse e-mail',
        password: 'Mot de passe',
        signIn: 'Se connecter',
        firstTimeTitle: 'Premiere visite ?',
        firstTimeBody: 'Utilisez votre e-mail d invitation pour definir votre mot de passe en toute securite.',
        trustedBadge: 'Entretiens IA de confiance',
        premiumDescription: 'Une plateforme premium d intelligence d entretien pour des decisions de recrutement equitables et explicables.',
        adaptiveAI: 'IA adaptative',
        adaptiveAIDescription: 'Relances dynamiques et analyse contextuelle.',
        biasAware: 'Consciente des biais',
        biasAwareDescription: 'Evaluation objective et coherente a grande echelle.',
        auditReady: 'Prete pour audit',
        auditReadyDescription: 'Traçabilite et gouvernance par defaut.',
        interviews: 'Entretiens',
        signalAccuracy: 'Precision du signal',
        organizations: 'Organisations',
        emailPlaceholder: 'nom@organisation.com',
        passwordPlaceholder: 'Entrez votre mot de passe',
        toggleTheme: 'Changer le theme'
      }
    },
    admin: {
      auditOrigin: auditOriginLabels.fr,
      readinessPreview: materialReadinessLabels.fr,
      previewHistory: materialPreviewHistoryLabels.fr,
      assignmentReadinessBlocked: "Activez l’entretien, activez une partie de l’évaluation et choisissez un modèle actif pour tout placement oral associé avant l’affectation.",
      privateOpeningPrompt: {
        label: "Ouverture orale personnalis?e (avanc?)",
        hint: "Facultatif. Vide, utilise l?ouverture par d?faut. Concerne l?accueil et la premi?re question ; les changements s?appliquent aux nouvelles sessions.",
        format: "Renvoyez un JSON avec greeting et first_question contenant question, question_type, difficulty, expected_topics et max_response_time_seconds. Variables disponibles :",
      },
      interviewForm: {
        answerReview: {
          title: 'Relecture Partie B',
          hint: 'Apres la transcription, le candidat peut corriger le texte avant la notation du tour. L envoi automatique reste disponible. Le minuteur de la Partie B ne s arrete pas.',
          allow: 'Autoriser le candidat a relire et corriger chaque reponse',
          defaultMode: 'Mode de capture initial',
          modeAuto: 'Envoi automatique apres un silence',
          modeReview: 'Relecture avant envoi',
          timeout: 'Delai de relecture (secondes, 0 = aucun)'
        }
      },
      nav: {
        overview: { label: 'Vue d ensemble', description: 'Etat de la plateforme, metriques et activite' },
        interviews: { label: 'Entretiens', description: 'Creer et gerer les modeles d entretien' },
        mcqSets: { label: 'Gestionnaire QCM', description: 'Concevoir et organiser les ensembles QCM' },
        materials: { label: 'Ressources', description: 'Televerser et organiser les ressources d entretien' },
        candidates: { label: 'Candidats', description: 'Inviter, affecter et suivre les candidats' },
        sections: { label: 'Groupes de candidats', description: 'Regrouper les candidats pour planification et inscription au placement' },
        invigilators: { label: 'Surveillants', description: 'Gerer les affectations des surveillants' },
        proctoringReports: { label: 'Rapports de surveillance', description: 'Historique des rapports d integrite et captures' },
        liveMonitoring: { label: 'Supervision en direct', description: 'Suivre les sessions actives avec camera et ecran en temps reel' },
        accounts: { label: 'Comptes', description: 'Gerer tous les comptes utilisateurs' },
        groups: { label: 'Institutions et groupes', description: 'Gerer la hierarchie des locataires et memberships' },
        myAccount: { label: 'Mon compte', description: 'Gerer votre profil admin et la securite' },
        llmSettings: { label: 'Parametres LLM', description: 'Configurer les fournisseurs et modeles IA' },
        voiceStudio: { label: 'Studio vocal IA', description: 'Gerer les voix d entretien et les surcharges' },
        assessments: { label: 'Evaluations', description: 'Examiner et approuver les resultats' },
        feedback: { label: 'Retour entretien', description: 'Examiner l experience candidat et exporter les donnees' },
        notifications: { label: 'Notifications', description: 'Gerer l historique d envoi et le comportement des e-mails de rapport' },
        systemIssues: { label: 'Incidents systeme', description: 'Suivre les avertissements et incidents de plateforme' },
        resetSettings: { label: 'Reinitialiser les parametres', description: 'Restaurer les valeurs par defaut (LLM, QCM, surveillance, preferences)' },
        auditLogs: { label: 'Journaux d audit', description: 'Inspecter les activites administratives' },
        help: { label: 'Aide', description: 'Guides pour ce tableau de bord' },
        userGuides: { label: 'Guides utilisateur', description: 'Creer et modifier les guides et captures' },
        privacy: { label: 'Confidentialite', description: 'Avis, demandes de droits et conservation' }
      },
      help: {
        title: 'Aide',
        subtitle: 'Guides publies pour les ecrans auxquels vous avez acces',
        back: 'Retour',
        loading: 'Chargement des guides...',
        loadFailed: 'Echec du chargement des guides.',
        emptyTitle: 'Aucun guide pour le moment',
        emptySubtitle: 'Les guides publies pour votre role apparaitront ici.',
        guides: 'Guides',
        noSteps: 'Ce guide n a pas encore d etapes.',
        selectGuide: 'Selectionnez un guide pour le lire.'
      },
      userGuides: {
        title: 'Guides utilisateur',
        subtitle: 'Creer, publier et traduire l aide in-app pour chaque role.',
        listTitle: 'Guides',
        loading: 'Chargement des guides...',
        empty: 'Aucun guide pour le moment',
        selectOrCreate: 'Selectionnez un guide ou creez-en un.',
        fields: {
          title: 'Titre',
          slug: 'Identifiant',
          audience: 'Audience',
          locale: 'Langue',
          module: 'Module',
          noModule: 'Aucun module',
          published: 'Publie',
          summary: 'Resume'
        },
        audiences: {
          candidate: 'Candidat',
          invigilator: 'Surveillant',
          admin: 'Admin',
          super_admin: 'Super admin'
        },
        status: {
          published: 'Publie',
          draft: 'Brouillon'
        },
        filters: {
          allAudiences: 'Toutes les audiences',
          allLocales: 'Toutes les langues'
        },
        steps: {
          title: 'Etapes',
          add: 'Ajouter une etape',
          stepNumber: 'Etape {{count}}',
          titlePlaceholder: 'Titre de l etape',
          bodyPlaceholder: 'Explication (texte ou markdown simple)',
          uploadImage: 'Televerser une capture',
          removeImage: 'Supprimer l image'
        },
        actions: {
          create: 'Creer un guide',
          save: 'Enregistrer',
          saving: 'Enregistrement...',
          delete: 'Supprimer',
          cancel: 'Annuler',
          duplicate: 'Copier vers une langue',
          preview: 'Apercu'
        },
        messages: {
          loadFailed: 'Echec du chargement des guides.',
          created: 'Guide cree.',
          saved: 'Guide enregistre.',
          saveFailed: 'Echec de l enregistrement du guide.',
          deleted: 'Guide supprime.',
          deleteFailed: 'Echec de la suppression du guide.',
          duplicated: 'Copie de langue creee.',
          duplicateFailed: 'Echec de la duplication du guide.',
          saveStepsFirst: 'Enregistrez d abord les etapes, puis televersez les captures.',
          imageUploaded: 'Capture televersee.',
          imageFailed: 'Echec de la mise a jour de la capture.'
        },
        modals: {
          createTitle: 'Creer un guide utilisateur',
          previewTitle: 'Apercu',
          deletePrompt: 'Supprimer ce guide et ses captures ?'
        }
      },
      privacy: {
        title: 'Confidentialite',
        subtitle: 'Avis, demandes de droits, conservation et sous-traitants. Console d exploitation, pas une certification juridique.',
        tabs: {
          requests: 'Demandes',
          notices: 'Avis',
          settings: 'Parametres',
          subprocessors: 'Sous-traitants',
          incidents: 'Incidents',
          holds: 'Objets bloqués'
        },
        loadFailed: 'Echec du chargement des donnees de confidentialite.',
        actionFailed: 'L action de confidentialite a echoue.',
        allStatuses: 'Tous les statuts',
        subject: 'Sujet',
        type: 'Type',
        status: 'Statut',
        due: 'Echeance',
        actionsLabel: 'Actions',
        export: 'Exporter',
        erase: 'Effacer',
        globalAuthorityNotice: 'Le personnel peut examiner les demandes de son établissement. Les exports et effacements du compte entier nécessitent un super-administrateur actif.',
        eraseReviewTitle: 'Examiner la demande d’effacement',
        executionRecords: 'Registre des effacements',
        downloadExport: 'Télécharger l’export',
        requestAccountErasure: 'Demander l’effacement',
        accountErasurePrompt: 'Demander l’examen de l’effacement de {{name}} ({{email}}) ? Le compte reste inchangé jusqu’à l’examen et l’exécution autorisés dans le centre de confidentialité.',
        accountErasureRequested: 'Demande d’examen {{id}} enregistrée dans le centre de confidentialité. Le compte n’a pas été effacé.',
        refreshRecords: 'Actualiser le registre',
        continueCleanup: 'Continuer le nettoyage enregistré',
        cleanupReviewTitle: 'Vérifier le nettoyage enregistré',
        cleanupReviewBody: 'L’effacement des données est déjà validé. Seules les suppressions de fichiers enregistrées sont poursuivies, jusqu’à 25 fichiers par action. Le compte n’est pas effacé ou anonymisé à nouveau. Les fichiers modifiés, partagés ou non attribués restent bloqués pour examen.',
        cleanupSummary: 'Effacement des données validé. Fichiers en attente : {{pending}} ; bloqués : {{blocked}}.',
        cleanupStillPending: 'Nettoyage traité. Certains fichiers nécessitent un examen ou un autre lot de nettoyage.',
        cleanupCompleted: 'Le nettoyage enregistré et son reçu d’exécution sont terminés.',
        eraseReviewBody: 'Vérifiez l’identité, les autorisations, la conservation et les éventuelles restrictions. Cette action concerne le compte dans tous les établissements. Les évaluations peuvent être conservées sous forme anonymisée. Une exécution interrompue doit être vérifiée avant toute autre action.',
        cancelReview: 'Annuler',
        confirmErase: 'Confirmer l’effacement examiné',
        reconciliationNeeded: 'Exécution commencée ; vérifiez son enregistrement avant toute autre action.',
        reject: 'Rejeter',
        noRequests: 'Aucune demande pour le moment.',
        published: 'Publie',
        draft: 'Brouillon',
        selectNotice: 'Selectionnez un avis a modifier.',
        saveDraft: 'Enregistrer le brouillon',
        publish: 'Publier',
        preview: 'Apercu',
        acknowledgements: 'Accuses de reception',
        loading: 'Chargement...',
        evidenceDays: 'Conservation des preuves (jours)',
        exportDays: 'Conservation des exports (jours)',
        exportLineageNote: 'Un nouvel export contient le texte d entretien appartenant a la personne, son rattachement institutionnel, et les metadonnees des tours, reponses, consignes, travaux fournisseur et nombres de documents. Il exclut les invites privees, le texte source, les cles, les empreintes, les charges fournisseur et les copies non enregistrees. Ce n est pas un dossier complet.',
        lawfulBasis: 'Base legale du proctoring (libelle)',
        dpoEmail: 'E-mail du DPO',
        doNotSell: 'Nous ne vendons pas les informations personnelles',
        requireAck: 'Exiger un accuse de reception apres connexion',
        eraseMode: 'Mode d effacement par defaut',
        transferNote: 'Note sur les transferts transfrontaliers',
        saved: 'Parametres enregistres.',
        saving: 'Enregistrement...',
        save: 'Enregistrer',
        subprocessorName: 'Nom',
        add: 'Ajouter',
        remove: 'Retirer',
        incidentTitle: 'Titre de l incident'
      },
      materialLibrary: {
        title: 'Bibliotheque de ressources',
        subtitle: 'Organisez les fichiers reutilisables par section et categorie.',
        metrics: {
          totalMaterials: 'Total des ressources',
          sections: 'Sections',
          filtered: 'Filtres',
          withExtractedText: 'Avec texte extrait'
        },
        sections: {
          title: 'Sections de ressources',
          none: 'Aucune section creee pour le moment.',
          noDescription: 'Aucune description',
          materialCount_one: '{{count}} ressource',
          materialCount_other: '{{count}} ressources',
          createCta: 'Creer une section'
        },
        labels: {
          selectedSection: 'Section selectionnee',
          section: 'Section',
          category: 'Categorie',
          search: 'Recherche',
          yes: 'Oui',
          no: 'Non',
          linkedInterviews: 'Entretiens lies',
          selectedCount: '{{count}} selectionne(s)',
          displayName: 'Nom affiche',
          description: 'Description'
        },
        filters: {
          allSections: 'Toutes les sections',
          unassigned: 'Non assignees',
          allCategories: 'Toutes les categories',
          searchPlaceholder: 'Rechercher par nom ou description',
          showing: 'Affichage : {{name}}'
        },
        actions: {
          refresh: 'Actualiser',
          refreshing: 'Actualisation...',
          createSection: 'Creer une section',
          uploadMaterials: 'Televerser des ressources',
          edit: 'Modifier',
          delete: 'Supprimer',
          download: 'Telecharger',
          deleting: 'Suppression...',
          clearFilters: 'Effacer les filtres',
          cancel: 'Annuler',
          save: 'Enregistrer',
          saving: 'Enregistrement...',
          viewText: 'Voir le texte',
          reextract: 'Reextraire',
          reextracting: 'Reextraction...',
          apply: 'Appliquer',
          selectAll: 'Tout selectionner',
          bulkAssignSection: 'Assigner une section',
          bulkSetCategory: 'Definir la categorie',
          bulkDelete: 'Supprimer la selection'
        },
        materials: {
          title: 'Ressources',
          loading: 'Chargement des ressources...',
          noneTitle: 'Aucune ressource trouvee',
          noneSubtitle: 'Televersez des fichiers ou ajustez les filtres.',
          noDescription: 'Aucune description',
          unassigned: 'Non assignee',
          general: 'General'
        },
        table: {
          name: 'Nom',
          section: 'Section',
          category: 'Categorie',
          size: 'Taille',
          extracted: 'Extrait',
          uploaded: 'Televerse',
          linked: 'Lie',
          actions: 'Actions'
        },
        uploadForm: {
          section: 'Section',
          noSection: 'Aucune section',
          category: 'Categorie',
          categoryPlaceholder: 'ex. Brief d evaluation',
          description: 'Description',
          files: 'Fichiers',
          clickToSelect: 'Cliquez pour selectionner des fichiers',
          orDragDrop: 'ou glissez-deposez',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (max 20 Mo, 5 fichiers)',
          filesSelected: '{{count}} fichier(s) selectionne(s)',
          remainingSlots: '{{count}} restant(s)',
          removeFile: 'Retirer {{name}}',
          cancel: 'Annuler',
          upload: 'Televerser',
          uploading: 'Televersement...',
          selectAtLeastOne: 'Selectionnez au moins un fichier.',
          tooManyFiles: 'Trop de fichiers. Maximum 5 par televersement.',
          fileTooLarge: '{{name}} est trop volumineux. Maximum 20 Mo.',
          unsupportedType: 'Type de fichier non pris en charge : {{name}}'
        },
        sectionForm: {
          createTitle: 'Creer une section de ressources',
          editTitle: 'Modifier la section',
          createSubtitle: 'Organisez les ressources reutilisables en sections.',
          editSubtitle: 'Mettez a jour cette section pour une meilleure organisation.',
          name: 'Nom de la section',
          namePlaceholder: 'ex. Structures de donnees',
          description: 'Description (facultatif)',
          descriptionPlaceholder: 'Ajoutez un contexte pour cette section',
          nameRequired: 'Le nom de la section est obligatoire.',
          cancel: 'Annuler',
          create: 'Creer la section',
          creating: 'Creation...',
          save: 'Enregistrer',
          saving: 'Enregistrement...'
        },
        editForm: {
          title: 'Modifier la ressource',
          subtitle: 'Mettre a jour les metadonnees. Le fichier stocke n est pas remplace.',
          displayNameRequired: 'Le nom affiche est obligatoire.'
        },
        extracted: {
          title: 'Texte extrait',
          loading: 'Chargement du texte extrait...',
          empty: 'Aucun texte n a ete extrait de ce fichier.',
          truncated: 'Cet extrait a ete tronque car il depassait la limite de taille.',
          charCount: '{{count}} caracteres',
          loadFailed: 'Echec du chargement du texte extrait.'
        },
        usage: {
          none: 'Lie a aucun entretien.',
          count_one: 'Lie a {{count}} entretien.',
          count_other: 'Lie a {{count}} entretiens.',
          unlinkWarning: 'La supprimer de la bibliotheque la retirera de ces entretiens.',
          andMore: 'et {{count}} de plus'
        },
        bulk: {
          categoryPlaceholder: 'Categorie',
          deleteTitle: 'Supprimer les ressources selectionnees',
          deletePrompt: 'Supprimer {{count}} ressource(s) ? Cela les detachera aussi de {{linked}} lien(s) d entretien.',
          noneSelected: 'Selectionnez au moins une ressource.',
          applied: '{{count}} ressource(s) mise(s) a jour.',
          deleted: '{{count}} ressource(s) supprimee(s).',
          partialDelete: '{{deleted}} ressource(s) supprimee(s). {{failed}} echec(s).'
        },
        modals: {
          createSectionTitle: 'Creer une section de ressources',
          editSectionTitle: 'Modifier la section',
          uploadMaterialsTitle: 'Televerser des ressources',
          editMaterialTitle: 'Modifier la ressource',
          extractedTextTitle: 'Texte extrait',
          deleteMaterialTitle: 'Supprimer la ressource',
          deleteMaterialPrompt: 'Supprimer {{name}} de la bibliotheque ?',
          deleteSectionTitle: 'Supprimer la section',
          deleteSectionPrompt: 'Supprimer la section {{name}} ? Les ressources liees resteront dans la bibliotheque et deviendront non assignees.'
        },
        messages: {
          loadSectionsFailed: 'Echec du chargement des sections.',
          loadMaterialsFailed: 'Echec du chargement des ressources.',
          materialDeleted: 'Ressource supprimee.',
          deleteMaterialFailed: 'Echec de la suppression de la ressource.',
          sectionDeleted: 'Section supprimee.',
          deleteSectionFailed: 'Echec de la suppression de la section.',
          refreshed: 'Bibliotheque actualisee.',
          sectionCreated: 'Section creee.',
          sectionUpdated: 'Section mise a jour.',
          materialsUploaded: 'Ressources televersees.',
          sectionCreateFailed: 'Echec de la creation de la section.',
          sectionUpdateFailed: 'Echec de la mise a jour de la section.',
          materialUpdated: 'Ressource mise a jour.',
          updateMaterialFailed: 'Echec de la mise a jour de la ressource.',
          reextractSuccess: 'Texte reextrait.',
          reextractFailed: 'Echec de la reextraction.',
          bulkUpdateFailed: 'Echec de la mise a jour de la selection.',
          bulkDeleteFailed: 'Echec de la suppression de la selection.'
        }
      }
    },
    candidate: {
      secureCodeEditor: {
        messages: {
          executionUnavailable: 'L’exécution du code est actuellement indisponible. Vous pouvez continuer à modifier et à soumettre votre code.',
          executeFailed: 'Impossible d’exécuter le code. Veuillez réessayer.'
        }
      },
      roomCamera: {
        title: 'Camera de salle (telephone, vue de cote)',
        hint: 'Placez le telephone de cote pour montrer le bureau, les mains et le sol. Ouvrez ce lien sur le telephone. Vous restez connecte sur cet ordinateur.',
        status: 'Statut : {{status}}',
        skip: 'Continuer sans la camera de salle',
        pill: 'Camera de salle',
        urlLabel: 'Ou ouvrez ce lien sur le telephone',
        systemCheck: 'Cette evaluation demande une seconde camera : votre telephone, de cote, pour montrer le bureau, les mains et le sol. Vous la couplez apres le verrouillage, sans vous connecter sur le telephone.',
        phoneTitle: 'Camera de salle',
        phonePurpose: 'Cette page montre le bureau, les mains et le sol a le surveillant. Elle ne montre pas les questions. Gardez cette page ouverte.',
        placeSide: 'Posez le telephone sur le cote du bureau, a environ 1–2 metres, en paysage.',
        placeShows: 'L image doit montrer le torse, les mains, le bureau et le sol sous la table. Le visage n a pas besoin de remplir l image.',
        checklist: 'Verification du placement',
        checkDesk: 'Le bureau et mes mains sont visibles',
        checkFloor: 'Le sol sous la table est visible',
        checkSide: 'C est une vue de cote, pas un gros plan du visage',
        start: 'Demarrer la camera de salle',
        streaming: 'En direct',
        paused: 'En pause tant que la page est cachee',
        missingToken: 'Ce lien n a pas de jeton. Scannez de nouveau le QR sur l ordinateur.',
        permission: 'L autorisation camera est requise',
        frameRejected: 'Image refusee ({{status}})',
        switchToFront: 'Passer a la camera avant',
        switchToBack: 'Passer a la camera arriere',
        otherCameraUnavailable: 'L autre camera n est pas disponible.',
        connectedCheck: 'La camera de la salle est connectee. Verifiez l image, puis continuez.',
        confirmView: 'Cette vue est correcte — continuer',
        hidePreview: 'Masquer la camera de la salle',
        showPreview: 'Afficher la camera de la salle',
        pictureWaiting: 'Une image en direct attend.',
        picturePending: 'Le telephone est connecte, mais l image n est pas encore arrivee.',
        previewLabel: 'Camera de la salle'
      },
      privacy: {
        title: 'Confidentialite'
      },
      help: {
        title: 'Aide',
        subtitle: 'Guides du portail candidat',
        back: 'Retour',
        loading: 'Chargement des guides...',
        loadFailed: 'Echec du chargement des guides.',
        emptyTitle: 'Aucun guide pour le moment',
        emptySubtitle: 'Les guides candidats publies apparaitront ici.',
        guides: 'Guides',
        noSteps: 'Ce guide n a pas encore d etapes.',
        selectGuide: 'Selectionnez un guide pour le lire.'
      },
      overview: {
        header: {
          help: 'Aide'
        }
      },
      portal: {
        myAccount: 'Mon compte',
        description: 'Gerez les details de votre profil et la securite de votre compte.',
        backLabel: 'Retour au portail'
      },
      voiceInterview: {
        chrome: {
          reviewEdited: 'Modifie',
          reviewCountdown: '{{seconds}} s pour envoyer',
          reviewHint: 'Corrigez les erreurs de reconnaissance, puis envoyez. Ctrl+Entree envoie. Limite {{count}} caracteres. Le minuteur oral continue.',
          reviewTooShort: 'Ajoutez au moins {{count}} mots avant d envoyer, ou reenregistrez.',
          reviewStale: 'Ce brouillon concernait une question precedente et a ete efface. Repondez a la question a l ecran.',
          reviewRestored: 'Une correction non envoyee a ete effacee a la reprise. Repondez a la question a l ecran.',
          showRecognised: 'Texte reconnu par le microphone',
          hideRecognised: 'Masquer le texte reconnu',
          restoreOriginal: 'Restaurer l original',
          rerecord: 'Reenregistrer'
        }
      }
    },
    invigilator: {
      nav: {
        liveMonitoring: 'Surveillance en direct',
        reportHistory: 'Historique des rapports',
        accountSettings: 'Parametres du compte',
        help: 'Aide',
        helpDescription: 'Guides pour les outils de surveillance'
      },
      help: {
        title: 'Aide',
        subtitle: 'Guides pour les outils de surveillance',
        back: 'Retour',
        loading: 'Chargement des guides...',
        loadFailed: 'Echec du chargement des guides.',
        emptyTitle: 'Aucun guide pour le moment',
        emptySubtitle: 'Les guides surveillant publies apparaitront ici.',
        guides: 'Guides',
        noSteps: 'Ce guide n a pas encore d etapes.',
        selectGuide: 'Selectionnez un guide pour le lire.'
      }
    },
    interviews: {
      status: {
        inProgress: 'En cours',
        completed: 'Termine',
        pending: 'En attente'
      }
    },
    reports: {
      generatedOn: 'Genere le {{date}}'
    },
    notifications: {
      newNotification: 'Vous avez une nouvelle notification.'
    },
    mainSite: {
      experienceFallback: {
        product: {
          title: 'Vue d ensemble du produit',
          summary: 'Explorez la plateforme VivaAssess et ses capacites pour des decisions d entretien fiables.'
        },
        solutions: {
          title: 'Solutions',
          summary: 'Decouvrez les solutions VivaAssess pour les equipes de recrutement et de formation.'
        },
        solutionsHrLeaders: {
          title: 'Solutions pour les responsables RH',
          summary: 'Renforcez la coherence des decisions et la confiance de gouvernance RH.'
        },
        solutionsRecruiters: {
          title: 'Solutions pour les recruteurs',
          summary: 'Reduisez les frictions du pipeline et accelerez la progression des candidats.'
        },
        solutionsEducationTraining: {
          title: 'Solutions pour les equipes education et formation',
          summary: 'Faites evoluer les workflows d entretiens avec des controles qualite solides.'
        },
        pricing: {
          title: 'Tarifs',
          summary: 'Choisissez un plan VivaAssess adapte a votre stade et votre croissance.'
        },
        securityTrustCenter: {
          title: 'Centre securite et confiance',
          summary: 'Consultez la securite, la confidentialite, la gouvernance IA et la fiabilite.'
        },
        caseStudies: {
          title: 'Etudes de cas',
          summary: 'Voyez comment les equipes ont transforme leurs operations d entretien.'
        },
        compare: {
          title: 'Comparer VivaAssess',
          summary: 'Comparez VivaAssess aux processus manuels, outils classiques et assistants IA generiques.'
        },
        compareManualProcess: {
          title: 'VivaAssess vs processus manuel',
          summary: 'Comprenez pourquoi les workflows structures depassent les operations manuelles.'
        },
        compareTraditionalTools: {
          title: 'VivaAssess vs outils traditionnels',
          summary: 'Voyez la difference entre les outils historiques et VivaAssess.'
        },
        compareGenericAiAssistant: {
          title: 'VivaAssess vs workflows assistants IA generiques',
          summary: 'Decouvrez pourquoi une intelligence d entretien specialisee produit de meilleurs resultats.'
        }
      }
    },
    errors: {
      actions: {
        goBack: 'Retour',
        goHome: 'Accueil',
        goToLogin: 'Aller a la connexion',
        retry: 'Reessayer'
      },
      requestIdLabel: 'ID de requete support',
      pages: {
        badRequest: {
          title: 'Requete invalide',
          message: 'La requete n a pas pu etre traitee. Verifiez vos donnees puis reessayez.'
        },
        unauthorized: {
          title: 'Non autorise',
          message: 'Votre session est absente ou expiree. Connectez-vous pour continuer.'
        },
        forbidden: {
          title: 'Acces interdit',
          message: 'Vous n avez pas l autorisation d acceder a cette page ou ressource.'
        },
        notFound: {
          title: 'Page introuvable',
          message: 'La page ou ressource demandee est introuvable.'
        },
        internalServerError: {
          title: 'Erreur interne du serveur',
          message: 'Une erreur est survenue cote serveur. Veuillez reessayer dans un instant.'
        },
        serviceUnavailable: {
          title: 'Service indisponible',
          message: 'Le service est temporairement indisponible. Veuillez reessayer bientot.'
        }
      },
      shortcuts: {
        title: 'Pages d erreur',
        description: 'Selectionnez un apercu de page d erreur.'
      }
    }
  },
  hu: {
    common: {
      notificationPanel: notificationPanelLabels.hu,
      transcriptExpiry: transcriptExpiryLabels.hu,
      blockedObjects: blockedObjectLabels.hu,
      authoringGrounding: authoringGroundingLabels.hu,
      writing:writingResources.hu,
      completionConfirm: {"submittingTitle": "Beküldés ellenőrzése", "submitting": "Tartsa nyitva ezt az oldalt, amíg ellenőrizzük a befejezett munkamenet szerveroldali elfogadását.", "uncertainTitle": "A beküldés nincs visszaigazolva", "uncertain": "A beküldést még nem igazoltuk vissza. Tartsa nyitva ezt a lapot; ne szerkessze a válaszokat, ne töltsön újra és ne nyisson másik munkamenetlapot. Ellenőrizze a kapcsolatot, majd próbálja újra a visszaigazolást. Az eredeti beküldést itt megőrizzük. Ha nem sikerül, forduljon az adminisztrátorhoz.", "acceptedTitle": "Beküldés elfogadva", "accepted": "A szerver visszaigazolta a befejezett munkamenetet. A feldolgozás és az eredmények közzététele a meglévő szabályok szerint történik.", "retry": "Visszaigazolás újrapróbálása", "continue": "Tovább"},
      evaluationDraft: {"acceptedDefinition": "A munkamenet ind?t?sakor r?gz?tett ?rt?kel?si defin?ci?t ?s min?s?gi be?ll?t?sokat haszn?lja.", "processing": {"queued": "A tervezet sorban ?ll. A be?ll?tott feldolgoz? fogja elk?sz?teni; friss?tsen az ellen?rz?shez.", "running": "A tervezet k?sz?t?se folyamatban. Friss?tsen az ellen?rz?shez.", "retry": "A tervezet k?sz?t?se a megl?v? pr?b?lkoz?si kereten bel?l ?jraindul.", "succeeded": "A feldolgoz?s befejez?d?tt. Friss?tse a tervezetet.", "failed": "A feldolgoz?s le?llt. Forduljon a rendszergazd?hoz; az ?j k?r?s nem ?ll?tja vissza a keretet.", "timed_out": "A feldolgoz?s id?korl?tja lej?rt. Friss?tsen a helyre?ll?t?s ellen?rz?s?hez.", "cancelled": "A feldolgoz?st megszak?tott?k. Forduljon a rendszergazd?hoz.", "missing": "Hi?nyzik a feldolgoz?si feladat. Rendszergazdai vizsg?lat sz?ks?ges."}, "reconciliation": {"title": "Főkönyvi egyeztetés", "note": "Rögzíti, hogy az elfogadott tervezet hogyan viszonyult az akkor írt emberi bírálati bejegyzéshez. Az emberi bírálat a mérvadó; a hiányzó bejegyzés azt jelenti, hogy nem történt összevetés, sosem egyetértést.", "unavailable": "Az egyeztetési előzmény nem érhető el.", "none": "Még egyetlen bírálati bejegyzést sem vetettek össze tanácsadói tervezettel.", "entry": "{{index}}. bírálati bejegyzés", "authoritative": "emberi pontszám", "relation": {"agreement": "az emberi pontszám tűréshatárán belül", "disagreement": "tűréshatáron túl eltér az emberi pontszámtól", "insufficient_evidence": "a tervezet elégtelen bizonyítékot jelzett", "not_comparable": "nincs összevethető emberi pontszám", "advisory_only": "csak tanácsadói megfigyelés"}, "evidence": {"current": "a bírált bizonyítékhoz rögzített tervezet", "stale": "korábbi bizonyítékhoz rögzített tervezet", "unknown": "a bizonyíték egyezése nem ellenőrizhető"}}, "strengths": "Er?ss?gek", "weaknesses": "Fejlesztend? ter?letek", "indicators": "Jelentett megfigyel?sek", "title": "Tan?csad?i ?rt?kel?si tervezet", "note": "Mentett bizony?t?kokat ?s r?gz?tett feladatdefin?ci?kat haszn?l. Nem r?gz?t jegyet, nem d?nt szab?lys?rt?sr?l, ?s nem m?dos?tja a d?nt?st vagy az eredm?nyek k?zz?t?tel?t. A bizonyoss?g nem kalibr?lt.", "final_evaluation": "?rt?kel?si tervezet", "integrity_analysis": "Integrit?si tervezet", "generate": "Tervezet k?sz?t?se", "refresh": "Tervezet friss?t?se", "loading": "Tervezet ellen?rz?se?", "unavailable": "A tervezet nem ?rhet? el. Ellen?rizze a mentett adatokat ?s a szolg?ltat? be?ll?t?s?t, majd friss?tsen vagy pr?b?lja ?jra.", "none": "Ehhez a bizony?t?khoz nincs tervezet.", "pending": "A tervezet feldolgoz?s alatt ?ll. Friss?tsen; csak az el?z? k?s?rlet lej?rta ut?n pr?b?lja ?jra.", "stale": "A mentett bizony?t?k megv?ltozott. K?sz?tsen ?j tervezetet.", "accepted": "Tan?csad?i tervezet el?rhet?", "score": "Javasolt pontsz?m", "risk": "Javasolt kock?zat", "confidence": "Nem kalibr?lt bizonyoss?g", "limitations": "A bizony?t?k korl?tai", "references": "Bizony?t?khivatkoz?sok", "entry": "?tiratbejegyz?s", "event": "Integrit?si esem?ny", "coverage": "{{included}} / {{total}} ?tiratbejegyz?s; {{eventsIncluded}} / {{eventsTotal}} esem?ny; {{materialIncluded}} / {{materialTotal}} referenciakarakter. {{truncated}} bejegyz?s r?vid?tve.", "unknown": "Ismeretlen"},
      sessionSave: {"conflict": "Az automatikus mentés leállt, mert újabb előrehaladás vagy másik vizsgaszakasz került rögzítésre. Tartsa nyitva ezt a lapot, itt ne szerkesszen tovább, és csak egy munkamenetlapot használjon. Ne küldje be újra a válaszokat; a nem mentett munka helyreállítása előtt forduljon az adminisztrátorhoz.", "unavailable": "Az előrehaladás mentése nincs visszaigazolva. Tartsa nyitva ezt a lapot és ellenőrizze a kapcsolatot. Bizonytalan helyreállítás alatt ne töltsön újra és ne nyisson másik lapot; ha a mentés nem folytatódik, forduljon az adminisztrátorhoz."},
      completionRepair: {"preview": "Helyreállítás előnézete", "summary": "Összetevő: {{component}}. Hátralévő próbálkozások: {{count}}. Az előnézet öt perc után lejár.", "part_a": "A rész rangsorolása", "oral": "Szóbeli pontozás", "confirm": "Kivizsgáltam és elhárítottam az átmeneti hibát. Csak az eredeti feladat induljon újra a meglévő kereten belül; a bizonyítékok, emberi döntések és közzétételi szabályok maradjanak érvényben.", "apply": "Helyreállítás megerősítése", "loading": "Helyreállítás ellenőrzése…", "unavailable": "A helyreállítás nem érhető el vagy kivizsgálást igényel. Aktív, törölt, kimerített, módosult forrású vagy már felülvizsgált feladat itt nem indítható újra.", "failed": "A helyreállítás nem igazolható. Ugyanezzel a megerősítéssel kérje le a bizonylatot, vagy lejárat után kérjen új előnézetet. Ne küldje be újra a válaszokat.", "accepted": "A helyreállítás elfogadva és naplózva. A feldolgozás után frissítsen. Az eredményekre a meglévő közzétételi szabályok vonatkoznak.", "receipt": "Naplózási bizonylat"},
      completionReview: {"title": "Befejezett munkamenetek feldolgozásának ellenőrzése", "note": "Hiányzó vagy többszörös értékelésű, leállt vagy nem követett feldolgozású befejezett munkamenetek. A változásokhoz frissítsen. A helyreállításhoz kivizsgálás és érvényes előnézet kell; a lista olvasása nem indít újra és nem tesz közzé semmit.", "empty": "Nincs megfelelő befejezett munkamenet ebben a csoportban.", "session": "Munkamenet", "candidate": "Jelölt", "interview": "Interjú", "assessment": "Értékelés", "available": "Elérhető", "missing": "Hiányzik", "ambiguous": "Több értékelés", "not_submitted": "Nincs beküldve", "more": "Továbbiak"},
      completionStatus: {"title": "Beküldés feldolgozása", "refresh": "Állapot frissítése", "loading": "Feldolgozási állapot ellenőrzése…", "unavailable": "Az állapot nem érhető el. Frissítsen vagy próbálja újra később.", "unknown": "Nincs rögzített pontozási állapot. Ha az eredmény függőben marad, forduljon az adminisztrátorhoz.", "not_submitted": "A munkamenet még nincs befejezettként elfogadva.", "queued": "A beküldést elfogadtuk. A pontozás várakozik.", "running": "A beküldést elfogadtuk. A pontozás folyamatban van.", "retrying": "A beküldést elfogadtuk. A pontozás automatikusan újraindul.", "processed": "A pontozás feldolgozása megtörtént.", "needs_attention": "A beküldést rögzítettük. A feldolgozás adminisztrátori beavatkozást igényel.", "releaseNote": "A feldolgozási állapot nem teszi közzé az eredményt, és nem módosítja a felülvizsgálati döntést."},
      app: {
        name: 'VivaAssess',
        tagline: 'Megbizhato MI interjuplatform'
      },
      skipToMainContent: 'Ugras a fo tartalomhoz',
      nav: {
        home: 'Kezdolap',
        product: 'Termek',
        solutions: 'Megoldasok',
        pricing: 'Arak',
        trust: 'Bizalom',
        compare: 'Osszehasonlitas',
        contact: 'Kapcsolat',
        resources: 'Forrasok',
        features: 'Funkciok',
        about: 'Rolunk',
        teams: 'Csapatok',
        docs: 'Dokumentacio',
        caseStudies: 'Esettanulmanyok'
      },
      actions: {
        goToDashboard: 'Ugras az iranyitopultra',
        signOut: 'Kijelentkezes',
        close: 'Bezaras',
        expandNavigation: 'Navigacio kibontasa',
        collapseNavigation: 'Navigacio osszecsukasa',
        backToPortal: 'Vissza a portalra',
        help: 'Súgó'
      },
      labels: {
        language: 'Nyelv',
        searchModules: 'Modulok keresese...',
        pinned: 'Rogzitett',
        allModules: 'Osszes modul',
        navigation: 'Navigacio',
        noModulesFound: 'Nincs talalat modulra',
        noShort: 'Nincs'
      },
      roles: {
        adminControl: 'Adminisztratori vezerles',
        invigilator: 'Felugyelo'
      },
      accessibility: {
        goToHomepage: 'Ugras a VivaAssess kezdolapra',
        closeNavigationMenu: 'Navigacios menu bezarasa',
        openNavigationMenu: 'Navigációs menü megnyitása',
        navigationMenu: 'Navigációs menü'
      },
      language: {
        english: 'Angol',
        french: 'Francia',
        hungarian: 'Magyar',
        russian: 'Orosz',
        german: 'Nemet'
      },
      privacy: {
        title: 'Adatvédelem',
        accountIntro: 'Tekintse meg a hatályos tájékoztatót, töltse le adatait, vagy kérjen törlést. A vizsgapontok anonimizálva megőrizhetők, ha az akadémiai integritás ezt megköveteli.',
        loading: 'Adatvédelmi beállítások betöltése...',
        loadFailed: 'Az adatvédelmi információk betöltése sikertelen.',
        actionFailed: 'A kérés nem teljesíthető.',
        acknowledge: 'Elolvastam ezt a tájékoztatót',
        acknowledged: 'Az elfogadás rögzítve.',
        alreadyAcked: 'A hatályos tájékoztatót már elfogadta.',
        downloadMyData: 'Adataim letöltése',
        requestDeletion: 'Törlés kérése',
        requestDoNotSell: 'Értékesítés megtagadásának rögzítése',
        requestSent: 'A kérés megérkezett.',
        exportReady: 'Az adatok letöltése elindult.',
        finishSavedExport: 'Mentett export befejezése',
        freshExport: 'Új export készítése',
        doNotSellTrue: 'Ez a platform nem értékesít személyes adatot, és nem osztja meg hirdetési célra.',
        doNotSellFalse: 'Kérdezze intézményét, hogyan osztják meg a személyes adatokat.',
        loginBanner: 'Csak nélkülözhetetlen sütik és tárolás (bejelentkezés, téma, nyelv).',
        readNotice: 'Adatvédelmi tájékoztató',
        ackTitle: 'Adatvédelmi tájékoztató',
        noNotice: 'Nincs közzétett tájékoztató.',
        publicTitle: 'Adatvédelmi tájékoztató',
        publicIntro: 'Csak a közzétett tájékoztatók. Jelentkezzen be az adatai letöltéséhez vagy törlés kéréséhez.',
        backToLogin: 'Vissza a bejelentkezéshez'
      }
    },
    auth: {
      login: {
        welcomeBack: 'Udvozoljuk ujra',
        subtitle: 'Jelentkezzen be a VivaAssess munkafolyamat folytatasahoz',
        emailAddress: 'E-mail cim',
        password: 'Jelszo',
        signIn: 'Bejelentkezes',
        firstTimeTitle: 'Elso alkalom?',
        firstTimeBody: 'Hasznalja a meghivo e-mailt a jelszo biztonsagos beallitasahoz.',
        trustedBadge: 'Megbizhato MI interju',
        premiumDescription: 'Premium interjuintelligencia platform igazsagos es adatalapu felveteli dontesekhez.',
        adaptiveAI: 'Adaptiv MI',
        adaptiveAIDescription: 'Dinamikus visszakerdezes es kontextusfuggo feltaras.',
        biasAware: 'Elfogultsag-tudatos',
        biasAwareDescription: 'Objektiv, kovetkezetes ertekeles nagy meretben.',
        auditReady: 'Auditra kesz',
        auditReadyDescription: 'Nyomon kovethetoseg es governance alapertelmezetten.',
        interviews: 'Interjuk',
        signalAccuracy: 'Jelpontossag',
        organizations: 'Szervezetek',
        emailPlaceholder: 'nev@szervezet.hu',
        passwordPlaceholder: 'Adja meg a jelszavat',
        toggleTheme: 'Tema valtasa'
      }
    },
    admin: {
      auditOrigin: auditOriginLabels.hu,
      readinessPreview: materialReadinessLabels.hu,
      previewHistory: materialPreviewHistoryLabels.hu,
      assignmentReadinessBlocked: "A hozzárendelés előtt aktiválja az interjút, engedélyezzen egy vizsgarészt, és válasszon aktív sablont a kapcsolt szóbeli felméréshez.",
      nav: {
        overview: { label: 'Áttekintés', description: 'Platform állapot, metrikák és aktivitás' },
        interviews: { label: 'Interjúk', description: 'Interjúsablonok létrehozása és kezelése' },
        mcqSets: { label: 'MCQ kezelő', description: 'MCQ-készletek tervezése és rendszerezése' },
        materials: { label: 'Anyagok', description: 'Interjúanyagok feltöltése és kezelése' },
        candidates: { label: 'Jelöltek', description: 'Jelöltek meghívása, kiosztása és monitorozása' },
        sections: { label: 'Jelöltcsoportok', description: 'Jelöltek csoportosítása ütemezéshez és placement jelentkezéshez' },
        invigilators: { label: 'Felügyelők', description: 'Felügyelői kiosztások kezelése' },
        proctoringReports: { label: 'Proktorálási jelentések', description: 'Integritási jelentések és képernyőképek története' },
        liveMonitoring: { label: 'Élő felügyelet', description: 'Aktív vizsgák követése valós idejű kamera- és képernyőképpel' },
        accounts: { label: 'Fiókok', description: 'Összes felhasználói fiók kezelése' },
        placement: { label: 'Szintfelmérő központ', description: 'Sablonok, kérdésbank, rangsor és hallgatói onboarding' },
        myAccount: { label: 'Saját fiók', description: 'Admin profil és biztonság kezelése' },
        llmSettings: { label: 'LLM beállítások', description: 'MI szolgáltatók és modellek beállítása' },
        voiceStudio: { label: 'MI hangstúdió', description: 'Interjúhangok és felülírások kezelése' },
        assessments: { label: 'Értékelések', description: 'Értékelési eredmények felülvizsgálata és jóváhagyása' },
        transcripts: { label: 'Átiratok', description: 'Interjú-munkamenetek Q&A átiratainak böngészése élőben és az előzményekben' },
        feedback: { label: 'Interjú visszajelzés', description: 'Jelölti élmény felülvizsgálata és exportja' },
        notifications: { label: 'Értesítések', description: 'Kézbesítési előzmények és riport e-mail viselkedés kezelése' },
        systemIssues: { label: 'Rendszerhibák', description: 'Platform figyelmeztetések és incidensek követése' },
        resetSettings: { label: 'Beállítások visszaállítása', description: 'Termék alapértelmezések visszaállítása (LLM, MCQ, proktorálás, preferenciák)' },
        auditLogs: { label: 'Audit naplók', description: 'Adminisztratív tevékenység naplóinak ellenőrzése' },
        help: { label: 'Súgó', description: 'Szerepkör szerinti útmutatók ehhez a vezérlőpulthoz' },
        userGuides: { label: 'Felhasználói útmutatók', description: 'Alkalmazáson belüli útmutatók és képernyőképek szerkesztése' },
        privacy: { label: 'Adatvédelem', description: 'Tájékoztatók, érintetti kérelmek és megőrzés' },
        groupHeadings: {
          examDay: 'Vizsga napja',
          people: 'Emberek',
          content: 'Tartalom',
          review: 'Felülvizsgálat',
          platform: 'Platform'
        }
      },
      help: {
        title: 'Súgó',
        subtitle: 'Közzétett útmutatók az elérhető képernyőkhöz',
        back: 'Vissza',
        loading: 'Útmutatók betöltése...',
        loadFailed: 'Az útmutatók betöltése sikertelen.',
        emptyTitle: 'Még nincs útmutató',
        emptySubtitle: 'A szerepköréhez közzétett útmutatók itt jelennek meg.',
        guides: 'Útmutatók',
        noSteps: 'Ehhez az útmutatóhoz még nincsenek lépések.',
        selectGuide: 'Válasszon egy útmutatót az olvasáshoz.'
      },
      userGuides: {
        title: 'Felhasználói útmutatók',
        subtitle: 'Alkalmazáson belüli súgó létrehozása, közzététele és fordítása szerepkörönként.',
        listTitle: 'Útmutatók',
        loading: 'Útmutatók betöltése...',
        empty: 'Még nincs útmutató',
        selectOrCreate: 'Válasszon egy útmutatót, vagy hozzon létre egyet.',
        fields: {
          title: 'Cím',
          slug: 'Azonosító',
          audience: 'Célközönség',
          locale: 'Nyelv',
          module: 'Modul',
          noModule: 'Nincs modul',
          published: 'Közzétéve',
          summary: 'Összefoglaló'
        },
        audiences: {
          candidate: 'Jelölt',
          invigilator: 'Felügyelő',
          admin: 'Admin',
          super_admin: 'Szuperadmin'
        },
        status: {
          published: 'Közzétéve',
          draft: 'Piszkozat'
        },
        filters: {
          allAudiences: 'Minden célközönség',
          allLocales: 'Minden nyelv'
        },
        steps: {
          title: 'Lépések',
          add: 'Lépés hozzáadása',
          stepNumber: '{{count}}. lépés',
          titlePlaceholder: 'Lépés címe',
          bodyPlaceholder: 'Magyarázat (egyszerű szöveg vagy markdown)',
          uploadImage: 'Képernyőkép feltöltése',
          removeImage: 'Kép eltávolítása'
        },
        actions: {
          create: 'Útmutató létrehozása',
          save: 'Mentés',
          saving: 'Mentés...',
          delete: 'Törlés',
          cancel: 'Mégse',
          duplicate: 'Másolás másik nyelvre',
          preview: 'Előnézet'
        },
        messages: {
          loadFailed: 'Az útmutatók betöltése sikertelen.',
          created: 'Útmutató létrehozva.',
          saved: 'Útmutató mentve.',
          saveFailed: 'Az útmutató mentése sikertelen.',
          deleted: 'Útmutató törölve.',
          deleteFailed: 'Az útmutató törlése sikertelen.',
          duplicated: 'Nyelvi másolat létrehozva.',
          duplicateFailed: 'Az útmutató másolása sikertelen.',
          saveStepsFirst: 'Először mentse a lépéseket, utána töltsön fel képernyőképeket.',
          imageUploaded: 'Képernyőkép feltöltve.',
          imageFailed: 'A képernyőkép frissítése sikertelen.'
        },
        modals: {
          createTitle: 'Felhasználói útmutató létrehozása',
          previewTitle: 'Előnézet',
          deletePrompt: 'Törli ezt az útmutatót és a képernyőképeit?'
        }
      },
      privacy: {
        title: 'Adatvédelem',
        subtitle: 'Tájékoztatók, érintetti kérelmek, megőrzés és adatfeldolgozók. Üzemeltetési konzol, nem jogi tanúsítvány.',
        tabs: {
          requests: 'Kérelmek',
          notices: 'Tájékoztatók',
          settings: 'Beállítások',
          subprocessors: 'Adatfeldolgozók',
          incidents: 'Incidensek',
          holds: 'Blokkolt objektumok'
        },
        loadFailed: 'Az adatvédelmi adatok betöltése sikertelen.',
        actionFailed: 'Az adatvédelmi művelet sikertelen.',
        allStatuses: 'Minden állapot',
        subject: 'Érintett',
        type: 'Típus',
        status: 'Állapot',
        due: 'Határidő',
        actionsLabel: 'Műveletek',
        export: 'Export',
        erase: 'Törlés',
        globalAuthorityNotice: 'Az intézményi munkatársak a saját intézményük kérelmeit vizsgálhatják. A teljes fiók exportálásához és törléséhez aktív szuperadminisztrátor szükséges.',
        eraseReviewTitle: 'Törlési kérelem ellenőrzése',
        executionRecords: 'Törlések végrehajtási nyilvántartása',
        downloadExport: 'Export letöltése',
        requestAccountErasure: 'Törlés kérelmezése',
        accountErasurePrompt: 'Törlési felülvizsgálatot kér {{name}} ({{email}}) fiókjához? A fiók változatlan marad az adatvédelmi központban engedélyezett felülvizsgálatig és végrehajtásig.',
        accountErasureRequested: 'A(z) {{id}} törlési felülvizsgálati kérelem bekerült az adatvédelmi központba. A fiók nem lett törölve.',
        refreshRecords: 'Nyilvántartás frissítése',
        continueCleanup: 'Rögzített takarítás folytatása',
        cleanupReviewTitle: 'Rögzített takarítás ellenőrzése',
        cleanupReviewBody: 'Az adatok törlése már végleges. Csak a mentett fájltörlési feladatok folytatódnak, műveletenként legfeljebb 25 fájllal. A fiók törlése vagy anonimizálása nem ismétlődik meg. A módosult, megosztott vagy nem igazolt tulajdonú fájlok ellenőrzésre várnak.',
        cleanupSummary: 'Az adatok törlése végleges. Függő fájlok: {{pending}}; blokkolt: {{blocked}}.',
        cleanupStillPending: 'A takarítási köteg feldolgozva. Egyes fájlok ellenőrzést vagy újabb köteget igényelnek.',
        cleanupCompleted: 'A rögzített fájltakarítás és a végrehajtási bizonylat elkészült.',
        eraseReviewBody: 'Ellenőrizze az érintettet, a jogosultságot, a megőrzési szabályokat és az esetleges korlátozásokat. A művelet a fiókot minden intézményben érinti. Az értékelési előzmények anonimizálva megmaradhatnak. Megszakadt végrehajtás esetén további művelet előtt egyeztetés szükséges.',
        cancelReview: 'Mégse',
        confirmErase: 'Ellenőrzött törlés megerősítése',
        reconciliationNeeded: 'A végrehajtás elindult; további művelet előtt ellenőrizze a nyilvántartását.',
        reject: 'Elutasítás',
        noRequests: 'Még nincs kérelem.',
        published: 'Közzétéve',
        draft: 'Piszkozat',
        selectNotice: 'Válasszon tájékoztatót a szerkesztéshez.',
        saveDraft: 'Piszkozat mentése',
        publish: 'Közzététel',
        preview: 'Előnézet',
        acknowledgements: 'Elfogadások',
        loading: 'Betöltés...',
        evidenceDays: 'Bizonyíték megőrzése (nap)',
        exportDays: 'Exportfájlok megőrzése (nap)',
        exportLineageNote: 'Az új export a személy saját beszélgetését, intézményi tagságát, valamint a körök, válaszok, útmutatások, szolgáltatói feladatok és anyagdarabszámok metaadatait tartalmazza. Kihagyja a privát promptokat, a forrásszöveget, a megoldókulcsot, a lenyomatokat, a szolgáltatói tartalmat és a nem nyilvántartott másolatokat. Ez nem teljes adatcsomag.',
        lawfulBasis: 'Proktorálás jogalapja (címke)',
        dpoEmail: 'DPO e-mail',
        doNotSell: 'Személyes adatot nem értékesítünk',
        requireAck: 'Bejelentkezés után adatvédelmi elfogadás kötelező',
        eraseMode: 'Alapértelmezett törlési mód',
        transferNote: 'Határokon átnyúló adattovábbítás megjegyzése',
        saved: 'Beállítások mentve.',
        saving: 'Mentés...',
        save: 'Mentés',
        subprocessorName: 'Név',
        add: 'Hozzáadás',
        remove: 'Eltávolítás',
        incidentTitle: 'Incidens címe'
      },
      voiceStudio: {
        title: 'MI hangstúdió',
        subtitle: 'Állítsa be a szóbeli vizsgáztató hangját: szolgáltató, katalógus-alapértelmezés és opcionális interjú-felülírás.',
        tabs: {
          label: 'Hangstúdió szakaszok',
          voices: 'Hangok',
          provider: 'Szolgáltató',
          assignment: 'Kiosztás'
        },
        status: {
          providerChip: 'Szolgáltató: {{name}}',
          globalChip: 'Globális: {{voice}}',
          globalFallback: 'Tartalék hang',
          interviewChip: 'Interjú: {{title}}',
          noInterview: 'Nincs kiválasztott interjú'
        },
        filters: {
          title: 'Katalóguszűrők',
          searchLabel: 'Keresés',
          languageLabel: 'Nyelv',
          accentLabel: 'Akcentus',
          genderLabel: 'Nem',
          styleLabel: 'Beszédstílus',
          providerLabel: 'Szolgáltató',
          searchPlaceholder: 'Hangok keresése…',
          allLanguages: 'Minden nyelv',
          allAccents: 'Minden akcentus',
          allGenders: 'Minden nem',
          allSpeakingStyles: 'Minden beszédstílus',
          allProviders: 'Minden szolgáltató',
          moreFilters: 'További szűrők',
          fewerFilters: 'Kevesebb szűrő',
          clearFilters: 'Szűrők törlése'
        },
        catalog: {
          title: 'Hangkatalógus',
          showing: '{{shown}} / {{total}} hang megjelenítve',
          loading: 'Hangok betöltése…',
          noMatches: 'Nincs a szűrőknek megfelelő hang.',
          defaultDescription: 'Természetes neurális hangprofil interjúkhoz.',
          qualityLabel: 'Minőség',
          free: 'Ingyenes',
          languageMatch: 'Illeszkedik az interjúhoz',
          nowPlaying: 'Most szól',
          emptyHint: 'Próbáljon más keresést, vagy törölje a szűrőket az összes hanghoz.',
          selected: 'Kiválasztva'
        },
        providerConfig: {
          title: 'Neurális hangszolgáltató beállítása',
          loading: 'Szolgáltatóbeállítás betöltése...',
          sourceDb: 'Adatbázisban mentve',
          sourceEnv: 'Környezeti alapértelmezés',
          configured: 'Beállítva',
          keyOptional: 'Kulcs opcionális',
          missingKey: 'Hiányzó kulcs',
          testPassed: 'Teszt sikeres',
          testFailed: 'Teszt sikertelen',
          providerLabel: 'Szolgáltató',
          modelLabel: 'Modell',
          modelsDirLabel: 'Hangmodell-mappa',
          baseUrlLabel: 'Alap URL',
          apiKeyLabel: 'API-kulcs',
          piperPathHelp: 'Helyi mappa a .onnx hangmodelleknek. Docker Compose alatt jellemzően /app/piper-voices.',
          confirmClearKey: 'Törli a tárolt API-kulcsot ennél a szolgáltatónál? A kulcs nem jelenik meg, és a felületről nem állítható vissza.',
          latencyMs: '{{ms}} ms',
          modelAvailable: 'A beállított modell elérhető.',
          modelUnavailable: 'A beállított modell nem szerepelt a szolgáltató listáján.',
          connectionSuccessful: 'A kapcsolat sikeres.',
          groupSpeaksWith: 'Ez a csoport a(z) {{name}} szolgáltatón keresztül beszél.',
          showAdvanced: 'Speciális beállítások mutatása',
          hideAdvanced: 'Speciális beállítások elrejtése',
          summaryKeyOn: 'Kulcs beállítva',
          summaryKeyOff: 'Nincs tárolt kulcs'
        },
        tuning: {
          title: 'Hanghangolás',
          selectedVoice: 'Kiválasztott hang: {{name}} ({{provider}})',
          languageLabel: 'Nyelv',
          styleLabel: 'Beszédstílus',
          stylePlaceholder: 'nyugodt, professzionális, energikus',
          rateLabel: 'Beszédtempó',
          pitchLabel: 'Hangmagasság',
          pitchFallbackOnly: '(csak szolgáltatói tartalék)',
          minLabel: 'Min. {{value}}',
          maxLabel: 'Max. {{value}}',
          pronunciationTitle: 'Kiejtés',
          pronunciationHelp: 'Ezek a kifejezések másképp hangzanak el. A képernyőn látható kérdés nem változik.',
          termLabel: 'Kifejezés',
          spokenLabel: 'Kiejtés',
          addTerm: 'Kifejezés hozzáadása',
          removeTerm: 'Eltávolítás'
        },
        preview: {
          title: 'Hangelőnézet',
          sampleTextLabel: 'Minta szöveg',
          statusLabel: 'Állapot',
          ready: 'Kész',
          generating: 'Hang generálása...',
          playing: 'Lejátszás',
          paused: 'Szünet',
          error: 'Hiba',
          elapsed: '{{current}} / {{duration}}'
        },
        assignment: {
          title: 'Hangkiosztási szabályok',
          globalTitle: 'Globális alapértelmezett hang',
          globalDescription: 'Akkor használatos, ha nincs interjú-specifikus felülírás.',
          noGlobalDefault: 'Nincs globális alapértelmezés',
          overrideTitle: 'Interjú-szintű felülírás',
          overrideDescription: 'Felülírja a globális beállítást egy interjúra.',
          interviewLabel: 'Interjú',
          selectInterview: 'Interjú kiválasztása...',
          loadingOverride: 'Interjú-felülírás betöltése...',
          overrideActive: 'Felülírás aktív: {{provider}} / {{voice}}',
          noInterviewOverride: 'Ehhez az interjúhoz nincs felülírás.',
          selectInterviewHint: 'Válasszon interjút a felülírás beállításához.',
          interviewLanguage: 'Az interjú értékelési nyelve: {{language}}',
          languageMismatch: 'Az interjú nyelve {{interviewLanguage}} ({{bcp47}}), a kiválasztott hang nyelve viszont {{voiceLanguage}}. A nyitó/záró beszéd hibásan szólalhat meg — válasszon illeszkedő hangot vagy nyelvkódot.',
          useMatchingVoice: 'Illeszkedő katalógushang használata ({{name}}) — csak mentés után lép életbe',
          effectiveTitle: 'Effektív hang ehhez az interjúhoz',
          effectiveSelectInterview: 'Válasszon interjút a feloldott hang megtekintéséhez.',
          effectiveLoading: 'Effektív hang feloldása…',
          effectiveUnavailable: 'Az effektív hang még nem érhető el.',
          effectiveSource: 'Forrás',
          sourceInterview: 'Interjú-felülírás',
          sourceGlobal: 'Globális alapértelmezés',
          sourceFallback: 'Biztonságos tartalék',
          sourceLanguageMatch: 'Nyelvileg illeszkedő katalógushang',
          sourceUnknown: 'Ismeretlen',
          languageMatchLabel: 'Nyelvi illeszkedés',
          languageMatchYes: 'Igen',
          languageMatchNo: 'Nem',
          effectiveOrderTitle: 'Effektív hangfeloldási sorrend',
          orderOverride: 'Interjú-specifikus felülírás',
          orderGlobal: 'Globális alapértelmezett hang',
          orderFallback: 'Biztonságos tartalékhang',
          fallbackReadOnly: 'A biztonságos tartalék környezeti változókból jön (VOICE_SAFE_FALLBACK_*), itt nem szerkeszthető.',
          chainTitle: 'Amit a jelölt hallani fog',
          stepActive: 'Aktív'
        },
        actions: {
          refresh: 'Frissítés',
          save: 'Mentés',
          saving: 'Mentés...',
          testing: 'Tesztelés...',
          testConnection: 'Kapcsolat tesztelése',
          clearing: 'Törlés...',
          clearKey: 'Kulcs törlése',
          play: 'Lejátszás',
          pause: 'Szünet',
          resume: 'Folytatás',
          stop: 'Leállítás',
          retry: 'Újra',
          saveGlobal: 'Globális mentése',
          resetToDefaults: 'Alapértelmezés visszaállítása',
          saveInterviewOverride: 'Interjú-felülírás mentése',
          clearInterviewOverride: 'Interjú-felülírás törlése'
        },
        messages: {
          catalogLoadFailed: 'A hangkatalógus nem tölthető be',
          loadSettingsFailed: 'A hangstúdió beállításai nem tölthetők be.',
          selectVoiceForPreview: 'Válasszon hangprofilt az előnézet előtt.',
          previewTextRequired: 'Az előnézeti szöveg kötelező.',
          speechSynthesisRequired: 'Az alkalmazáskönyvtár-hang előnézetéhez böngészős beszédszintézis kell.',
          localPreviewFailed: 'A helyi alkalmazáskönyvtár-hang előnézete sikertelen.',
          audioPlaybackFailed: 'A lejátszás sikertelen. Próbálja újra.',
          previewGenerationFailed: 'Az előnézet generálása sikertelen',
          resumePreviewFailed: 'Az előnézet nem folytatható.',
          providerRequired: 'A szolgáltató megadása kötelező.',
          baseUrlRequired: 'Az alap URL megadása kötelező.',
          modelRequired: 'A modell megadása kötelező.',
          providerConfigSaved: 'A neurális hangszolgáltató beállítása mentve.',
          saveProviderConfigFailed: 'A szolgáltatóbeállítás mentése sikertelen.',
          providerFieldsRequiredForTest: 'Tesztelés előtt szolgáltató, alap URL és modell szükséges.',
          connectionTestFailed: 'A kapcsolatteszt sikertelen.',
          providerFieldsRequiredForClear: 'Kulcstörlés előtt szolgáltató, alap URL és modell szükséges.',
          providerKeyCleared: 'A szolgáltató API-kulcsa törölve.',
          clearProviderKeyFailed: 'Az API-kulcs törlése sikertelen.',
          selectVoiceForGlobal: 'Válasszon hangot a globális beállítás mentése előtt.',
          globalSaved: 'A globális hangbeállítás mentve.',
          saveGlobalFailed: 'A globális beállítás mentése sikertelen.',
          globalCleared: 'A globális hangbeállítás törölve (a termék alapértelmezett feloldása érvényes).',
          clearGlobalFailed: 'A globális beállítás törlése sikertelen.',
          selectInterviewForOverride: 'Válasszon interjút a felülírás mentése előtt.',
          selectVoiceForOverride: 'Válasszon hangot az interjú-felülírás mentése előtt.',
          overrideSaved: 'Az interjú hangfelülírása mentve.',
          saveOverrideFailed: 'Az interjú hangfelülírásának mentése sikertelen.',
          selectInterviewForClearOverride: 'Válasszon interjút a felülírás törlése előtt.',
          overrideCleared: 'Az interjú-felülírás törölve.',
          clearOverrideFailed: 'Az interjú-felülírás törlése sikertelen.',
          selectVoiceToTune: 'Válasszon hangprofilt a hangolásához.'
        },
        hints: {
          storedKey: 'Tárolt kulcs: ****{{last4}}',
          keyOptional: 'Nincs tárolt API-kulcs. Ez a szolgáltató kulcs nélkül is futhat, ha a végpont engedélyezi.',
          keyMissing: 'Ehhez a szolgáltatóhoz nincs tárolt API-kulcs.'
        },
        placeholders: {
          keepCurrentKey: 'Hagyja üresen a jelenlegi kulcs megtartásához',
          optionalApiKey: 'Opcionális: adja meg az API-kulcsot, ha a végpont hitelesítést kér',
          enterApiKey: 'Adja meg a szolgáltató API-kulcsát'
        },
        guidance: {
          title: 'Természetességi útmutató',
          item1: 'Használjon írásjeleket az előnézeti szövegben, hogy hallja a valós szüneteket és nyomatékot.',
          item2: 'Tartsa a tempót 0,90 és 1,10 között a legtöbb interjú emberi ritmusához.',
          item3: 'Használjon stílusjeleket, például „professzionális”, „meleg” vagy „elemző”.'
        },
        reset: {
          globalTitle: 'Globális alapértelmezett hang visszaállítása',
          globalScope: 'Ez a csoport (globális hangfelülírás)',
          globalWill: 'Nincs globális alapértelmezés — az interjúk a termék hangfeloldására esnek vissza',
          globalWont: {
            overrides: 'Interjú-szintű hangfelülírások',
            provider: 'Szolgáltató API-beállítás és katalógus'
          }
        }
      },
      assessmentReview: {
        title: 'Értékelés-felülvizsgálat',
        subtitle: 'Tekintse át a MI pontszámokat, az A/B rész bizonyítékait, az integritást, és rögzítsen döntést.',
        searchPlaceholder: 'Keresés név, e-mail vagy interjú szerint',
        loading: 'Értékelések betöltése…',
        empty: 'Nincs a szűrőknek megfelelő értékelés.',
        emptyCriteria: 'Nincsenek szempontpontszámok.',
        emptyMcq: 'Ehhez a munkamenethez nincs A rész kísérlet.',
        emptyTranscript: 'Nincs szóbeli átirat.',
        emptyHistory: 'Még nincs felülvizsgálati előzmény.',
        filters: {
          allStatuses: 'Minden állapot',
          pending: 'Függőben',
          inReview: 'Felülvizsgálat alatt',
          reviewed: 'Felülvizsgálva',
          approved: 'Jóváhagyva',
          rejected: 'Elutasítva',
          requiresHumanReview: 'Emberi felülvizsgálat kell',
          resultsHidden: 'Eredmények rejtve',
          sortBy: 'Rendezés',
          order: 'Sorrend',
          from: 'Ettől',
          to: 'Eddig',
          clear: 'Szűrők törlése',
          sortCreated: 'Létrehozva',
          sortStatus: 'Állapot',
          sortAi: 'MI pontszám',
          sortFinal: 'Végső pontszám',
          sortIntegrity: 'Integritás',
          orderAsc: 'Növekvő',
          orderDesc: 'Csökkenő'
        },
        status: {
          pending: 'Függőben',
          inReview: 'Felülvizsgálat alatt',
          reviewed: 'Felülvizsgálva',
          approved: 'Jóváhagyva',
          rejected: 'Elutasítva'
        },
        origin: {
          human: 'Adminisztrátor döntött',
          auto_approve: 'Automatikusan jóváhagyva',
          ai: 'MI döntött',
          fallback: 'Emberi felülvizsgálatra küldve'
        },
        aiReview: {
          confidence: 'MI magabiztosság',
          fallbackReason: 'Visszalépés oka'
        },
        decision: {
          proceed: 'Továbbengedés',
          reject: 'Elutasítás',
          hold: 'Felfüggesztés',
          reinterview: 'Új interjú'
        },
        tabs: {
          overview: 'Áttekintés',
          partA: 'A rész',
          partB: 'B rész',
          integrity: 'Integritás',
          review: 'Felülvizsgálat'
        },
        fields: {
          candidate: 'Jelölt',
          interview: 'Interjú',
          duration: 'Időtartam',
          integrityScore: 'Integritás',
          aiScore: 'MI pontszám',
          aiConfidence: 'MI magabiztosság',
          recommendation: 'Ajánlás',
          finalScore: 'Végső pontszám',
          mcqScore: 'MCQ pontszám',
          reviewer: 'Felülvizsgáló',
          createdAt: 'Létrehozva',
          sessionStarted: 'Kezdés',
          sessionCompleted: 'Befejezés',
          criterion: 'Szempontok',
          strengths: 'Erősségek',
          weaknesses: 'Gyengeségek',
          detailedFeedback: 'Részletes visszajelzés',
          comments: 'Megjegyzések',
          scoreReason: 'Pontszámfelülírás indoka',
          reviewHistory: 'Felülvizsgálati előzmény',
          unknownReviewer: 'Ismeretlen felülvizsgáló',
          adminOverride: 'admin felülírás',
          aiOrMcq: 'MI vagy MCQ'
        },
        actions: {
          viewDetails: 'Részletek',
          startReview: 'Felülvizsgálat indítása',
          markReviewed: 'Felülvizsgálva',
          approve: 'Jóváhagyás',
          reject: 'Elutasítás',
          hold: 'Felfüggesztés',
          reinterview: 'Új interjú',
          saveScore: 'Pontszám mentése',
          downloadPdf: 'PDF letöltése',
          downloadJson: 'JSON letöltése',
          grantRetake: 'Újrapróbálkozás engedélyezése',
          toggleVisibility: 'Eredmény-láthatóság',
          messageCandidate: 'Üzenet a jelöltnek',
          overrideQuestionScore: 'Szóbeli pont felülírása',
          openPlacement: 'Szintfelmérő központ',
          openProctoring: 'Proktorálási jelentések',
          openTranscript: 'Átiratok',
          openAuditLogs: 'Audit naplók',
          bulkSetStatus: 'Kijelöltek állapotának beállítása',
          apply: 'Alkalmaz',
          selectPage: 'Oldal kijelölése',
          assignmentRequired: 'Ehhez a munkamenethez nincs kapcsolt kiosztás'
        },
        badges: {
          requiresHumanReview: 'Emberi felülvizsgálat',
          resultsHidden: 'Eredmények rejtve',
          proctoringFlags: 'Proktorálási jelzések',
          figure: 'Ábra'
        },
        pagination: {
          range: '{{start}}–{{end}} / {{total}}',
          previous: 'Előző',
          next: 'Következő'
        },
        mcq: {
          correct: 'Helyes',
          incorrect: 'Helytelen',
          unanswered: 'Megválaszolatlan'
        },
        placement: {
          partA: 'A rész (MCQ)',
          partB: 'B rész (szóbeli)',
          notLinked: 'Ez az interjú nincs szintfelmérőhöz kapcsolva, ezért itt nincs A/B rangsorpontszám.',
          transferHistory: 'Átviteli előzmény',
          humanAdjusted: 'Ember által módosított',
          autoScored: 'Automatikus',
          aiScored: 'MI'
        },
        recommendation: {
          strongly_recommend: 'Erősen ajánlott',
          recommend: 'Ajánlott',
          recommended: 'Ajánlott',
          needsReview: 'További felülvizsgálat kell',
          notRecommended: 'Nem ajánlott',
          not_recommend: 'Nem ajánlott',
          strongly_not_recommend: 'Erősen nem ajánlott',
          strong_hire: 'Erős felvétel',
          hire: 'Felvétel',
          no_hire: 'Elutasítás',
          neutral: 'Semleges',
          pending: 'Függőben',
          provisional: 'Ideiglenes'
        },
        messages: {
          updateStatusFailed: 'Az állapot frissítése sikertelen.',
          loadDetailsFailed: 'Az értékelés részleteinek betöltése sikertelen.',
          approved: 'Az értékelés jóváhagyva.',
          rejected: 'Az értékelés elutasítva.',
          hold: 'Az értékelés felfüggesztve.',
          reinterview: 'Új interjú rögzítve.',
          submitReviewFailed: 'A felülvizsgálat mentése sikertelen.',
          reportDownloaded: 'A jelentés letöltve.',
          downloadReportFailed: 'A jelentés letöltése sikertelen.',
          movedInReview: 'Az értékelés felülvizsgálat alá került.',
          markedReviewed: 'Az értékelés felülvizsgálva.',
          scoreSaved: 'A pontszámfelülírás elmentve.',
          visibilityUpdated: 'Az eredmény-láthatóság frissítve.',
          overrideSaved: 'A szóbeli pont felülírva.',
          retakeGranted: 'Újrapróbálkozás engedélyezve.',
          bulkComplete: 'Frissítve: {{applied}}, sikertelen: {{failed}}.'
        },
        validation: {
          commentsRequired: 'A megjegyzés kötelező.',
          scoreOverrideRange: 'A pontszámnak 0 és 100 között kell lennie.',
          scoreReasonRequired: 'Pontszámváltoztatáskor indoklás szükséges.'
        },
        errors: {
          loadListFailed: 'Az értékelések betöltése sikertelen.',
          saveScoreFailed: 'A pontszámfelülírás mentése sikertelen.',
          grantRetakeFailed: 'Az újrapróbálkozás engedélyezése sikertelen.',
          visibilityFailed: 'Az eredmény-láthatóság frissítése sikertelen.',
          overrideFailed: 'A szóbeli pont felülírása sikertelen.'
        }
      },
      placement: {
        common: {
          yes: 'Igen',
          no: 'Nem',
          notApplicable: '—',
          close: 'Bezárás',
          refresh: 'Frissítés'
        },
        errors: {
          loadRankingFailed: 'A rangsor betöltése sikertelen',
          exportFailed: 'Az export sikertelen',
          loadTemplatesFailed: 'A sablonok betöltése sikertelen',
          saveFailed: 'A mentés sikertelen',
          importFailed: 'Az import sikertelen',
          retentionSaveFailed: 'A megőrzési beállítás mentése sikertelen',
          resultVisibilityFailed: 'Az eredmény-láthatóság frissítése sikertelen',
          transferPreviewFailed: 'Az eredményátvitel előnézete sikertelen',
          transferFailed: 'Az eredmények átvitele sikertelen'
        },
        tabs: {
          placements: 'Szintfelmérők',
          ranking: 'Rangsor',
          fairness: 'Méltányosság',
          outcomes: 'Eredmények',
          reports: 'Jelentések',
          template: 'Sablon',
          declarations: 'Nyilatkozatok',
          bank: 'Kérdésbank',
          students: 'Hallgatói onboarding'
        },
        switcher: {
          label: 'Aktív szintfelmérő',
          empty: 'Még nincs szintfelmérő',
          stats: '{{enrolled}} beíratva · {{completed}} teljesítve · {{interviews}} interjú'
        },
        placements: {
          create: 'Szintfelmérő létrehozása',
          name: 'Név',
          namePlaceholder: 'Név (pl. 2026 ősz)',
          code: 'Kód',
          codePlaceholder: 'Kód (nem kötelező)',
          partAPoints: 'A rész maximális pontszáma (MCQ)',
          partBPoints: 'B rész maximális pontszáma (szóbeli, irányadó)',
          partBPointsHint: 'A szóbeli maximum továbbra is a pontozott kérdéssorból jön; ez a keret csak tervezési támpont.',
          createButton: 'Létrehozás',
          empty: 'Még nincs szintfelmérő.',
          showArchived: 'Archivált szintfelmérők megjelenítése ({{count}})',
          columns: {
            name: 'Név',
            mode: 'Mód',
            status: 'Állapot',
            enrolled: 'Beíratva',
            progress: 'Haladás',
            actions: 'Műveletek'
          },
          statuses: {
            draft: 'Piszkozat',
            open: 'Nyitott',
            closed: 'Lezárt',
            archived: 'Archivált'
          },
          modes: {
            hybrid: 'Vegyes',
            part_a: 'A rész',
            part_b: 'B rész'
          },
          actions: {
            open: 'Megnyitás',
            close: 'Lezárás',
            edit: 'Szerkesztés',
            duplicate: 'Másolat',
            archive: 'Archiválás',
            delete: 'Törlés'
          },
          confirm: {
            cancel: 'Mégse',
            proceed: 'Folytatás',
            duplicateTitle: 'Szintfelmérő másolása',
            duplicateBody: 'Létrehoz egy piszkozat másolatot a(z) „{{name}}” szintfelmérőről? A szóbeli sablon is másolódik; a jelöltek, az eredmények és a beíratások nem.',
            archiveTitle: 'Szintfelmérő archiválása',
            archiveBody: 'Archiválja a(z) „{{name}}” szintfelmérőt? Nem fogad több munkamenetet, de minden eredmény megmarad és visszaállítható.'
          },
          edit: {
            title: 'Szintfelmérő szerkesztése',
            description: 'Leírás',
            status: 'Állapot',
            opensAt: 'Nyitás időpontja',
            closesAt: 'Zárás időpontja',
            save: 'Módosítások mentése',
            cancel: 'Mégse',
            staleWarning: 'Az értékelési mód vagy a pontkeret módosítása nem számolja újra a már tárolt {{count}} eredményt. A rangsor a régi összpontszámokat mutatja, amíg ezeket újra nem pontozzák vagy át nem viszik.'
          },
          delete: {
            title: 'Szintfelmérő törlése',
            intro: 'Véglegesen törli a(z) „{{name}}” ({{code}}) szintfelmérőt. A művelet nem vonható vissza.',
            loading: 'A szintfelmérőhöz tartozó adatok ellenőrzése…',
            willDestroy: 'Véglegesen törlődik',
            willUnlink: '{{count}} kapcsolt interjú megmarad, de leválik erről a szintfelmérőről.',
            counts: {
              results: 'Eredmények és rangsorsorok',
              question_sets: 'Generált szóbeli kérdéssorok',
              declarations: 'Jelölti nyilatkozatok',
              academic_outcomes: 'Tanulmányi eredményrekordok',
              enrollments: 'Beíratások',
              section_links: 'Kapcsolt jelöltcsoportok'
            },
            blocked: 'Ez a szintfelmérő még értékelési adatokat tartalmaz, ezért nem törölhető. Archiválja helyette, vagy kérjen szuperadminisztrátort a kényszerített törléshez.',
            forceAcknowledge: 'Tudomásul veszem, hogy ez visszaállíthatatlanul megsemmisíti az értékelési adatokat.',
            typeToConfirm: 'A megerősítéshez írja be a szintfelmérő kódját: „{{code}}”',
            recommendArchive: 'Az archiválás a biztonságosabb megoldás: elrejti a szintfelmérőt a napi használatból, de minden eredményt megőriz.',
            confirm: 'Végleges törlés',
            cancel: 'Mégse'
          },
          errors: {
            createFailed: 'A szintfelmérő létrehozása sikertelen',
            updateFailed: 'A szintfelmérő frissítése sikertelen',
            duplicateFailed: 'A szintfelmérő másolása sikertelen',
            deleteFailed: 'A szintfelmérő törlése sikertelen',
            dependenciesFailed: 'A szintfelmérőhöz tartozó adatok ellenőrzése sikertelen'
          }
        },
        ranking: {
          title: 'Szintfelmérő rangsor',
          name: 'Név',
          neptun: 'Neptun',
          score: 'Pontszám',
          mcqScore: 'A rész (MCQ)',
          oralScore: 'B rész (szóbeli)',
          percentile: 'Percentilis',
          integrity: 'Integritás',
          flags: 'Jelzések',
          acceptsCourse: 'Alg. kurzus',
          acceptsAdvanced: 'Kiemelt csoport',
          cutoffLabel: 'Kiemelt csoport ponthatára',
          cutoffPreview: '{{eligible}} hallgató a határ felett, {{accepting}} vállalja a kiemelt csoportot',
          declarationDetail: 'Nyilatkozat',
          empty: 'Még nincsenek hallgatók.',
          exportCsv: 'CSV',
          exportXlsx: 'XLSX',
          releaseScores: 'Pontszámok közzététele',
          hideScores: 'Pontszámok elrejtése',
          confirmRelease: 'Közzéteszi a pontszámokat a szintfelmérőhöz kapcsolt összes kiosztásra? A teljes rangsor továbbra is csak oktatóknak látható. A jelöltek a portálon a saját pontszámukat és helyezésüket látják.',
          confirmHide: 'Elrejti a pontszámokat a szintfelmérőhöz kapcsolt összes kiosztásra? A jelöltek nem látnak számokat vagy helyezést, amíg újra nem teszi közzé őket.',
          scoresReleased: 'Pontszámok közzétéve {{count}} kiosztásra. A jelöltek a saját eredményüket és helyezésüket látják; a teljes rangsor továbbra is csak oktatóknak látható.',
          scoresHidden: 'Pontszámok elrejtve {{count}} kiosztásra.'
        },
        transfer: {
          title: 'Eredmények átvitele',
          sourcePlacement: 'Forrás szintfelmérő',
          components: 'Átviendő részek',
          partA: 'A rész (MCQ)',
          partB: 'B rész (szóbeli)',
          partUnavailable: 'Ez a szintfelmérő nem pontozza ezt a részt.',
          conflictOverride: 'Meglévő eredmények felülírása',
          conflictFill: 'Csak a hiányzó eredmények kitöltése',
          reason: 'Indoklás',
          preview: 'Előnézet',
          apply: 'Átvitel alkalmazása',
          previewRequired: 'Az alkalmazás előtt tekintse meg az előnézetet.',
          success: 'Átvíve: {{applied}}, kihagyva: {{skipped}}.',
          selectedCount: '{{count}} kijelölve',
          noOtherPlacements: 'Nincs másik szintfelmérő ebben a csoportban.',
          skip: {
            notEnrolled: 'Nincs beíratva a cél szintfelmérőre',
            noSourceResult: 'Nincs eredmény a forrás szintfelmérőn',
            sourceMissingPartA: 'A forrásnak nincs A rész pontszáma',
            sourceMissingPartB: 'A forrásnak nincs B rész pontszáma',
            targetHasPartA: 'A célnak már van A rész pontszáma',
            targetHasPartB: 'A célnak már van B rész pontszáma'
          }
        },
        template: {
          title: 'Szintfelmérő sablon',
          totalPoints: 'Összpontszám',
          namePlaceholder: 'Sablon neve',
          save: 'Mentés és aktiválás',
          saved: 'Sablon elmentve.',
          mustBe35: 'Az összegnek pontosan 35 pontnak kell lennie 10 kérdésre elosztva',
          topicFallback: '{{number}}. témakör',
          formats: {
            numeric: 'Automatikusan pontozott numerikus',
            verbal: 'MI által értékelt szöveges',
            numeric_with_followup: 'Numerikus + szóbeli visszakérdezés'
          }
        },
        bank: {
          importTitle: 'Moodle XML kérdésbank importálása',
          importHint: 'Töltse fel a korábbi Moodle XML exportot. A kategóriák, numerikus/cloze válaszok, TeX képletek és beágyazott képek automatikusan feldolgozásra kerülnek; a három nyilatkozatkérdés (q1–q3) kimarad.',
          chooseFile: 'XML fájl kiválasztása',
          importing: 'Importálás…',
          summaryTitle: 'Import összegzés',
          imported: 'Importálva',
          skippedDeclaration: 'Kihagyva (nyilatkozat q1–q3)',
          unsupported: 'Nem támogatott interaktív',
          images: 'Tárolt képek',
          categories: 'Kategória csomópontok',
          errors: 'Hibák',
          unmapped: 'Témakörhöz nem rendelt családok',
          browseTitle: 'Referenciakérdések',
          allTopics: 'Összes témakör',
          familyFilter: 'Család…',
          topic: 'témakör',
          points: 'pont',
          referenceOnly: 'csak referencia',
          empty: 'Még nincsenek importált referenciakérdések.'
        },
        students: {
          title: 'Tömeges hallgatói onboarding',
          hint: 'Töltsön fel CSV vagy XLSX névsort a következő oszlopokkal: név, e-mail, és opcionális Neptun kód (max. 500 sor). A név és az e-mail kötelező; a Neptun kód hiányozhat vagy üresen hagyható. A fiókok létrejönnek, a meghívók kötegekben kerülnek kiküldésre.',
          chooseFile: 'Névsorfájl kiválasztása',
          importing: 'Importálás…',
          created: 'Létrehozva',
          skipped: 'Kihagyva',
          invitations: 'Kiküldött meghívók',
          totalRows: 'Összes sor',
          rowErrors: 'Validációs hibák soronként',
          retentionTitle: 'Proctoring bizonyítékok megőrzése (GDPR)',
          retentionHint: 'Az ennél régebbi proctoring bizonyítékok törölhetővé válnak. A hallgatónkénti adatexport és -törlés a rangsor táblázatból érhető el.',
          days: 'nap',
          saveRetention: 'Mentés'
        },
        declaration: {
          item1Short: 'Vállalja az Algoritmikus gondolkodás kurzust',
          item2Short: 'Vállalja a kiemelt csoportot',
          item3Short: 'Programozási háttér (csak információ — a pontozásból kizárva)',
          noBackground: 'Még nem tanult programozni'
        },
        declarationForms: {
          title: 'Nyilatkozatűrlapok',
          subtitle: 'Kérdőívek létrehozása, és egy űrlap hozzákapcsolása több szintfelmérőhöz. A jelölt a szintfelmérőhöz kötött űrlapot tölti ki.',
          create: 'Űrlap létrehozása',
          edit: 'Szerkesztés',
          duplicate: 'Másolat',
          archive: 'Archiválás',
          save: 'Mentés',
          cancel: 'Mégse',
          name: 'Név',
          description: 'Leírás',
          status: 'Állapot',
          addQuestion: 'Kérdés hozzáadása',
          labelEn: 'Címke (angol)',
          labelHu: 'Címke (magyar)',
          instructorOnly: 'Csak oktatóknak (soha nem megy az MI-nek)',
          linkPlacements: 'Szintfelmérőkhöz kapcsolás',
          linkedForm: 'Nyilatkozatűrlap',
          none: 'Nincs — nem kötelező',
          defaultBadge: 'alapértelmezett',
          columns: { name: 'Név', status: 'Állapot', linked: 'Kapcsolt szintfelmérők', actions: 'Műveletek' },
          statuses: { draft: 'Piszkozat', published: 'Közzétéve', archived: 'Archivált' },
          types: {
            boolean: 'Igen / Nem',
            checkbox: 'Jelölőnégyzet',
            text: 'Rövid szöveg',
            enum: 'Választás',
            repeating_group: 'Ismétlődő sorok'
          },
          errors: {
            loadFailed: 'A nyilatkozatűrlapok betöltése sikertelen',
            saveFailed: 'A nyilatkozatűrlap mentése sikertelen'
          }
        }
      },
      candidateManagement: {
        header: {
          title: 'Jelöltkezelés',
          subtitle: 'Jelöltek kezelése és MI-alapú interjúk ütemezése'
        },
        stats: {
          totalCandidates: 'Összes jelölt',
          active: 'Aktív',
          scheduled: 'Ütemezett',
          unscheduled: 'Nem ütemezett'
        },
        actions: {
          addCandidate: 'Jelölt hozzáadása',
          bulkImport: 'Tömeges import',
          exportCsv: 'CSV export',
          scheduleBatch: 'Köteg ütemezése ({{count}})',
          unselectVisible: 'Láthatók kijelölésének megszüntetése',
          selectAllVisible: 'Összes látható kijelölése',
          clearSelection: 'Kijelölés törlése',
          close: 'Bezárás',
          runImport: 'Import futtatása',
          importing: 'Importálás...',
          addFirstCandidate: 'Első jelölt hozzáadása',
          cancel: 'Mégse',
          removeCandidate: 'Jelölt eltávolítása',
          viewProfile: 'Profil megtekintése',
          scheduleInterview: 'Interjú ütemezése',
          reschedule: 'Átütemezés',
          setSchedule: 'Időpont beállítása',
          sendEmail: 'E-mail küldése',
          resendInvitation: 'Meghívó újraküldése',
          remove: 'Eltávolítás',
          assignNow: 'Kiosztás most',
          setScheduleLower: 'Időpont beállítása',
          view: 'Megtekintés',
          rescheduleInterview: 'Interjú átütemezése',
          delete: 'Törlés',
          enableDetailedResults: 'Részletes eredmények engedélyezése',
          hideDetailedResults: 'Részletes eredmények elrejtése',
          cancelling: 'Visszavonás...',
          cancelAssignment: 'Kiosztás visszavonása',
          grantRetake: 'Újrapróbálkozás engedélyezése',
          editProfile: 'Profil szerkesztése'
        },
        filters: {
          searchPlaceholder: 'Keresés név, e-mail vagy Neptun alapján...',
          allStatus: 'Minden állapot',
          pendingInvitation: 'Függő meghívó',
          allInterviews: 'Minden interjú',
          scheduled: 'Ütemezett',
          unscheduled: 'Nem ütemezett',
          allSections: 'Minden csoport',
          allDeadlines: 'Minden határidő',
          dueSoonHours: 'Hamarosan lejár ({{hours}} óra)',
          dueSoonHint: 'A „Hamarosan lejár” a következő {{hours}} órában lejáró határidőket jelenti.',
          clearChipFilter: '{{label}} szűrő törlése',
          filterByChip: 'Szűrés: {{label}}'
        },
        status: {
          pending: 'Függőben',
          active: 'Aktív',
          inactive: 'Inaktív',
          suspended: 'Felfüggesztett'
        },
        assignmentStatus: {
          scheduled: 'Ütemezve',
          pending: 'Függőben',
          in_progress: 'Folyamatban',
          completed: 'Befejezve',
          cancelled: 'Visszavonva'
        },
        deadline: {
          upcoming: 'Közelgő',
          dueSoon: 'Hamarosan lejár',
          expired: 'Lejárt',
          noDeadline: 'Nincs határidő'
        },
        relativeTime: {
          inDays: '{{count}} nap múlva',
          tomorrow: 'Holnap',
          inHours: '{{count}} óra múlva',
          startingSoon: 'Hamarosan indul',
          past: 'Elmúlt'
        },
        selection: {
          selected_one: '{{count}} jelölt kijelölve',
          selected_other: '{{count}} jelölt kijelölve',
          visibleWithFilters_one: '{{count}} kijelölt jelölt látható a jelenlegi szűrőkkel',
          visibleWithFilters_other: '{{count}} kijelölt jelölt látható a jelenlegi szűrőkkel'
        },
        loading: 'Jelöltek betöltése...',
        empty: {
          noMatchTitle: 'Egy jelölt sem felel meg a szűrőknek',
          noMatchDescription: 'Nincs illeszkedő időpont, nyitott ablak vagy névsor ezekhez a szűrőkhöz.',
          noCandidatesTitle: 'Még nincsenek jelöltek',
          noCandidatesDescription: 'Adja hozzá az első jelöltet az MI-alapú interjúk ütemezéséhez.'
        },
        fields: {
          neptun: 'Neptun'
        },
        schedule: {
          kindSlot: 'Időpont',
          kindWindow: 'Ablak',
          kindNone: 'Nem ütemezett',
          windowOpen: 'Ablak nyitva',
          window: 'Értékelési ablak'
        },
        card: {
          interviewsCount_other: '{{count}} interjú',
          deadline: 'Határidő',
          interviewAssigned: 'Interjú kiosztva',
          noInterviewAssigned: 'Nincs kiosztott interjú'
        },
        table: {
          candidate: 'Jelölt',
          status: 'Állapot',
          interview: 'Interjú',
          resultsAccess: 'Eredmény-hozzáférés',
          deadline: 'Határidő',
          created: 'Létrehozva',
          actions: 'Műveletek',
          moreAssignments_one: '+{{count}} további kiosztás',
          moreAssignments_other: '+{{count}} további kiosztás',
          schedulePending: 'Ütemezés függőben',
          notAssigned: 'Nincs kiosztva',
          hidden: 'Rejtett',
          visible: 'Látható'
        },
        aria: {
          selectCandidate: '{{name}} kijelölése',
          selectAllVisible: 'Összes látható jelölt kijelölése',
          openMenu: '{{name}} műveletei'
        },
        modals: {
          addNewCandidate: 'Új jelölt hozzáadása',
          bulkImportCandidates: 'Jelöltek tömeges importja',
          scheduleAssessment: 'Felmérés ütemezése',
          candidateProfile: 'Jelölt profilja',
          removeCandidate: 'Jelölt eltávolítása',
          editProfile: 'Profil szerkesztése',
          sendMessage: 'Üzenet küldése'
        },
        edit: {
          fullName: 'Teljes név',
          neptunOptional: 'Neptun-kód (opcionális)',
          neptunPlaceholder: '6 alfanumerikus karakter',
          neptunInvalid: 'A Neptun-kód 6 alfanumerikus karakter legyen, vagy hagyja üresen.',
          status: 'Állapot',
          save: 'Mentés',
          saving: 'Mentés...'
        },
        bulkImport: {
          csvIntro: 'Illessze be a CSV sorokat a következő fejlécekkel: {{headers}}',
          neptunHint: 'A {{neptunCode}} opcionális — hagyja üresen, ha a névsorban nincs kód. Ha meg van adva, 6 alfanumerikus karakternek kell lennie; a jelölt profilján tárolódik és megjelenik a szintfelmérő rangsor exportban. A {{interviewId}}/{{scheduledAt}}/{{interviewType}} oszlopok soronkénti felülírások — teljes csoport onboarding esetén hagyja őket üresen, és használja az alábbi alapértelmezéseket.',
          cohortTitle: 'Teljes csoport kiosztása és ütemezése (opcionális)',
          cohortHint: 'Minden sorra érvényes, amely nem ad meg saját interview_id/scheduled_at értéket.',
          interview: 'Interjú',
          scheduledAt: 'Ütemezve',
          modality: 'Mód',
          noneAssignLater: 'Nincs (később kiosztás)',
          text: 'Szöveges',
          voice: 'Hang',
          totalRows: 'Összes sor: {{count}}',
          createdUsers: 'Létrehozott felhasználók: {{count}}',
          reusedUsers: 'Újrahasznált felhasználók: {{count}}',
          assignmentsUpdated: 'Frissített kiosztások: {{count}}',
          invitationsDispatched: 'Kiküldött meghívók: {{count}}',
          rowError: '{{row}}. sor: {{error}}'
        },
        profile: {
          accountStatus: 'Fiók állapota',
          memberSince: 'Tagság kezdete',
          assignedInterviews: 'Kiosztott interjúk ({{count}})',
          assignedInterviewTemplate: 'Kiosztott interjúsablon',
          statusLabel: 'Állapot: {{status}}',
          scheduleNotSet: 'Az időpont még nincs beállítva.',
          resultsVisible: 'Eredmények láthatók',
          resultsHidden: 'Eredmények rejtve',
          noAssignments: 'Ennek a jelöltnek még nincs interjú-kiosztása.',
          reviewModeLabel: 'Felülvizsgálati mód',
          reviewMode: {
            manual: 'Kézi felülvizsgálat',
            auto_approve: 'Automatikus jóváhagyás',
            auto_review: 'Automatikus felülvizsgálat (MI dönt)'
          }
        },
        delete: {
          confirmTitle: 'Eltávolítja „{{name}}” jelöltet?',
          confirmBody: 'Ez a művelet nem vonható vissza. A jelölthöz tartozó összes adat véglegesen törlődik.'
        },
        confirm: {
          cancelAssignment: 'Visszavonja a(z) „{{interviewTitle}}” kiosztást {{candidateName}} számára?'
        },
        email: {
          subject: 'VivaAssess interjú-frissítés'
        },
        messages: {
          selectVisibleCandidatesForAssign: 'Jelöljön ki legalább egy látható jelöltet az interjú kiosztásához.',
          candidateRemoved: 'A jelölt sikeresen eltávolítva.',
          removeCandidateFailed: 'A jelölt eltávolítása sikertelen.',
          invitationCopied: 'A meghívó link újragenerálva és a vágólapra másolva.',
          invitationRegeneratedShare: 'A meghívó link újragenerálva. Kérjük, ossza meg a jelölttel.',
          unknownError: 'Ismeretlen hiba.',
          resendInvitationFailed: 'A meghívó újraküldése sikertelen: {{reason}}',
          noActiveAssignmentFound: 'Nem található aktív interjú-kiosztás ehhez a jelölthöz.',
          resultsVisible: 'A jelölt mostantól megtekintheti a részletes eredményeket.',
          resultsHidden: 'A jelölt részletes eredményei el lettek rejtve.',
          updateResultVisibilityFailed: 'Az eredmény-láthatóság frissítése sikertelen: {{reason}}',
          assignmentCancelUnsupported: 'Ez a kiosztás nem vonható vissza ebből a nézetből. Frissítsen, majd próbálja újra.',
          assignmentStatusNotCancellable: 'Nem vonható vissza {{status}} állapotú kiosztás.',
          assignmentCancelled: 'Az interjú-kiosztás visszavonva.',
          cancelAssignmentFailed: 'A kiosztás visszavonása sikertelen: {{reason}}',
          candidateCsvExported: 'A jelöltek CSV exportja elkészült.',
          exportCandidatesFailed: 'A jelöltek exportja sikertelen.',
          bulkImportSuccess: '{{total}} rekord sikeresen importálva.',
          bulkImportPartial: 'Importálva {{errorCount}} sori hibával.',
          bulkImportFailed: 'A tömeges import sikertelen.',
          profileUpdated: 'A jelölt profilja frissítve.',
          actionFailed: 'Sikertelen: {{reason}}'
        }
      },
      createCandidateForm: {
        intro: 'Hozzon létre egy új jelöltfiókot. Meghívó link készül, amellyel a jelölt beállíthatja a jelszavát.',
        fields: {
          fullName: 'Teljes név',
          fullNamePlaceholder: 'Adja meg a jelölt teljes nevét',
          email: 'E-mail-cím',
          emailPlaceholder: 'Adja meg a jelölt e-mail-címét',
          neptun: 'Neptun',
          neptunOptional: 'Neptun-kód (opcionális)',
          neptunPlaceholder: '6 alfanumerikus karakter'
        },
        validation: {
          neptun: 'A Neptun-kód 6 alfanumerikus karakter legyen, vagy hagyja üresen.'
        },
        next: {
          title: 'Mi történik ezután?',
          invite: 'Meghívó link készül (7 napig érvényes)',
          share: 'Ossza meg a linket a jelölttel a jelszó beállításához',
          assign: 'Ezután erről az oldalról kioszthat egy interjút'
        },
        actions: {
          cancel: 'Mégse',
          create: 'Jelölt létrehozása'
        },
        success: {
          title: 'Jelölt létrehozva',
          shareWith: 'Ossza meg az alábbi meghívó linket {{name}} jelölttel',
          expires: 'A link lejár: {{date}}',
          invitationLink: 'Meghívó link',
          copy: 'Másolás',
          copied: 'Másolva',
          nextStepsTitle: 'Következő lépések',
          stepShare: 'Ossza meg ezt a meghívó linket a jelölttel',
          stepPassword: 'A jelölt ezzel a linkkel állítja be a jelszavát',
          stepAssign: 'Amikor készen áll, osszon ki egy interjút',
          sendEmail: 'E-mail küldése',
          assignInterview: 'Interjú kiosztása',
          done: 'Kész'
        },
        messages: {
          createFailed: 'A jelölt létrehozása nem sikerült. Próbálja újra.'
        }
      },
      invigilatorManagement: {
        hero: {
          title: 'Felügyelők',
          subtitle: 'Felügyelők készenlétének kezelése, terhelés kiegyenlítése és gyors beavatkozás a jelzett üléseken.',
          statusSummary: '{{active}} aktív, {{pending}} aktiválásra vár, {{inactive}} inaktív'
        },
        actions: {
          assignInvigilator: 'Felügyelő kiosztása',
          addInvigilator: 'Felügyelő hozzáadása',
          activate: 'Aktiválás',
          deactivate: 'Deaktiválás',
          updating: 'Frissítés...',
          activateSelected: 'Kijelöltek aktiválása',
          deactivateSelected: 'Kijelöltek deaktiválása',
          clearSelection: 'Kijelölés törlése',
          removing: 'Eltávolítás...',
          remove: 'Eltávolítás',
          cancel: 'Mégse',
          confirm: 'Megerősítés'
        },
        shortcuts: {
          title: 'Műveleti gyorsgombok',
          subtitle: 'Szűrje a kockázatos sorokat egy koppintással, és tartsa a terhelést {{threshold}} aktív kiosztás alatt felügyelőnként.',
          focusFlagged: 'Jelzett ülések',
          focusLive: 'Élő ülések',
          showOverloaded: 'Túlterhelt csapat',
          clearFilters: 'Szűrők törlése'
        },
        metrics: {
          invigilators: 'Felügyelők',
          overloaded: 'Túlterhelt',
          assignments: 'Kiosztások',
          liveSessions: 'Élő ülések',
          flaggedSessions: 'Jelzett ülések',
          active: 'Aktív',
          inactive: 'Inaktív',
          threshold: 'Küszöb',
          activeLower: 'aktív',
          liveSessionsHint: 'A jelenlegi kiosztási szűrők alapján',
          flaggedHint: 'Pontszám < {{threshold}} vagy ülésprobléma',
          invigilatorsAria: 'Minden felügyelő megjelenítése',
          overloadedAria: 'Túlterhelt felügyelők megjelenítése',
          assignmentsAria: 'Minden kiosztás megjelenítése',
          liveAria: 'Élő ülésekre szűrés',
          flaggedAria: 'Jelzett ülésekre szűrés'
        },
        status: {
          active: 'Aktív',
          pending: 'Függő',
          inactive: 'Inaktív'
        },
        sync: {
          none: 'Még nincs szinkron',
          unknown: 'Ismeretlen szinkronidő'
        },
        team: {
          title: 'Felügyelői csapat',
          searchPlaceholder: 'Keresés név vagy e-mail szerint',
          pending: 'Függő',
          selectVisible: 'Láthatók kijelölése',
          unselectVisible: 'Láthatók kijelölésének törlése',
          showingCount: '{{visible}} / {{total}} felügyelő',
          selectedForBulk: '{{count}} felügyelő kijelölve tömeges művelethez',
          loading: 'Felügyelők betöltése...',
          empty: 'Nincs felügyelő',
          emptyHint: 'Módosítsa a szűrőket, vagy adjon hozzá új felügyelőt.',
          selectInvigilator: '{{name}} kijelölése',
          workload: 'Terhelés: {{total}} aktív kiosztás, {{live}} élő ülés',
          highWorkload: 'magas terhelés',
          highWorkloadTitle: 'Ennek a felügyelőnek jelenleg {{threshold}} vagy több aktív kiosztása van.'
        },
        filters: {
          allStatuses: 'Minden állapot',
          sortWorkloadHigh: 'Rendezés: terhelés csökkenő',
          sortWorkloadLow: 'Rendezés: terhelés növekvő',
          sortName: 'Rendezés: név (A-Z)',
          sortStatus: 'Rendezés: állapot',
          showOverloadedOnly: 'Csak túlterheltek',
          showingOverloadedOnly: 'Csak túlterheltek látszanak'
        },
        queue: {
          title: 'Felügyelői kiosztások',
          refresh: 'Frissítés',
          searchPlaceholder: 'Keresés jelölt, Neptun, felügyelő, interjú szerint',
          allInvigilators: 'Minden felügyelő',
          allInterviews: 'Minden interjú',
          activeAssignments: 'Aktív kiosztások',
          inactiveAssignments: 'Inaktív kiosztások',
          allVisibility: 'Minden láthatóság',
          liveOnly: 'Csak élő ülések',
          flaggedOnly: 'Csak jelzett ülések',
          sortRecent: 'Rendezés: legutóbbi frissítések',
          sortIntegrityLow: 'Rendezés: legalacsonyabb integritás',
          sortIntegrityHigh: 'Rendezés: legmagasabb integritás',
          sortCandidate: 'Rendezés: jelölt neve (A-Z)',
          total: 'összesen: {{count}}',
          live: 'élő: {{count}}',
          flagged: 'jelzett: {{count}}',
          lastSync: 'utolsó szinkron: {{time}}',
          loading: 'Kiosztások betöltése...',
          empty: 'Nincs kiosztás a kiválasztott szűrőkre.',
          invigilatorLine: 'Felügyelő: {{name}} ({{email}})',
          sessionStatus: 'ülés: {{status}}',
          integrity: 'integritás: {{score}}',
          flaggedTitle: 'Jelzett integritási esemény, ülésprobléma vagy {{threshold}} alatti pontszám miatt',
          flaggedChip: 'jelzett',
          viewInteractions: 'Interakciók',
          noSessionYet: 'Még nincs ülés',
          deactivateAssignment: 'Kiosztás deaktiválása',
          reactivateAssignment: 'Kiosztás újraaktiválása',
          reassignTo: 'Átrendezés erre...',
          autoPick: 'Automatikus választás',
          reassign: 'Átrendezés',
          reassigning: 'Átrendezés...',
          suggestedTarget: 'Javasolt cél: {{name}}'
        },
        interaction: {
          title: 'Interakciófigyelő',
          interview: 'Interjú: {{title}}',
          invigilator: 'Felügyelő: {{name}}',
          liveRefresh: 'Élő ülés (automatikus frissítés 5 mp-enként)',
          historyRefresh: 'Üléstörténet (automatikus frissítés 5 mp-enként)',
          started: 'Kezdés: {{time}}',
          openLiveSupervision: 'Megnyitás élő felügyeletben',
          loading: 'Interakciós adatok betöltése...',
          chatTitle: 'Felügyelő–jelölt csevegés',
          noChat: 'Ebben az ülésben még nem volt csevegés.',
          timelineTitle: 'Proktorálási idővonal',
          noTimeline: 'Ehhez az üléshez még nincs idővonal-esemény.',
          invigilatorRole: 'Felügyelő',
          candidateRole: 'Jelölt',
          systemRole: 'Rendszer'
        },
        modals: {
          create: 'Felügyelő hozzáadása',
          assign: 'Felügyelő kiosztása',
          confirm: 'Művelet megerősítése',
          sendEmail: 'E-mail küldése'
        },
        labels: {
          thisInvigilator: 'ez a felügyelő',
          targetInvigilator: 'egy célfelügyelő',
          unknown: 'Ismeretlen',
          notAvailable: 'nincs',
          unassigned: 'Nincs kiosztva'
        },
        confirm: {
          removeInvigilator: 'Eltávolítja {{name}} felügyelőt? A fiókhoz kötött aktív kiosztások leállhatnak.',
          deactivateAssignment: 'Deaktiválja {{candidateName}} kiosztását a(z) {{interviewTitle}} interjúban?',
          toggleInvigilatorStatus: '{{action}} {{name}} fiókját?',
          bulkStatusUpdate: '{{action}} {{count}} kijelölt felügyelői fiókot?'
        },
        messages: {
          auditUnknownError: 'Ismeretlen audit hiba',
          loadAssignmentsFailed: 'A felügyelői kiosztások betöltése nem sikerült.',
          loadSessionDetailsFailed: 'Az ülés részletei nem tölthetők be.',
          loadChatMessagesFailed: 'A csevegés nem tölthető be.',
          loadTimelineFailed: 'Az idővonal nem tölthető be.',
          loadInteractionDataFailed: 'Az interakciófigyelő adatai nem tölthetők be.',
          invigilatorRemoved: 'A felügyelő eltávolítva.',
          removeInvigilatorFailed: 'A felügyelő eltávolítása nem sikerült.',
          assignmentDeactivated: 'A kiosztás deaktiválva.',
          removeAssignmentFailed: 'A kiosztás eltávolítása nem sikerült.',
          invigilatorDeactivated: 'A felügyelő deaktiválva.',
          invigilatorActivated: 'A felügyelő aktiválva.',
          updateInvigilatorStatusFailed: 'A felügyelő állapotának frissítése nem sikerült.',
          selectForBulkStatus: 'Jelöljön ki legalább egy felügyelőt a tömeges állapotváltáshoz.',
          bulkAlreadyInStatus: 'Minden kijelölt felügyelő már ebben az állapotban van.',
          bulkUpdatedSuccess: '{{successCount}} felügyelői fiók frissítve.',
          bulkUpdatedPartial: '{{successCount}} frissítve, {{failedCount}} sikertelen. Ellenőrizze a jogosultságokat.',
          reactivateWithoutInvigilator: 'A kiosztás felügyelő nélkül nem aktiválható újra.',
          assignmentReactivated: 'A kiosztás újraaktiválva.',
          reactivateAssignmentFailed: 'A kiosztás újraaktiválása nem sikerült.',
          selectInvigilatorForReassign: 'Válasszon felügyelőt az átrendezéshez.',
          assignmentAlreadyOwned: 'Ez a kiosztás már a kiválasztott felügyelőé.',
          assignmentReassigned: 'A kiosztás átrendezve.',
          reassignAssignmentFailed: 'Az átrendezés nem sikerült.',
          noReassignTarget: 'Nincs elérhető aktív átrendezési cél.',
          suggestedTarget: '{{name}} javasolva a jelenlegi terhelés alapján.',
          noSessionYet: 'Ehhez a kiosztáshoz még nem indult ülés.'
        }
      },
      createInvigilatorForm: {
        intro: 'Hozzon létre új felügyelői fiókot. Meghívó link készül, amellyel beállíthatják a jelszót.',
        fields: {
          fullName: 'Teljes név',
          fullNamePlaceholder: 'Adja meg a felügyelő teljes nevét',
          email: 'E-mail-cím',
          emailPlaceholder: 'Adja meg a felügyelő e-mail-címét'
        },
        pendingHint: 'A felügyelők függő állapotban maradnak, amíg el nem fogadják a meghívót.',
        actions: {
          creating: 'Létrehozás...',
          create: 'Felügyelő létrehozása'
        },
        success: {
          title: 'Felügyelő létrehozva',
          shareWith: 'Ossza meg az alábbi meghívó linket {{name}} felügyelővel',
          expires: 'A link lejár: {{date}}',
          invitationLink: 'Meghívó link',
          copy: 'Link másolása',
          copied: 'Másolva',
          nextStepsTitle: 'Következő lépések',
          stepShare: 'Küldje el a meghívó linket a felügyelőnek.',
          stepPassword: 'Kérje meg, hogy a jelszó beállítása az első felügyeleti műszak előtt történjen meg.',
          stepAssign: 'Jelölteket csak a meghívó elfogadása és az aktív fiók után osszon ki.',
          sendEmail: 'E-mail küldése',
          emailSubject: 'VivaAssess felügyelői meghívó',
          done: 'Kész'
        },
        messages: {
          nameAndEmailRequired: 'A teljes név és az e-mail kötelező.',
          createFailed: 'A felügyelő létrehozása nem sikerült. Próbálja újra.'
        }
      },
      assignInvigilatorForm: {
        title: 'Felügyelő kiosztása',
        subtitle: 'Párosítsa a jogosult jelölteket egy elérhető felügyelővel az aktív interjúfelügyelethez.',
        mode: {
          candidates: 'Jelöltek kiválasztása',
          section: 'Egész jelöltcsoport'
        },
        fields: {
          interview: 'Interjú',
          selectInterview: 'Interjú kiválasztása',
          invigilator: 'Felügyelő',
          selectInvigilator: 'Felügyelő kiválasztása',
          candidateGroup: 'Jelöltcsoport',
          selectGroup: 'Jelöltcsoport kiválasztása',
          memberCount: '{{count}} tag',
          groupHint: 'A kiválasztott interjúhoz már kiosztott tagok ehhez a felügyelőhöz kapcsolódnak.'
        },
        status: {
          pending: 'Függő',
          scheduled: 'Ütemezett',
          inProgress: 'Folyamatban'
        },
        picker: {
          title: 'Jogosult jelöltek',
          total: 'összesen: {{count}}',
          pendingCount: 'függő: {{count}}',
          scheduledCount: 'ütemezett: {{count}}',
          inProgressCount: 'folyamatban: {{count}}',
          selectVisible: 'Láthatók kijelölése',
          selectAll: 'Összes kijelölése',
          clearVisible: 'Láthatók törlése',
          clearAll: 'Összes törlése',
          searchPlaceholder: 'Keresés név, e-mail vagy Neptun szerint',
          allStatuses: 'Minden jelöltállapot',
          selectInterviewHint: 'Válasszon interjút a jogosult jelöltek betöltéséhez.',
          empty: 'Nincs a szűrőknek megfelelő jelölt.',
          selectCandidate: '{{name}} kijelölése',
          selectedCount: 'Kijelölve: {{count}} jelölt'
        },
        actions: {
          assigning: 'Kiosztás...',
          assign: 'Felügyelő kiosztása',
          assignGroup: 'Kiosztás a jelöltcsoportra'
        },
        messages: {
          interviewAndInvigilatorRequired: 'Válasszon interjút és felügyelőt a kiosztás előtt.',
          selectGroupRequired: 'Válasszon jelöltcsoportot a kiosztáshoz.',
          groupPartialSuccess: '{{addedCount}} kiosztás mentve a jelöltcsoportból. {{skippedCount}} tag kihagyva (nincs kiosztva erre az interjúra).',
          groupNoneCreated: 'Nem jött létre kiosztás. Ellenőrizze, hogy a csoporttagok ki vannak-e osztva erre az interjúra.',
          groupAssignFailed: 'A felügyelő jelöltcsoportos kiosztása nem sikerült.',
          selectCandidateRequired: 'Válasszon legalább egy jogosult jelöltet.',
          assignmentsPartialSuccess: '{{addedCount}} kiosztás mentve. {{skippedCount}} jelölt kihagyva, mert nincs kiosztva erre az interjúra.',
          noAssignmentsCreated: 'Nem jött létre kiosztás. Ellenőrizze a jogosultságot, és próbálja újra.',
          assignFailed: 'A felügyelő kiosztása nem sikerült. Próbálja újra.',
          noActiveInvigilators: 'Jelenleg nincs aktív felügyelő.'
        }
      },
      interviewManagement: {
        title: 'Interjúsablonok',
        subtitle: 'MI-alapú interjú-felmérések létrehozása és kezelése',
        actions: {
          createInterview: 'Interjú létrehozása',
          createFirstInterview: 'Első interjú létrehozása',
          view: 'Megtekintés',
          viewDetails: 'Részletek megtekintése',
          edit: 'Szerkesztés',
          editInterview: 'Interjú szerkesztése',
          duplicate: 'Másolás',
          uploadMaterials: 'Anyagok feltöltése',
          activate: 'Aktiválás',
          deactivate: 'Deaktiválás',
          delete: 'Törlés',
          deleteInterview: 'Interjú törlése',
          cancel: 'Mégse',
          done: 'Kész',
          releaseScores: 'Pontszámok közzététele',
          hideScores: 'Pontszámok elrejtése',
          openPlacementTemplate: 'Szintfelmérő központ megnyitása → Sablon',
          addOralQuestions: 'Szóbeli kérdések hozzáadása',
          linkPlacement: 'Szintfelmérő hozzárendelése a hivatalos sablonbankhoz'
        },
        stats: {
          total: 'Összes',
          active: 'Aktív',
          draft: 'Piszkozat',
          archived: 'Archivált'
        },
        filters: {
          searchPlaceholder: 'Keresés cím, szintfelmérő, fókuszterület vagy nehézség alapján...',
          allStatus: 'Minden állapot',
          allLevels: 'Minden szint',
          allPlacements: 'Minden szintfelmérő',
          placementLinked: 'Szintfelmérő hozzárendelve',
          placementUnlinked: 'Nincs hozzárendelve',
          allModes: 'Minden mód',
          allPartB: 'Minden B rész forrás',
          partBAuthored: 'Szerkesztett kérdések',
          placementAria: 'Szűrés szintfelmérő-kapcsolat szerint',
          modeAria: 'Szűrés értékelési mód szerint',
          partBAria: 'Szűrés B rész forrása szerint'
        },
        status: {
          active: 'Aktív',
          draft: 'Piszkozat',
          archived: 'Archivált'
        },
        states: {
          loading: 'Betöltés...',
          loadingInterviews: 'Interjúk betöltése...',
          preparingCopy: 'Másolat előkészítése...'
        },
        empty: {
          noMatchTitle: 'Egy interjú sem felel meg a szűrőknek',
          noMatchDescription: 'Próbálja módosítani a keresést vagy a szűrőfeltételeket',
          noInterviewsTitle: 'Még nincsenek interjúk',
          noInterviewsDescription: 'Hozza létre az első interjúsablont, hogy MI-alapú értékelésekkel kezdhesse a jelöltek felmérését.'
        },
        card: {
          noFocusAreas: 'Nincs fókuszterület',
          moreFocusAreas: '+{{count}} további'
        },
        window: {
          range: '{{from}} → {{until}}',
          opens: 'Nyitás: {{from}}',
          closes: 'Zárás: {{until}}',
          perCandidate: 'Jelöltenkénti'
        },
        table: {
          interview: 'Interjú',
          status: 'Állapot',
          duration: 'Időtartam',
          difficulty: 'Nehézség',
          language: 'Nyelv',
          candidates: 'Jelöltek',
          window: 'Időablak',
          actions: 'Műveletek',
          durationMinutes: '{{minutes}} perc'
        },
        modals: {
          duplicateInterview: 'Interjú másolása',
          createNewInterview: 'Új interjú létrehozása',
          interviewDetails: 'Interjú részletei',
          editInterview: 'Interjú szerkesztése',
          manageMaterials: 'Referenciaanyagok kezelése',
          deleteInterview: 'Interjú törlése'
        },
        view: {
          noDescription: 'Nincs megadott leírás',
          status: 'Állapot',
          duration: 'Időtartam',
          difficulty: 'Nehézség',
          language: 'Nyelv',
          examinerName: 'Vizsgáztató neve',
          examinerNameUnset: 'Nincs megadva',
          durationMinutes: '{{minutes}} perc',
          focusAreas: 'Fókuszterületek',
          instructions: 'Utasítások',
          interviewQuestions: 'Interjúkérdések ({{count}})',
          questionType: 'Típus: {{type}}',
          questionDifficulty: 'Nehézség: {{difficulty}}',
          questionTime: 'Idő: {{seconds}} mp',
          noPredefinedQuestions: 'Nincsenek előre definiált kérdések.',
          questionsFromTemplate: 'Nincsenek szerkesztett kérdések ezen az interjún. A B rész a szintfelmérő sablonbankját használja.',
          questionsDynamic: 'Nincsenek szerkesztett szóbeli kérdések. A jelöltek dinamikus MI-kérdéseket kapnak.',
          questionsNotReady: 'Nincsenek szerkesztett szóbeli kérdések, és a hozzárendelt szintfelmérőnek még nincs aktív sablonja.',
          materialsHint: 'Töltsön fel anyagokat, amelyekre az MI hivatkozhat a kérdésgenerálás során',
          placement: 'Hozzárendelt szintfelmérő',
          assessmentMode: 'Értékelési mód',
          timers: 'Időzítők: {{duration}}',
          oralSource: 'B rész szóbeli forrás'
        },
        readiness: {
          placementBadge: 'Szintfelmérő: {{label}}',
          placementLinked: '{{name}} · {{status}}',
          placementUnnamed: 'Szintfelmérő',
          placementNotLinked: 'Nincs hozzárendelve',
          modeBadge: 'Mód: {{mode}}',
          partBBadge: 'B rész: {{source}}',
          partAEnabled: 'A rész be',
          partAReady: 'A rész · MCQ-beállítások',
          partADisabled: 'A rész ki',
          sourceTemplate: 'Sablon',
          sourceAuthored: 'Szerkesztett kérdések ({{count}})',
          sourceDynamic: 'Dinamikus MI',
          sourceNotReady: 'Nem kész',
          sourcePartAOnly: 'B rész ki',
          mode: {
            hybrid: 'Hibrid',
            part_a: 'A rész',
            part_b: 'B rész'
          },
          explainTemplate: 'A B rész a hozzárendelt szintfelmérő aktív szóbeli sablonját (rangsor bank) használja.',
          explainAuthored: 'A B rész {{count}} szerkesztett interjúkérdést használ ezen a sablonon.',
          explainDynamic: 'A B rész dinamikus MI-kérdésekkel indul. A viva elindulhat; a rangsor nem a szintfelmérő sablonbankját használja.',
          explainNotReady: 'A B rész rangsor-generálásához aktív sablon kell a hozzárendelt szintfelmérőn. A jelöltek továbbra is kaphatnak szerkesztett vagy dinamikus kérdéseket.',
          explainPartAOnly: 'A B rész ki van kapcsolva. A jelöltek csak az A részt (MCQ) írják.'
        },
        guards: {
          notReadyBlock: 'Ez az interjú olyan szintfelmérőhöz van kötve, amelynek nincs aktív szóbeli sablonja. A B rész generate 409-cel hibázik, amíg a Szintfelmérő központban sablont nem ment és aktivál.',
          dynamicConfirm: 'Nincs szintfelmérő sablon és nincsenek szerkesztett szóbeli kérdések. A jelöltek dinamikus MI-kérdéseket kapnak. Folytatja?'
        },
        delete: {
          confirmTitle: 'Törli a(z) „{{title}}” interjút?',
          confirmBody: 'Ez a művelet nem vonható vissza. Az interjúhoz tartozó összes adat véglegesen törlődik.'
        },
        duplicate: {
          copySuffix: '(Másolat)'
        },
        confirm: {
          releaseScores: 'Közzéteszi a pontszámokat a(z) „{{title}}” összes kiosztott jelöltjének? A jelöltek a portálon a saját pontszámukat és helyezésüket látják. A teljes rangsor továbbra is csak oktatóknak látható.',
          hideScores: 'Elrejti a pontszámokat a(z) „{{title}}” összes kiosztott jelöltjétől? A jelöltek nem látnak számokat vagy helyezést, amíg újra nem teszi közzé őket.'
        },
        messages: {
          loadDetailsFailed: 'Az interjú részletei nem tölthetők be teljesen.',
          loadDetailsForEditFailed: 'Az interjú részletei nem tölthetők be teljesen. Csak az alapmezők szerkeszthetők.',
          deleted: 'Az interjú sikeresen törölve.',
          deleteFailed: 'Az interjú törlése sikertelen.',
          statusMoved: 'Az interjú áthelyezve: {{status}}.',
          updateStatusFailed: 'Az interjú állapotának frissítése sikertelen.',
          duplicated: 'Az interjú másolva. Ellenőrizze és mentse az új sablont.',
          duplicatedBasicOnly: 'Az interjú csak alapmezőkkel másolva.',
          saved: 'Sikeresen mentve.',
          interviewAndMcqSaved: 'Az interjú és az MCQ-beállítások sikeresen mentve.',
          interviewSaved: 'Az interjú sikeresen mentve.',
          mcqSaved: 'Az MCQ-beállítások sikeresen mentve.',
          mcqRegenerated: 'Az MCQ-k sikeresen újragenerálva.',
          scoresReleased: 'Pontszámok közzétéve {{count}} kiosztásra. A jelöltek a saját eredményüket és helyezésüket látják.',
          scoresHidden: 'Pontszámok elrejtve {{count}} kiosztásra.',
          resultVisibilityFailed: 'Az eredmény-láthatóság frissítése sikertelen.'
        }
      },
      privateOpeningPrompt: {
        label: "Egy?ni sz?beli nyit?s (halad?)",
        hint: "Nem k?telez?. ?resen az alap?rtelmezett nyit?s ?rv?nyes. A k?sz?nt?t ?s az els? k?rd?st vez?rli; a m?dos?t?sok az ?j munkamenetekre vonatkoznak.",
        format: "JSON v?laszt adjon greeting ?s first_question mez?kkel; az ut?bbi tartalmazza a question, question_type, difficulty, expected_topics ?s max_response_time_seconds mez?ket. Hely?rz?k:",
      },
      interviewForm: {
        answerReview: {
          title: 'B rész válaszellenőrzés',
          hint: 'A beszéd átírása után a jelölt javíthatja a szöveget, mielőtt a kör pontot kap. Az automatikus beküldés megmarad. A B rész időzítője nem áll meg.',
          allow: 'A jelölt minden válaszát ellenőrizheti és javíthatja',
          defaultMode: 'Kezdő rögzítési mód',
          modeAuto: 'Automatikus beküldés csend után',
          modeReview: 'Ellenőrzés beküldés előtt',
          timeout: 'Ellenőrzési időkorlát (másodperc, 0 = nincs)'
        },
        fields: {
          title: 'Interjú címe *',
          titlePlaceholder: 'pl. Szoftvermérnök L3, Orvostudományi felvételi',
          titlePlaceholderShort: 'pl. Szoftvermérnök L3',
          description: 'Leírás',
          descriptionPlaceholder: 'Írja le az interjú célját és tartalmát',
          difficultyLevel: 'Nehézségi szint',
          durationMinutes: 'Időtartam (perc) *',
          language: 'Nyelv',
          examinerName: 'Vizsgáztató neve',
          examinerNamePlaceholder: 'pl. Dr. Kovács',
          examinerNameHint: 'A köszöntőben hangzik el az interjú nyelvén. Nem a Hangstúdió hangneve.',
          maxCandidates: 'Max. jelöltszám',
          status: 'Állapot',
          focusAreas: 'Fókuszterületek',
          focusAreaPlaceholder: 'pl. Szakmai tudás, Kommunikáció, Problémamegoldás',
          focusAreaPlaceholderShort: 'pl. Problémamegoldás',
          addFocusArea: 'Fókuszterület hozzáadása',
          instructionsForCandidates: 'Utasítások a jelölteknek',
          instructionsPlaceholder: 'Adjon meg konkrét utasításokat az interjút teljesítő jelölteknek',
          instructionsPlaceholderShort: 'Adjon meg konkrét utasításokat a jelölteknek',
          placement: 'Szintfelmérő',
          noPlacement: 'Nincs hozzárendelt szintfelmérő',
          placementModeAuthoritative: 'A szintfelmérő értékelési módja a mérvadó: az A/B rész jelzők a hozzárendeléskor vagy mentéskor szinkronizálódnak.'
        },
        difficulty: {
          beginner: 'Kezdő',
          intermediate: 'Középhaladó',
          advanced: 'Haladó',
          expert: 'Szakértő'
        },
        interviewStatus: {
          draft: 'Piszkozat',
          active: 'Aktív',
          archived: 'Archivált'
        },
        languages: {
          en: 'Angol',
          es: 'Spanyol',
          fr: 'Francia',
          de: 'Német',
          hu: 'Magyar'
        },
        proctoring: {
          title: 'MI-proktorálási szabályzat',
          description: 'Kamera/hang anomália-észlelés alapértelmezései az interjú összes kiosztásához.',
          sensitivity: 'Érzékenység',
          evidenceMode: 'Bizonyíték mód',
          requireConsent: 'Jelölt hozzájárulása szükséges',
          sensitivityLow: 'Alacsony',
          sensitivityBalanced: 'Kiegyensúlyozott',
          sensitivityHigh: 'Magas',
          metadataOnly: 'Csak metaadat',
          extendedEvidence: 'Kiterjesztett bizonyíték',
          secondaryCamera: 'Második szobakamera (telefon, oldalnézet)',
          secondaryCameraRequired: 'A szobakamera kötelező az indulás előtt'
        },
        assessmentWindow: {
          title: 'Felmérési időablak (ütemezés)',
          descriptionCreate: 'Opcionális csoportszintű időablak, amely alatt MINDEN kiosztott jelölt (pl. egy teljes ~400 fős évfolyam) elindíthatja a felmérést. Hagyja üresen, ha jelöltenként külön ütemez.',
          descriptionEdit: 'Opcionális csoportszintű időablak, amely alatt MINDEN kiosztott jelölt elindíthatja a felmérést. Hagyja üresen, ha jelöltenként külön ütemez.',
          opensAt: 'Nyitás',
          closesAt: 'Zárás'
        },
        questions: {
          title: 'Interjúkérdések',
          subtitleCreate: 'Opcionális strukturált kérdések az interjú folyamatához.',
          subtitleEdit: 'Előre definiált interjúkérdések és sorrend kezelése.',
          addQuestion: 'Kérdés hozzáadása',
          emptyCreate: 'Nincsenek előre definiált kérdések. A jelöltek dinamikus kérdésekre/MCQ-kra támaszkodnak.',
          emptyEdit: 'Nincsenek előre definiált kérdések.',
          questionNumber: '{{number}}. kérdés',
          removeQuestionAria: '{{number}}. kérdés eltávolítása',
          questionPlaceholder: 'Adja meg az interjúkérdést',
          type: 'Típus',
          difficulty: 'Nehézség',
          expectedTimeSec: 'Várható idő (mp)',
          required: 'Kötelező',
          types: {
            open: 'Nyílt',
            technical: 'Szakmai',
            behavioral: 'Viselkedési',
            situational: 'Szituációs',
            follow_up: 'Követő'
          }
        },
        assessmentMode: {
          title: 'Értékelési mód',
          subtitle: 'Válassza ki, mit ír a jelölt. Legalább egy rész mindig ki van választva.',
          hybridTitle: 'A rész + B rész',
          hybridBody: 'Időzített MCQ, majd az MI szóbeli viva. Szintfelmérőhöz ajánlott.',
          recommended: 'Ajánlott',
          partATitle: 'Csak A rész (MCQ)',
          partABody: 'A jelöltek csak a feleletválasztós tesztet írják. A munkamenet a beküldés után véget ér.',
          partBTitle: 'Csak B rész (szóbeli viva)',
          partBBody: 'A jelöltek kihagyják az MCQ-t, és rögtön a hangos interjúra mennek.',
          partASettingsHidden: 'Az A rész (MCQ) beállításai rejtve vannak, mert az értékelési mód csak B rész.'
        },
        partBSource: {
          title: 'B rész szóbeli kérdések',
          subtitle: 'Válassza ki, honnan kapja a viva a kérdéseket. A szintfelmérő értékelési módja továbbra is szinkronizálódik, ha van hozzárendelés.',
          templateTitle: 'Szintfelmérő sablon',
          templateBody: 'Rangsoroláshoz ajánlott. Hozzárendelt szintfelmérőt és aktív szóbeli sablont igényel.',
          recommended: 'Ajánlott',
          authoredTitle: 'Szerkesztett interjúkérdések',
          authoredBody: 'Az interjú kérdéslistáját használja. A jelöltek kihagyják a szintfelmérő generate lépést.',
          dynamicTitle: 'Dinamikus MI vizsgáztató',
          dynamicBody: 'Nincs sablon és nincs szerkesztett lista. A viva LLM-kérdéseket és a tartalék kérdésbankot használja.',
          needPlacement: 'Válasszon szintfelmérőt fent. A rangsor a Szintfelmérő központ aktív sablonját használja.',
          templateReady: 'A(z) „{{name}}” szintfelmérőnek van aktív szóbeli sablonja. Az ezen az interjún szerkesztett kérdések opcionálisak.',
          templateMissing: 'A B rész generate 409-et ad (placement_template_required), amíg ehhez a szintfelmérőhöz sablont nem ment és aktivál.',
          openPlacementTemplate: 'Szintfelmérő központ megnyitása → Sablon',
          authoredStillUsed: 'Még {{count}} szerkesztett szóbeli kérdés van ezen az interjún, és a dinamikus MI előtt ezeket használja. Távolítsa el őket, ha csak az LLM-vizsgáztatót szeretné.',
          dynamicWarn: 'A jelöltek dinamikus szóbeli vizsgát írnak, nem a PlacementAB sablonbankot.',
          dynamicSaveConfirm: 'A jelöltek dinamikus szóbeli vizsgát írnak, nem a hivatalos szintfelmérő sablonbankot. Folytatja?',
          notReadySaveConfirm: 'A B rész generate 409-et ad (placement_template_required), amíg sablont nem ment és aktivál. Menti így is?'
        },
        mcq: {
          moduleTitle: 'MCQ modul',
          moduleSubtitle: 'MI által generált feleletválasztós felmérés beállítása.',
          settingsTitle: 'MCQ-beállítások',
          settingsSubtitle: 'Az interjú MCQ-viselkedésének közvetlen szerkesztése.',
          enable: 'MCQ engedélyezése',
          partARequiredBadge: 'A rész kötelező',
          partARequiredHint: 'Az A rész (MCQ) mindig engedélyezve van a kombinált A + B rész felméréshez.',
          loadingSettings: 'MCQ-beállítások betöltése...',
          partASourceTitle: 'A rész kérdésforrása',
          partASourceDescription: 'Az A rész kérdései a feltöltött Moodle XML referenciakérdés-bankra (reference_questions) épüljenek — amely a témaköröket, nehézséget és stílust tanulja az importált példákból anélkül, hogy ismétlné őket —, vagy az MI függetlenül generálja az interjú anyagaiból és utasításaiból?',
          useQuestionBank: 'Igen — kérdésbank használata',
          useQuestionBankHint: 'Az A rész alapja az importált referenciakérdések',
          generateIndependently: 'Nem — független generálás',
          generateIndependentlyHintCreate: 'Csak MI, az alábbi anyagokból/utasításokból',
          generateIndependentlyHintEdit: 'Csak MI, anyagokból/utasításokból',
          topicScopeHint: 'Opcionálisan korlátozhatja meghatározott témakörökre (hagyja mindet kijelöletlenül az összes témakör használatához):',
          allTopicsUsed: 'Minden témakör felhasználásra kerül.',
          totalPointsPartA: 'Összpontszám (A rész)',
          totalPointsHintCreate: 'A B rész szóbeli pontszámával együtt adja a végső rangsorpontszámot (a régi szintfelmérő 35-öt használt).',
          totalPointsHintEdit: 'A B rész szóbeli pontszámával együtt adja a végső rangsorpontszámot.',
          questionCount: 'Kérdésszám',
          optionsPerQuestion: 'Válaszlehetőségek száma',
          optionsCount: '{{count}} lehetőség',
          difficulty: 'Nehézség',
          questionStyle: 'Kérdésstílus',
          estimatedTime: 'Becsült MCQ-idő:',
          sectionMismatch: 'A szekciók összege {{sectionTotal}}, de a kérdésszám {{questionCount}}.',
          needsMaterials: 'A kiválasztott generálási mód anyagokat igényel, de nincs kiválasztott anyag vagy link.',
          timeLimitMode: 'Időkorlát mód',
          perQuestion: 'Kérdésenként',
          totalTestTime: 'Teljes tesztidő',
          secondsPerQuestion: 'Másodperc kérdésenként',
          totalTimeSeconds: 'Teljes idő (másodperc)',
          optional: 'Opcionális',
          randomizeQuestions: 'Kérdések véletlenszerű sorrendje',
          randomizeOptions: 'Válaszlehetőségek véletlenszerű sorrendje',
          allowReview: 'Áttekintés és megjelölés engedélyezése',
          allowReviewEdit: 'Áttekintés és megjelölés engedélyezése',
          aiFeedback: 'MI visszajelzés-összefoglaló',
          requireExplanations: 'Magyarázatok kötelezőek',
          avoidTrickQuestions: 'Kerülje a trükkös kérdéseket',
          referenceMaterialsOnly: 'Csak referenciaanyagok használata',
          negativeMarking: 'Negatív pontozás',
          negativeMarkRatio: 'Negatív pont arány',
          mcqInstructions: 'MCQ utasítások (MI)',
          mcqInstructionsEdit: 'MCQ utasítások (MI)',
          mcqInstructionsPlaceholder: 'pl. Algoritmusokra és adatszerkezetekre fókuszáljon. Kerülje a definíciókat.',
          mcqInstructionsPlaceholderEdit: 'pl. Alkalmazott érvelésre fókuszáljon, kerülje a memorizálás-központú kérdéseket.',
          sectionGrouping: 'Szekció-alapú csoportosítás (opcionális)',
          addSection: 'Szekció hozzáadása',
          noSections: 'Nincsenek szekciók. A kérdések keverve jelennek meg.',
          sectionNamePlaceholder: 'Szekció neve',
          resourceLinks: 'Forrás linkek (opcionális)',
          addLink: 'Link hozzáadása',
          addResourceLink: 'Forrás link hozzáadása',
          resourceLinkPlaceholder: 'https://pelda.hu/forras',
          resourceLinkPlaceholderEdit: 'https://pelda.hu/referencia',
          generateOnSave: 'MCQ-k azonnali generálása mentés után',
          regenerateAfterSave: 'MCQ-k újragenerálása a beállítások mentése után',
          generationSource: 'MCQ generálási forrás',
          generationSourceBoth: 'Interjú + anyagok',
          generationSourceInterviewOnly: 'Csak interjú-információ',
          generationSourceMaterialsOnly: 'Csak anyagok',
          generationLabelMaterials: 'anyagok',
          generationLabelInterview: 'interjú-információ',
          generationLabelBoth: 'interjú-információ és anyagok',
          mcqDifficulty: {
            easy: 'Könnyű',
            medium: 'Közepes',
            hard: 'Nehéz',
            adaptive: 'Adaptív'
          },
          questionStyles: {
            mixed: 'Vegyes',
            direct: 'Közvetlen',
            scenario: 'Szituáció-alapú',
            application: 'Alkalmazott'
          }
        },
        materials: {
          title: 'Referenciaanyagok',
          description: 'Töltsön fel PDF-eket, dokumentumokat vagy szövegfájlokat. Az MI ezek tartalmából generál releváns interjúkérdéseket.',
          section: 'Szekció',
          noSection: 'Nincs szekció',
          category: 'Kategória',
          categoryPlaceholder: 'pl. Munkaköri leírás',
          clickToUpload: 'Kattintson a feltöltéshez',
          dragAndDrop: 'vagy húzza ide',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (max. 20 MB/fájl)',
          readyToUpload: 'Feltöltésre kész',
          filesWillUpload: '{{count}} fájl lesz feltöltve az interjúval együtt',
          libraryTitle: 'Könyvtári anyagok',
          libraryDescription: 'Válasszon korábban feltöltött anyagokat újrafelhasználáshoz.',
          selectedCount: 'Kijelölve: {{count}}',
          allSections: 'Minden szekció',
          search: 'Keresés',
          searchPlaceholder: 'Anyagok keresése',
          loadingLibrary: 'Könyvtári anyagok betöltése...',
          noLibraryMaterials: 'Nem található anyag a könyvtárban.',
          sectionLabel: 'Szekció: {{name}}',
          categoryLabel: 'Kategória: {{category}}'
        },
        actions: {
          cancel: 'Mégse'
        }
      },
      createInterviewForm: {
        actions: {
          createInterview: 'Interjú létrehozása'
        },
        validation: {
          unsupportedFileType: 'Nem támogatott fájltípus: {{ext}}',
          fileTooLarge: 'A fájl túl nagy (max. 20 MB)'
        },
        messages: {
          linkingLibraryMaterials: '{{count}} könyvtári anyag összekapcsolása...',
          linkLibraryMaterialsFailed: 'Az interjú létrejött, de a könyvtári anyagok összekapcsolása sikertelen. Később hozzáadhatja őket.',
          uploadingReferenceMaterials: '{{count}} referenciaanyag feltöltése...',
          materialsUploaded: 'Az anyagok sikeresen feltöltve!',
          materialUploadFailed: 'Az interjú létrejött, de az anyagfeltöltés sikertelen. Később feltöltheti az interjú részleteiből.',
          savingMcqSettings: 'MCQ-beállítások mentése...',
          saveMcqSettingsFailed: 'Az interjú létrejött, de az MCQ-beállítások mentése sikertelen. Később beállíthatja őket.',
          mcqGenerationSkipped: 'MCQ-generálás kihagyva: adjon hozzá referenciaanyagokat vagy módosítsa a generálási forrást.',
          generatingMcq: 'MCQ-k generálása: {{generationLabel}}...',
          mcqGenerated: 'Az MCQ-k sikeresen generálva.',
          mcqGenerationFailed: 'Az interjú létrejött, de az MCQ-generálás sikertelen. Később újragenerálhatja.',
          createFailed: 'Az interjú létrehozása sikertelen.'
        }
      },
      editInterviewForm: {
        actions: {
          saveChanges: 'Változások mentése'
        },
        messages: {
          loadMcqSettingsFailed: 'Az MCQ-beállítások nem tölthetők be. Az interjú adatai továbbra is menthetők.',
          titleRequired: 'Az interjú címe kötelező.',
          mcqRegenerationFailed: 'Az MCQ újragenerálása sikertelen.',
          savedButMcqFailed: 'Sikeresen mentve, de az MCQ-generálás sikertelen: {{message}}',
          updateInterviewFailed: 'Az interjú frissítése sikertelen.'
        }
      },
      assignInterviewForm: {
        header: {
          title: 'Felmérés-köteg ütemezése',
          description: 'Egy kombinált A + B rész interjút és ütemtervet alkalmaz erre a kötegre. Futtassa újra más kijelöléssel (és ha az interjún nincs rögzített időablak, más időponttal) egy újabb köteg ütemezéséhez.'
        },
        batch: {
          summary_one: '{{count}} jelöltből álló köteg',
          summary_other: '{{count}} jelöltből álló köteg',
          moreCandidates: ', +{{count}} további',
          currentAssignment: 'Jelenlegi kiosztás: {{title}}{{statusSuffix}}',
          interviewAlreadyAssigned: 'Interjú már kiosztva'
        },
        fields: {
          interview: 'Interjú (A + B rész felmérés)',
          chooseInterviewTemplate: 'Válasszon interjúsablont',
          noActiveInterviews: 'Nincs elérhető aktív interjú. Először aktiváljon egy interjút.',
          batchDate: 'Köteg dátuma',
          batchTime: 'Köteg időpontja',
          deadlineDate: 'Teljesítési határidő dátuma (opcionális)',
          deadlineTime: 'Teljesítési határidő időpontja (opcionális)'
        },
        assessmentWindow: {
          title: 'Az interjú felmérési időablakát használja',
          description: '{{start}} → {{end}}. Ez a köteg automatikusan követi ezt az időablakot — nincs szükség kötegenkénti időpontra. Az időablakot az interjú beállításaiban módosíthatja.',
          noStartLimit: 'Nincs kezdési korlát',
          noEndLimit: 'Nincs záró korlát'
        },
        modality: {
          label: 'B rész módja (szóbeli interjú)',
          text: 'Szöveges',
          voice: 'Hangalapú'
        },
        combinedFormat: {
          title: 'Kombinált felmérési forma',
          description: 'Minden ütemezett jelölt először az A részt (MCQ), majd a B részt (hangalapú interjú) kapja. Az interjú típusa rögzített, és nem módosítható.',
          partA: 'A rész: MCQ',
          partB: 'B rész: Hangalapú interjú'
        },
        advanced: {
          toggle: 'Speciális beállítások (proctoring felülírás, hozzáférés-korlátozás, automatikus jóváhagyás)'
        },
        proctoring: {
          title: 'MI proctoring felülírás',
          hint: 'Hagyja kikapcsolva az interjú-szintű házirend örökléséhez, vagy kapcsolja be a köteg explicit beállításához.',
          enabledLabel: 'Proctoring engedélyezve',
          enabled: 'Engedélyezve',
          disabled: 'Letiltva',
          sensitivity: 'Érzékenység',
          sensitivityLow: 'Alacsony',
          sensitivityBalanced: 'Kiegyensúlyozott',
          sensitivityHigh: 'Magas'
        },
        accessRestriction: {
          title: 'Opcionális hozzáférés-korlátozás',
          hint: 'Korlátozza az interjú indítását jóváhagyott nyilvános IP-tartományokra és/vagy észlelt helyszínekre.',
          allowedIp: 'Engedélyezett IP vagy CIDR bejegyzések',
          allowedIpPlaceholder: '203.0.113.10\n198.51.100.0/24\n2001:db8::/32',
          allowedIpHint: 'Használjon vesszőt, pontosvesszőt vagy sortörést. Támogatja az IPv4 és IPv6 egyedi IP-ket és CIDR-tartományokat.',
          allowedLocations: 'Engedélyezett helyszínnevek',
          allowedLocationsPlaceholder: 'Budapest\nBudapest, Pest\nMagyarország',
          allowedLocationsHint: 'Adjon meg várost, régiót, országot vagy kombinált értékeket, pl. „Város, Régió”.',
          matchMode: 'Egyezési mód',
          matchModeAny: 'bármely (IP vagy helyszín egyezhet)',
          matchModeAll: 'mind (minden beállított csoportnak egyeznie kell)',
          enforcementMode: 'Kikényszerítési mód',
          enforcementStartOnly: 'start_only',
          enforcementContinuous: 'continuous',
          failAction: 'Hiba esetén',
          failActionBlock: 'interjú indítás blokkolása',
          failActionWarn: 'figyelmeztetés és indítás engedélyezése'
        },
        autoApprove: {
          label: 'Végső értékelés automatikus jóváhagyása',
          hint: 'Kihagyja a manuális admin felülvizsgálatot, és a kötegben minden befejezett értékelést jóváhagyottként jelöl.'
        },
        reviewMode: {
          label: 'Értékelés felülvizsgálati módja',
          hint: 'Válassza ki, ki dönt a végső jóváhagyásról vagy elutasításról a befejezés után.',
          manual: 'Kézi felülvizsgálat',
          manualHint: 'Egy adminisztrátor vizsgálja felül és dönt.',
          autoApprove: 'Automatikus jóváhagyás',
          autoApproveHint: 'Minden befejezett értékelés felülvizsgálat nélkül jóváhagyott lesz.',
          autoReview: 'Automatikus felülvizsgálat (MI dönt)',
          autoReviewHint: 'A nyelvi modell a MI értékelés alapján jóváhagy vagy elutasít. Bizonytalanság vagy hiba esetén ember vizsgálja felül.'
        },
        resultVisibility: {
          label: 'A jelöltek a befejezés után megtekinthetik a pontszámokat',
          hint: 'Alapértelmezés szerint ki. Ha nincs bejelölve, az összpontszám, az MCQ %, az integritás és a jelentések rejtve maradnak, amíg egy adminisztrátor közzé nem teszi őket.'
        },
        afterScheduling: {
          title: 'Ütemezés után',
          notifyEmail: 'A köteg minden jelöltje e-mailben értesítést kap a felmérés részleteiről',
          partOrder: 'Először az A részt (MCQ), majd ugyanabban a munkamenetben a B részt (szóbeli) teljesítik',
          singleAttempt: 'Minden hallgató pontosan egy próbálkozást kap; szükség esetén a Szintfelmérő központból indítható újrapróbálkozás',
          resultsPolicy: 'A pontszámok rejtve maradnak, hacsak fent nem engedélyezi a jelölti eredmény-láthatóságot (vagy később az Interjúkezelésben közzé nem teszi)',
          trackProgress: 'Kövesse az előrehaladást és az eredményeket a Szintfelmérő központ rangsor irányítópultján'
        },
        actions: {
          cancel: 'Mégse',
          scheduling: 'Ütemezés...',
          scheduleBatch: 'Köteg ütemezése ({{count}})'
        },
        validation: {
          restrictionEntryRequired: 'A korlátozás engedélyezésekor legalább egy IP/CIDR vagy helyszín bejegyzés szükséges.',
          invalidIpEntries: 'Érvénytelen IP/CIDR bejegyzések: {{entries}}',
          selectBatchAndInterview: 'Válasszon ki legalább egy jelöltet és egy interjút.',
          requiredFieldsBeforeAssign: 'Töltse ki az összes kötelező mezőt a köteg ütemezése előtt.',
          deadlineDateTimePairRequired: 'Adja meg a határidő dátumát és időpontját is, vagy hagyja mindkettőt üresen.',
          invalidInterviewSchedule: 'Érvénytelen interjú dátum vagy időpont. Válasszon érvényes ütemtervet.',
          invalidCompletionDeadline: 'Érvénytelen teljesítési határidő. Válasszon érvényes dátumot és időpontot.',
          deadlineBeforeSchedule: 'A teljesítési határidő nem lehet korábbi, mint az interjú ütemezése.'
        },
        messages: {
          unknownError: 'Ismeretlen hiba',
          partialAssignmentFailure: '{{totalCount}} jelöltből {{successfulCount}} ütemezve. Sikertelen: {{failedNames}}.',
          assignSelectedCandidatesFailed: 'A kijelölt jelöltek ütemezése sikertelen: {{failedNames}}.',
          assignInterviewFailed: 'A köteg ütemezése sikertelen. Próbálja újra.'
        }
      },
      sections: {
        title: 'Jelöltcsoportok',
        subtitle: 'Jelöltek kötegelése ütemezéshez, placement jelentkezéshez és tömeges interjú-kiosztáshoz. Nem azonos az intézményi/tenant csoportokkal.',
        searchPlaceholder: 'Keresés név vagy leírás alapján',
        showingCount: '{{filtered}} / {{total}} megjelenítve',
        loading: 'Jelöltcsoportok betöltése…',
        listTitle: 'Jelöltcsoportok',
        noDescription: 'Nincs leírás',
        archiveConfirm: 'Archiválja a(z) „{{name}}” csoportot? A tagok és korábbi kiosztások megmaradnak; a csoport kikerül az aktív listákból, és később visszaállítható. Az archiválás javasolt a végleges törlés helyett.',
        fields: {
          neptun: 'Neptun'
        },
        status: {
          active: 'Aktív',
          archived: 'Archivált'
        },
        filters: {
          active: 'Aktív',
          archived: 'Archivált',
          all: 'Összes'
        },
        stats: {
          total: 'Összes',
          active: 'Aktív',
          archived: 'Archivált',
          candidates: 'Jelöltek: {{count}}',
          interviews: 'Interjú-kiosztások: {{count}}'
        },
        empty: {
          title: 'Nem található jelöltcsoport',
          hint: 'Hozzon létre egy csoportot (pl. MSc Cohort A), adjon hozzá tagokat, majd iratkoztassa be placementre vagy ütemezzen interjúkat.'
        },
        actions: {
          create: 'Jelöltcsoport létrehozása',
          edit: 'Szerkesztés',
          members: 'Tagok',
          schedule: 'Interjúk ütemezése',
          enrolPlacement: 'Jelentkeztetés placementre',
          archive: 'Archiválás',
          archiving: 'Archiválás…',
          restore: 'Visszaállítás',
          cancel: 'Mégse',
          done: 'Kész'
        },
        modals: {
          edit: 'Jelöltcsoport szerkesztése',
          create: 'Jelöltcsoport létrehozása',
          members: 'Tagok kezelése',
          assign: 'Interjú ütemezése a jelöltcsoportnak',
          enrol: 'Jelöltcsoport jelentkeztetése placementre',
          archive: 'Jelöltcsoport archiválása'
        },
        messages: {
          groupArchived: 'A jelöltcsoport archiválva.',
          archiveFailed: 'A jelöltcsoport archiválása sikertelen.',
          groupRestored: 'A jelöltcsoport visszaállítva.',
          restoreFailed: 'A jelöltcsoport visszaállítása sikertelen.',
          enrolledInPlacement: 'A(z) „{{group}}” csoportot jelentkeztettük a kiválasztott placementre (tagok + kapcsolat).'
        },
        enrol: {
          blurb: 'Összekapcsolja ezt a jelöltcsoportot egy placementtel, és az összes jelenlegi tagot jelentkezteti (enrolled_via = group). Rangsoroláshoz és jelentésekhez a Szintfelmérő központot használja.',
          placement: 'Placement',
          loading: 'Placementek betöltése…',
          none: 'Még nincs placement',
          loadFailed: 'A placementek betöltése sikertelen',
          failed: 'A jelentkeztetés sikertelen',
          memberCount: '{{count}} tag',
          enrolledCount: '{{count}} jelentkezett',
          alreadyLinked: 'Már kapcsolt csoportok: {{names}}',
          enrolling: 'Jelentkeztetés…',
          submit: 'Csoport jelentkeztetése',
          status: {
            draft: 'piszkozat',
            open: 'nyitott',
            closed: 'zárt',
            archived: 'archivált'
          }
        },
        forms: {
          create: {
            createTitle: 'Jelöltcsoport létrehozása',
            editTitle: 'Jelöltcsoport szerkesztése',
            subtitle: 'Jelöltek kötegelése ütemezéshez és placement jelentkezéshez. Nem azonos az intézményi/tenant csoportokkal.',
            nameLabel: 'Csoportnév',
            namePlaceholder: 'pl. MSc Cohort A',
            descriptionLabel: 'Leírás (opcionális)',
            descriptionPlaceholder: 'Kohorszjegyzetek vagy felvételi részletek',
            creating: 'Létrehozás…',
            saving: 'Mentés…',
            create: 'Csoport létrehozása',
            save: 'Módosítások mentése',
            validation: {
              nameRequired: 'A jelöltcsoport neve kötelező.'
            },
            messages: {
              createdSuccess: 'A jelöltcsoport sikeresen létrejött.',
              updatedSuccess: 'A jelöltcsoport sikeresen frissítve.',
              saveFailed: 'A jelöltcsoport mentése sikertelen.'
            }
          },
          members: {
            title: 'Csoporttagok kezelése',
            groupLabel: 'Jelöltcsoport: {{name}}',
            unboundedHint: 'Nincs létszámkorlát. Az intézmény minden jelöltjét hozzáadhatja.',
            addHeading: 'Jelöltek hozzáadása',
            currentHeading: 'Jelenlegi tagok ({{count}})',
            searchAvailable: 'Keresés név, e-mail vagy Neptun alapján',
            searchMembers: 'Jelenlegi tagok keresése név, e-mail vagy Neptun alapján',
            showingNone: 'Nincs jogosult jelölt',
            showingRange: '{{start}}–{{end}} / {{total}} jogosult',
            selectedCount: '{{count}} kijelölve',
            selectVisible: 'Láthatók kijelölése',
            unselectVisible: 'Láthatók kijelölésének törlése',
            selectAllMatching: 'Minden illeszkedő kijelölése',
            selecting: 'Kijelölés…',
            clearSelection: 'Kijelölés törlése',
            loadingAvailable: 'Jogosult jelöltek betöltése…',
            noneAvailable: 'Nincs elérhető jelölt ehhez a csoporthoz.',
            loadingMembers: 'Tagok betöltése…',
            noneMembers: 'Ebben a csoportban még nincsenek tagok.',
            previous: 'Előző',
            next: 'Következő',
            adding: 'Hozzáadás…',
            addSelected: 'Kijelöltek hozzáadása ({{count}})',
            remove: 'Eltávolítás',
            removing: 'Eltávolítás…',
            removeNamed: '{{name}} eltávolítása',
            selectCandidate: '{{name}} kijelölése',
            confirmRemove: 'Eltávolítja {{name}} tagot a csoportból? A meglévő interjú-kiosztások nem törlődnek.',
            validation: {
              selectAtLeastOne: 'Jelöljön ki legalább egy jelöltet.'
            },
            messages: {
              loadFailed: 'A tagok betöltése sikertelen',
              loadAvailableFailed: 'A jogosult jelöltek betöltése sikertelen',
              selectedMatching: '{{count}} illeszkedő jelölt kijelölve.',
              selectAllFailed: 'Az illeszkedő jelöltek kijelölése sikertelen.',
              addedNotice: '{{count}} tag hozzáadva.',
              addedToSection: '{{count}} tag hozzáadva a(z) {{sectionName}} csoporthoz.',
              addFailed: 'A tagok hozzáadása sikertelen',
              removedNotice: 'A tagot eltávolítottuk a jelöltcsoportból. A meglévő interjú-kiosztások megmaradtak.',
              removedFromSection: 'Tag eltávolítva a(z) {{sectionName}} csoportból.',
              removeFailed: 'A tag eltávolítása sikertelen'
            }
          }
        },
        assign: {
          title: 'Interjú ütemezése',
          groupLabel: 'Jelöltcsoport: {{name}}',
          policyNote: 'Az A rész (MCQ) + B rész (hang) ütemezése az összes jelenlegi tagnak. A pontszámok rejtve maradnak, hacsak alább nem engedélyezi. Az újrapróbálkozás ki van kapcsolva, hacsak nem engedélyezi.',
          interviewTemplate: 'Interjúsablon',
          chooseTemplate: 'Válasszon interjúsablont',
          date: 'Interjú dátuma',
          time: 'Interjú időpontja',
          deadlineDate: 'Leadási határidő dátuma (opcionális)',
          deadlineTime: 'Leadási határidő időpontja (opcionális)',
          deadlineLabel: 'Határidő',
          currentList: 'Jelenlegi csoportütemezések',
          loading: 'Ütemezések betöltése…',
          empty: 'Ehhez a csoporthoz még nincs ütemezés.',
          untitledInterview: 'Interjú',
          kindSlot: 'Időpont',
          kindWindow: 'Ablak',
          kindNone: 'Nem ütemezett',
          remove: 'Eltávolítás',
          removing: 'Eltávolítás…',
          removeHint: 'Csak a csoportszintű ütemezést távolítja el; az egyéni kiosztások megmaradnak.',
          removeDisclaimer: 'A csoportütemezés eltávolítása nem törli automatikusan az egyéni kiosztásokat.',
          scheduling: 'Ütemezés…',
          submit: 'Interjú ütemezése',
          window: {
            title: 'Az interjú értékelési ablakát használja',
            description: '{{start}} → {{end}}. A csoport ezt az ablakot követi — tagankénti időpont nem kell.',
            noStart: 'Nincs kezdeti korlát',
            noEnd: 'Nincs záró korlát'
          },
          resultVisibility: {
            label: 'A jelöltek megtekinthetik a pontszámokat befejezés után',
            hint: 'Alapból ki. A jelenlegi tagokra és a később öröklő tagokra is vonatkozik.'
          },
          retake: {
            title: 'Újrapróbálkozási szabály',
            hint: 'Alapból nincs újrapróbálkozás. A hatókör a jelenlegi és a később öröklő tagokra vonatkozik.',
            allow: 'Újrapróbálkozás engedélyezése',
            scope: 'Újrapróbálkozás hatóköre',
            maxAttempts: 'Max. kísérletek (0 = korlátlan)',
            scopeOption: {
              both: 'Mindkettő — teljes értékelés',
              part_a: 'Csak A rész — MCQ; szóbeli megmarad',
              part_b: 'Csak B rész — szóbeli; MCQ megmarad'
            }
          },
          validation: {
            interviewRequired: 'Válasszon interjút.',
            interviewDateTimeRequired: 'Válasszon interjút, dátumot és időpontot.',
            deadlineDateTimePairRequired: 'Adja meg a határidő dátumát és időpontját is, vagy hagyja mindkettőt üresen.',
            invalidScheduleDateTime: 'Érvénytelen dátum/idő.',
            invalidCompletionDeadline: 'Érvénytelen leadási határidő.',
            deadlineBeforeSchedule: 'A leadási határidő nem lehet az interjú időpontja előtt.'
          },
          messages: {
            loadAssignmentsFailed: 'A csoportkiosztások betöltése sikertelen.',
            assignmentSavedNotice: 'Az interjú ütemezve ehhez a jelöltcsoporthoz.',
            assignmentSavedForSection: 'Az interjú ütemezve a(z) {{sectionName}} csoporthoz.',
            assignInterviewFailed: 'Az interjú ütemezése sikertelen.',
            assignmentRemovedNotice: 'A csoportszintű ütemezés eltávolítva. Az egyéni kiosztások megmaradtak.',
            assignmentRemovedFromSection: 'Ütemezés eltávolítva a(z) {{sectionName}} csoportból.',
            removeAssignmentFailed: 'A kiosztás eltávolítása sikertelen.'
          },
          reviewMode: {
            label: 'Értékelés felülvizsgálati módja',
            hint: 'Válassza ki, ki dönt a csoport végső jóváhagyásáról vagy elutasításáról.',
            manual: 'Kézi felülvizsgálat',
            manualHint: 'Egy adminisztrátor vizsgálja felül és dönt.',
            autoApprove: 'Automatikus jóváhagyás',
            autoApproveHint: 'A befejezett interjúk felülvizsgálat nélkül jóváhagyottak lesznek.',
            autoReview: 'Automatikus felülvizsgálat (MI dönt)',
            autoReviewHint: 'A nyelvi modell jóváhagy vagy elutasít. Hiba esetén ember vizsgálja felül.',
            autoApproveBadge: 'Automatikus jóváhagyás bekapcsolva',
            autoReviewBadge: 'MI automatikus felülvizsgálat bekapcsolva'
          }
        }
      },
      llmAutoReview: {
        title: 'Automatikus értékelés-felülvizsgálat',
        hint: 'Akkor fut, ha a kiosztás Auto-reviewed. Hibák mindig emberi felülvizsgálatra esnek vissza.',
        passThreshold: 'Átmenő küszöb (0–100)',
        minConfidence: 'Minimális magabiztosság (0–1)',
        escalateIntegrity: 'Integritási jelzéseket embernek küldeni',
        stuckMinutes: 'Beragadt felülvizsgálat időkorlátja (perc)',
        save: 'Automatikus felülvizsgálat mentése',
        saved: 'Automatikus felülvizsgálati beállítások mentve.'
      },
      overview: {
        title: 'Irányítópult áttekintés',
        subtitle: 'Kövesse nyomon az MI-alapú interjúplatform teljesítményét',
        loading: 'Irányítópult betöltése...',
        updated: 'Frissítve',
        refresh: 'Frissítés',
        systemOperational: 'Rendszer működőképes',
        health: {
          allClear: 'Minden rendben',
          inProgress: '{{count}} folyamatban',
          inProgress_one: '{{count}} folyamatban',
          inProgress_other: '{{count}} folyamatban',
          issues: '{{count}} hiba',
          issues_one: '{{count}} hiba',
          issues_other: '{{count}} hiba',
          unknown: 'Állapot ismeretlen'
        },
        error: {
          title: 'Az irányítópult betöltése sikertelen',
          tryAgain: 'Újrapróbálás'
        },
        messages: {
          loadDashboardUnavailable: 'Az irányítópult adatai jelenleg nem tölthetők be. Próbálja újra.',
          loadDashboardFailed: 'Az irányítópult adatainak betöltése sikertelen'
        },
        modules: {
          statistics: 'Statisztikák',
          recentActivity: 'Legutóbbi aktivitás',
          upcomingInterviews: 'Közelgő interjúk',
          pendingReviews: 'Függőben lévő felülvizsgálatok',
          dropoffAnalytics: 'Lemorzsolódási analitika',
          completionTrends: 'Teljesítési trendek',
          cohortComparison: 'Kohorsz-összehasonlítás',
          completionDistribution: 'Teljesítési eloszlás',
          systemIssues: 'Rendszerhibák',
          interviewReadiness: 'Interjú-készenlét',
          openPlacements: 'Nyitott felvételik'
        },
        warnings: {
          partialData: 'Néhány irányítópult-adat nem érhető el.',
          affectedWidgets: 'Érintett modulok: {{widgets}}.'
        },
        stats: {
          totalInterviews: 'Összes interjú',
          activeSub: '{{count}} aktív',
          activeCandidates: 'Aktív jelöltek',
          totalSub: '{{count}} összesen',
          pendingReviews: 'Függő felülvizsgálatok',
          totalAssessments: '{{count}} értékelés összesen',
          completionRate: 'Teljesítési arány',
          completedSessions: '{{count}} befejezett munkamenet',
          vsLastMonth: 'az elmúlt hónaphoz képest',
          vsPrevious30Days: 'az előző 30 naphoz képest'
        },
        examDay: {
          liveNow: 'Élőben most',
          openLive: 'Élő felügyelet',
          liveCount: '{{count}} munkamenet folyamatban',
          liveCount_one: '{{count}} munkamenet folyamatban',
          liveCount_other: '{{count}} munkamenet folyamatban',
          noLiveSessions: 'Nincs élő munkamenet',
          readiness: 'Felvételi / készenlét',
          openInterviews: 'Interjúk',
          notReady: '{{count}} B rész nem kész',
          openPlacements: '{{count}} nyitott felvételi'
        },
        analytics: {
          windowLabel: 'Analitikai ablak',
          windowDays: '{{count}} nap',
          dropoffFunnel: 'Kiosztási tölcsér ({{days}} nap)',
          assignmentCompletion: 'Kiosztás teljesítése',
          assignmentDropoff: 'Kiosztás lemorzsolódása',
          sessionDropoff: 'Munkamenet lemorzsolódás: {{rate}}% ({{started}} elindítva)',
          noAnalyticsData: 'Nincs elérhető analitikai adat.',
          completionTrend: 'Teljesítési trend ({{days}} nap)',
          completedRatio: '{{completed}}/{{created}} teljesítve',
          noTrendData: 'Nincs elérhető trendadat.',
          topCohorts: 'Legjobb kohorszok és pontsávok',
          interviews: 'Interjúk',
          completionDistribution: 'Teljesítési eloszlás',
          stagePending: 'Függőben',
          stageScheduled: 'Ütemezett',
          stageInProgress: 'Folyamatban',
          stageCompleted: 'Befejezett',
          stageCancelled: 'Törölt',
          roleComparison: 'Hang vs. szöveg',
          roleVoice: 'Hang / B rész',
          roleText: 'Szöveg / A rész',
          roleOther: 'Egyéb'
        },
        activity: {
          justNow: 'épp most',
          minutesAgo: '{{count}} perce',
          minutesAgo_one: '{{count}} perce',
          minutesAgo_other: '{{count}} perce',
          hoursAgo: '{{count}} órája',
          hoursAgo_one: '{{count}} órája',
          hoursAgo_other: '{{count}} órája',
          daysAgo: '{{count}} napja',
          daysAgo_one: '{{count}} napja',
          daysAgo_other: '{{count}} napja',
          createInterview: '„{{title}}” interjú létrehozva',
          updateInterview: '„{{title}}” interjú frissítve',
          deleteInterview: '„{{title}}” interjú törölve',
          createCandidate: '„{{name}}” jelölt hozzáadva',
          assignInterview: 'Interjú kiosztva neki: {{name}}',
          startSession: '{{user}} elindította a munkamenetet',
          completeSession: '{{user}} befejezte a munkamenetet',
          approveAssessment: 'Értékelés jóváhagyva: {{name}}',
          rejectAssessment: 'Értékelés elutasítva: {{name}}',
          retakeGranted: 'Újrapróba engedélyezve: {{name}}',
          retakeStarted: 'Újrapróba elindult: {{name}}',
          placementCreated: '„{{title}}” felvételi létrehozva',
          placementUpdated: '„{{title}}” felvételi frissítve',
          systemIssue: 'Rendszerhiba naplózva',
          login: '{{user}} bejelentkezett',
          logout: '{{user}} kijelentkezett',
          generic: '{{action}} a következőn: {{resource}}'
        },
        recentActivity: {
          title: 'Legutóbbi aktivitás',
          viewAll: 'Összes megtekintése',
          empty: 'Nincs legutóbbi aktivitás'
        },
        upcomingInterviews: {
          title: 'Közelgő interjúk',
          viewAll: 'Összes megtekintése',
          empty: 'Nincs közelgő időpont vagy nyitott értékelési ablak',
          unknownCandidate: 'Ismeretlen jelölt',
          untitledInterview: 'Névtelen interjú',
          durationMinutes: '{{count}} perc',
          scheduleUnavailable: 'Ütemterv nem elérhető',
          inDays: '{{count}} nap múlva',
          tomorrow: 'Holnap',
          inHours: '{{count}} óra múlva',
          startingSoon: 'Hamarosan kezdődik',
          openNow: 'Ablak nyitva',
          kindSlot: 'Időpont',
          kindWindow: 'Ablak'
        },
        pendingReviews: {
          title: 'Felülvizsgálatra váró értékelések',
          subtitle: 'MI-értékelések, amelyek admin felülvizsgálatra várnak vagy alatt vannak',
          reviewAll: 'Összes felülvizsgálata',
          allCaughtUp: 'Minden naprakész!',
          allReviewed: 'Nincs függőben lévő vagy felülvizsgálat alatti értékelés',
          queueUnavailable: 'A felülvizsgálati sor nem érhető el',
          queueUnavailableHint: 'A függőben lévő vagy felülvizsgálat alatti értékelések itt nem listázhatók. Használja az Összes felülvizsgálata gombot, vagy frissítsen.',
          openReview: '{{name}} felülvizsgálatának megnyitása',
          table: {
            candidate: 'Jelölt',
            interview: 'Interjú',
            aiScore: 'MI-pontszám',
            integrity: 'Integritás',
            queueStatus: 'Sor státusza',
            recommendation: 'Ajánlás',
            submitted: 'Beküldve'
          },
          status: {
            inReview: 'Felülvizsgálat alatt',
            pending: 'Függőben'
          },
          recommendation: {
            strongHire: 'Erős felvétel',
            hire: 'Felvétel',
            neutral: 'Semleges',
            noHire: 'Nincs felvétel',
            strongNoHire: 'Erős elutasítás',
            pending: 'Függőben'
          },
          unknown: 'Ismeretlen',
          notAvailable: '—'
        },
        quickActions: {
          createInterview: {
            title: 'Interjú létrehozása',
            description: 'Új MI-felmérés beállítása'
          },
          addCandidate: {
            title: 'Jelölt hozzáadása',
            description: 'Új interjú ütemezése'
          },
          reviewAssessments: {
            title: 'Értékelések felülvizsgálata',
            description: '{{count}} a sorban'
          },
          liveSupervision: {
            title: 'Élő felügyelet',
            description: '{{count}} folyamatban'
          }
        }
      },
      mcqManager: {
        title: 'MCQ kezelő',
        subtitle: 'MCQ-készletek generálása, felülvizsgálata és aktiválása interjúnként',
        refresh: 'Frissítés',
        generateSet: 'MCQ-készlet generálása',
        noInterviews: {
          title: 'Nincs elérhető interjú',
          description: 'Először hozzon létre egy interjút, majd itt konfigurálja és generálja az MCQ-készleteket.'
        },
        fields: {
          interview: 'Interjú',
          noInterviewsOption: 'Nincs elérhető interjú',
          generationSource: 'Generálás forrása',
          forceGenerate: 'Új generálás kényszerítése'
        },
        generationSource: {
          both: 'Interjú + anyagok',
          interviewOnly: 'Csak interjú-információ',
          materialsOnly: 'Csak anyagok'
        },
        settings: {
          enabled: 'Engedélyezve',
          questions: 'Kérdések',
          options: 'Opciók',
          style: 'Stílus',
          difficulty: 'Nehézség',
          strictMaterials: 'Szigorú anyagok',
          yes: 'Igen',
          no: 'Nem'
        },
        disabledWarning: 'Az MCQ le van tiltva ehhez az interjúhoz. Kapcsolja be az interjú beállításaiban az új készletek generálásához.',
        activeSet: {
          title: 'Aktív készlet',
          setLabel: 'Készlet {{id}}',
          notSet: 'Nincs beállítva',
          selectedHint: 'A jelöltek a kiválasztott készletet kapják.',
          latestHint: 'A jelöltek a legutóbbi kész készletet használják.',
          status: 'Státusz:',
          questions: 'Kérdések:',
          created: 'Létrehozva:',
          interview: 'Interjú:',
          clear: 'Aktív készlet törlése'
        },
        status: {
          ready: 'Kész',
          draft: 'Piszkozat',
          failed: 'Sikertelen',
          active: 'Aktív'
        },
        generationSourceBadge: {
          both: 'Interjú + anyagok',
          interviewOnly: 'Csak interjú',
          materialsOnly: 'Csak anyagok'
        },
        generationNotes: 'Generálási megjegyzések',
        sets: {
          title: 'MCQ-készletek',
          subtitle: 'Tekintse át a generált készleteket, és válassza ki, melyiket vegyék fel a jelöltek',
          lastSync: 'Utolsó szinkron: {{time}}',
          count: '{{count}} készlet',
          count_one: '{{count}} készlet',
          count_other: '{{count}} készlet',
          empty: 'Még nincsenek MCQ-készletek. Generálja az első készletet a kezdéshez.',
          created: 'Létrehozva: {{time}}',
          questions: 'Kérdések:',
          llm: 'LLM:',
          generatedBy: 'Generálta:',
          notes: 'Megjegyzések:',
          editInStudio: 'Szerkesztés stúdióban',
          viewQuestions: 'Kérdések megtekintése',
          useThisSet: 'E készlet használata'
        },
        modal: {
          setTitle: 'MCQ-készlet {{id}}',
          questionsTitle: 'MCQ kérdések',
          questionLabel: '{{number}}. kérdés',
          explanation: 'Magyarázat:',
          topic: 'Témakör:',
          difficulty: 'Nehézség:',
          section: 'Szekció:',
          noQuestions: 'Nem találhatók kérdések ebben a készletben.'
        },
        badges: {
          figure: 'Ábra'
        },
        messages: {
          loadFailed: 'Az MCQ-adatok betöltése sikertelen.',
          generationQueued: 'Az MCQ-generálás sorba került. Háttérben feldolgozás alatt...',
          generationCompleted: 'Az MCQ-generálás befejeződött ({{count}} kérdés).',
          generationCompleted_other: 'Az MCQ-generálás befejeződött ({{count}} kérdés).',
          generationCompletedSuccess: 'Az MCQ-generálás sikeresen befejeződött.',
          generationInProgress: 'Az MCQ-generálás még folyamatban van. Frissítse az oldalt egy pillanat múlva az új készlet megtekintéséhez.',
          generationJobFailed: 'A generálási feladat ezzel a státusszal ért véget: {{status}}',
          generateFailed: 'Az MCQ-készlet generálása sikertelen.',
          updateActiveFailed: 'Az aktív készlet frissítése sikertelen.',
          clearActiveSuccess: 'Az aktív készlet törölve. A jelöltek automatikusan a legutóbbi kész készletet használják.',
          clearActiveFailed: 'Az aktív készlet törlése sikertelen.',
          loadQuestionsFailed: 'A készlet kérdéseinek betöltése sikertelen.',
          reusedSet: 'Meglévő MCQ-készlet újra felhasználva azonos beállítások és anyagok alapján.',
          unknownDate: 'Ismeretlen'
        }
      },
      mcqAuthoringStudio: {
        actions: {
          regenerateFigures: 'Ábrák újragenerálása'
        },
        messages: {
          regenerateFiguresSuccess: '{{count}} ábra újragenerálva.',
          regenerateFiguresFailure: 'Az ábrák újragenerálása sikertelen.'
        }
      },
      llmSettings: {
        errors: {
          figuresRequireImageModel: 'Az „Include figures” bekapcsolása előtt engedélyezze és konfigurálja az MCQ-képgenerálást.'
        }
      },
      materialLibrary: {
        title: 'Anyagtár',
        subtitle: 'Újrafelhasználható anyagok rendszerezése szekció és kategória szerint.',
        metrics: {
          totalMaterials: 'Összes anyag',
          sections: 'Szekciók',
          filtered: 'Szűrt',
          withExtractedText: 'Kinyert szöveggel'
        },
        sections: {
          title: 'Anyagszekciók',
          none: 'Még nincsenek létrehozott szekciók.',
          noDescription: 'Nincs megadott leírás',
          materialCount_one: '{{count}} anyag',
          materialCount_other: '{{count}} anyag',
          createCta: 'Szekció létrehozása'
        },
        labels: {
          selectedSection: 'Kiválasztott szekció',
          section: 'Szekció',
          category: 'Kategória',
          search: 'Keresés',
          yes: 'Igen',
          no: 'Nem',
          linkedInterviews: 'Kapcsolt interjúk',
          selectedCount: '{{count}} kijelölve',
          displayName: 'Megjelenített név',
          description: 'Leírás'
        },
        filters: {
          allSections: 'Összes szekció',
          unassigned: 'Nincs hozzárendelve',
          allCategories: 'Összes kategória',
          searchPlaceholder: 'Keresés név vagy leírás alapján',
          showing: 'Megjelenítve: {{name}}'
        },
        actions: {
          refresh: 'Frissítés',
          refreshing: 'Frissítés...',
          createSection: 'Szekció létrehozása',
          uploadMaterials: 'Anyagok feltöltése',
          edit: 'Szerkesztés',
          delete: 'Törlés',
          download: 'Letöltés',
          deleting: 'Törlés...',
          clearFilters: 'Szűrők törlése',
          cancel: 'Mégse',
          save: 'Mentés',
          saving: 'Mentés...',
          viewText: 'Szöveg megtekintése',
          reextract: 'Újrakinyerés',
          reextracting: 'Újrakinyerés...',
          apply: 'Alkalmaz',
          selectAll: 'Összes kijelölése',
          bulkAssignSection: 'Szekció hozzárendelése',
          bulkSetCategory: 'Kategória beállítása',
          bulkDelete: 'Kijelöltek törlése'
        },
        materials: {
          title: 'Anyagok',
          loading: 'Anyagok betöltése...',
          noneTitle: 'Nem találhatók anyagok',
          noneSubtitle: 'Töltsön fel fájlokat, vagy módosítsa a szűrőket a tár feltöltéséhez.',
          noDescription: 'Nincs leírás',
          unassigned: 'Nincs hozzárendelve',
          general: 'Általános'
        },
        table: {
          name: 'Név',
          section: 'Szekció',
          category: 'Kategória',
          size: 'Méret',
          extracted: 'Kinyerve',
          uploaded: 'Feltöltve',
          linked: 'Kapcsolva',
          actions: 'Műveletek'
        },
        uploadForm: {
          section: 'Szekció',
          noSection: 'Nincs szekció',
          category: 'Kategória',
          categoryPlaceholder: 'pl. Értékelési kiírás',
          description: 'Leírás',
          files: 'Fájlok',
          clickToSelect: 'Kattintson a fájlok kiválasztásához',
          orDragDrop: 'vagy húzza ide',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (max. 20 MB, 5 fájl)',
          filesSelected: '{{count}} fájl kiválasztva',
          remainingSlots: '{{count}} hely maradt',
          removeFile: '{{name}} eltávolítása',
          cancel: 'Mégse',
          upload: 'Feltöltés',
          uploading: 'Feltöltés...',
          selectAtLeastOne: 'Válasszon legalább egy fájlt.',
          tooManyFiles: 'Túl sok fájl. Maximum 5 feltöltésenként.',
          fileTooLarge: 'A(z) {{name}} túl nagy. Maximum 20 MB.',
          unsupportedType: 'Nem támogatott fájltípus: {{name}}'
        },
        sectionForm: {
          createTitle: 'Anyagszekció létrehozása',
          editTitle: 'Anyagszekció szerkesztése',
          createSubtitle: 'Szervezze az újrafelhasználható anyagokat szekciókba.',
          editSubtitle: 'Frissítse a szekciót a jobb rendszerezéshez.',
          name: 'Szekció neve',
          namePlaceholder: 'pl. Adatszerkezetek',
          description: 'Leírás (opcionális)',
          descriptionPlaceholder: 'Adjon kontextust ehhez a szekcióhoz',
          nameRequired: 'A szekció neve kötelező.',
          cancel: 'Mégse',
          create: 'Szekció létrehozása',
          creating: 'Létrehozás...',
          save: 'Mentés',
          saving: 'Mentés...'
        },
        editForm: {
          title: 'Anyag szerkesztése',
          subtitle: 'A tár metaadatainak frissítése. A tárolt fájl nem cserélődik.',
          displayNameRequired: 'A megjelenített név kötelező.'
        },
        extracted: {
          title: 'Kinyert szöveg',
          loading: 'Kinyert szöveg betöltése...',
          empty: 'Ebből a fájlból nem sikerült szöveget kinyerni.',
          truncated: 'A kinyert szöveg rövidítve lett, mert túllépte a méretkorlátot.',
          charCount: '{{count}} karakter',
          loadFailed: 'A kinyert szöveg betöltése sikertelen.'
        },
        usage: {
          none: 'Nincs interjúhoz kapcsolva.',
          count_one: '{{count}} interjúhoz kapcsolva.',
          count_other: '{{count}} interjúhoz kapcsolva.',
          unlinkWarning: 'A tárból törlés az összes kapcsolódó interjúból is eltávolítja.',
          andMore: 'és még {{count}}'
        },
        bulk: {
          categoryPlaceholder: 'Kategória',
          deleteTitle: 'Kijelölt anyagok törlése',
          deletePrompt: '{{count}} anyag törlése? Ez {{linked}} interjúkapcsolatot is megszüntet.',
          noneSelected: 'Válasszon legalább egy anyagot.',
          applied: '{{count}} anyag frissítve.',
          deleted: '{{count}} anyag törölve.',
          partialDelete: '{{deleted}} anyag törölve. {{failed}} sikertelen.'
        },
        modals: {
          createSectionTitle: 'Anyagszekció létrehozása',
          editSectionTitle: 'Anyagszekció szerkesztése',
          uploadMaterialsTitle: 'Anyagok feltöltése a tárba',
          editMaterialTitle: 'Anyag szerkesztése',
          extractedTextTitle: 'Kinyert szöveg',
          deleteMaterialTitle: 'Anyag törlése',
          deleteMaterialPrompt: 'Törli a(z) {{name}} anyagot a tárból?',
          deleteSectionTitle: 'Szekció törlése',
          deleteSectionPrompt: 'Törli a(z) {{name}} szekciót? A kapcsolódó anyagok a tárból megmaradnak, és hozzárendelés nélkül maradnak.'
        },
        messages: {
          loadSectionsFailed: 'A szekciók betöltése sikertelen.',
          loadMaterialsFailed: 'A tár anyagainak betöltése sikertelen.',
          materialDeleted: 'Az anyag sikeresen törölve.',
          deleteMaterialFailed: 'Az anyag törlése sikertelen.',
          sectionDeleted: 'Az anyagszekció sikeresen törölve.',
          deleteSectionFailed: 'A szekció törlése sikertelen.',
          refreshed: 'A tár frissítve.',
          sectionCreated: 'Anyagszekció létrehozva.',
          sectionUpdated: 'Anyagszekció frissítve.',
          materialsUploaded: 'Az anyagok sikeresen feltöltve.',
          sectionCreateFailed: 'A szekció létrehozása sikertelen.',
          sectionUpdateFailed: 'A szekció frissítése sikertelen.',
          materialUpdated: 'Az anyag frissítve.',
          updateMaterialFailed: 'Az anyag frissítése sikertelen.',
          reextractSuccess: 'A szöveg újrakinyerése sikerült.',
          reextractFailed: 'A szöveg újrakinyerése sikertelen.',
          bulkUpdateFailed: 'A kijelölt anyagok frissítése sikertelen.',
          bulkDeleteFailed: 'A kijelölt anyagok törlése sikertelen.'
        }
      },
      dashboard: {
        quickSearch: 'Gyors keresés',
        openCommandPalette: 'Parancspaletta megnyitása',
        interview: 'Interjú',
        candidate: 'Jelölt',
        stats: {
          activeInterviews: '{{count}} aktív interjú',
          scheduledCandidates: '{{count}} ütemezett jelölt',
          pendingReviews: '{{count}} függő felülvizsgálat'
        },
        commandPalette: {
          searchPlaceholder: 'Modulok és műveletek keresése...',
          noResults: 'Nincs egyező modul vagy művelet.',
          quickActions: 'Gyors műveletek',
          modules: 'Modulok',
          open: 'Megnyitás',
          title: 'Parancspaletta',
          pressEsc: 'Esc a bezáráshoz',
          createInterview: {
            label: 'Interjú létrehozása',
            description: 'Interjúsablon-készítő megnyitása'
          },
          createCandidate: {
            label: 'Jelölt létrehozása',
            description: 'Jelölthívó űrlap megnyitása'
          },
          createSection: {
            label: 'Jelöltcsoport létrehozása',
            description: 'Jelöltcsoport-készítő megnyitása'
          },
          createInvigilator: {
            label: 'Felügyelő létrehozása',
            description: 'Felügyelői meghívó űrlap megnyitása'
          }
        },
        account: {
          title: 'Admin fiók',
          description: 'Admin profil adatainak és jelszóbeállításainak kezelése.',
          backLabel: 'Vissza az áttekintéshez'
        },
        sidebar: {
          addFavorite: '{{label}} hozzáadása a kedvencekhez',
          removeFavorite: '{{label}} eltávolítása a kedvencekből',
          profilePictureAlt: '{{name}} profilképe',
          adminFallback: 'Admin'
        }
      }
    },
    candidate: {
      secureCodeEditor: {
        messages: {
          executionUnavailable: 'A kód futtatása jelenleg nem érhető el. Folytathatja a szerkesztést, és beküldheti a kódját.',
          executeFailed: 'A kód futtatása sikertelen. Kérjük, próbálja újra.'
        }
      },
      privacy: {
        title: 'Adatvédelem'
      },
      help: {
        title: 'Súgó',
        subtitle: 'Útmutatók a jelölti portálhoz',
        back: 'Vissza',
        loading: 'Útmutatók betöltése...',
        loadFailed: 'Az útmutatók betöltése sikertelen.',
        emptyTitle: 'Még nincs útmutató',
        emptySubtitle: 'A közzétett jelölti útmutatók itt jelennek meg.',
        guides: 'Útmutatók',
        noSteps: 'Ehhez az útmutatóhoz még nincsenek lépések.',
        selectGuide: 'Válasszon egy útmutatót az olvasáshoz.'
      },
      portal: {
        myAccount: 'Fiokom',
        description: 'Kezelje profiladatait es fiokja biztonsagi beallitasait.',
        backLabel: 'Vissza a portalra'
      },
      placementResult: {
        title: 'Szintfelmérő eredmény',
        rank: 'Helyezés',
        partA: 'A rész',
        partB: 'B rész',
        total: 'Összesen'
      },
      overview: {
        modeLabels: {
          voice: 'Hangalapú',
          mcq: 'MCQ felmérés',
          text: 'Szöveges',
          combined: 'A + B rész',
          partAOnly: 'Csak A rész (MCQ)',
          partBOnly: 'Csak B rész (szóbeli)'
        },
        combinedBriefing: {
          title: 'Ez a felmérés két részből áll',
          description: 'Ez a felmérés két részből áll. Az A rész feleletválasztós teszt. Az A rész befejezése után a B részre, az MI hangalapú interjújára lép tovább.',
          partA: 'A rész: feleletválasztós kérdések (először ezt kell teljesíteni)',
          partB: 'B rész: hangalapú interjú az MI interjúztatóval'
        },
        header: {
          title: 'Jelölti portál',
          subtitle: 'VivaAssess interjú műveletek',
          autoRefreshOn: 'Automatikus frissítés be',
          autoRefreshOff: 'Automatikus frissítés ki',
          refreshTitle: 'Interjú adatainak frissítése',
          refresh: 'Frissítés',
          history: 'Előzmények',
          help: 'Súgó',
          myAccount: 'Fiókom',
          signOut: 'Kijelentkezés'
        },
        hero: {
          welcome: 'Üdvözöljük újra, {{name}}',
          defaultCandidate: 'Jelölt',
          description: 'Kövesse a felkészültségét, indítsa el az interjút, és figyelje a kiosztás frissítéseit egy helyen.',
          available: 'Az interjú elérhető',
          waitingForWindow: 'Várakozás az induló időszakra',
          windowClosed: 'Az értékelési ablak lezárult',
          autoRefreshIn: 'Automatikus frissítés {{seconds}} másodperc múlva'
        },
        assigned: {
          title: 'Kiosztott interjúk',
          countHint_one: 'Önnek {{count}} aktív interjúja van. Válasszon egyet a folytatáshoz.',
          countHint_other: 'Önnek {{count}} aktív interjúja van. Válasszon egyet a folytatáshoz.',
          selected: 'Kiválasztva: {{value}}',
          statusLabel: 'Állapot: {{value}}',
          scheduled: 'Ütemezve',
          notSet: 'Nincs beállítva',
          attempts: 'Próbálkozások',
          deadline: 'Határidő',
          none: 'Nincs',
          availability: 'Elérhetőség',
          inProgress: 'Folyamatban',
          availableNow: 'Most elérhető',
          upcoming: 'Közelgő',
          selectedButton: 'Kiválasztva',
          chooseInterview: 'Interjú kiválasztása',
          slot: 'Időpont',
          window: 'Időablak',
          windowClosed: 'Ablak lezárult'
        },
        status: {
          pending: 'Függőben',
          scheduled: 'Ütemezve',
          inProgress: 'Folyamatban',
          completed: 'Befejezve',
          retakeAvailable: 'Újrapróbálkozás elérhető'
        },
        countdown: {
          windowOpen: 'Az interjú időszaka megnyílt',
          windowOpenHelper: 'Az interjút bármikor elindíthatja, amikor készen áll.',
          daysUnit_one: '{{count}} nap',
          daysUnit_other: '{{count}} nap',
          hoursUnit_one: '{{count}} óra',
          hoursUnit_other: '{{count}} óra',
          minutesUnit_one: '{{count}} perc',
          minutesUnit_other: '{{count}} perc',
          veryClose: 'Nagyon hamar kezdődik. Kérjük, most készítse elő a rendszerét.',
          soon: 'Az interjú hamarosan kezdődik. Futtasson egy gyors ellenőrzést.',
          plentyOfTime: 'Még van ideje felkészülni.',
          windowClosed: 'Az értékelési ablak lezárult',
          windowClosedHelper: 'Ez az értékelés már nem elérhető. Ha mégis teljesítenie kell, lépjen kapcsolatba az adminisztrátorral.'
        },
        active: {
          retakeAvailable: 'Az újrapróbálkozás elérhető',
          scheduled: 'Az interjúja ütemezve van',
          assigned: 'Interjú kiosztva',
          reviewHint: 'Ellenőrizze a részleteket, és teljesítse az indítási ellenőrzőlistát a kezdés előtt.',
          statusLabel: 'Állapot: {{value}}',
          dateTime: 'Dátum és idő',
          tba: 'Később kerül meghatározásra',
          duration: 'Időtartam',
          durationValue: '{{minutes}} perc',
          countdown: 'Visszaszámlálás',
          awaitingSchedule: 'Ütemezésre várakozás',
          awaitingScheduleHelper: 'Az adminisztrátor hamarosan beállítja az időpontot.',
          windowRange: '{{from}} – {{until}}',
          windowFrom: 'Ettől: {{from}}',
          windowUntil: 'Eddig: {{until}}',
          readiness: 'Felkészültség'
        },
        readiness: {
          excellent: 'Kiváló',
          good: 'Jó',
          needsAttention: 'Figyelmet igényel',
          notActive: 'Nem aktív'
        },
        details: {
          title: 'Interjú címe',
          difficulty: 'Nehézség',
          unspecified: 'nincs megadva',
          completionDeadline: 'Teljesítési határidő',
          focusAreas: 'Fókuszterületek',
          description: 'Leírás'
        },
        actions: {
          continueInterview: 'Interjú folytatása',
          beginRetake: 'Újrapróbálkozás indítása (rendszerellenőrzés)',
          beginRetakePartA: 'A rész újrapróbálása (rendszerellenőrzés)',
          beginRetakePartB: 'B rész újrapróbálása (rendszerellenőrzés)',
          beginRetakeBoth: 'Teljes újrapróbálkozás indítása (rendszerellenőrzés)',
          beginSystemCheck: 'Rendszerellenőrzés indítása',
          continueHint: 'Folytassa a jelenlegi interjú munkamenetét onnan, ahol abbahagyta.',
          retakeHint: 'Most újrapróbálhatja ezt az interjút. A rendszerellenőrzés indítás előtt ellenőrzi a beállításait.',
          systemCheckHint: 'A rendszerellenőrzés indítás előtt ellenőrzi a kamerát, a mikrofont, a böngészőt és a hálózatot.'
        },
        noAssignment: {
          title: 'Nincs aktív interjú-kiosztás',
          deadlinePassed: 'A legutóbbi kiosztás teljesítési határideje lejárt. Kérjük, lépjen kapcsolatba az adminisztrátorral.',
          selectAbove: 'Válasszon egyet a fent felsorolt kiosztott interjúi közül a folytatáshoz.',
          notScheduledYet: 'Az interjúja még nincs ütemezve. A teljes részleteket a kiosztás aktiválása után látja majd.'
        },
        placementResult: {
          title: 'Az Ön szintfelmérő eredménye',
          subtitle: 'A közzétett pontszámok és a helyezés. Más jelöltek nem jelennek meg.',
          viewHistory: 'Megtekintés az Előzményekben',
          untitled: 'Felmérés'
        },
        preparation: {
          environment: {
            label: 'Csendes és jól megvilágított környezet előkészítve',
            helper: 'Minimalizálja a zajt, és tartsa jól láthatóan az arcát.'
          },
          device: {
            label: 'Egyetlen monitoros és stabil eszközbeállítás',
            helper: 'Csatlakoztassa le a külső kijelzőket, és zárja be a háttérben futó alkalmazásokat.'
          },
          network: {
            label: 'Megbízható internetkapcsolat megerősítve',
            helper: 'A zavartalan interjú érdekében részesítse előnyben a vezetékes vagy erős Wi-Fi kapcsolatot.'
          },
          materials: {
            label: 'A szerepkör kontextusa és a fókuszterületek átnézve',
            helper: 'Nézze át a kiosztott interjú részleteit és fókuszterületeit.'
          }
        },
        checklist: {
          title: 'Felkészülési ellenőrzőlista',
          completeCount: '{{completed}}/{{total}} teljesítve',
          markAllComplete: 'Összes megjelölése kész',
          resetChecklist: 'Ellenőrzőlista visszaállítása'
        },
        console: {
          title: 'Felkészültségi konzol',
          subtitle: 'Ezen a panelen ellenőrizheti a felkészültségét, és megoszthat egy gyors interjú-összefoglalót.',
          readinessScore: 'Felkészültségi pontszám',
          completionPercent: 'Felkészülés teljesítve: {{percent}}%',
          briefCopied: 'Összefoglaló másolva',
          copyBrief: 'Interjú-összefoglaló másolása',
          openHistory: 'Előzmények megnyitása',
          refreshAssignment: 'Kiosztás adatainak frissítése',
          autoRefreshStatus: 'Automatikus frissítés állapota',
          nextRefreshIn: 'Következő frissítés {{seconds}} másodperc múlva',
          enabled: 'Engedélyezve',
          disabled: 'Letiltva'
        },
        expect: {
          title: 'Mire számítson',
          step1Title: '1. Rendszerellenőrzés',
          step1Description: 'Ellenőrizze a kamerát, a mikrofont, a böngésző készenlétét és a hálózatot az interjú indítása előtt.',
          step2TitleVoice: '2. Hangalapú interjú',
          step2TitleMcq: '2. MCQ felmérés',
          step2TitleDefault: '2. MI-beszélgetés',
          step2DescriptionVoice: 'Beszéljen természetesen, amíg a válaszok valós időben átírásra és elemzésre kerülnek.',
          step2DescriptionMcq: 'Töltse ki az időzített feleletválasztós kérdéseket biztonságos felügyeleti vezérlőkkel.',
          step2DescriptionDefault: 'Válaszoljon a szerepkörére szabott, strukturált MI-kérdésekre.',
          step3TitleMcq: '3. Eredmények és visszajelzés',
          step3TitleDefault: '3. Írásbeli feladat és zárás',
          step3DescriptionMcq: 'Kövesse a felülvizsgálat állapotát és a végleges eredményeket a felmérés kiértékelése után.',
          step3DescriptionDefault: 'Zárja le egy rövid írásbeli feladattal, mielőtt a teljes értékelés véglegesítésre kerül.',
          remindersTitle: 'Fontos emlékeztetők',
          reminder1: 'Tartsa láthatóan az arcát az interjú teljes időtartama alatt.',
          reminder2: 'Kerülje a lapok váltását vagy nem kapcsolódó alkalmazások megnyitását.',
          reminder3: 'Tartson fenn stabil hálózati kapcsolatot.',
          reminder4: 'Használjon egyetlen kijelzős beállítást a felügyelt szakaszokban.',
          reminder5: 'Nézze át az utasításokat az interjú elindítása előtt.',
          reminder6: 'Ha probléma jelentkezik, térjen vissza a rendszerellenőrzéshez, mielőtt újra próbálkozna.'
        },
        footer: {
          tagline: 'ahol a beszélgetések hiteles értékelésekké válnak.'
        },
        brief: {
          notAssigned: 'Még nincs kiosztva',
          notScheduled: 'Nincs ütemezve',
          noDeadline: 'Nincs megadott határidő',
          notSpecified: 'Nincs megadva',
          interview: 'Interjú: {{value}}',
          mode: 'Mód: {{value}}',
          scheduled: 'Ütemezve: {{value}}',
          deadline: 'Határidő: {{value}}',
          duration: 'Becsült időtartam: {{minutes}} perc',
          focusAreas: 'Fókuszterületek: {{value}}',
          preparationProgress: 'Felkészülés állapota: {{percent}}%'
        }
      },
      voiceInterview: {
        messages: {
          reconnectingMicrophone: 'Mikrofon újracsatlakoztatása...',
          microphoneUnstable: 'A mikrofonkapcsolat instabil. Újrapróbálkozás...',
          noSpeechDetected: 'Egy ideje nem észleltünk beszédet. Kattintson a Hallgatás folytatása gombra, és beszéljen a folytatáshoz.',
          localVoiceInUse: 'Az App Library ingyenes helyi hangját használjuk ehhez a kérdéshez.',
          primaryVoiceFallbackUsed: 'Az elsődleges neurális hang nem volt elérhető. Ehhez a kérdéshez egy tartalék hangot használtunk.',
          localFallbackVoiceActive: 'A neurális hangszolgáltatás jelenleg nem elérhető. A helyi tartalék hang aktív.',
          audioUnavailableContinueOnScreen: 'A hangkimenet nem elérhető. Folytassa a képernyőn megjelenő kérdésekkel, amíg az interjú aktív marad.',
          generateNextQuestionFailed: 'Nem sikerült generálni a következő kérdést. Kérjük, próbálja újra.',
          initializeFailed: 'Nem sikerült elindítani az interjút. Kérjük, ellenőrizze a kamera- és mikrofonengedélyeket.',
          partAOnlyComplete: 'Az A rész kész. Ez az értékelés nem tartalmaz szóbeli vizsgát. Köszönjük — elhagyhatja ezt az oldalt.',
          partARetakeComplete: 'Az A rész újrapróbálása kész. A szóbeli pontszám változatlan. Köszönjük — elhagyhatja ezt az oldalt.',
          browserNotSupported: 'Az Ön böngészője nem támogatja a hangalapú funkciókat. Kérjük, használjon Chrome vagy Edge böngészőt.',
          neuralVoiceConfigUnavailable: 'A neurális hangbeállítást nem sikerült betölteni. Ha elérhető, a helyi tartalék beszéd kerül használatra.',
          serviceTemporarilyUnavailable: 'Az MI interjúszolgáltatás jelenleg nem érhető el. Kérjük, próbálja újra később.',
          responseRecoveryBlocked: 'Egy korábbi interjúválaszt nem sikerült biztonságosan visszaállítani. Tartsa meg ezt a munkamenetet, és kérjen helyreállítási segítséget a rendszergazdától.',
          repeatFailed: 'Jelenleg nem sikerült megismételni az utolsó kérdést.',
          sessionRestored: 'Munkamenet visszaállítva. A hangalapú interjú folytatása onnan, ahol abbahagyta.',
          sessionRestoredPartA: 'Munkamenet visszaállítva. Visszatérés az A részhez, ahol abbahagyta.',
          resumeContinuePrompt: 'A munkamenet szünetelt. Folytassa, amikor készen áll.'
        },
        partTransition: {
          title: 'Az A rész kész',
          description: 'Az A rész kész. Most a B részre, a hangalapú interjúra lép tovább.',
          bulletPartADone: 'Az A rész (feleletválasztós) beadva.',
          bulletPartBNext: 'A B rész élő hangalapú szóbeli vizsga a vizsgáztatóval.',
          bulletMicReady: 'Győződjön meg róla, hogy a mikrofon és a kamera bekapcsolva marad.',
          continue: 'Tovább a B részhez (hangalapú interjú)'
        },
        conversation: {
          yourTurn: 'Öné a szó. Nyugodtan gondolkodjon — ha végzett, egyszerűen tartson szünetet.',
          yourTurnInterruptible: 'Öné a szó. Nyugodtan gondolkodjon — akkor is elkezdhet beszélni, ha a vizsgáztató még beszél.',
          examinerThinking: 'A vizsgáztató mérlegeli a válaszát…',
          repeatingQuestion: 'Megismétlem a kérdést.',
          rephrasingQuestion: 'Átfogalmazom a kérdést.',
          movingOn: 'Továbblépünk a következő kérdésre.',
          bargeInEnabled: 'Bármikor közbeszólhat a vizsgáztatónak.'
        },
        chrome: {
          aiState: {
            idleLabel: 'Készen áll',
            listeningLabel: 'Hallgatás',
            speakingLabel: 'Beszél',
            thinkingLabel: 'Elemzés',
            endingLabel: 'Lezárás',
            completedLabel: 'Interjú befejezve',
            idleDescription: 'Az interjúztató készen áll a következő párbeszédre.',
            listeningDescription: 'A hangrögzítés aktív. Mondja el válaszát érthetően és természetesen.',
            speakingDescription: 'Az interjúztató átadja a következő kérdést.',
            thinkingDescription: 'Az utolsó válasza feldolgozás alatt van a következő kérdés kialakításához.',
            endingDescription: 'A végső interjú-összegzés átadása folyamatban van a befejezés előtt.',
            completedDescription: 'A munkamenet lezárva. Válaszai mentve az értékeléshez.'
          },
          browserNotSupportedTitle: 'A böngésző nem támogatott',
          browserNotSupportedDescription: 'A hangalapú interjúkhoz Web Speech API támogatás szükséges. Kérjük, használjon Google Chrome-ot vagy Microsoft Edge-et.',
          goBack: 'Vissza',
          aiServiceUnavailableTitle: 'MI szolgáltatás nem elérhető',
          backToDashboard: 'Vissza az irányítópultra',
          voiceStudio: 'Hangstúdió',
          questionAbbrev: '{{number}}. kérdés',
          integrityPercent: 'Integritás {{percent}}%',
          voiceSource: 'Hang {{source}}',
          endInterview: 'Interjú befejezése',
          aiVoiceConductor: 'Vizsgáztató',
          examiner: 'Vizsgáztató',
          studioTitle: 'Élő hangalapú interjústúdió',
          interviewStep: 'Interjú lépés',
          questionLabel: '{{number}}. kérdés',
          elapsed: 'Eltelt idő',
          captureMode: 'Rögzítési mód',
          autoSubmit: 'Automatikus beküldés',
          reviewBeforeSubmit: 'Ellenőrzés beküldés előtt',
          candidateCamera: 'Jelölt kamerája',
          liveFeedActive: 'Élő közvetítés aktív',
          videoStreamPaused: 'Videófolyam szüneteltetve',
          initializing: 'Inicializálás',
          muted: 'Elnémítva',
          micLive: 'Mikrofon aktív',
          inputSensitivity: 'Bemeneti érzékenység',
          integritySignal: 'Integritási jel',
          recognitionConfidence: 'Felismerési megbízhatóság',
          notAvailable: 'Nincs adat',
          liveTranscription: 'Élő átirat',
          responseAwaitingReview: 'Válasz felülvizsgálatra vár',
          wordCount_other: '{{count}} szó',
          submitResponse: 'Válasz beküldése',
          reviewEdited: 'Szerkesztve',
          reviewCountdown: '{{seconds}} mp a beküldésig',
          reviewHint: 'Javítsa a felismerési hibákat, majd küldje be. A Ctrl+Enter beküld. Legfeljebb {{count}} karakter. A szóbeli időzítő nem áll meg.',
          reviewTooShort: 'A beküldéshez legalább {{count}} szó kell, vagy vegye fel újra.',
          reviewStale: 'A piszkozat egy korábbi kérdéshez tartozott, ezért töröltük. Válaszoljon a képernyőn lévő kérdésre.',
          reviewRestored: 'A be nem küldött javítást a folytatáskor töröltük. Válaszoljon a képernyőn lévő kérdésre.',
          showRecognised: 'Mikrofon által felismert szöveg',
          hideRecognised: 'Felismert szöveg elrejtése',
          restoreOriginal: 'Eredeti visszaállítása',
          rerecord: 'Újrafelvétel',
          clearAndContinue: 'Törlés és folytatás',
          latestAiPrompt: 'Kérdés',
          currentQuestion: 'Kérdés',
          sessionControls: 'Munkamenet-vezérlők',
          sessionControlsHint: 'Kezelje a rögzítést, a hallgatási folyamatot és a kérdés megismétlését.',
          autoSubmitAfterSilence: 'Automatikus beküldés csend után',
          micOn: 'Mikrofon be',
          videoOn: 'Videó be',
          videoOff: 'Videó ki',
          pause: 'Szüneteltetés',
          resume: 'Folytatás',
          submit: 'Beküldés',
          repeat: 'Megismétlés',
          clarify: 'Pontosítás',
          end: 'Befejezés',
          voiceDiagnostics: 'Hangdiagnosztika',
          audioLevel: 'Hangszint',
          yourResponses: 'Az Ön válaszai',
          violations: 'Szabálysértések',
          quickNotes: 'Gyors jegyzetek',
          clear: 'Törlés',
          quickNotesPlaceholder: 'Privát jegyzetek a válaszai felépítéséhez...',
          autosavedLocally: 'Automatikusan elmentve, helyileg, ehhez a munkamenethez.',
          conversationTranscript: 'Beszélgetés átirata',
          roleAi: 'Vizsgáztató',
          roleYou: 'Te',
          preparingInterview: 'Az interjú előkészítése...',
          invigilatorSupport: 'Felügyelői támogatás',
          endInterviewConfirmTitle: 'Befejezi az interjút?',
          endInterviewConfirmDescription: 'Biztosan be szeretné fejezni az interjút? Válaszai mentésre és beküldésre kerülnek értékelés céljából.',
          continueInterview: 'Interjú folytatása',
          errorLabel: 'Hiba'
        }
      },
      interviewHistory: {
        notAvailable: 'Nincs adat',
        hidden: 'Rejtett',
        loading: 'Az interjú előzmények betöltése...',
        title: 'Interjú előzmények',
        subtitle: 'Nézze át a befejezett interjúkat, a pontszám-eredményeket és az értékelői visszajelzéseket.',
        durationFormat: '{{mins}} p {{secs}} mp',
        stats: {
          total: 'Összesen',
          approved: 'Jóváhagyva',
          inReview: 'Felülvizsgálat alatt',
          avgScore: 'Átlagpontszám',
          hidden: 'Rejtett'
        },
        status: {
          approved: 'Jóváhagyva',
          rejected: 'Elutasítva',
          inReview: 'Felülvizsgálat alatt',
          pendingReview: 'Felülvizsgálatra vár'
        },
        recommendation: {
          stronglyRecommend: 'Erősen ajánlott',
          recommend: 'Ajánlott',
          neutral: 'Semleges',
          notRecommend: 'Nem ajánlott',
          stronglyNotRecommend: 'Erősen nem ajánlott',
          recommendProvisional: 'Ajánlott (ideiglenes)',
          neutralProvisional: 'Semleges (ideiglenes)',
          notRecommendProvisional: 'Nem ajánlott (ideiglenes)',
          pending: 'Folyamatban'
        },
        filters: {
          searchPlaceholder: 'Keresés cím, leírás vagy értékelő szerint',
          statusAll: 'Minden státusz',
          statusPending: 'Függőben',
          statusInReview: 'Felülvizsgálat alatt',
          statusReviewed: 'Felülvizsgálva',
          statusApproved: 'Jóváhagyva',
          statusRejected: 'Elutasítva',
          visibilityAll: 'Minden láthatóság',
          visibilityVisible: 'Látható eredmények',
          visibilityHidden: 'Rejtett eredmények',
          sortRecent: 'Rendezés: legutóbbi',
          sortOldest: 'Rendezés: legrégebbi',
          sortScoreDesc: 'Rendezés: pontszám csökkenő',
          sortScoreAsc: 'Rendezés: pontszám növekvő',
          sortStatus: 'Rendezés: státusz',
          showingCount: '{{shown}}/{{total}} interjú megjelenítve',
          clearFilters: 'Szűrők törlése'
        },
        empty: {
          noHistoryTitle: 'Még nincs interjú előzmény',
          noHistoryDescription: 'Fejezzen be egy interjút, hogy megjelenjen az első értékelése ezen az irányítópulton.',
          noMatchesTitle: 'Nincs egyezés a jelenlegi szűrőkkel',
          noMatchesDescription: 'Próbálja meg tágabbra állítani a keresést, a státuszt vagy a láthatósági szűrőket.'
        },
        card: {
          score: 'Pontszám',
          integrity: 'Integritás',
          recommendation: 'Ajánlás',
          mcqScoreLine: 'MCQ pontszám: {{score}}% ({{correct}}/{{total}})',
          resultsHiddenMessage: 'A részletes eredményeket jelenleg elrejtette az adminisztrátor.'
        },
        detail: {
          date: 'Dátum',
          duration: 'Időtartam',
          finalScore: 'Végső pontszám',
          integrity: 'Integritás',
          resultsHiddenTitle: 'A részletes eredmények rejtve vannak',
          resultsHiddenMessage: 'Az adminisztrátor elrejtette ennek az interjúnak a kérdésszintű és pontozási részleteit.',
          mcqSummaryTitle: 'MCQ összegzés',
          mcqScoreLine: 'Pontszám: {{score}}%',
          mcqCorrectLine: 'Helyes: {{correct}}/{{total}}',
          reviewerComments: 'Értékelői megjegyzések',
          reviewedBy: 'Értékelte: {{name}}',
          reviewedByOn: 'Értékelte: {{name}}, {{date}}',
          aiEvaluation: 'MI értékelés',
          strengths: 'Erősségek',
          areasForImprovement: 'Fejlesztendő területek',
          detailedFeedback: 'Részletes visszajelzés',
          criterionScores: 'Részletes kritériumpontszámok'
        },
        actions: {
          backToHistory: 'Vissza az előzményekhez',
          backToDashboard: 'Vissza az irányítópultra',
          summaryCopied: 'Összegzés vágólapra másolva',
          copySummary: 'Összegzés másolása',
          exportCopied: 'Exportálás másolva',
          copyFilteredExport: 'Szűrt exportálás másolása',
          generatingReport: 'Jelentés készítése...',
          reportHidden: 'A jelentést az adminisztrátor elrejtette',
          downloadReport: 'Teszteredmény letöltése',
          retry: 'Újrapróbálás',
          goToDashboard: 'Ugrás az irányítópultra',
          resetFilters: 'Szűrők visszaállítása',
          resultsHiddenByAdmin: 'Az eredményeket az adminisztrátor elrejtette',
          viewFullDetails: 'Teljes részletek megtekintése'
        },
        export: {
          csvHeader: 'Interjú címe,Állapot,Láthatóság,Pontszám,Integritás,Létrehozva,Értékelő',
          visibilityHidden: 'rejtett',
          visibilityVisible: 'látható',
          summaryInterview: 'Interjú: {{title}}',
          summaryStatus: 'Állapot: {{status}}',
          summaryScore: 'Pontszám: {{score}}',
          summaryIntegrity: 'Integritás: {{score}}',
          summaryCompleted: 'Befejezve: {{date}}',
          summaryRecommendation: 'Ajánlás: {{value}}'
        },
        messages: {
          loadFailed: 'Nem sikerült betölteni az interjú előzményeket.',
          downloadReportFailed: 'Nem sikerült letölteni a teszteredményt.'
        }
      },
      mcqPartA: {
        badge: 'A rész · Feleletválasztós',
        progress: '{{current}}. kérdés / {{total}} — {{answered}} megválaszolva',
        loading: 'Az A rész (feleletválasztós kérdések) előkészítése...',
        flag: 'Megjelölés felülvizsgálatra',
        previous: 'Előző',
        next: 'Következő',
        submit: 'A rész beküldése és folytatás a B résszel',
        retry: 'Újrapróbálás',
        messages: {
          startFailed: 'Nem sikerült elindítani a feleletválasztós részt. Kérjük, próbálja újra.',
          submitFailed: 'Jelenleg nem sikerült beküldeni a válaszokat. Kérjük, próbálja újra.',
          sessionRestored: 'Folytatás onnan, ahol abbahagyta. A válaszai megmaradtak.{{savedHint}} Az A rész időzítője szünetelt, amíg távol volt. Szükség esetén kapcsolja be újra a kamerát és a mikrofont.',
          sessionRestoredSavedHint: ' Utoljára mentve: {{time}}.',
          timerPaused: 'Időzítő szünetel — csatlakozzon újra, vagy térjen vissza erre a lapra a folytatáshoz.'
        }
      },
      systemCheck: {
        header: {
          title: 'Rendszerellenőrzés',
          subtitle: 'Ellenőrizze, hogy rendszere megfelel-e minden követelménynek'
        },
        camera: {
          name: 'Kamera hozzáférés',
          description: 'Videórögzítési képesség',
          denied: 'A kamera hozzáférés megtagadva',
          previewTitle: 'Kamera előnézet',
          previewPlaceholder: 'A kamera előnézete itt fog megjelenni'
        },
        microphone: {
          name: 'Mikrofon hozzáférés',
          description: 'Hangrögzítési képesség',
          denied: 'A mikrofon hozzáférés megtagadva',
          levelTitle: 'Mikrofon szintje',
          speakPrompt: 'Beszéljen a mikrofon teszteléséhez'
        },
        speaker: {
          name: 'Hangszóró teszt',
          description: 'Hanglejátszási képesség',
          title: 'Hangszóró teszt',
          clickToPlay: 'Kattintson a teszthang lejátszásához',
          available: 'A hangkimenet elérhető',
          unverified: 'Nem sikerült ellenőrizni a hangszóró kimenetét',
          completed: 'Hangszóró teszt befejezve',
          playing: 'Lejátszás...',
          playButton: 'Teszthang lejátszása'
        },
        internet: {
          name: 'Internetsebesség',
          description: 'Minimum 1 Mbps szükséges',
          failed: 'Az internetkapcsolat instabil vagy nem érhető el',
          unableToConnect: 'Nem sikerült csatlakozni az internethez',
          connectionQuality: 'A kapcsolat {{quality}} ({{latency}} ms késleltetés)',
          quality: {
            excellent: 'kiváló',
            good: 'jó',
            moderate: 'közepes'
          }
        },
        browser: {
          name: 'Böngésző kompatibilitás',
          description: 'Modern böngésző funkciók'
        },
        screen: {
          name: 'Egyetlen képernyős elrendezés',
          description: 'Nincs külső monitor',
          multipleMonitors: 'Több monitor észlelhető. Kérjük, csatlakoztassa le a külső kijelzőket.',
          singleDisplay: 'Egyetlen kijelző megerősítve',
          unverified: 'Nem sikerült ellenőrizni a képernyő elrendezését'
        },
        fullscreen: {
          name: 'Teljes képernyős mód',
          description: 'Az interjúhoz szükséges',
          available: 'A teljes képernyős mód elérhető',
          unsupported: 'A teljes képernyős mód nem támogatott ebben a böngészőben'
        },
        requirements: {
          title: 'Rendszerkövetelmények'
        },
        actions: {
          retry: 'Újrapróbálás',
          start: 'Rendszerellenőrzés indítása',
          proceed: 'Tovább az interjúra',
          runAgain: 'Ellenőrzések újrafuttatása'
        },
        status: {
          checking: '{{name}} ellenőrzése...',
          someFailedTitle: 'Néhány ellenőrzés nem sikerült',
          someFailedDescription: 'Kérjük, a folytatás előtt oldja meg a fenti problémákat.'
        },
        tips: {
          title: 'Tippek',
          item1: 'Engedélyezze a kamera- és mikrofonhozzáférést, amikor a böngésző kéri',
          item2: 'A legjobb kompatibilitás érdekében használjon Chrome, Firefox vagy Edge böngészőt',
          item3: 'Csatlakoztassa le a külső monitorokat a felmérés módhoz',
          item4: 'Biztosítson stabil internetkapcsolatot'
        }
      },
      roomCamera: {
        title: 'Szobakamera (telefon, oldalnézet)',
        hint: 'Tegye a telefont oldalra úgy, hogy az asztal, a kezei és az asztal melletti padló látszódjon. A linket a telefonon nyissa meg. Ezen a számítógépen bejelentkezve marad.',
        status: 'Állapot: {{status}}',
        skip: 'Folytatás szobakamera nélkül',
        pill: 'Szobakamera',
        urlLabel: 'Vagy nyissa meg ezt a linket a telefonon',
        systemCheck: 'Ehhez a felméréshez második kamera kell: a telefonja, oldalnézetben, hogy az asztal, a kezei és a padló látszódjon. A biztonságos zár után párosítja, a telefonon bejelentkezés nélkül.',
        phoneTitle: 'Szobakamera',
        phonePurpose: 'Ez az oldal az asztalt, a kezeket és a padlót mutatja a felügyelőnek. Nincsenek vizsgakérdések. Hagyja nyitva az oldalt.',
        placeSide: 'Tegye a telefont az asztal oldalára, körülbelül 1–2 méterre, fekvő helyzetben.',
        placeShows: 'A képen látszódjon a törzs, a kezek, az asztal és az asztal alatti padló. Az arcnak nem kell kitöltenie a képet.',
        checklist: 'Elhelyezés ellenőrzése',
        checkDesk: 'Az asztal és a kezeim látszanak',
        checkFloor: 'Az asztal alatti padló látszik',
        checkSide: 'Ez oldalnézet, nem az arcom közeli képe',
        start: 'Szobakamera indítása',
        streaming: 'Közvetítés',
        paused: 'Szünetel, amíg az oldal a háttérben van',
        missingToken: 'A linken nincs párosító kód. Olvassa be újra a QR-kódot a vizsgagépen.',
        permission: 'A kameraengedély szükséges',
        frameRejected: 'A kép elutasítva ({{status}})',
        switchToFront: 'Váltás az elülső kamerára',
        switchToBack: 'Váltás a hátulsó kamerára',
        otherCameraUnavailable: 'A másik kamera nem érhető el.',
        connectedCheck: 'A szobakamera csatlakozott. Ellenőrizze a képet, majd folytassa.',
        confirmView: 'Ez a nézet megfelelő — folytatás',
        hidePreview: 'Szobakamera elrejtése',
        showPreview: 'Szobakamera mutatása',
        pictureWaiting: 'Élő szobakép várakozik.',
        picturePending: 'A telefon csatlakozott, de a kép még nem érkezett meg.',
        previewLabel: 'Szobakamera'
      },
      systemCheckFlow: {
        modeLabels: { voice: 'hang', mcq: 'MCQ', text: 'szöveges', combined: 'A + B rész' },
        modeConfig: {
          voice: { title: 'Hangalapú interjú mód', description: 'A munkamenet hangos beszélgetésként, élő beszédfelismeréssel fog futni.' },
          mcq: { title: 'Feleletválasztós interjú mód', description: 'Ez a munkamenet biztonságos feleletválasztós felmérésként fut.' },
          text: { title: 'Szöveges interjú mód', description: 'Ez a munkamenet strukturált, szöveges alapú beszélgetésként fut.' },
          combined: {
            title: 'Kombinált felmérési mód',
            description: 'Ez a felmérés két részből áll. Az A rész feleletválasztós teszt. Az A rész befejezése után a B részre, az MI hangalapú interjújára lép tovább.'
          }
        },
        combinedBriefing: {
          title: 'Ez a felmérés két részből áll',
          partA: 'A rész: feleletválasztós kérdések (először ezt kell teljesíteni)',
          partB: 'B rész: hangalapú interjú az MI interjúztatóval'
        },
        header: {
          backToDashboard: 'Vissza az irányítópultra',
          title: 'Rendszerellenőrzés',
          stepLabel: '1. lépés a 3-ból, az interjú indítása előtt',
          modeSuffix: '{{mode}} mód'
        },
        intro: {
          title: 'Készen áll az ellenőrzés elindítására?',
          description: 'Az interjú megkezdése előtt ellenőrizzük az engedélyeket, a böngésző kompatibilitását és a felügyeleti követelményeket.',
          preparingFor: 'Előkészítés a következőhöz',
          beforeYouBegin: 'Mielőtt elkezdi',
          tip1: 'Zárja be a felesleges alkalmazásokat és böngészőlapokat.',
          tip2: 'Tartózkodjon csendes, jól megvilágított helyen.',
          tip3: 'Használjon egyetlen kijelzőt és stabil internetkapcsolatot.',
          tip4: 'Fogadja el a kamera- és mikrofonhozzáférési kéréseket.',
          startButton: 'Rendszerellenőrzés indítása',
          copyBaseline: 'Alapdiagnosztika másolása'
        },
        progress: {
          running: 'Ellenőrzések futtatása...',
          allReady: 'Minden rendszer készen áll',
          actionNeeded: 'Beavatkozás szükséges',
          passedCount: '{{passed}}/{{total}} sikeres'
        },
        checks: {
          camera: { label: 'Kamera hozzáférés', description: 'Videórögzítési engedélyek', errorHint: 'Engedélyezze a kamerát a böngészőjében, és próbálja újra.' },
          microphone: { label: 'Mikrofon hozzáférés', description: 'Hangrögzítési engedélyek', errorHint: 'Engedélyezze a mikrofont a böngészőjében, és próbálja újra.' },
          internet: { label: 'Hálózati stabilitás', description: 'Kapcsolat a VivaAssess szolgáltatásaihoz', errorHint: 'Ellenőrizze a hálózat stabilitását, és győződjön meg arról, hogy a tűzfal engedélyezi a hozzáférést.' },
          browser: {
            label: 'Böngésző kompatibilitás',
            voiceDescription: 'Beszédfelismerés és médiatámogatás',
            defaultDescription: 'Modern médiafunkciók támogatása',
            voiceHint: 'A hangalapú mód beszédfelismerési támogatást igényel (Chrome vagy Edge ajánlott).',
            defaultHint: 'A legjobb kompatibilitás érdekében használjon Chrome, Firefox vagy Edge böngészőt.'
          },
          singleScreen: { label: 'Egyetlen képernyős elrendezés', description: 'Nincs aktív külső monitor', errorHint: 'Csatlakoztassa le a külső kijelzőket az interjú megkezdése előtt.' }
        },
        network: {
          latencyLabel: 'Hálózati késleltetés: {{value}}',
          lastCheckedAt: 'Utolsó ellenőrzés: {{time}}',
          accessCheckTitle: 'Hálózati hozzáférés-ellenőrzés',
          checkingPolicy: 'A kiosztás hozzáférési szabályzatának ellenőrzése...',
          publicIp: 'Nyilvános IP: {{value}}',
          detectedLocation: 'Észlelt helyszín: {{value}}',
          unavailable: 'Nem elérhető',
          startOrRerun: 'Indítsa el vagy futtassa újra az ellenőrzéseket az IP és helyszín állapotának felméréséhez.'
        },
        actions: {
          retryAll: 'Összes újrapróbálása',
          rerunFailed: 'Sikertelen ellenőrzések újrafuttatása',
          diagnosticsCopied: 'Diagnosztika másolva',
          copyDiagnostics: 'Diagnosztika másolása',
          verifyingAi: 'MI szolgáltatás ellenőrzése',
          continueToInterview: 'Tovább az interjúra',
          continueRestore: 'Visszaállított munkamenet folytatása'
        },
        preview: {
          title: 'Kamera- és mikrofon-előnézet',
          accessingCamera: 'Kamera elérése...',
          awaitingPermission: 'Az előnézet az engedély megadása után jelenik meg.',
          livePreview: 'Élő előnézet',
          microphoneLevel: 'Mikrofon szintje',
          waiting: 'Várakozás',
          goodSignal: 'Jó jel',
          detected: 'Érzékelve',
          low: 'Alacsony',
          speakHint: 'Beszéljen néhány másodpercig, és ellenőrizze, hogy a mérő reagál.',
          disableMirror: 'Tükrözött előnézet kikapcsolása',
          enableMirror: 'Tükrözött előnézet bekapcsolása',
          probeNetwork: 'Hálózat tesztelése'
        },
        diagnostics: {
          notRunYet: 'még nem futott',
          notMeasured: 'nem mért',
          unknown: 'ismeretlen',
          header: 'VivaAssess jelölti diagnosztika',
          checkedAt: 'Ellenőrzés időpontja: {{value}}',
          interviewMode: 'Interjú mód: {{mode}}',
          networkLatency: 'Hálózati késleltetés: {{value}}',
          accessDecision: 'Hozzáférési szabályzat döntése: {{value}}',
          detectedIp: 'Észlelt nyilvános IP: {{value}}',
          userAgent: 'Felhasználói ügynök: {{value}}',
          systemChecksHeader: 'Rendszerellenőrzések:',
          pass: 'SIKERES',
          fail: 'SIKERTELEN',
          pending: 'FOLYAMATBAN'
        },
        messages: {
          noAssignment: 'Jelenleg nincs kiválasztott interjú-kiosztás. Térjen vissza az irányítópultra, és próbálja újra.',
          accessVerificationFailed: 'Nem sikerült ellenőrizni a hálózati hozzáférési korlátozásokat. Kérjük, próbálja újra a rendszerellenőrzést.',
          accessPolicyDenied: 'Az Ön jelenlegi hálózata nem felel meg az interjú-hozzáférési szabályzatnak.',
          aiUnavailable: 'Az MI interjúszolgáltatás jelenleg nem érhető el. Kérjük, próbálja újra később.'
        }
      },
      declaration: {
        title: 'Szintfelmérő előtti nyilatkozat',
        subtitle: 'Kérjük, válaszoljon az alábbi kérdésekre a szintfelmérő megkezdése előtt. Ez a lépés kötelező.',
        placementLabel: 'Szintfelmérő: {{name}}',
        item1: 'Ha a szintfelmérő teszten elért eredménye indokolja, vállalja-e az „Algoritmikus gondolkodás” kurzus felvételét?',
        item2: 'Ha a szintfelmérő teszten elért eredménye lehetővé teszi, vállalja-e a kiemelt csoportban való részvételt?',
        item3: 'Programozási előzmények',
        item3Notice: 'Ez az információ kizárólag az oktatók tájékoztatására szolgál. Nem számít bele a pontozásba és a csoportbeosztásba, és soha nem kerül átadásra az MI vizsgáztatónak.',
        yes: 'Igen',
        no: 'Nem',
        noProgrammingBackground: 'Még nem tanultam programozni',
        languagePlaceholder: 'Programozási nyelv',
        durationPlaceholder: 'Időtartam (pl. 2 év)',
        sources: {
          school: 'Iskolai keretek között',
          extracurricular: 'Iskolán kívüli tanfolyamon',
          independent: 'Önállóan',
          other: 'Egyéb'
        },
        addRow: 'Sor hozzáadása',
        removeRow: 'Sor törlése',
        errors: {
          answerBoth: 'Kérjük, válaszoljon mindkét nyilatkozatkérdésre (Igen/Nem).',
          backgroundRequired: 'Adjon meg legalább egy programozási előzményt, vagy jelölje be a „Még nem tanultam programozni” lehetőséget.',
          submitFailed: 'A nyilatkozat benyújtása nem sikerült. Kérjük, próbálja újra.'
        },
        saving: 'Mentés…',
        submitAndStart: 'Nyilatkozat benyújtása és a felmérő indítása',
        alreadySubmittedTitle: 'A nyilatkozat már benyújtva',
        alreadySubmittedBody: 'A szintfelmérő előtti nyilatkozatot már kitöltötte. Folytathatja a felmérőt.',
        continueButton: 'Tovább a felmérőhöz'
      },
      interviewComplete: {
        header: {
          subtitle: 'A felmérés befejeződött',
          exitPortal: 'Kilépés a portálról'
        },
        hero: {
          badge: 'Kiváló munka',
          title: 'Az interjú befejeződött',
          description: 'Válaszait biztonságosan elmentettük, és felülvizsgálatra várnak. A folyamatot az előzmények panelen követheti nyomon.'
        },
        stats: {
          duration: 'Időtartam',
          durationValue: '{{minutes}} perc',
          mcqScore: 'MCQ pontszám',
          answered: 'Megválaszolva',
          questions: 'Kérdések',
          integrity: 'Integritás',
          pending: 'Folyamatban',
          submitted: 'Beküldve',
          complete: 'Kész'
        },
        nextSteps: {
          title: 'Mi következik ezután',
          immediate: 'Azonnal',
          oneTwoDays: '1-2 nap',
          threeFiveDays: '3-5 nap',
          scoreValidation: {
            title: 'Pontszám ellenőrzése',
            description: 'A beküldött feleletválasztós próbálkozás ellenőrzésre és teljes integritási metaadatokkal együtt tárolásra kerül.'
          },
          aiAnalysis: {
            title: 'MI elemzés',
            description: 'Az átiratot és a válaszok minőségét elemezzük a szerkezet, a mélység és az interjú céljainak való megfelelés szempontjából.'
          },
          evaluatorReview: {
            title: 'Értékelői felülvizsgálat',
            description: 'Egy értékelő ellenőrzi az automatizált értékelést, és kontextuális megjegyzéseket ad hozzá a végső jelentéshez.'
          },
          resultsDelivery: {
            title: 'Eredmények kézbesítése',
            description: 'A végleges státuszt és a részletes visszajelzést a jelölti előzményekben és értesítési csatornákon kapja meg.',
            hiddenDescription: 'Amikor az adminisztrátor közzéteszi az eredményeket, a pontszámok és a visszajelzés az Előzményekben jelennek meg. Addig az Előzmények rejtett eredményeket mutatnak.'
          }
        },
        completionSummary: {
          title: 'Összegzés',
          candidate: 'Jelölt',
          completed: 'Befejezve',
          interview: 'Interjú',
          mcqScore: 'MCQ pontszám',
          correctAnswers: 'Helyes válaszok'
        },
        feedback: {
          title: 'Interjú visszajelzés',
          subtitle: 'Ossza meg tapasztalatait, hogy segítsen javítani az interjú minőségét és a jelölti élményt.',
          receivedTitle: 'Visszajelzés beérkezett',
          alreadyRecorded: 'A visszajelzését már rögzítettük.',
          overallRating: 'Az interjú általános értékelése',
          difficultyBalance: 'Nehézségi szint egyensúlya',
          questionClarity: 'A kérdések érthetősége',
          platformExperience: 'Platformélmény',
          fairness: 'Igazságosság',
          recommendQuestion: 'Ajánlaná a VivaAssess-t?',
          yes: 'Igen',
          no: 'Nem',
          positiveLabel: 'Mi működött jól?',
          positivePlaceholder: 'Ossza meg az interjú pozitív tapasztalatait',
          improvementLabel: 'Mit fejlesszünk?',
          improvementPlaceholder: 'Javasoljon fejlesztéseket a jövőbeli interjúkhoz',
          submitting: 'Beküldés...',
          submit: 'Visszajelzés beküldése'
        },
        confirmation: {
          title: 'Visszaigazolási hivatkozás',
          interviewId: 'Interjú azonosító:',
          keepReference: 'Őrizze meg ezt a hivatkozást a támogatási megkeresésekhez és a folyamat nyomon követéséhez.',
          mcqSubmittedOn: 'MCQ beküldve: {{date}}',
          autoSubmitSuffix: '(automatikus beküldés)',
          summaryCopied: 'Összegzés vágólapra másolva',
          copySummary: 'Összegzés másolása',
          autoReturnActive: 'Automatikus visszatérés {{seconds}} másodperc múlva',
          enableAutoReturn: 'Automatikus visszatérés engedélyezése',
          resultsHidden: 'A részletes pontszámokat az adminisztrátor elrejtette. Az irányítópulton és az Előzményekben akkor jelennek meg, ha közzéteszik őket.'
        },
        actions: {
          goToDashboard: 'Ugrás az irányítópultra',
          viewHistory: 'Előzmények megtekintése',
          generatingReport: 'Jelentés készítése...',
          checkingReport: 'Jelentés ellenőrzése...',
          downloadReport: 'Teszteredmény letöltése',
          reportHidden: 'A jelentést az adminisztrátor elrejtette',
          openHistoryForReport: 'Előzmények megnyitása a jelentéshez',
          exitPortal: 'Kilépés a portálról',
          reportReady: 'A teszteredmény letöltésre készen áll.',
          reportPending: 'A teszteredmény az értékelői feldolgozás után lesz elérhető. Az interjú előzményeiből érheti el.',
          safeToClose: 'Ezt az oldalt bármikor biztonságosan bezárhatja.'
        },
        footer: {
          tagline: 'ahol a beszélgetések hiteles értékelésekké válnak.',
          supportLabel: 'Támogatás:'
        },
        summary: {
          notAvailable: 'Nincs adat',
          header: 'VivaAssess interjú befejezési összegzés',
          candidate: 'Jelölt: {{name}}',
          interview: 'Interjú: {{title}}',
          completed: 'Befejezve: {{date}}',
          duration: 'Becsült időtartam: {{minutes}} perc',
          mcqScore: 'MCQ pontszám: {{score}}',
          mcqCorrect: 'MCQ helyes válaszok: {{correct}} / {{total}}',
          mcqAnswered: 'MCQ megválaszolva: {{answered}} / {{total}}',
          submissionMode: 'Beküldési mód: {{mode}}',
          autoSubmit: 'Automatikus beküldés',
          manualSubmit: 'Kézi beküldés',
          questionCount: 'Kérdések száma: {{count}}',
          rank: 'Helyezés: {{rank}}',
          partA: 'A rész: {{score}}',
          partB: 'B rész: {{score}}',
          total: 'Összesen: {{score}}',
          integrityScore: 'Integritási pontszám: {{score}}%',
          resultsHidden: 'A részletes pontszámokat az adminisztrátor elrejtette.'
        },
        messages: {
          feedbackSubmitFailed: 'Jelenleg nem sikerült beküldeni a visszajelzést.',
          missingContext: 'Az interjú kontextusa hiányzik. Frissítse az oldalt, és próbálja újra.',
          rateOverall: 'Kérjük, értékelje az interjúval kapcsolatos általános tapasztalatát.',
          additionalFeedbackRequired: 'Kérjük, adjon meg legalább egy további visszajelzési részletet.',
          commentTooLong: 'A megjegyzés mezők legfeljebb 2000 karakter hosszúak lehetnek.',
          feedbackSubmitted: 'Köszönjük. Visszajelzését beküldtük.',
          feedbackAlreadySubmitted: 'Ehhez az interjú-kiosztáshoz már küldött visszajelzést.'
        }
      },
      interviewSession: {
        chrome: {
          accuracy: 'Pontosság',
          aiInsights: 'AI elemzés',
          aiInterviewer: 'Vizsgáztató',
          examiner: 'Vizsgáztató',
          aiIsSpeaking: 'Beszél',
          aiIsThinking: 'Gondolkodik...',
          aiPreparingNextPrompt: 'A következő kérdés előkészítése...',
          aiProctoringActive: 'AI-felügyelet aktív',
          aiPrompt: 'Kérdés',
          aiSpeaking: 'Beszél',
          aiThinking: 'Gondolkodik',
          all: 'Összes',
          allCount: 'Összes ({{count}})',
          allQuestionsAnswered: 'Minden kérdésre válaszolt.',
          answered: 'Megválaszolva',
          answeredFlaggedSummary: 'Megválaszolva {{answered}}/{{total}} • Megjelölve {{flagged}}',
          answeredN: 'Megválaszolva {{count}}',
          answersLockedHint: 'Válaszai rögzítve lesznek, és az értékelés azonnal megkezdődik.',
          approxMinutes: '~{{minutes}} perc',
          assessmentSummary: 'Értékelés összefoglalása',
          autoSubmitSuffix: '(automatikus beküldés)',
          backToDashboard: 'Vissza az irányítópulthoz',
          buildingMcqAnalytics: 'A végleges MCQ-elemzés és a részletes kérdésáttekintés készül...',
          cameraIsOff: 'A kamera ki van kapcsolva',
          cameraMiniWindowHint: 'A kamera a mozgatható mini ablakban marad.',
          cameraOff: 'Kamera kikapcsolva',
          candidateCam: 'Jelölt kamerája',
          candidateCamera: 'Jelölt kamerája',
          characterCount: '{{count}} karakter',
          clear: 'Törlés',
          clearCurrentAnswer: 'Jelenlegi válasz törlése',
          completeInterview: 'Interjú befejezése',
          completeInterviewFlow: 'Interjú folyamat befejezése',
          completePopupToContinue: 'Fejezze be a felugró ablakot az interjú folytatásához.',
          completePopupToFinish: 'Fejezze be a felugró ablakot az értékelés befejezéséhez.',
          completingEllipsis: 'Befejezés...',
          completingInterview: 'Interjú befejezése',
          conceptMasteryCheck: 'Fogalmi tudásellenőrzés',
          consentAndContinue: 'Elfogadom és folytatom',
          consentPending: 'Hozzájárulás függőben',
          continueInterview: 'Interjú folytatása',
          continueMcq: 'MCQ folytatása',
          conversationTimeline: 'Beszélgetés idővonala',
          correct: 'Helyes',
          correctXOfY: 'Helyes {{correct}} / {{total}}',
          currentQuestion: 'Jelenlegi kérdés',
          elapsed: 'Eltelt idő',
          emotion: 'Emóció',
          enable: 'Engedélyezés',
          endInterview: 'Interjú befejezése',
          endInterviewEarly: 'Interjú korai befejezése?',
          endInterviewEarlyConfirm: 'A munkamenet befejezése előtt szeretné lezárni az interjút. Biztos benne?',
          entriesCount: '{{count}} bejegyzés',
          exitSession: 'Munkamenet elhagyása',
          explanation: 'Magyarázat',
          filterQuestions: 'Kérdések szűrése',
          finalizeAssessment: 'Értékelés véglegesítése',
          finalizeToCloseHint: 'A véglegesítés lezárja ezt a szakaszt, és a befejezési oldalra lépteti a visszaigazolási adatokkal.',
          finalizingInterview: 'Interjú véglegesítése',
          finalizingInterviewHint: 'Válaszainak biztonságos feldolgozása folyamatban van. Kérjük, ne zárja be ezt az oldalt.',
          flag: 'Megjelölés',
          flagged: 'Megjelölve',
          flaggedCount: 'Megjelölve ({{count}})',
          flaggedN: 'Megjelölve {{count}}',
          focus: 'Fókusz',
          hide: 'Elrejtés',
          hideEmotionAnalysis: 'Emócióelemzés elrejtése',
          incorrect: 'Helytelen',
          integrity: 'Integritás',
          integrityPercent: 'Integritás: {{percent}}%',
          interviewInProgress: 'Az interjú folyamatban van.',
          interviewServiceUnavailable: 'Az interjúszolgáltatás nem elérhető',
          invigilatorSupport: 'Felügyelői támogatás',
          keepWindowActiveHint: 'Tartsa aktívan ezt az ablakot. A fülek váltása vagy a teljes képernyős mód elhagyása naplózásra kerül.',
          listening: 'Hallgatás',
          listeningEllipsis: 'Hallgatás...',
          live: 'Élő',
          liveDraft: 'Élő piszkozat',
          liveInterview: 'ÉLŐ INTERJÚ',
          liveSupportDesk: 'Élő ügyfélszolgálat',
          liveTranscript: 'Élő átirat',
          mcqAssessment: 'MCQ-értékelés',
          mcqCheckpointInProgress: 'MCQ ellenőrzőpont folyamatban',
          mcqCompletion: 'MCQ befejezése',
          mcqResults: 'MCQ eredmények',
          mcqResultsReady: 'MCQ eredmények készen állnak',
          mcqXOfY: 'MCQ {{current}}/{{total}}',
          mic: 'Mikrofon',
          micLevel: 'Mikrofonszint',
          micLiveShort: 'Mikrofon élő',
          micMuted: 'Mikrofon némítva',
          microphoneLevel: 'Mikrofonszint',
          mute: 'Némítás',
          muteMicrophone: 'Mikrofon némítása',
          muted: 'Némítva',
          next: 'Következő',
          nextAction: 'Következő lépés',
          nextQuestion: 'Következő kérdés',
          nextUnanswered: 'Következő megválaszolatlan',
          noMcqsAvailable: 'Még nincsenek elérhető MCQ kérdések.',
          noQuestionsMatchFilter: 'Nincs a szűrésnek megfelelő kérdés.',
          noQuestionsMatchSelectedFilter: 'Nincs a kiválasztott szűrésnek megfelelő kérdés.',
          off: 'Ki',
          pending: 'Függőben',
          pendingReview: 'Felülvizsgálatra vár',
          performanceCommandCenter: 'Teljesítmény központ',
          performanceCommandCenterHint: 'Tekintse át a részletes eredményeket, szűrje a kérdésáttekintést, majd véglegesítse a beküldést, ha készen áll.',
          phaseMcq: 'MCQ',
          phaseQA: 'Kérdés-válasz',
          phaseResults: 'Eredmények',
          phaseWritten: 'Írásbeli',
          pleaseWaitProcessing: 'Kérjük, várjon, amíg feldolgozzuk a válaszait...',
          preparingFirstQuestion: 'Az első kérdés előkészítése...',
          preparingMcqSet: 'Az MCQ-készlet előkészítése...',
          preparingNextPrompt: 'A következő kérdés előkészítése...',
          preparingWrittenQuestions: 'Írásbeli kérdések előkészítése...',
          previous: 'Előző',
          proceedAnyway: 'Folytatás mindenképpen',
          proctorControls: 'Felügyeleti vezérlők',
          proctoringConsentBullet1: 'Kamera láthatósági és arcjelenlét-ellenőrzések',
          proctoringConsentBullet2: 'Háttérzaj-anomália észlelése',
          proctoringConsentBullet3: 'Automatikus figyelmeztetés és felügyelői eszkaláció magas kockázatú események esetén',
          proctoringConsentDescription: 'Ez az interjú AI-alapú kamera- és hangalapú anomáliaészlelést használ. A folytatáshoz hozzájárulás szükséges.',
          proctoringConsentRequired: 'Felügyeleti hozzájárulás szükséges',
          progress: 'Előrehaladás',
          qTime: 'Kérdés ideje',
          questionNumber: '{{number}}. kérdés',
          questionPalette: 'Kérdéspaletta',
          questionUnavailable: 'A kérdés nem elérhető.',
          questionXOfY: '{{current}}. kérdés / {{total}}',
          quickActions: 'Gyors műveletek',
          recordedSecurelyNotice: 'Az interjú rögzítése biztonságosan történik',
          reset: 'Visszaállítás',
          resetCam: 'Kamera visszaállítása',
          responseStudio: 'Válaszstúdió',
          responseStudioHint: 'Készítsen világos, jól strukturált válaszokat.',
          resultSummary: 'Eredmény összefoglaló',
          results: 'Eredmények',
          resultsNotAvailableYet: 'Az eredmények még nem elérhetők.',
          reviewLocked: 'Felülvizsgálat zárolva',
          reviewResultsInPopup: 'Tekintse át eredményeit a felugró ablakban, majd fejezze be az értékelést.',
          reviewUnanswered: 'Megválaszolatlanok áttekintése',
          riskScoreRecommendation: 'Kockázati pontszám: {{score}} • Javaslat: {{recommendation}}',
          saving: 'Mentés...',
          score: 'Pontszám',
          scoringInProgress: 'Az értékelés folyamatban van. Az eredménypanel előkészítése...',
          sessionPulse: 'Munkamenet állapota',
          show: 'Megjelenítés',
          showEmotionAnalysis: 'Emócióelemzés megjelenítése',
          skip: 'Kihagyás',
          skipThisQuestion: 'Kérdés kihagyása',
          starMethodTip: 'Tipp: használja a STAR módszert (Helyzet, Feladat, Cselekvés, Eredmény) az erősebb válaszokért.',
          stillHaveUnanswered: 'Még van {{count}} megválaszolatlan kérdése.',
          strengths: 'Erősségek',
          structured: 'Strukturált',
          submissionCompleted: 'Beküldés befejezve',
          submissionCompletedHint: 'A válaszai biztonságosan beküldve. A részletes kérdésenkénti elemzés még készül.',
          submit: 'Beküldés',
          submitMcq: 'MCQ beküldése',
          submitMcqSection: 'Beküldi az MCQ szakaszt?',
          submitMcqs: 'MCQ-k beküldése',
          submitResponse: 'Válasz beküldése',
          submittedAt: 'Beküldve: {{datetime}}',
          submitting: 'Beküldés...',
          submittingAnswersSecurely: 'A válaszai biztonságos beküldése folyamatban van. Kérjük, ne zárja be ezt az ablakot.',
          switchToListView: 'Váltás listanézetre',
          switchToSingleView: 'Váltás egykérdéses nézetre',
          textInterviewStudio: 'SZÖVEGES INTERJÚ STÚDIÓ',
          topicGaps: 'Témabeli hiányosságok',
          total: 'Összesen',
          totalQuestions: 'Összes kérdés',
          transcriptAppearsHere: 'Az átirat itt jelenik meg, amint az interjú elkezdődik.',
          transcriptWillAppear: 'Az átirat itt jelenik meg, amint a beszélgetés folytatódik...',
          turnCameraOff: 'Kamera kikapcsolása',
          turnCameraOn: 'Kamera bekapcsolása',
          turnOffCamera: 'Kamera kikapcsolása',
          turnOnCamera: 'Kamera bekapcsolása',
          typeResponsePlaceholder: 'Írja be válaszát. Nyomjon Entert a beküldéshez, Shift+Entert új sorhoz.',
          typeYourResponsePlaceholder: 'Írja be válaszát itt... (Enter a küldéshez, Shift+Enter új sorhoz)',
          unanswered: 'Megválaszolatlan',
          unansweredCount: 'Megválaszolatlan ({{count}})',
          unansweredN: 'Megválaszolatlan {{count}}',
          unmute: 'Némítás megszüntetése',
          unmuteMicrophone: 'Mikrofon némítás megszüntetése',
          video: 'Videó',
          waiting: 'Várakozás...',
          waitingEllipsis: 'Várakozás...',
          waitingForAiResponse: 'Várakozás az AI válaszára...',
          weaknesses: 'Gyengeségek',
          wordCount: '{{count}} szó',
          wordsCount: '{{count}} szó',
          wrappingUp: 'Lezárás',
          writeYourAnswerPlaceholder: 'Írja le válaszát itt... (3-5 mondat javasolt)',
          writtenAnswerPlaceholder: 'Írja le válaszát konkrét példákkal és mérhető eredményekkel.',
          writtenNumber: '{{number}}. írásbeli',
          writtenQuestion: 'Írásbeli kérdés',
          writtenQuestionOf: '{{current}}. kérdés / {{total}}',
          writtenResponse: 'Írásbeli válasz',
          writtenXOfY: '{{current}}. írásbeli / {{total}}',
          you: 'Ön',
          yourPick: 'Az Ön választása',
          yourResponse: 'Az Ön válasza',
          yourTurn: 'Önön a sor',
          yourTurnToRespond: 'Önön a sor a válaszadásra'
        },
        messages: {
          aiInterviewerNotAvailable: 'Az AI interjúztató nem elérhető',
          aiInterviewerNotInitialized: 'Az AI interjúztató nincs inicializálva'
        }
      }
    },
    invigilator: {
      nav: {
        liveMonitoring: 'Kiosztási sor',
        liveAv: 'Élő kamera és képernyő',
        reportHistory: 'Jelentéselőzmények',
        accountSettings: 'Fiókbeállítások',
        liveMonitoringDescription: 'Kiosztások, riasztások és beavatkozások',
        liveAvDescription: 'Jelölt kamera- és teljes képernyő-közvetítései',
        reportHistoryDescription: 'Integritási jelentések és képernyőképek',
        accountSettingsDescription: 'Profiladatok és fiókbiztonság',
        help: 'Súgó',
        helpDescription: 'Útmutatók a felügyelői eszközökhöz'
      },
      help: {
        title: 'Súgó',
        subtitle: 'Útmutatók a felügyelői eszközökhöz',
        back: 'Vissza',
        loading: 'Útmutatók betöltése...',
        loadFailed: 'Az útmutatók betöltése sikertelen.',
        emptyTitle: 'Még nincs útmutató',
        emptySubtitle: 'A közzétett felügyelői útmutatók itt jelennek meg.',
        guides: 'Útmutatók',
        noSteps: 'Ehhez az útmutatóhoz még nincsenek lépések.',
        selectGuide: 'Válasszon egy útmutatót az olvasáshoz.'
      },
      shell: {
        overviewTitle: 'Kiosztási sor',
        overviewSubtitle: 'Beavatkozások és munkamenet-állapot',
        liveAvTitle: 'Élő kamera és képernyő',
        liveAvSubtitle: 'Valós idejű jelölti közvetítések',
        reportHistoryTitle: 'Jelentéselőzmények',
        reportHistorySubtitle: 'Integritási evidenciatár',
        accountTitle: 'Fiókom',
        accountSubtitle: 'Profil és biztonság',
        accountDescription: 'Kezelje profiladatait és fiókja biztonsági beállításait.',
        backToQueue: 'Vissza a sorhoz'
      },
      overview: {
        title: 'Felügyeleti irányítópult',
        subtitle: 'Figyelje a kiosztott jelölteket, és avatkozzon be valós időben.',
        refresh: 'Sor frissítése',
        stats: {
          assigned: 'Kiosztott jelöltek',
          live: 'Élő munkamenetek',
          flagged: 'Megjelölt munkamenetek',
          idle: 'Még nem indult'
        },
        searchPlaceholder: 'Keresés név, e-mail, Neptun vagy interjú szerint',
        filters: {
          all: 'Minden kiosztás',
          live: 'Csak élő',
          flagged: 'Csak megjelölt',
          idle: 'Még nem indult'
        },
        emptyQueue: 'Még nincs kiosztott jelölt.',
        emptyFilter: 'Nincs kiosztás ehhez a szűrőhöz.',
        selectCandidate: 'Válasszon egy jelöltet a munkamenet figyeléséhez.',
        listTitle: 'Kiosztott jelöltek',
        neptun: 'Neptun',
        candidate: 'Jelölt',
        noActiveSession: 'Nincs aktív munkamenet',
        integrity: 'Integritás: {{score}}%',
        sessionNotStarted: 'A munkamenet még nem indult el. Az élő átirat, a csevegés és az idővonal a jelölt indítása után válik elérhetővé.',
        openLiveAv: 'Élő kamera és képernyő megnyitása',
        status: {
          live: 'ÉLŐ',
          idle: 'Indításra vár',
          completed: 'Befejezve',
          flagged: 'Megjelölve',
          notStarted: 'Nem indult'
        },
        schedule: {
          slot: 'Időpont',
          window: 'Időablak',
          none: 'Nincs ütemezve'
        },
        violationBanner: 'Új integritássértés észlelve.',
        notices: {
          messageSent: 'Az üzenet sikeresen elküldve.',
          interventionSent: '{{label}} elküldve a jelöltnek.',
          timelineLogged: 'Az idővonal-esemény rögzítve.',
          alertStatus: 'A riasztás állapota: {{status}}.'
        },
        errors: {
          loadAssignments: 'A kiosztások betöltése sikertelen',
          loadSession: 'A munkamenet betöltése sikertelen',
          loadMessages: 'Az üzenetek betöltése sikertelen',
          loadTimeline: 'A felügyeleti idővonal betöltése sikertelen',
          loadAlerts: 'A riasztások betöltése sikertelen',
          sendMessage: 'Az üzenet küldése sikertelen',
          runIntervention: 'A beavatkozás futtatása sikertelen',
          logEvent: 'Az idővonal-esemény rögzítése sikertelen',
          updateAlert: 'A riasztás állapotának frissítése sikertelen',
          eventRequired: 'Válasszon eseménytípust, és adjon meg egy jegyzetet a rögzítés előtt.',
          sessionInactive: 'A jelölt munkamenete még nem aktív.',
          timelineUpdateFailed: 'Az idővonal frissítése sikertelen'
        },
        interventions: {
          title: 'Gyors beavatkozások',
          running: 'Futtatás…',
          focusWarning: {
            label: 'Figyelemfelhívás',
            message: 'Kérjük, tartsa a figyelmét az interjúablakon, és kövesse az értékelési szabályokat.'
          },
          identityCheck: {
            label: 'Személyazonosság-ellenőrzés',
            message: 'Kérjük, igazítsa a kamerát a személyazonosság ellenőrzéséhez, és tartsa teljesen láthatóan az arcát.'
          },
          technicalPause: {
            label: 'Technikai szünet',
            message: 'Technikai problémát észleltünk. Szüneteljen röviden, és jelezze, ha a beállítás ismét stabil.'
          }
        },
        transcript: {
          title: 'Élő átirat',
          empty: 'Az átirat az interjú indulása után jelenik meg.',
          ai: 'MI interjúztató',
          candidate: 'Jelölt'
        },
        alerts: {
          title: 'Integritási riasztások',
          empty: 'Még nincs integritási esemény.',
          status: 'Állapot: {{value}}',
          acknowledge: 'Nyugtázás',
          investigating: 'Vizsgálat',
          resolve: 'Lezárás',
          violation: 'Szabálysértés',
          policyViolation: 'Szabálysértés észlelve',
          statuses: {
            new: 'Új',
            acknowledged: 'Nyugtázva',
            investigating: 'Vizsgálat alatt',
            resolved: 'Lezárva',
            dismissed: 'Elutasítva'
          }
        },
        chat: {
          title: 'Csevegés a jelölttel',
          empty: 'Még nincs üzenet.',
          invigilator: 'Felügyelő',
          candidate: 'Jelölt',
          placeholder: 'Utasítás vagy pontosítás küldése…',
          waiting: 'Várakozás az aktív munkamenetre…',
          send: 'Küldés',
          sending: 'Küldés…'
        },
        timeline: {
          title: 'Felügyeleti idővonal',
          empty: 'Még nincs rögzített idővonal-esemény.',
          log: 'Idővonal-esemény rögzítése',
          logging: 'Rögzítés…',
          notePlaceholder: 'Írja le, mit észlelt…',
          waiting: 'Várakozás az aktív munkamenetre…',
          system: 'Rendszer',
          severityLow: 'Alacsony súlyosság',
          severityMedium: 'Közepes súlyosság',
          severityHigh: 'Magas súlyosság',
          severityCritical: 'Kritikus súlyosság',
          events: {
            manual_note: 'Kézi jegyzet',
            behavioral_concern: 'Magatartási észrevétel',
            verification_step: 'Ellenőrzési lépés',
            technical_observation: 'Technikai észlelés',
            policy_reminder: 'Szabályemlékeztető'
          }
        }
      }
    },
    interviews: {
      status: {
        inProgress: 'Folyamatban',
        completed: 'Befejezve',
        pending: 'Fuggo'
      }
    },
    reports: {
      generatedOn: 'Generalva: {{date}}'
    },
    notifications: {
      newNotification: 'Uj ertesitese erkezett.'
    },
    mainSite: {
      experienceFallback: {
        product: {
          title: 'Termek attekintes',
          summary: 'Fedezze fel a VivaAssess platformot es a megbizhato interjudonteseket tamogato kepessegeket.'
        },
        solutions: {
          title: 'Megoldasok',
          summary: 'Talalja meg a VivaAssess megoldasait toborzo es kepzesi csapatok szamara.'
        },
        solutionsHrLeaders: {
          title: 'Megoldasok HR vezetoknek',
          summary: 'Novelje a dontesek kovetkezetesseget es a governance bizalmat HR szinten.'
        },
        solutionsRecruiters: {
          title: 'Megoldasok toborzoknak',
          summary: 'Csokkentse a pipeline surlodasat es gyorsitsa a jeloltek elorehaladasat.'
        },
        solutionsEducationTraining: {
          title: 'Megoldasok oktatasi es kepzesi csapatoknak',
          summary: 'Skalazza az interju es szobeli ertekelesi folyamatokat minosegi kontrollal.'
        },
        pricing: {
          title: 'Arak',
          summary: 'Valasszon VivaAssess csomagot, amely illik a fejlodesi szakaszahoz.'
        },
        securityTrustCenter: {
          title: 'Biztonsag es bizalmi kozpont',
          summary: 'Tekintse at a biztonsagi, adatvedelmi, MI governance es megbizhatosagi allapotot.'
        },
        caseStudies: {
          title: 'Esettanulmanyok',
          summary: 'Nezze meg, hogyan alakitottak at a csapatok interjufolyamataikat a VivaAssess segitsegevel.'
        },
        compare: {
          title: 'VivaAssess osszehasonlitasa',
          summary: 'Hasonlitsa ossze a VivaAssess megoldast a manualis folyamatokkal es hagyomanyos eszkozokkel.'
        },
        compareManualProcess: {
          title: 'VivaAssess vs manualis folyamat',
          summary: 'Ismerje meg, miert teljesitenek jobban a strukturalt workflow-k a manualis interjuzashoz kepest.'
        },
        compareTraditionalTools: {
          title: 'VivaAssess vs hagyomanyos eszkozok',
          summary: 'Lassa a kulonbseget a regi interjueszkozok es a VivaAssess kozott.'
        },
        compareGenericAiAssistant: {
          title: 'VivaAssess vs altalanos MI asszisztens workflow-k',
          summary: 'Fedezze fel, miert ad jobb eredmenyt a domain-specifikus interjuintelligencia.'
        }
      }
    },
    errors: {
      actions: {
        goBack: 'Vissza',
        goHome: 'Kezdolap',
        goToLogin: 'Bejelentkezes',
        retry: 'Ujraprobal'
      },
      requestIdLabel: 'Tamogatasi keresi azonosito',
      pages: {
        badRequest: {
          title: 'Hibas keres',
          message: 'A keres nem dolgozhato fel. Ellenorizze a megadott adatokat es probalja ujra.'
        },
        unauthorized: {
          title: 'Nincs jogosultsag',
          message: 'A munkamenet hianyzik vagy lejart. A folytatashoz jelentkezzen be.'
        },
        forbidden: {
          title: 'Tiltott',
          message: 'Nincs jogosultsaga ehhez az oldalhoz vagy eroforrashoz.'
        },
        notFound: {
          title: 'Az oldal nem talalhato',
          message: 'A keresett oldal vagy eroforras nem talalhato.'
        },
        internalServerError: {
          title: 'Belso szerverhiba',
          message: 'Hiba tortent a rendszer oldalan. Krem, probalja ujra rovidesen.'
        },
        serviceUnavailable: {
          title: 'A szolgaltatas nem erheto el',
          message: 'A szolgaltatas atmenetileg nem erheto el. Krem, probalja ujra hamarosan.'
        }
      },
      shortcuts: {
        title: 'Hibaoldalak',
        description: 'Valasszon egy hibaoldal elonezetet.'
      }
    }
  },
  ru: {
    common: {
      notificationPanel: notificationPanelLabels.ru,
      transcriptExpiry: transcriptExpiryLabels.ru,
      blockedObjects: blockedObjectLabels.ru,
      authoringGrounding: authoringGroundingLabels.ru,
      sessionSave: {"conflict": "Автосохранение остановлено: записан более новый прогресс или другой этап экзамена. Оставьте эту вкладку открытой, прекратите редактирование здесь и используйте одну вкладку сессии. Не отправляйте ответы повторно; перед восстановлением несохранённой работы обратитесь к администратору.", "unavailable": "Сохранение прогресса не подтверждено. Оставьте вкладку открытой и проверьте соединение. Не перезагружайте страницу и не открывайте другую вкладку, пока восстановление не подтверждено; если сохранение не возобновилось, обратитесь к администратору."},
      completionRepair: {"preview": "Предпросмотр восстановления", "summary": "Компонент: {{component}}. Осталось попыток: {{count}}. Предпросмотр действует пять минут.", "part_a": "Рейтинг части A", "oral": "Устное оценивание", "confirm": "Я проверил и устранил временный сбой. Повторить только исходную задачу в пределах текущего лимита; сохранить доказательства, решения человека и правила публикации.", "apply": "Подтвердить восстановление", "loading": "Проверка восстановления…", "unavailable": "Восстановление недоступно или требует проверки. Активные, отменённые, исчерпанные, изменённые или проверенные задачи здесь не перезапускаются.", "failed": "Восстановление не подтверждено. Повторите то же подтверждение для получения квитанции или запросите новый предпросмотр после истечения срока. Не отправляйте ответы повторно.", "accepted": "Восстановление принято и записано в аудит. Обновите статус после обработки. Действующие правила публикации сохраняются.", "receipt": "Квитанция аудита"},
      completionReview: {"title": "Проверка обработки завершённых сессий", "note": "Завершённые сессии с отсутствующей или повторной оценкой, остановленной или неотслеживаемой обработкой. Обновите список. Восстановление требует проверки и допустимого предпросмотра; чтение списка ничего не перезапускает и не публикует.", "empty": "В этой группе нет подходящих завершённых сессий.", "session": "Сессия", "candidate": "Кандидат", "interview": "Интервью", "assessment": "Оценка", "available": "Доступна", "missing": "Отсутствует", "ambiguous": "Несколько оценок", "not_submitted": "Не отправлено", "more": "Показать ещё"},
      completionStatus: {"title": "Обработка отправки", "refresh": "Обновить статус", "loading": "Проверка обработки…", "unavailable": "Статус недоступен. Обновите или повторите позже.", "unknown": "Статус оценивания не записан. Если результаты остаются в ожидании, обратитесь к администратору.", "not_submitted": "Сессия ещё не принята как завершённая.", "queued": "Ответы приняты. Оценивание в очереди.", "running": "Ответы приняты. Идёт оценивание.", "retrying": "Ответы приняты. Оценивание будет повторено автоматически.", "processed": "Оценивание обработано.", "needs_attention": "Отправка записана. Для обработки требуется помощь администратора.", "releaseNote": "Статус обработки не открывает результаты и не меняет решение проверяющего."},
      app: {
        name: 'VivaAssess',
        tagline: 'Nadezhnaya platforma AI sobesedovaniy'
      },
      skipToMainContent: 'Pereyti k osnovnomu soderzhaniyu',
      nav: {
        home: 'Glavnaya',
        product: 'Produkt',
        solutions: 'Resheniya',
        pricing: 'Tseni',
        trust: 'Doverie',
        compare: 'Sravnenie',
        contact: 'Kontakt',
        resources: 'Resursy',
        features: 'Vozmozhnosti',
        about: 'O nas',
        teams: 'Komandy',
        docs: 'Dokumentatsiya',
        caseStudies: 'Keisy'
      },
      actions: {
        goToDashboard: 'Pereyti v panel',
        signOut: 'Vyiti',
        close: 'Zakryt',
        expandNavigation: 'Razvernut navigatsiyu',
        collapseNavigation: 'Svernut navigatsiyu',
        backToPortal: 'Nazad v portal',
        help: 'Spravka'
      },
      labels: {
        language: 'Yazyk',
        searchModules: 'Poisk moduley...',
        pinned: 'Zakrepleno',
        allModules: 'Vse moduli',
        navigation: 'Navigatsiya',
        noModulesFound: 'Moduli ne naydeny',
        noShort: 'Net'
      },
      roles: {
        adminControl: 'Panel administratora',
        invigilator: 'Nablyudatel'
      },
      accessibility: {
        goToHomepage: 'Pereyti na glavnuyu VivaAssess',
        closeNavigationMenu: 'Zakryt menyu navigatsii'
      },
      language: {
        english: 'Angliyskiy',
        french: 'Frantsuzskiy',
        hungarian: 'Vengerskiy',
        russian: 'Russkiy',
        german: 'Nemeckiy'
      },
      privacy: {
        title: 'Konfidentsialnost',
        accountIntro: 'Prosmotrite aktualnoe uvedomlenie, skachayte kopiyu svoih dannyh ili zaprosite udalenie. Balli ekzamenov mogut sohranyatsya v anonimizirovannom vide, esli etogo trebuet akademicheskaya chestnost.',
        loading: 'Zagruzka nastroek konfidentsialnosti...',
        loadFailed: 'Ne udalos zagruzit svedeniya o konfidentsialnosti.',
        actionFailed: 'Zapros ne udalos vypolnit.',
        acknowledge: 'Ya prochital eto uvedomlenie',
        acknowledged: 'Podtverzhdenie sohraneno.',
        alreadyAcked: 'Vy uzhe podtverdili aktualnoe uvedomlenie.',
        downloadMyData: 'Skachat moi dannye',
        requestDeletion: 'Zaprosit udalenie',
        requestDoNotSell: 'Zafiksirovat otkaz ot prodazhi',
        requestSent: 'Zapros poluchen.',
        exportReady: 'Zagruzka dannyh nachalas.',
        finishSavedExport: 'Завершить сохранённый экспорт',
        freshExport: 'Создать новый экспорт',
        doNotSellTrue: 'Platforma ne prodaet personalnye dannye i ne peredaet ih dlya mezkontekstnoy reklamy.',
        doNotSellFalse: 'Utochnite v uchrezhdenii, kak peredayutsya personalnye dannye.',
        loginBanner: 'Tolko neobhodimye cookie i hranilishche (vhod, tema, yazyk).',
        readNotice: 'Prochitat uvedomlenie o konfidentsialnosti',
        ackTitle: 'Uvedomlenie o konfidentsialnosti',
        noNotice: 'Opublikovannogo uvedomleniya net.',
        publicTitle: 'Uvedomlenie o konfidentsialnosti',
        publicIntro: 'Tolko opublikovannye uvedomleniya. Voydite, chtoby skachat svoi dannye ili zaprosit udalenie.',
        backToLogin: 'Nazad ko vhodu'
      }
    },
    auth: {
      login: {
        welcomeBack: 'S vozvrashcheniem',
        subtitle: 'Voydite, chtoby prodolzhit rabotu v VivaAssess',
        emailAddress: 'Email adres',
        password: 'Parol',
        signIn: 'Voyti',
        firstTimeTitle: 'Vy zdes vpervye?',
        firstTimeBody: 'Ispolzuyte priglasitelnoe pismo dlya bezopasnoy ustanovki parolya.',
        trustedBadge: 'Doverennye AI sobesedovaniya',
        premiumDescription: 'Premium platforma interview-analitiki dlya spravedlivyh i obyasnimyh resheniy po naymu.',
        adaptiveAI: 'Adaptivnyy AI',
        adaptiveAIDescription: 'Dinamicheskie utochneniya i kontekstnye voprosy.',
        biasAware: 'Uchet predvzyatosti',
        biasAwareDescription: 'Obektivnaya i posledovatelnaya otsenka v masshtabe.',
        auditReady: 'Gotovo k auditu',
        auditReadyDescription: 'Prozrachnost i kontrol po umolchaniyu.',
        interviews: 'Sobesedovaniya',
        signalAccuracy: 'Tochnost signala',
        organizations: 'Organizatsii',
        emailPlaceholder: 'name@organization.com',
        passwordPlaceholder: 'Vvedite parol',
        toggleTheme: 'Pereklyuchit temu'
      }
    },
    admin: {
      auditOrigin: auditOriginLabels.ru,
      readinessPreview: materialReadinessLabels.ru,
      previewHistory: materialPreviewHistoryLabels.ru,
      assignmentReadinessBlocked: "Перед назначением активируйте интервью, включите часть оценки и выберите активный шаблон для связанного устного тестирования.",
      privateOpeningPrompt: {
        label: "?????? ?????? ??????? ???????? (?????????????)",
        hint: "?????????????. ?????? ???? ????????? ??????????? ??????. ??????????? ??????????? ? ?????? ??????; ????????? ????????? ??? ????? ??????.",
        format: "??????? JSON ? greeting ? first_question, ??????? question, question_type, difficulty, expected_topics ? max_response_time_seconds. ??????????:",
      },
      interviewForm: {
        answerReview: {
          title: 'Proverka otveta chasti B',
          hint: 'Posle transkriptsii kandidat mozhet ispravit tekst do otsenki hoda. Avtootpravka ostaetsya. Taymer chasti B ne ostanavlivaetsya.',
          allow: 'Razreshit kandidatu proveryat i ispravlyat kazhdyy otvet',
          defaultMode: 'Nachalnyy rezhim zahvata',
          modeAuto: 'Avtootpravka posle tishiny',
          modeReview: 'Proverka pered otpravkoy',
          timeout: 'Limit proverki (sekundy, 0 = net)'
        }
      },
      nav: {
        overview: { label: 'Obzor', description: 'Status platformy, metriky i aktivnost' },
        interviews: { label: 'Sobesedovaniya', description: 'Sozdanie i upravlenie shablonami sobesedovaniy' },
        mcqSets: { label: 'Menedzher MCQ', description: 'Proektirovanie i organizatsiya naborov MCQ' },
        materials: { label: 'Materialy', description: 'Zagruzka i kurirovanie materialov dlya sobesedovaniy' },
        candidates: { label: 'Kandidaty', description: 'Priglashenie, naznachenie i monitoring kandidatov' },
        sections: { label: 'Gruppy kandidatov', description: 'Gruppirovka kandidatov dlya raspisaniya i zapisi na placement' },
        invigilators: { label: 'Nablyudateli', description: 'Upravlenie naznacheniyami nablyudateley' },
        proctoringReports: { label: 'Otchety proktoringu', description: 'Istoriya otchetov chestnosti i skrinshotov' },
        liveMonitoring: { label: 'Zhivoy kontrol', description: 'Nablyudenie za aktivnymi sessiyami s kameroy i ekranom v realnom vremeni' },
        accounts: { label: 'Uchetnye zapisi', description: 'Upravlenie vsemi uchetnymi zapisami' },
        groups: { label: 'Instituty i gruppy', description: 'Upravlenie ierarkhiey tenantov i uchastiem' },
        myAccount: { label: 'Moy akkaunt', description: 'Upravlenie profilem administratora i bezopasnostyu' },
        llmSettings: { label: 'Nastroyki LLM', description: 'Nastroyka AI provayderov i modeley' },
        voiceStudio: { label: 'AI Voice Studio', description: 'Upravlenie golosami intervyuerov i pereopredeleniyami' },
        assessments: { label: 'Otsenki', description: 'Proverka i utverzhdenie rezultatov otsenki' },
        feedback: { label: 'Otzyvy ob interview', description: 'Analiz opyta kandidata i eksport dannyh otzyvov' },
        notifications: { label: 'Uvedomleniya', description: 'Upravlenie istoriyey dostavki i povedeniem email-otchetov' },
        systemIssues: { label: 'Sistemnye problemy', description: 'Otslezhivanie preduprezhdeniy i incidentov' },
        resetSettings: { label: 'Sbros nastroyek', description: 'Vosstanovlenie znacheniy po umolchaniyu (LLM, MCQ, proktoring, predpochteniya)' },
        auditLogs: { label: 'Zhurnaly audita', description: 'Prosmotr zhurnalov administrativnoy aktivnosti' },
        help: { label: 'Spravka', description: 'Rukovodstva dlya etoy paneli' },
        userGuides: { label: 'Rukovodstva', description: 'Sozdanie i redaktirovanie vstroennyh rukovodstv i skrinshotov' },
        privacy: { label: 'Konfidentsialnost', description: 'Uvedomleniya, zaprosy subektov dannyh i sroki hraneniya' }
      },
      help: {
        title: 'Spravka',
        subtitle: 'Opublikovannye rukovodstva dlya dostupnyh ekranov',
        back: 'Nazad',
        loading: 'Zagruzka rukovodstv...',
        loadFailed: 'Ne udalos zagruzit rukovodstva.',
        emptyTitle: 'Rukovodstv poka net',
        emptySubtitle: 'Opublikovannye rukovodstva dlya vashey roli poyavyatsya zdes.',
        guides: 'Rukovodstva',
        noSteps: 'V etom rukovodstve eshche net shagov.',
        selectGuide: 'Vyberite rukovodstvo, chtoby prochitat ego.'
      },
      userGuides: {
        title: 'Rukovodstva',
        subtitle: 'Sozdavayte, publikuyte i perevodite vstroennuyu spravku dlya kazhdoy roli.',
        listTitle: 'Rukovodstva',
        loading: 'Zagruzka rukovodstv...',
        empty: 'Rukovodstv poka net',
        selectOrCreate: 'Vyberite rukovodstvo ili sozdayte novoe.',
        fields: {
          title: 'Zaglavie',
          slug: 'Slug',
          audience: 'Auditoriya',
          locale: 'Yazyk',
          module: 'Modul',
          noModule: 'Bez modulya',
          published: 'Opublikovano',
          summary: 'Kratkoe opisanie'
        },
        audiences: {
          candidate: 'Kandidat',
          invigilator: 'Nablyudatel',
          admin: 'Administrator',
          super_admin: 'Super administrator'
        },
        status: {
          published: 'Opublikovano',
          draft: 'Chernovik'
        },
        filters: {
          allAudiences: 'Vse auditorii',
          allLocales: 'Vse yazyki'
        },
        steps: {
          title: 'Shagi',
          add: 'Dobavit shag',
          stepNumber: 'Shag {{count}}',
          titlePlaceholder: 'Nazvanie shaga',
          bodyPlaceholder: 'Poyasnenie (obychnyy tekst ili prostoy markdown)',
          uploadImage: 'Zagruzit skrinshot',
          removeImage: 'Udalit izobrazhenie'
        },
        actions: {
          create: 'Sozdat rukovodstvo',
          save: 'Sohranit',
          saving: 'Sohranenie...',
          delete: 'Udalit',
          cancel: 'Otmena',
          duplicate: 'Kopirovat na yazyk',
          preview: 'Predprosmot'
        },
        messages: {
          loadFailed: 'Ne udalos zagruzit rukovodstva.',
          created: 'Rukovodstvo sozdano.',
          saved: 'Rukovodstvo sohraneno.',
          saveFailed: 'Ne udalos sohranit rukovodstvo.',
          deleted: 'Rukovodstvo udaleno.',
          deleteFailed: 'Ne udalos udalit rukovodstvo.',
          duplicated: 'Yazykovaya kopiya sozdana.',
          duplicateFailed: 'Ne udalos dublirovat rukovodstvo.',
          saveStepsFirst: 'Snachala sohranite shagi, zatem zagruzite skrinshoty.',
          imageUploaded: 'Skrinshot zagruzhen.',
          imageFailed: 'Ne udalos obnovit skrinshot.'
        },
        modals: {
          createTitle: 'Sozdat rukovodstvo',
          previewTitle: 'Predprosmot',
          deletePrompt: 'Udalit eto rukovodstvo i ego skrinshoty?'
        }
      },
      privacy: {
        title: 'Konfidentsialnost',
        subtitle: 'Uvedomleniya, zaprosy, sroki hraneniya i subpodryadchiki. Eto operatsionnaya konsol, a ne yuridicheskaya sertifikatsiya.',
        tabs: {
          requests: 'Zaprosy',
          notices: 'Uvedomleniya',
          settings: 'Nastroyki',
          subprocessors: 'Subpodryadchiki',
          incidents: 'Incidenty',
          holds: 'Zablokirovannye obekty'
        },
        loadFailed: 'Ne udalos zagruzit dannye o konfidentsialnosti.',
        actionFailed: 'Operatsiya ne udalas.',
        allStatuses: 'Vse statusy',
        subject: 'Subekt',
        type: 'Tip',
        status: 'Status',
        due: 'Srok',
        actionsLabel: 'Deystviya',
        export: 'Eksport',
        erase: 'Udalit',
        globalAuthorityNotice: 'Сотрудники учреждения могут рассматривать его запросы. Экспорт и удаление всего аккаунта доступны только активному суперадминистратору.',
        eraseReviewTitle: 'Проверить запрос на удаление',
        executionRecords: 'Записи выполнения удаления',
        downloadExport: 'Скачать экспорт',
        requestAccountErasure: 'Запросить удаление',
        accountErasurePrompt: 'Подать запрос на рассмотрение удаления {{name}} ({{email}})? Аккаунт останется без изменений до проверки и разрешённого выполнения в центре конфиденциальности.',
        accountErasureRequested: 'Запрос {{id}} зарегистрирован в центре конфиденциальности. Аккаунт не удалён.',
        refreshRecords: 'Обновить записи',
        continueCleanup: 'Продолжить сохранённую очистку',
        cleanupReviewTitle: 'Проверить сохранённую очистку',
        cleanupReviewBody: 'Удаление данных уже зафиксировано. Продолжается только сохранённая очистка файлов, до 25 файлов за действие. Аккаунт не удаляется и не обезличивается повторно. Изменённые, общие файлы и файлы с неподтверждённым владельцем остаются заблокированными для проверки.',
        cleanupSummary: 'Удаление данных зафиксировано. Файлов в ожидании: {{pending}}; заблокировано: {{blocked}}.',
        cleanupStillPending: 'Пакет очистки обработан. Некоторые файлы требуют проверки или ещё одного пакета.',
        cleanupCompleted: 'Сохранённая очистка файлов и запись выполнения завершены.',
        eraseReviewBody: 'Проверьте личность, полномочия, правила хранения и ограничения. Действие затрагивает аккаунт во всех учреждениях. История оценок может сохраняться в обезличенном виде. После прерывания выполнения необходима сверка перед дальнейшими действиями.',
        cancelReview: 'Отмена',
        confirmErase: 'Подтвердить проверенное удаление',
        reconciliationNeeded: 'Выполнение начато; проверьте его запись перед дальнейшими действиями.',
        reject: 'Otklonit',
        noRequests: 'Zaprosov poka net.',
        published: 'Opublikovano',
        draft: 'Chernovik',
        selectNotice: 'Vyberite uvedomlenie dlya redaktirovaniya.',
        saveDraft: 'Sohranit chernovik',
        publish: 'Opublikovat',
        preview: 'Predprosmot',
        acknowledgements: 'Podtverzhdeniya',
        loading: 'Zagruzka...',
        evidenceDays: 'Srok hraneniya dokazatelstv (dni)',
        exportDays: 'Srok hraneniya eksportov (dni)',
        exportLineageNote: 'Novyy eksport vklyuchaet sobstvennyy tekst sobesedovaniya, chlenstvo v uchrezhdenii i metadannye hodov, otvetov, podskazok, zadach provaydera i chisla materialov. On ne vklyuchaet chastnye prompty, tekst istochnikov, klyuchi, heshi, dannye provaydera i neuchtennye kopii. Eto ne polnyy paket dannyh.',
        lawfulBasis: 'Pravovaya osnova proktoringa (metka)',
        dpoEmail: 'Email DPO',
        doNotSell: 'My ne prodaem personalnye dannye',
        requireAck: 'Trebovat podtverzhdenie posle vhoda',
        eraseMode: 'Rezhim udaleniya po umolchaniyu',
        transferNote: 'Primechanie o transgranichnoy peredache',
        saved: 'Nastroyki sohraneny.',
        saving: 'Sohranenie...',
        save: 'Sohranit',
        subprocessorName: 'Imya',
        add: 'Dobavit',
        remove: 'Udalit',
        incidentTitle: 'Nazvanie incidenta'
      },
      materialLibrary: {
        title: 'Biblioteka materialov',
        subtitle: 'Organizuyte povtorno ispolzuemye fayly po sektsiyam i kategoriyam.',
        metrics: {
          totalMaterials: 'Vsego materialov',
          sections: 'Sektsii',
          filtered: 'Otfiltrovano',
          withExtractedText: 'S izvlechonnym tekstom'
        },
        sections: {
          title: 'Sektsii materialov',
          none: 'Sektsii eshche ne sozdany.',
          noDescription: 'Opisanie ne ukazano',
          materialCount_one: '{{count}} material',
          materialCount_few: '{{count}} materiala',
          materialCount_many: '{{count}} materialov',
          materialCount_other: '{{count}} materialov',
          createCta: 'Sozdat sektsiyu'
        },
        labels: {
          selectedSection: 'Vybrannaya sektsiya',
          section: 'Sektsiya',
          category: 'Kategoriya',
          search: 'Poisk',
          yes: 'Da',
          no: 'Net',
          linkedInterviews: 'Svyazannye sobesedovaniya',
          selectedCount: 'Vybrano: {{count}}',
          displayName: 'Otobrazhaemoe imya',
          description: 'Opisanie'
        },
        filters: {
          allSections: 'Vse sektsii',
          unassigned: 'Bez sektsii',
          allCategories: 'Vse kategorii',
          searchPlaceholder: 'Poisk po imeni ili opisaniyu',
          showing: 'Pokazano: {{name}}'
        },
        actions: {
          refresh: 'Obnovit',
          refreshing: 'Obnovlenie...',
          createSection: 'Sozdat sektsiyu',
          uploadMaterials: 'Zagruzit materialy',
          edit: 'Izmenit',
          delete: 'Udalit',
          download: 'Skachat',
          deleting: 'Udalenie...',
          clearFilters: 'Sbrocit filtry',
          cancel: 'Otmena',
          save: 'Sokhranit',
          saving: 'Sokhranenie...',
          viewText: 'Pokazat tekst',
          reextract: 'Izvlech snova',
          reextracting: 'Izvlechenie...',
          apply: 'Primenit',
          selectAll: 'Vybrat vse',
          bulkAssignSection: 'Naznachit sektsiyu',
          bulkSetCategory: 'Zadat kategoriyu',
          bulkDelete: 'Udalit vybrannye'
        },
        materials: {
          title: 'Materialy',
          loading: 'Zagruzka materialov...',
          noneTitle: 'Materialy ne naydeny',
          noneSubtitle: 'Zagruzite fayly ili izmenite filtry.',
          noDescription: 'Net opisaniya',
          unassigned: 'Bez sektsii',
          general: 'Obshchee'
        },
        table: {
          name: 'Imya',
          section: 'Sektsiya',
          category: 'Kategoriya',
          size: 'Razmer',
          extracted: 'Izvlecheno',
          uploaded: 'Zagruzheno',
          linked: 'Svyazano',
          actions: 'Deystviya'
        },
        uploadForm: {
          section: 'Sektsiya',
          noSection: 'Bez sektsii',
          category: 'Kategoriya',
          categoryPlaceholder: 'napr., Brief otsenki',
          description: 'Opisanie',
          files: 'Fayly',
          clickToSelect: 'Nazhmite, chtoby vybrat fayly',
          orDragDrop: 'ili peretashchite',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (maks. 20 MB, 5 faylov)',
          filesSelected: 'Vybrano faylov: {{count}}',
          remainingSlots: 'Ostalos: {{count}}',
          removeFile: 'Udalit {{name}}',
          cancel: 'Otmena',
          upload: 'Zagruzit',
          uploading: 'Zagruzka...',
          selectAtLeastOne: 'Vyberite khotya by odin fayl.',
          tooManyFiles: 'Slishkom mnogo faylov. Maksimum 5 za zagruzku.',
          fileTooLarge: '{{name}} slishkom bolshoy. Maksimum 20 MB.',
          unsupportedType: 'Nepodderzhivaemyy tip fayla: {{name}}'
        },
        sectionForm: {
          createTitle: 'Sozdat sektsiyu materialov',
          editTitle: 'Izmenit sektsiyu',
          createSubtitle: 'Organizuyte povtorno ispolzuemye materialy po sektsiyam.',
          editSubtitle: 'Obnovite sektsiyu dlya luchshey organizatsii.',
          name: 'Nazvanie sektsii',
          namePlaceholder: 'napr., Struktury dannykh',
          description: 'Opisanie (neobyazatelno)',
          descriptionPlaceholder: 'Dobavte kontekst dlya etoy sektsii',
          nameRequired: 'Nazvanie sektsii obyazatelno.',
          cancel: 'Otmena',
          create: 'Sozdat sektsiyu',
          creating: 'Sozdanie...',
          save: 'Sokhranit',
          saving: 'Sokhranenie...'
        },
        editForm: {
          title: 'Izmenit material',
          subtitle: 'Obnovite metadannye biblioteki. Fayl na diske ne zamenyaetsya.',
          displayNameRequired: 'Otobrazhaemoe imya obyazatelno.'
        },
        extracted: {
          title: 'Izvlechennyy tekst',
          loading: 'Zagruzka izvlechonnogo teksta...',
          empty: 'Iz etogo fayla tekst ne izvlechen.',
          truncated: 'Izvlechenie obrezano iz-za prevysheniya limita.',
          charCount: '{{count}} simvolov',
          loadFailed: 'Ne udalos zagruzit izvlechennyy tekst.'
        },
        usage: {
          none: 'Ne svyazan ni s odnim sobesedovaniem.',
          count_one: 'Svyazan s {{count}} sobesedovaniem.',
          count_few: 'Svyazan s {{count}} sobesedovaniyami.',
          count_many: 'Svyazan s {{count}} sobesedovaniyami.',
          count_other: 'Svyazan s {{count}} sobesedovaniyami.',
          unlinkWarning: 'Udalenie iz biblioteki takzhe otvyazhet fayl ot etikh sobesedovaniy.',
          andMore: 'i eshche {{count}}'
        },
        bulk: {
          categoryPlaceholder: 'Kategoriya',
          deleteTitle: 'Udalit vybrannye materialy',
          deletePrompt: 'Udalit {{count}} material(ov)? Takzhe budet otvyazano {{linked}} svyazey s sobesedovaniyami.',
          noneSelected: 'Vyberite khotya by odin material.',
          applied: 'Obnovleno materialov: {{count}}.',
          deleted: 'Udaleno materialov: {{count}}.',
          partialDelete: 'Udaleno {{deleted}}. Ne udalos: {{failed}}.'
        },
        modals: {
          createSectionTitle: 'Sozdat sektsiyu materialov',
          editSectionTitle: 'Izmenit sektsiyu',
          uploadMaterialsTitle: 'Zagruzit materialy v biblioteku',
          editMaterialTitle: 'Izmenit material',
          extractedTextTitle: 'Izvlechennyy tekst',
          deleteMaterialTitle: 'Udalit material',
          deleteMaterialPrompt: 'Udalit {{name}} iz biblioteki?',
          deleteSectionTitle: 'Udalit sektsiyu',
          deleteSectionPrompt: 'Udalit sektsiyu {{name}}? Materialy ostanutsya v biblioteke bez sektsii.'
        },
        messages: {
          loadSectionsFailed: 'Ne udalos zagruzit sektsii.',
          loadMaterialsFailed: 'Ne udalos zagruzit materialy.',
          materialDeleted: 'Material udalen.',
          deleteMaterialFailed: 'Ne udalos udalit material.',
          sectionDeleted: 'Sektsiya udalena.',
          deleteSectionFailed: 'Ne udalos udalit sektsiyu.',
          refreshed: 'Biblioteka obnovlena.',
          sectionCreated: 'Sektsiya sozdana.',
          sectionUpdated: 'Sektsiya obnovlena.',
          materialsUploaded: 'Materialy zagruzheny.',
          sectionCreateFailed: 'Ne udalos sozdat sektsiyu.',
          sectionUpdateFailed: 'Ne udalos obnovit sektsiyu.',
          materialUpdated: 'Material obnovlen.',
          updateMaterialFailed: 'Ne udalos obnovit material.',
          reextractSuccess: 'Tekst izvlechen povtorno.',
          reextractFailed: 'Ne udalos izvlech tekst.',
          bulkUpdateFailed: 'Ne udalos obnovit vybrannye materialy.',
          bulkDeleteFailed: 'Ne udalos udalit vybrannye materialy.'
        }
      }
    },
    candidate: {
      secureCodeEditor: {
        messages: {
          executionUnavailable: 'Выполнение кода сейчас недоступно. Вы можете продолжить редактирование и отправить свой код.',
          executeFailed: 'Не удалось выполнить код. Попробуйте ещё раз.'
        }
      },
      roomCamera: {
        title: 'Kamera komnaty (telefon, vid sboku)',
        hint: 'Postavte telefon sboku, chtoby bylo vidno stol, ruki i pol. Otkrojte ssylku na telefone. Na etom kompyutere vy ostaetes v sisteme.',
        status: 'Status: {{status}}',
        skip: 'Prodolzhit bez kamery komnaty',
        pill: 'Kamera komnaty',
        urlLabel: 'Ili otkrojte etu ssylku na telefone',
        systemCheck: 'Dlya etoj proverki nuzhna vtoraya kamera: telefon sboku, chtoby bylo vidno stol, ruki i pol. Sopryazhenie posle blokirovki, bez vhoda na telefone.',
        phoneTitle: 'Kamera komnaty',
        phonePurpose: 'Eta stranica pokazyvaet stol, ruki i pol nablyudatelyu. Voprosov ekzamena zdes net. Ne zakryvajte stranicu.',
        placeSide: 'Postavte telefon sboku stola, primerno v 1–2 metrah, gorizontalno.',
        placeShows: 'V kadre dolzhny byt tors, ruki, stol i pol pod stolom. Lico ne obyazano zapolnyat kadr.',
        checklist: 'Proverka polozheniya',
        checkDesk: 'Vidny stol i moi ruki',
        checkFloor: 'Viden pol pod stolom',
        checkSide: 'Eto vid sboku, a ne krupnyj plan lica',
        start: 'Zapustit kameru komnaty',
        streaming: 'Translyaciya',
        paused: 'Pauza, poka stranica skryta',
        missingToken: 'V ssylke net tokena. Snova otkrojte QR na kompyutere.',
        permission: 'Nuzhen dostup k kamere',
        frameRejected: 'Kadr otklonen ({{status}})',
        switchToFront: 'Perekluchit na perednyuyu kameru',
        switchToBack: 'Perekluchit na zadnyuyu kameru',
        otherCameraUnavailable: 'Drugaya kamera nedostupna.',
        connectedCheck: 'Kamera komnaty podklyuchena. Proverte izobrazhenie, zatem prodolzhite.',
        confirmView: 'Etot vid vernyi — prodolzhit',
        hidePreview: 'Skryt kameru komnaty',
        showPreview: 'Pokazat kameru komnaty',
        pictureWaiting: 'Zhivoe izobrazhenie ozhidaet.',
        picturePending: 'Telefon podklyuchen, no izobrazhenie eshche ne prishlo.',
        previewLabel: 'Kamera komnaty'
      },
      privacy: {
        title: 'Konfidentsialnost'
      },
      help: {
        title: 'Spravka',
        subtitle: 'Rukovodstva dlya portala kandidata',
        back: 'Nazad',
        loading: 'Zagruzka rukovodstv...',
        loadFailed: 'Ne udalos zagruzit rukovodstva.',
        emptyTitle: 'Rukovodstv poka net',
        emptySubtitle: 'Opublikovannye rukovodstva kandidata poyavyatsya zdes.',
        guides: 'Rukovodstva',
        noSteps: 'V etom rukovodstve eshche net shagov.',
        selectGuide: 'Vyberite rukovodstvo, chtoby prochitat ego.'
      },
      overview: {
        header: {
          help: 'Spravka'
        }
      },
      portal: {
        myAccount: 'Moy akkaunt',
        description: 'Upravlyayte dannymi profilya i nastroykami bezopasnosti akkaunta.',
        backLabel: 'Nazad v portal'
      },
      voiceInterview: {
        chrome: {
          reviewEdited: 'Izmeneno',
          reviewCountdown: 'Ostalos {{seconds}} s',
          reviewHint: 'Ispravte oshibki raspoznavaniya i otpravte. Ctrl+Enter otpravlyaet. Limi {{count}} simvolov. Taymer ustnoy chasti ne ostanavlivaetsya.',
          reviewTooShort: 'Dobavte ne menshe {{count}} slov ili zapishite snova.',
          reviewStale: 'Chernovik otnosilsya k predydushchemu voprosu i byl udalen. Otvette na vopros na ekrane.',
          reviewRestored: 'Neotpravlennaya pravka udalena pri vozobnovlenii. Otvette na vopros na ekrane.',
          showRecognised: 'Tekst, raspoznannyy mikrofonom',
          hideRecognised: 'Skryt raspoznannyy tekst',
          restoreOriginal: 'Vernut original',
          rerecord: 'Zapisat snova'
        }
      }
    },
    invigilator: {
      nav: {
        liveMonitoring: 'Monitorинг v realnom vremeni',
        reportHistory: 'Istoriya otchetov',
        accountSettings: 'Nastroyki akkaunta',
        help: 'Spravka',
        helpDescription: 'Rukovodstva dlya instrumentov nablyudatelya'
      },
      help: {
        title: 'Spravka',
        subtitle: 'Rukovodstva dlya instrumentov nablyudatelya',
        back: 'Nazad',
        loading: 'Zagruzka rukovodstv...',
        loadFailed: 'Ne udalos zagruzit rukovodstva.',
        emptyTitle: 'Rukovodstv poka net',
        emptySubtitle: 'Opublikovannye rukovodstva nablyudatelya poyavyatsya zdes.',
        guides: 'Rukovodstva',
        noSteps: 'V etom rukovodstve eshche net shagov.',
        selectGuide: 'Vyberite rukovodstvo, chtoby prochitat ego.'
      }
    },
    interviews: {
      status: {
        inProgress: 'V protsesse',
        completed: 'Zaversheno',
        pending: 'Ozhidaet'
      }
    },
    reports: {
      generatedOn: 'Sformirovano: {{date}}'
    },
    notifications: {
      newNotification: 'U vas novoe uvedomlenie.'
    },
    mainSite: {
      experienceFallback: {
        product: {
          title: 'Obzor produkta',
          summary: 'Izuchite platformu VivaAssess i vozmozhnosti dlya nadezhnyh resheniy po sobesedovaniyam.'
        },
        solutions: {
          title: 'Resheniya',
          summary: 'Naydite resheniya VivaAssess dlya komand nayma i obucheniya.'
        },
        solutionsHrLeaders: {
          title: 'Resheniya dlya HR rukovoditeley',
          summary: 'Povyshayte posledovatelnost resheniy i doverie k upravleniyu HR-processami.'
        },
        solutionsRecruiters: {
          title: 'Resheniya dlya rekruterov',
          summary: 'Snizte trenie v voronke i uskor te dvizhenie kandidatov.'
        },
        solutionsEducationTraining: {
          title: 'Resheniya dlya komand obrazovaniya i obucheniya',
          summary: 'Masshtabiruyte processy sobesedovaniy s kontrolom kachestva.'
        },
        pricing: {
          title: 'Tarify',
          summary: 'Vyberite plan VivaAssess pod vash etap i temp rosta.'
        },
        securityTrustCenter: {
          title: 'Centr bezopasnosti i doveriya',
          summary: 'Prover te bezopasnost, konfidentsialnost, AI-governance i nadezhnost.'
        },
        caseStudies: {
          title: 'Keisy',
          summary: 'Posmotrite, kak komandy transformirovali interview-processy s VivaAssess.'
        },
        compare: {
          title: 'Sravnit VivaAssess',
          summary: 'Sravnite VivaAssess s ruchnymi processami, klassicheskimi instrumentami i obshchimi AI-assistentami.'
        },
        compareManualProcess: {
          title: 'VivaAssess protiv ruchnogo processa',
          summary: 'Uznayte, pochemu strukturirovannye workflow prevoshodyat ruchnuyu organizatsiyu sobesedovaniy.'
        },
        compareTraditionalTools: {
          title: 'VivaAssess protiv traditsionnyh instrumentov',
          summary: 'Uvid te raznitsu mezhdu legacy-instrumentami i VivaAssess.'
        },
        compareGenericAiAssistant: {
          title: 'VivaAssess protiv obshchih workflow AI-assistenta',
          summary: 'Uznaite, pochemu otraslevaya interview-analitika daet bolee silny rezultat.'
        }
      }
    },
    errors: {
      actions: {
        goBack: 'Nazad',
        goHome: 'Na glavnuyu',
        goToLogin: 'K vhodu',
        retry: 'Povtorit'
      },
      requestIdLabel: 'Identifikator zaprosa v podderzhku',
      pages: {
        badRequest: {
          title: 'Nekorrektnyy zapros',
          message: 'Zapros ne mozhet byt obrabotan. Proverte dannye i poprobuyte snova.'
        },
        unauthorized: {
          title: 'Ne avtorizovan',
          message: 'Vasha sessiya otsutstvuet ili istekla. Vypolnite vhod, chtoby prodolzhit.'
        },
        forbidden: {
          title: 'Dostup zapreshchen',
          message: 'U vas net prav dostupa k etoy stranitse ili resursu.'
        },
        notFound: {
          title: 'Stranitsa ne naydena',
          message: 'Zaprashivaemaya stranitsa ili resurs ne naydeny.'
        },
        internalServerError: {
          title: 'Vnutrennyaya oshibka servera',
          message: 'Na nashey storone proizoshla oshibka. Poprobuyte snova cherez nekotoroe vremya.'
        },
        serviceUnavailable: {
          title: 'Servis nedostupen',
          message: 'Servis vremenno nedostupen. Pozhaluysta, povtorite popitku pozhe.'
        }
      },
      shortcuts: {
        title: 'Stranitsy oshibok',
        description: 'Vyberite predprosmotr stranitsy oshibki.'
      }
    }
  },
  de: {
    common: {
      notificationPanel: notificationPanelLabels.de,
      transcriptExpiry: transcriptExpiryLabels.de,
      blockedObjects: blockedObjectLabels.de,
      authoringGrounding: authoringGroundingLabels.de,
      sessionSave: {"conflict": "Automatisches Speichern wurde gestoppt, weil neuerer Fortschritt oder eine andere Prüfungsphase erfasst wurde. Diesen Tab offen lassen, hier nicht weiter bearbeiten und nur einen Sitzungstab verwenden. Antworten nicht erneut einreichen; vor der Wiederherstellung ungespeicherter Arbeit die Administration kontaktieren.", "unavailable": "Das Speichern des Fortschritts wurde nicht bestätigt. Diesen Tab offen lassen und die Verbindung prüfen. Während der unklaren Wiederherstellung weder neu laden noch einen weiteren Tab öffnen; bei anhaltendem Problem die Administration kontaktieren."},
      completionRepair: {"preview": "Wiederherstellung prüfen", "summary": "Komponente: {{component}}. Verbleibende Versuche: {{count}}. Die Vorschau läuft nach fünf Minuten ab.", "part_a": "Rangfolge für Teil A", "oral": "Mündliche Bewertung", "confirm": "Ich habe den vorübergehenden Fehler untersucht und behoben. Nur die ursprüngliche Aufgabe innerhalb des bestehenden Limits wiederholen; Belege, menschliche Entscheidungen und Freigaberegeln erhalten.", "apply": "Wiederherstellung bestätigen", "loading": "Wiederherstellung wird geprüft…", "unavailable": "Wiederherstellung nicht verfügbar oder weitere Prüfung nötig. Aktive, abgebrochene, ausgeschöpfte, veränderte oder bereits geprüfte Aufgaben können hier nicht neu gestartet werden.", "failed": "Wiederherstellung nicht bestätigt. Dieselbe Bestätigung wiederholen, um den Beleg abzurufen, oder nach Ablauf eine neue Vorschau anfordern. Antworten nicht erneut einreichen.", "accepted": "Wiederherstellung angenommen und protokolliert. Status nach der Verarbeitung aktualisieren. Bestehende Freigaberegeln gelten weiter.", "receipt": "Auditbeleg"},
      completionReview: {"title": "Verarbeitung abgeschlossener Sitzungen prüfen", "note": "Abgeschlossene Sitzungen mit fehlenden oder mehrfachen Bewertungen, gestoppter oder nicht erfasster Verarbeitung. Für Änderungen aktualisieren. Wiederherstellung erfordert eine Prüfung und eine zulässige Vorschau; das Lesen startet oder veröffentlicht nichts.", "empty": "Keine passenden abgeschlossenen Sitzungen in dieser Gruppe.", "session": "Sitzung", "candidate": "Kandidat", "interview": "Interview", "assessment": "Bewertung", "available": "Verfügbar", "missing": "Fehlt", "ambiguous": "Mehrere Bewertungen", "not_submitted": "Nicht eingereicht", "more": "Mehr laden"},
      completionStatus: {"title": "Verarbeitung der Abgabe", "refresh": "Status aktualisieren", "loading": "Verarbeitungsstatus wird geprüft…", "unavailable": "Status nicht verfügbar. Aktualisieren Sie oder versuchen Sie es später erneut.", "unknown": "Kein Bewertungsstatus erfasst. Wenden Sie sich bei ausstehenden Ergebnissen an die Administration.", "not_submitted": "Diese Sitzung wurde noch nicht als abgeschlossen angenommen.", "queued": "Ihre Abgabe wurde angenommen. Die Bewertung wartet.", "running": "Ihre Abgabe wurde angenommen. Die Bewertung läuft.", "retrying": "Ihre Abgabe wurde angenommen. Die Bewertung wird automatisch erneut versucht.", "processed": "Die Bewertung wurde verarbeitet.", "needs_attention": "Ihre Abgabe ist erfasst. Die Verarbeitung erfordert administrative Hilfe.", "releaseNote": "Der Verarbeitungsstatus veröffentlicht keine Ergebnisse und ändert keine Prüfentscheidung."},
      app: {
        name: 'VivaAssess',
        tagline: 'Vertrauenswuerdige KI-Interviewplattform'
      },
      skipToMainContent: 'Zum Hauptinhalt springen',
      nav: {
        home: 'Startseite',
        product: 'Produkt',
        solutions: 'Loesungen',
        pricing: 'Preise',
        trust: 'Vertrauen',
        compare: 'Vergleich',
        contact: 'Kontakt',
        resources: 'Ressourcen',
        features: 'Funktionen',
        about: 'Ueber uns',
        teams: 'Teams',
        docs: 'Dokumente',
        caseStudies: 'Fallstudien'
      },
      actions: {
        goToDashboard: 'Zum Dashboard',
        signOut: 'Abmelden',
        close: 'Schliessen',
        expandNavigation: 'Navigation ausklappen',
        collapseNavigation: 'Navigation einklappen',
        backToPortal: 'Zurueck zum Portal',
        help: 'Hilfe'
      },
      labels: {
        language: 'Sprache',
        searchModules: 'Module suchen...',
        pinned: 'Angeheftet',
        allModules: 'Alle Module',
        navigation: 'Navigation',
        noModulesFound: 'Keine Module gefunden',
        noShort: 'Nein'
      },
      roles: {
        adminControl: 'Adminsteuerung',
        invigilator: 'Aufsicht'
      },
      accessibility: {
        goToHomepage: 'Zur VivaAssess Startseite',
        closeNavigationMenu: 'Navigationsmenue schliessen'
      },
      language: {
        english: 'Englisch',
        french: 'Franzoesisch',
        hungarian: 'Ungarisch',
        russian: 'Russisch',
        german: 'Deutsch'
      },
      privacy: {
        title: 'Datenschutz',
        accountIntro: 'Lesen Sie den aktuellen Hinweis, laden Sie eine Kopie Ihrer Daten herunter oder beantragen Sie die Loeschung. Pruefungspunkte koennen anonymisiert behalten werden, wenn die akademische Integritaet das erfordert.',
        loading: 'Datenschutzoptionen werden geladen...',
        loadFailed: 'Datenschutzinformationen konnten nicht geladen werden.',
        actionFailed: 'Die Anfrage konnte nicht abgeschlossen werden.',
        acknowledge: 'Ich habe diesen Hinweis gelesen',
        acknowledged: 'Bestaetigung gespeichert.',
        alreadyAcked: 'Sie haben den aktuellen Hinweis bereits bestaetigt.',
        downloadMyData: 'Meine Daten herunterladen',
        requestDeletion: 'Loeschung beantragen',
        requestDoNotSell: 'Verkaufsverzicht erfassen',
        requestSent: 'Anfrage eingegangen.',
        exportReady: 'Der Download Ihrer Daten hat begonnen.',
        finishSavedExport: 'Gespeicherten Export abschließen',
        freshExport: 'Neuen Export erstellen',
        doNotSellTrue: 'Diese Plattform verkauft keine personenbezogenen Daten und gibt sie nicht fuer kontextuebergreifende Werbung weiter.',
        doNotSellFalse: 'Fragen Sie Ihre Einrichtung, wie personenbezogene Daten weitergegeben werden.',
        loginBanner: 'Nur essenzielle Cookies und Speicherung (Anmeldung, Theme, Sprache).',
        readNotice: 'Datenschutzhinweis lesen',
        ackTitle: 'Datenschutzhinweis',
        noNotice: 'Kein veroeffentlichter Hinweis vorhanden.',
        publicTitle: 'Datenschutzhinweis',
        publicIntro: 'Nur veroeffentlichte Hinweise. Melden Sie sich an, um Ihre Daten herunterzuladen oder die Loeschung zu beantragen.',
        backToLogin: 'Zurueck zur Anmeldung'
      }
    },
    auth: {
      login: {
        welcomeBack: 'Willkommen zurueck',
        subtitle: 'Melden Sie sich an, um Ihren VivaAssess Workflow fortzusetzen',
        emailAddress: 'E-Mail-Adresse',
        password: 'Passwort',
        signIn: 'Anmelden',
        firstTimeTitle: 'Zum ersten Mal hier?',
        firstTimeBody: 'Nutzen Sie Ihre Einladungs-E-Mail, um Ihr Passwort sicher festzulegen.',
        trustedBadge: 'Vertrauenswuerdige KI-Interviews',
        premiumDescription: 'Eine Premium-Interview-Intelligence-Plattform fuer faire und datengetriebene Einstellungsentscheidungen.',
        adaptiveAI: 'Adaptive KI',
        adaptiveAIDescription: 'Dynamische Rueckfragen und kontextbezogenes Nachhaken.',
        biasAware: 'Bias-bewusst',
        biasAwareDescription: 'Objektive, konsistente Bewertung in grossem Umfang.',
        auditReady: 'Audit-ready',
        auditReadyDescription: 'Nachvollziehbarkeit und Governance standardmaessig.',
        interviews: 'Interviews',
        signalAccuracy: 'Signalgenauigkeit',
        organizations: 'Organisationen',
        emailPlaceholder: 'name@organisation.de',
        passwordPlaceholder: 'Passwort eingeben',
        toggleTheme: 'Thema wechseln'
      }
    },
    admin: {
      auditOrigin: auditOriginLabels.de,
      readinessPreview: materialReadinessLabels.de,
      previewHistory: materialPreviewHistoryLabels.de,
      assignmentReadinessBlocked: "Aktivieren Sie vor der Zuweisung das Interview und einen Prüfungsteil und wählen Sie eine aktive Vorlage für eine verknüpfte mündliche Einstufung.",
      privateOpeningPrompt: {
        label: "Eigene m?ndliche Er?ffnung (erweitert)",
        hint: "Optional. Leer verwendet die Standarder?ffnung. Gilt f?r Begr??ung und erste Frage; ?nderungen gelten f?r neue Sitzungen.",
        format: "JSON mit greeting und first_question zur?ckgeben, einschlie?lich question, question_type, difficulty, expected_topics und max_response_time_seconds. Platzhalter:",
      },
      interviewForm: {
        answerReview: {
          title: 'Antwortpruefung Teil B',
          hint: 'Nach der Transkription kann die kandidierende Person den Text korrigieren, bevor der Zug bewertet wird. Auto-Senden bleibt verfuegbar. Der Teil-B-Timer pausiert nicht.',
          allow: 'Korrektur jeder Antwort vor dem Senden erlauben',
          defaultMode: 'Startmodus der Erfassung',
          modeAuto: 'Automatisch nach Stille senden',
          modeReview: 'Pruefen vor dem Senden',
          timeout: 'Pruefzeit (Sekunden, 0 = keine)'
        }
      },
      nav: {
        overview: { label: 'Uebersicht', description: 'Plattformstatus, Metriken und Aktivitaet' },
        interviews: { label: 'Interviews', description: 'Interviewvorlagen erstellen und verwalten' },
        mcqSets: { label: 'MCQ-Manager', description: 'MCQ-Sets entwerfen und organisieren' },
        materials: { label: 'Materialien', description: 'Interviewressourcen hochladen und pflegen' },
        candidates: { label: 'Kandidaten', description: 'Kandidaten einladen, zuweisen und ueberwachen' },
        sections: { label: 'Kandidatengruppen', description: 'Kandidaten für Terminplanung und Placement-Einschreibung bündeln' },
        invigilators: { label: 'Aufsichten', description: 'Aufsichtszuteilungen verwalten' },
        proctoringReports: { label: 'Proctoring-Berichte', description: 'Integritatsbericht-Historie und Screenshots' },
        liveMonitoring: { label: 'Live-Aufsicht', description: 'Aktive Sitzungen mit Echtzeit-Kamera- und Bildschirmfeeds ueberwachen' },
        accounts: { label: 'Konten', description: 'Alle Benutzerkonten verwalten' },
        groups: { label: 'Institutionen und Gruppen', description: 'Mandantenhierarchie und Mitgliedschaften verwalten' },
        myAccount: { label: 'Mein Konto', description: 'Adminprofil und Sicherheit verwalten' },
        llmSettings: { label: 'LLM-Einstellungen', description: 'KI-Anbieter und Modelle konfigurieren' },
        voiceStudio: { label: 'KI Voice Studio', description: 'Interviewstimmen und Overrides verwalten' },
        assessments: { label: 'Bewertungen', description: 'Bewertungsergebnisse pruefen und freigeben' },
        feedback: { label: 'Interview-Feedback', description: 'Kandidatenerlebnis pruefen und Feedbackdaten exportieren' },
        notifications: { label: 'Benachrichtigungen', description: 'Zustellhistorie und Bericht-E-Mail-Verhalten verwalten' },
        systemIssues: { label: 'Systemprobleme', description: 'Warnungen und Vorfaelle verfolgen' },
        resetSettings: { label: 'Einstellungen zuruecksetzen', description: 'Produktstandards fuer LLM, MCQ, Proctoring und Praeferenzen wiederherstellen' },
        auditLogs: { label: 'Audit-Logs', description: 'Administrative Aktivitaetsprotokolle einsehen' },
        help: { label: 'Hilfe', description: 'Rollenspezifische Anleitungen fuer dieses Dashboard' },
        userGuides: { label: 'Benutzeranleitungen', description: 'In-App-Anleitungen und Screenshots erstellen und bearbeiten' },
        privacy: { label: 'Datenschutz', description: 'Hinweise, Betroffenenanfragen und Aufbewahrung' }
      },
      help: {
        title: 'Hilfe',
        subtitle: 'Veroeffentlichte Anleitungen fuer die Bildschirme, die Sie nutzen duerfen',
        back: 'Zurueck',
        loading: 'Anleitungen werden geladen...',
        loadFailed: 'Anleitungen konnten nicht geladen werden.',
        emptyTitle: 'Noch keine Anleitungen',
        emptySubtitle: 'Veroeffentlichte Anleitungen fuer Ihre Rolle erscheinen hier.',
        guides: 'Anleitungen',
        noSteps: 'Diese Anleitung hat noch keine Schritte.',
        selectGuide: 'Waehlen Sie eine Anleitung zum Lesen.'
      },
      userGuides: {
        title: 'Benutzeranleitungen',
        subtitle: 'In-App-Hilfe fuer jede Rolle erstellen, veroeffentlichen und uebersetzen.',
        listTitle: 'Anleitungen',
        loading: 'Anleitungen werden geladen...',
        empty: 'Noch keine Anleitungen',
        selectOrCreate: 'Waehlen Sie eine Anleitung oder erstellen Sie eine.',
        fields: {
          title: 'Titel',
          slug: 'Slug',
          audience: 'Zielgruppe',
          locale: 'Sprache',
          module: 'Modul',
          noModule: 'Kein Modul',
          published: 'Veroeffentlicht',
          summary: 'Kurzbeschreibung'
        },
        audiences: {
          candidate: 'Kandidat',
          invigilator: 'Aufsicht',
          admin: 'Admin',
          super_admin: 'Super-Admin'
        },
        status: {
          published: 'Veroeffentlicht',
          draft: 'Entwurf'
        },
        filters: {
          allAudiences: 'Alle Zielgruppen',
          allLocales: 'Alle Sprachen'
        },
        steps: {
          title: 'Schritte',
          add: 'Schritt hinzufuegen',
          stepNumber: 'Schritt {{count}}',
          titlePlaceholder: 'Schritttitel',
          bodyPlaceholder: 'Erklaerung (Klartext oder einfaches Markdown)',
          uploadImage: 'Screenshot hochladen',
          removeImage: 'Bild entfernen'
        },
        actions: {
          create: 'Anleitung erstellen',
          save: 'Speichern',
          saving: 'Speichern...',
          delete: 'Loeschen',
          cancel: 'Abbrechen',
          duplicate: 'In Sprache kopieren',
          preview: 'Vorschau'
        },
        messages: {
          loadFailed: 'Anleitungen konnten nicht geladen werden.',
          created: 'Anleitung erstellt.',
          saved: 'Anleitung gespeichert.',
          saveFailed: 'Anleitung konnte nicht gespeichert werden.',
          deleted: 'Anleitung geloescht.',
          deleteFailed: 'Anleitung konnte nicht geloescht werden.',
          duplicated: 'Sprachkopie erstellt.',
          duplicateFailed: 'Anleitung konnte nicht dupliziert werden.',
          saveStepsFirst: 'Speichern Sie zuerst die Schritte, dann laden Sie Screenshots hoch.',
          imageUploaded: 'Screenshot hochgeladen.',
          imageFailed: 'Screenshot konnte nicht aktualisiert werden.'
        },
        modals: {
          createTitle: 'Benutzeranleitung erstellen',
          previewTitle: 'Vorschau',
          deletePrompt: 'Diese Anleitung und ihre Screenshots loeschen?'
        }
      },
      privacy: {
        title: 'Datenschutz',
        subtitle: 'Hinweise, Betroffenenanfragen, Aufbewahrung und Auftragsverarbeiter. Betriebskonsole, keine rechtliche Zertifizierung.',
        tabs: {
          requests: 'Anfragen',
          notices: 'Hinweise',
          settings: 'Einstellungen',
          subprocessors: 'Auftragsverarbeiter',
          incidents: 'Vorfaelle',
          holds: 'Blockierte Objekte'
        },
        loadFailed: 'Datenschutzdaten konnten nicht geladen werden.',
        actionFailed: 'Die Datenschutzaktion ist fehlgeschlagen.',
        allStatuses: 'Alle Status',
        subject: 'Betroffene Person',
        type: 'Typ',
        status: 'Status',
        due: 'Faellig',
        actionsLabel: 'Aktionen',
        export: 'Export',
        erase: 'Loeschen',
        globalAuthorityNotice: 'Mitarbeitende können Anträge ihrer Einrichtung prüfen. Export und Löschung des gesamten Kontos erfordern einen aktiven Superadministrator.',
        eraseReviewTitle: 'Löschantrag prüfen',
        executionRecords: 'Ausführungsprotokoll der Löschungen',
        downloadExport: 'Export herunterladen',
        requestAccountErasure: 'Löschung beantragen',
        accountErasurePrompt: 'Löschprüfung für {{name}} ({{email}}) beantragen? Das Konto bleibt bis zur autorisierten Prüfung und Ausführung im Datenschutzcenter unverändert.',
        accountErasureRequested: 'Prüfantrag {{id}} im Datenschutzcenter erfasst. Das Konto wurde nicht gelöscht.',
        refreshRecords: 'Einträge aktualisieren',
        continueCleanup: 'Gespeicherte Bereinigung fortsetzen',
        cleanupReviewTitle: 'Gespeicherte Bereinigung prüfen',
        cleanupReviewBody: 'Die Datenlöschung ist bereits bestätigt. Nur gespeicherte Dateilöschungen werden fortgesetzt, bis zu 25 Dateien je Aktion. Das Konto wird nicht erneut gelöscht oder anonymisiert. Geänderte, gemeinsam genutzte oder nicht eindeutig zugeordnete Dateien bleiben zur Prüfung gesperrt.',
        cleanupSummary: 'Datenlöschung bestätigt. Dateien ausstehend: {{pending}}; gesperrt: {{blocked}}.',
        cleanupStillPending: 'Bereinigung verarbeitet. Einige Dateien erfordern eine Prüfung oder einen weiteren Durchlauf.',
        cleanupCompleted: 'Gespeicherte Dateibereinigung und Ausführungsbeleg sind abgeschlossen.',
        eraseReviewBody: 'Prüfen Sie Identität, Berechtigung, Aufbewahrungsregeln und mögliche Sperren. Die Aktion betrifft das Konto in allen Einrichtungen. Bewertungsdaten können anonymisiert erhalten bleiben. Eine unterbrochene Ausführung muss vor weiteren Aktionen abgeglichen werden.',
        cancelReview: 'Abbrechen',
        confirmErase: 'Geprüfte Löschung bestätigen',
        reconciliationNeeded: 'Ausführung gestartet; prüfen Sie den Eintrag vor weiteren Aktionen.',
        reject: 'Ablehnen',
        noRequests: 'Noch keine Anfragen.',
        published: 'Veroeffentlicht',
        draft: 'Entwurf',
        selectNotice: 'Waehlen Sie einen Hinweis zum Bearbeiten.',
        saveDraft: 'Entwurf speichern',
        publish: 'Veroeffentlichen',
        preview: 'Vorschau',
        acknowledgements: 'Bestaetigungen',
        loading: 'Wird geladen...',
        evidenceDays: 'Aufbewahrung von Belegen (Tage)',
        exportDays: 'Aufbewahrung von Exportdateien (Tage)',
        exportLineageNote: 'Ein neuer Export enthält den eigenen Gesprächstext, die institutionelle Zugehörigkeit und Metadaten zu Zügen, Antworten, Hinweisen, Anbieteraufträgen und Materialanzahl. Private Prompts, Quelltext, Lösungsschlüssel, Prüfsummen, Anbieterinhalte und nicht erfasste Kopien bleiben außen vor. Das ist keine vollständige Datenauskunft.',
        lawfulBasis: 'Rechtsgrundlage Proctoring (Bezeichnung)',
        dpoEmail: 'DPO-E-Mail',
        doNotSell: 'Wir verkaufen keine personenbezogenen Daten',
        requireAck: 'Datenschutzbestaetigung nach der Anmeldung verlangen',
        eraseMode: 'Standard-Loeschmodus',
        transferNote: 'Hinweis zu grenzueberschreitenden Uebermittlungen',
        saved: 'Einstellungen gespeichert.',
        saving: 'Speichern...',
        save: 'Speichern',
        subprocessorName: 'Name',
        add: 'Hinzufuegen',
        remove: 'Entfernen',
        incidentTitle: 'Vorfalltitel'
      },
      materialLibrary: {
        title: 'Materialbibliothek',
        subtitle: 'Wiederverwendbare Dateien nach Abschnitt und Kategorie organisieren.',
        metrics: {
          totalMaterials: 'Materialien gesamt',
          sections: 'Abschnitte',
          filtered: 'Gefiltert',
          withExtractedText: 'Mit extrahiertem Text'
        },
        sections: {
          title: 'Materialabschnitte',
          none: 'Noch keine Abschnitte erstellt.',
          noDescription: 'Keine Beschreibung',
          materialCount_one: '{{count}} Material',
          materialCount_other: '{{count}} Materialien',
          createCta: 'Abschnitt erstellen'
        },
        labels: {
          selectedSection: 'Ausgewaehlter Abschnitt',
          section: 'Abschnitt',
          category: 'Kategorie',
          search: 'Suche',
          yes: 'Ja',
          no: 'Nein',
          linkedInterviews: 'Verknuepfte Interviews',
          selectedCount: '{{count}} ausgewaehlt',
          displayName: 'Anzeigename',
          description: 'Beschreibung'
        },
        filters: {
          allSections: 'Alle Abschnitte',
          unassigned: 'Nicht zugewiesen',
          allCategories: 'Alle Kategorien',
          searchPlaceholder: 'Nach Name oder Beschreibung suchen',
          showing: 'Anzeige: {{name}}'
        },
        actions: {
          refresh: 'Aktualisieren',
          refreshing: 'Aktualisierung...',
          createSection: 'Abschnitt erstellen',
          uploadMaterials: 'Materialien hochladen',
          edit: 'Bearbeiten',
          delete: 'Loeschen',
          download: 'Herunterladen',
          deleting: 'Loeschen...',
          clearFilters: 'Filter zuruecksetzen',
          cancel: 'Abbrechen',
          save: 'Speichern',
          saving: 'Speichern...',
          viewText: 'Text anzeigen',
          reextract: 'Erneut extrahieren',
          reextracting: 'Extrahiere...',
          apply: 'Anwenden',
          selectAll: 'Alle auswaehlen',
          bulkAssignSection: 'Abschnitt zuweisen',
          bulkSetCategory: 'Kategorie setzen',
          bulkDelete: 'Auswahl loeschen'
        },
        materials: {
          title: 'Materialien',
          loading: 'Materialien werden geladen...',
          noneTitle: 'Keine Materialien gefunden',
          noneSubtitle: 'Laden Sie Dateien hoch oder passen Sie die Filter an.',
          noDescription: 'Keine Beschreibung',
          unassigned: 'Nicht zugewiesen',
          general: 'Allgemein'
        },
        table: {
          name: 'Name',
          section: 'Abschnitt',
          category: 'Kategorie',
          size: 'Groesse',
          extracted: 'Extrahiert',
          uploaded: 'Hochgeladen',
          linked: 'Verknuepft',
          actions: 'Aktionen'
        },
        uploadForm: {
          section: 'Abschnitt',
          noSection: 'Kein Abschnitt',
          category: 'Kategorie',
          categoryPlaceholder: 'z. B. Assessment Brief',
          description: 'Beschreibung',
          files: 'Dateien',
          clickToSelect: 'Klicken, um Dateien auszuwaehlen',
          orDragDrop: 'oder per Drag-and-Drop',
          acceptedFormats: 'PDF, DOCX, TXT, MD, PNG, JPG, WEBP (max. 20 MB, 5 Dateien)',
          filesSelected: '{{count}} Datei(en) ausgewaehlt',
          remainingSlots: '{{count}} verbleibend',
          removeFile: '{{name}} entfernen',
          cancel: 'Abbrechen',
          upload: 'Hochladen',
          uploading: 'Hochladen...',
          selectAtLeastOne: 'Waehlen Sie mindestens eine Datei aus.',
          tooManyFiles: 'Zu viele Dateien. Maximal 5 pro Upload.',
          fileTooLarge: '{{name}} ist zu gross. Maximal 20 MB.',
          unsupportedType: 'Nicht unterstuetzter Dateityp: {{name}}'
        },
        sectionForm: {
          createTitle: 'Materialabschnitt erstellen',
          editTitle: 'Abschnitt bearbeiten',
          createSubtitle: 'Organisieren Sie wiederverwendbare Materialien in Abschnitten.',
          editSubtitle: 'Aktualisieren Sie diesen Abschnitt zur besseren Organisation.',
          name: 'Abschnittsname',
          namePlaceholder: 'z. B. Datenstrukturen',
          description: 'Beschreibung (optional)',
          descriptionPlaceholder: 'Kontext fuer diesen Abschnitt hinzufuegen',
          nameRequired: 'Abschnittsname ist erforderlich.',
          cancel: 'Abbrechen',
          create: 'Abschnitt erstellen',
          creating: 'Erstellen...',
          save: 'Speichern',
          saving: 'Speichern...'
        },
        editForm: {
          title: 'Material bearbeiten',
          subtitle: 'Metadaten aktualisieren. Die gespeicherte Datei wird nicht ersetzt.',
          displayNameRequired: 'Anzeigename ist erforderlich.'
        },
        extracted: {
          title: 'Extrahierter Text',
          loading: 'Extrahierter Text wird geladen...',
          empty: 'Aus dieser Datei wurde kein Text extrahiert.',
          truncated: 'Dieser Auszug wurde gekuerzt, weil das Limit ueberschritten wurde.',
          charCount: '{{count}} Zeichen',
          loadFailed: 'Extrahierter Text konnte nicht geladen werden.'
        },
        usage: {
          none: 'Mit keinem Interview verknuepft.',
          count_one: 'Mit {{count}} Interview verknuepft.',
          count_other: 'Mit {{count}} Interviews verknuepft.',
          unlinkWarning: 'Das Loeschen aus der Bibliothek entfernt die Datei auch aus diesen Interviews.',
          andMore: 'und {{count}} weitere'
        },
        bulk: {
          categoryPlaceholder: 'Kategorie',
          deleteTitle: 'Ausgewaehlte Materialien loeschen',
          deletePrompt: '{{count}} Material(ien) loeschen? Dadurch werden auch {{linked}} Interview-Verknuepfung(en) entfernt.',
          noneSelected: 'Waehlen Sie mindestens ein Material aus.',
          applied: '{{count}} Material(ien) aktualisiert.',
          deleted: '{{count}} Material(ien) geloescht.',
          partialDelete: '{{deleted}} geloescht. {{failed}} fehlgeschlagen.'
        },
        modals: {
          createSectionTitle: 'Materialabschnitt erstellen',
          editSectionTitle: 'Abschnitt bearbeiten',
          uploadMaterialsTitle: 'Materialien in die Bibliothek laden',
          editMaterialTitle: 'Material bearbeiten',
          extractedTextTitle: 'Extrahierter Text',
          deleteMaterialTitle: 'Material loeschen',
          deleteMaterialPrompt: '{{name}} aus der Bibliothek loeschen?',
          deleteSectionTitle: 'Abschnitt loeschen',
          deleteSectionPrompt: 'Abschnitt {{name}} loeschen? Zugehoerige Materialien bleiben in der Bibliothek und werden nicht zugewiesen.'
        },
        messages: {
          loadSectionsFailed: 'Abschnitte konnten nicht geladen werden.',
          loadMaterialsFailed: 'Materialien konnten nicht geladen werden.',
          materialDeleted: 'Material geloescht.',
          deleteMaterialFailed: 'Material konnte nicht geloescht werden.',
          sectionDeleted: 'Abschnitt geloescht.',
          deleteSectionFailed: 'Abschnitt konnte nicht geloescht werden.',
          refreshed: 'Bibliothek aktualisiert.',
          sectionCreated: 'Abschnitt erstellt.',
          sectionUpdated: 'Abschnitt aktualisiert.',
          materialsUploaded: 'Materialien hochgeladen.',
          sectionCreateFailed: 'Abschnitt konnte nicht erstellt werden.',
          sectionUpdateFailed: 'Abschnitt konnte nicht aktualisiert werden.',
          materialUpdated: 'Material aktualisiert.',
          updateMaterialFailed: 'Material konnte nicht aktualisiert werden.',
          reextractSuccess: 'Text erneut extrahiert.',
          reextractFailed: 'Text konnte nicht erneut extrahiert werden.',
          bulkUpdateFailed: 'Auswahl konnte nicht aktualisiert werden.',
          bulkDeleteFailed: 'Auswahl konnte nicht geloescht werden.'
        }
      }
    },
    candidate: {
      secureCodeEditor: {
        messages: {
          executionUnavailable: 'Die Codeausführung ist derzeit nicht verfügbar. Sie können Ihren Code weiter bearbeiten und einreichen.',
          executeFailed: 'Der Code konnte nicht ausgeführt werden. Bitte versuchen Sie es erneut.'
        }
      },
      roomCamera: {
        title: 'Raumkamera (Telefon, Seitenansicht)',
        hint: 'Stellen Sie das Telefon seitlich so auf, dass Tisch, Hände und Boden zu sehen sind. Öffnen Sie den Link auf dem Telefon. An diesem Computer bleiben Sie angemeldet.',
        status: 'Status: {{status}}',
        skip: 'Ohne Raumkamera fortfahren',
        pill: 'Raumkamera',
        urlLabel: 'Oder öffnen Sie diesen Link auf dem Telefon',
        systemCheck: 'Diese Prüfung verlangt eine zweite Kamera: Ihr Telefon seitlich, sodass Tisch, Hände und Boden sichtbar sind. Die Kopplung erfolgt nach der Sperre, ohne Anmeldung auf dem Telefon.',
        phoneTitle: 'Raumkamera',
        phonePurpose: 'Diese Seite zeigt Tisch, Hände und Boden der Aufsicht. Es gibt keine Prüfungsfragen. Lassen Sie die Seite offen.',
        placeSide: 'Stellen Sie das Telefon seitlich am Tisch auf, etwa 1–2 Meter entfernt, im Querformat.',
        placeShows: 'Oberkörper, Hände, Tisch und der Boden unter dem Tisch sollen zu sehen sein. Das Gesicht muss das Bild nicht ausfüllen.',
        checklist: 'Platzierung prüfen',
        checkDesk: 'Tisch und meine Hände sind sichtbar',
        checkFloor: 'Der Boden unter dem Tisch ist sichtbar',
        checkSide: 'Das ist eine Seitenansicht, keine Großaufnahme des Gesichts',
        start: 'Raumkamera starten',
        streaming: 'Übertragung läuft',
        paused: 'Pausiert, solange die Seite im Hintergrund ist',
        missingToken: 'Diesem Link fehlt das Kopplungszeichen. Scannen Sie den QR-Code am Prüfungscomputer erneut.',
        permission: 'Kamerazugriff ist erforderlich',
        frameRejected: 'Bild abgelehnt ({{status}})',
        switchToFront: 'Zur Frontkamera wechseln',
        switchToBack: 'Zur Rückkamera wechseln',
        otherCameraUnavailable: 'Die andere Kamera ist nicht verfügbar.',
        connectedCheck: 'Die Raumkamera ist verbunden. Prüfen Sie das Bild, dann weiter.',
        confirmView: 'Diese Ansicht stimmt — weiter',
        hidePreview: 'Raumkamera ausblenden',
        showPreview: 'Raumkamera einblenden',
        pictureWaiting: 'Ein Live-Bild wartet.',
        picturePending: 'Das Telefon ist verbunden, aber das Bild ist noch nicht da.',
        previewLabel: 'Raumkamera'
      },
      privacy: {
        title: 'Datenschutz'
      },
      help: {
        title: 'Hilfe',
        subtitle: 'Anleitungen fuer das Kandidatenportal',
        back: 'Zurueck',
        loading: 'Anleitungen werden geladen...',
        loadFailed: 'Anleitungen konnten nicht geladen werden.',
        emptyTitle: 'Noch keine Anleitungen',
        emptySubtitle: 'Veroeffentlichte Kandidatenanleitungen erscheinen hier.',
        guides: 'Anleitungen',
        noSteps: 'Diese Anleitung hat noch keine Schritte.',
        selectGuide: 'Waehlen Sie eine Anleitung zum Lesen.'
      },
      overview: {
        header: {
          help: 'Hilfe'
        }
      },
      portal: {
        myAccount: 'Mein Konto',
        description: 'Verwalten Sie Ihre Profildaten und Kontosicherheitseinstellungen.',
        backLabel: 'Zurueck zum Portal'
      },
      voiceInterview: {
        chrome: {
          reviewEdited: 'Bearbeitet',
          reviewCountdown: 'Noch {{seconds}} s',
          reviewHint: 'Korrigieren Sie Erkennungsfehler und senden Sie dann. Strg+Eingabe sendet. Limit {{count}} Zeichen. Der muendliche Timer laeuft weiter.',
          reviewTooShort: 'Fuegen Sie mindestens {{count}} Woerter hinzu oder nehmen Sie neu auf.',
          reviewStale: 'Der Entwurf gehoerte zu einer frueheren Frage und wurde geloescht. Beantworten Sie die Frage auf dem Bildschirm.',
          reviewRestored: 'Eine nicht gesendete Korrektur wurde beim Fortsetzen geloescht. Beantworten Sie die Frage auf dem Bildschirm.',
          showRecognised: 'Vom Mikrofon erkannt',
          hideRecognised: 'Erkannten Text ausblenden',
          restoreOriginal: 'Original wiederherstellen',
          rerecord: 'Neu aufnehmen'
        }
      }
    },
    invigilator: {
      nav: {
        liveMonitoring: 'Live-Ueberwachung',
        reportHistory: 'Berichtshistorie',
        accountSettings: 'Kontoeinstellungen',
        help: 'Hilfe',
        helpDescription: 'Anleitungen fuer Aufsichtswerkzeuge'
      },
      help: {
        title: 'Hilfe',
        subtitle: 'Anleitungen fuer Aufsichtswerkzeuge',
        back: 'Zurueck',
        loading: 'Anleitungen werden geladen...',
        loadFailed: 'Anleitungen konnten nicht geladen werden.',
        emptyTitle: 'Noch keine Anleitungen',
        emptySubtitle: 'Veroeffentlichte Aufsichtsanleitungen erscheinen hier.',
        guides: 'Anleitungen',
        noSteps: 'Diese Anleitung hat noch keine Schritte.',
        selectGuide: 'Waehlen Sie eine Anleitung zum Lesen.'
      }
    },
    interviews: {
      status: {
        inProgress: 'In Bearbeitung',
        completed: 'Abgeschlossen',
        pending: 'Ausstehend'
      }
    },
    reports: {
      generatedOn: 'Erstellt am {{date}}'
    },
    notifications: {
      newNotification: 'Sie haben eine neue Benachrichtigung.'
    },
    mainSite: {
      experienceFallback: {
        product: {
          title: 'Produktuebersicht',
          summary: 'Entdecken Sie die VivaAssess Plattform und ihre Faehigkeiten fuer verlaessliche Interviewentscheidungen.'
        },
        solutions: {
          title: 'Loesungen',
          summary: 'Finden Sie VivaAssess Loesungen fuer Hiring-, Recruiting- und Learning-Teams.'
        },
        solutionsHrLeaders: {
          title: 'Loesungen fuer HR-Leitungen',
          summary: 'Erhoehen Sie Entscheidungskonsistenz und Governance-Vertrauen in HR-Organisationen.'
        },
        solutionsRecruiters: {
          title: 'Loesungen fuer Recruiter',
          summary: 'Reduzieren Sie Pipeline-Reibung und beschleunigen Sie den Kandidatenfortschritt.'
        },
        solutionsEducationTraining: {
          title: 'Loesungen fuer Bildungs- und Trainingsteams',
          summary: 'Skalieren Sie Interview- und muendliche Bewertungsprozesse mit Qualitaetskontrollen.'
        },
        pricing: {
          title: 'Preise',
          summary: 'Waehlen Sie einen VivaAssess Plan, der zu Ihrer Phase und Ihrem Wachstum passt.'
        },
        securityTrustCenter: {
          title: 'Sicherheits- und Trust-Center',
          summary: 'Pruefen Sie Sicherheits-, Datenschutz-, KI-Governance- und Zuverlaessigkeitsstandards.'
        },
        caseStudies: {
          title: 'Fallstudien',
          summary: 'Sehen Sie, wie Teams ihre Interviewprozesse mit VivaAssess transformiert haben.'
        },
        compare: {
          title: 'VivaAssess vergleichen',
          summary: 'Vergleichen Sie VivaAssess mit manuellen Prozessen, traditionellen Tools und generischen KI-Assistenten.'
        },
        compareManualProcess: {
          title: 'VivaAssess vs manueller Prozess',
          summary: 'Verstehen Sie, warum strukturierte Workflows manuelle Interviewablaeufe uebertreffen.'
        },
        compareTraditionalTools: {
          title: 'VivaAssess vs traditionelle Tools',
          summary: 'Sehen Sie den Unterschied zwischen Legacy-Interviewtools und VivaAssess.'
        },
        compareGenericAiAssistant: {
          title: 'VivaAssess vs generische KI-Assistenten-Workflows',
          summary: 'Entdecken Sie, warum domain-spezifische Interview-Intelligenz bessere Ergebnisse liefert.'
        }
      }
    },
    errors: {
      actions: {
        goBack: 'Zurueck',
        goHome: 'Startseite',
        goToLogin: 'Zum Login',
        retry: 'Erneut versuchen'
      },
      requestIdLabel: 'Support-Anfrage-ID',
      pages: {
        badRequest: {
          title: 'Ungueltige Anfrage',
          message: 'Die Anfrage konnte nicht verarbeitet werden. Bitte pruefen Sie Ihre Eingaben und versuchen Sie es erneut.'
        },
        unauthorized: {
          title: 'Nicht autorisiert',
          message: 'Ihre Sitzung fehlt oder ist abgelaufen. Bitte melden Sie sich erneut an.'
        },
        forbidden: {
          title: 'Verboten',
          message: 'Sie haben keine Berechtigung fuer diese Seite oder Ressource.'
        },
        notFound: {
          title: 'Seite nicht gefunden',
          message: 'Die angeforderte Seite oder Ressource wurde nicht gefunden.'
        },
        internalServerError: {
          title: 'Interner Serverfehler',
          message: 'Auf unserer Seite ist ein Fehler aufgetreten. Bitte versuchen Sie es in kurzer Zeit erneut.'
        },
        serviceUnavailable: {
          title: 'Dienst nicht verfuegbar',
          message: 'Der Dienst ist voruebergehend nicht verfuegbar. Bitte versuchen Sie es bald erneut.'
        }
      },
      shortcuts: {
        title: 'Fehlerseiten',
        description: 'Waehlen Sie eine Vorschau fuer eine Fehlerseite aus.'
      }
    }
  }
} as const;
